<template>
  <div
    class="ai-assistant-root"
    :class="{
      'ai-assistant-root--chrome-hidden': chromeHidden,
      'ai-assistant-root--embedded': embeddedLayout,
    }"
  >
  <!-- 收起时仅显示可拖动悬浮「AI」按钮，点击后恢复面板 -->
  <button
    v-if="showPanel && isCollapsed && !embeddedLayout"
    type="button"
    class="ai-float-btn"
    :class="{
      'ai-float-btn--dragging': floatBtnDragging,
      'ai-float-btn--working': floatBtnWorking,
    }"
    :style="floatBtnStyle"
    title="打开一诺（可拖动）"
    @mousedown="onFloatBtnPointerDown"
    @touchstart="onFloatBtnTouchStart"
    @click="onFloatBtnClick"
  >
    AI
  </button>
  <aside
    v-if="showPanel && !isCollapsed"
    class="ai-content-side"
    :class="{ 'is-resize-left-edge': !embeddedLayout && (isOnLeftEdge || isResizingWidth) }"
    :style="embeddedLayout ? { width: '100%' } : { width: aiPanelWidthPx }"
    @mousemove="onAsideMouseMove"
    @mouseleave="onAsideMouseLeave"
    @mousedown="onAsideMouseDown"
  >
    <div class="ai-panel">
      <div
        ref="aiHeaderRef"
        class="ai-header"
        :class="{ 'ai-header--compact-actions': compactHeaderActions }"
      >
        <button
          v-if="!embeddedLayout"
          type="button"
          class="ai-expand-toggle-btn"
          :title="leadingToggleTitle"
          :aria-label="leadingToggleTitle"
          @click="onLeadingPanelToggleClick"
        >
          <span class="ai-expand-toggle-icon" aria-hidden="true"></span>
        </button>
        <div class="ai-header-title-wrap">
          <template v-if="editingSessionTitle">
            <input
              ref="sessionTitleInputRef"
              v-model="editingSessionTitleDraft"
              class="ai-title-input"
              type="text"
              @keydown.enter.prevent="confirmHeaderTitleEdit"
              @keydown.esc.prevent="cancelHeaderTitleEdit"
              @blur="confirmHeaderTitleEdit"
            />
          </template>
          <h3
            v-else
            class="ai-title ai-title--editable"
            title="双击修改标题"
            @dblclick="startHeaderTitleEdit"
          >
            {{ currentSessionTitle }}
          </h3>
        </div>
        <div v-if="!isCollapsed" class="ai-header-right-cluster">
          <div v-if="!compactHeaderActions" class="ai-main-tabs">
            <button
              type="button"
              class="ai-tab-btn"
              :class="{ active: mainTab === 'history', 'has-unread': historyHasUnread }"
              @click="setConversationTab('history')"
            >
              <span class="ai-tab-btn-label">历史会话</span>
              <span v-if="historyHasUnread" class="ai-tab-unread-dot" aria-label="有未读会话" />
            </button>
            <button
              type="button"
              class="ai-tab-btn"
              :class="{ active: mainTab === 'new' }"
              @click="setConversationTab('new')"
            >
              当前会话
            </button>
          </div>
          <div class="ai-session-round-actions">
            <div
              v-if="compactHeaderActions"
              ref="compactHistoryWrapRef"
              class="ai-compact-history-wrap"
            >
              <button
                type="button"
                class="ai-compact-history-btn"
                :class="{ active: compactHistoryOpen, 'has-unread': historyHasUnread }"
                title="历史会话"
                aria-label="历史会话"
                :aria-expanded="compactHistoryOpen"
                @click="toggleCompactHistoryPopover"
              >
                <svg class="ai-history-clock-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
                  />
                  <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M3 3v5h5" />
                  <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M12 7v5l4 2" />
                </svg>
                <span v-if="historyHasUnread" class="ai-tab-unread-dot" aria-label="有未读会话" />
              </button>
            </div>
            <button
              type="button"
              class="ai-new-session-btn"
              title="新建会话（当前对话将保存到历史）"
              @click="handleNewConversation"
            >
              +
            </button>
            <button
              v-if="!embeddedLayout"
              type="button"
              class="ai-collapse-to-float-btn"
              title="收起为悬浮按钮"
              @click="collapseToFloatingButton"
            >
              −
            </button>
          </div>
        </div>
        <div ref="aiHeaderTabsMeasureRef" class="ai-header-tabs-measure" aria-hidden="true">
          <div class="ai-main-tabs">
            <span class="ai-tab-btn">历史会话</span>
            <span class="ai-tab-btn">当前会话</span>
          </div>
        </div>
      </div>

      <Teleport to="body">
        <div
          v-if="compactHistoryOpen"
          ref="compactHistoryPopoverRef"
          class="ai-compact-history-popover"
          :style="compactHistoryPopoverStyle"
          role="dialog"
          aria-label="历史会话"
        >
          <div class="ai-compact-history-popover-title">历史会话</div>
          <div class="ai-compact-history-popover-list">
            <button
              v-for="item in historyList"
              :key="item.id"
              type="button"
              class="ai-compact-history-item"
              :class="{
                active: item.sessionId === currentSessionId || activeHistoryId === item.id,
                'has-unread': isHistorySessionUnread(item),
                'is-running': isHistorySessionRunning(item.sessionId),
              }"
              @click="onCompactHistorySelect(item)"
            >
              <div class="ai-compact-history-item-main">
                <div class="ai-compact-history-item-title">
                  <span class="ai-compact-history-item-title-text">{{ item.title || '对话' }}</span>
                  <span
                    v-if="isHistorySessionRunning(item.sessionId)"
                    class="ai-session-running-dot"
                    title="任务进行中"
                    aria-hidden="true"
                  />
                  <span
                    v-if="isHistorySessionUnread(item)"
                    class="ai-session-unread-dot"
                    title="有未读完成项"
                    aria-hidden="true"
                  />
                </div>
                <div class="ai-compact-history-item-meta">
                  {{ item.createdAt.slice(0, 16).replace('T', ' ') }}
                </div>
              </div>
            </button>
            <div v-if="!historyList.length" class="ai-compact-history-empty">
              暂无历史对话
            </div>
          </div>
        </div>
      </Teleport>

      <!-- 展开态主体内容：用单层容器保证 flex 子节点正确占位与滚动；整区支持拖入附件 -->
      <div
        class="ai-panel-body"
        :class="{ 'is-file-dragging': isDragging && mainTab === 'new' }"
        @dragenter.prevent="onPanelDragEnter"
        @dragover.prevent="onPanelDragOver"
        @dragleave.prevent="onPanelDragLeave"
        @drop.prevent="onDrop"
      >
        <div
          v-if="isDragging && mainTab === 'new'"
          class="ai-panel-drop-overlay"
          aria-hidden="true"
        >
          松开上传文件
        </div>
        <!-- 历史对话 Tab：仅展示历史列表，当前会话置为选中，多则滚动 -->
        <div v-if="mainTab === 'history'" class="ai-history-panel">
            <div class="ai-history-hint">
              这里展示当前页面下的历史对话，点击可重新加载该轮对话继续沟通。
            </div>
            <div class="ai-history-list">
              <button
                v-for="item in historyList"
                :key="item.id"
                class="ai-history-item"
                :class="{
                  active: item.sessionId === currentSessionId || activeHistoryId === item.id,
                  'has-unread': isHistorySessionUnread(item),
                  'is-running': isHistorySessionRunning(item.sessionId),
                }"
                type="button"
                @click="loadHistoryItem(item, { userInitiated: true })"
              >
                <div class="ai-history-main">
                  <div class="ai-history-title">
                    <template v-if="editingHistoryId === item.id">
                      <input
                        ref="historyTitleInputRef"
                        v-model="editingHistoryTitle"
                        class="ai-history-title-input"
                        type="text"
                        @click.stop
                        @keydown.enter.prevent="confirmInlineRename(item)"
                        @keydown.esc.prevent="cancelInlineRename"
                        @blur="confirmInlineRename(item)"
                      />
                    </template>
                    <template v-else>
                      <span class="ai-history-title-text">{{ item.title || '对话' }}</span>
                      <span
                        v-if="isHistorySessionRunning(item.sessionId)"
                        class="ai-session-running-dot"
                        title="任务进行中"
                        aria-hidden="true"
                      />
                      <span
                        v-if="isHistorySessionUnread(item)"
                        class="ai-session-unread-dot"
                        title="有未读完成项"
                        aria-hidden="true"
                      />
                    </template>
                  </div>
                  <div class="ai-history-meta">
                    {{ item.createdAt.slice(0, 16).replace('T', ' ') }}
                  </div>
                </div>
                <div class="ai-history-actions" @click.stop>
                  <button
                    class="ai-history-icon-btn"
                    type="button"
                    title="重命名"
                    @click="startInlineRename(item)"
                  >
                    ✎
                  </button>
                  <button
                    class="ai-history-icon-btn"
                    type="button"
                    title="删除"
                    @click="confirmDelete(item)"
                  >
                    ✕
                  </button>
                </div>
              </button>
            </div>
            <div v-if="!historyList.length" class="ai-history-empty">
              暂无历史对话，发送消息后会自动为当前项目创建对话记录。
            </div>
        </div>

        <!-- 当前对话 Tab：仅展示对话内容 -->
        <template v-else>
        <AiProjectTodoBar v-if="projectId > 0" :project-id="projectId" />
        <div ref="aiChatDisplayRef" class="ai-chat-display" @scroll="onChatScroll">
          <div
            class="chat-message system"
            v-if="!aiMessages.length && !isTaskThinkingVisible"
          >
            <p>您好！我是一诺，请问有什么可以帮您的？</p>
          </div>
          <div
            v-for="msg in aiMessages"
            :key="msg.id"
            :data-msg-id="msg.id"
            :class="['chat-message', 'chat-message-with-copy', msg.role]"
            @click="onChatMessageClick"
          >
            <div v-if="msg.role === 'user'" class="chat-message-actions">
              <button
                type="button"
                class="chat-message-action-btn"
                title="复制"
                :disabled="!getMessageCopyText(msg)"
                @click.stop="copyMessageContent(getMessageCopyText(msg))"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 1H6a2 2 0 0 0-2 2v12h2V3h10V1zm3 4H10a2 2 0 0 0-2 2v14h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm0 16H10V7h9v14z"/></svg>
              </button>
              <button
                type="button"
                class="chat-message-action-btn"
                title="删除"
                @click.stop="removeUserMessage(msg.id)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 3h6l1 2h5v2H3V5h5l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM6 7h12l-1 13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 7z"/></svg>
              </button>
            </div>
            <div v-if="shouldShowPanelInlineThought(msg)" class="ai-message-thought ai-message-thought--inline">
              <button type="button" class="ai-thought-toggle" @click="toggleCollapsedThought(msg.id)">
                <span>思考步骤</span>
                <span>{{ expandedThoughtIds.has(msg.id) ? '收起' : '展开' }}</span>
              </button>
              <div v-show="expandedThoughtIds.has(msg.id)" class="ai-thought-preview ai-thought-preview-clip">
                <div v-for="step in getMessageThoughtSteps(msg)" :key="step.key" class="ai-thought-step">
                  <div class="ai-thought-step-head">
                    <strong>{{ step.title }}</strong>
                  </div>
                  <div class="ai-thought-step-body">{{ step.text }}</div>
                </div>
              </div>
            </div>
            <div
              v-if="msg.role === 'user' && getAiPageContentUserRefFromMessage(msg)"
              class="chat-message-user-pc-ref"
            >
              <button
                type="button"
                class="ai-pc-ref-chip ai-pc-ref-chip--in-chat ai-pc-ref-chip--clickable"
                :aria-expanded="pcRefDetailOpenForMessageId === msg.id ? 'true' : 'false'"
                :title="pcRefDetailOpenForMessageId === msg.id ? '点击收起引用原文' : '点击定位页面原文'"
                @click.stop="onPageContentUserRefChipClick(msg)"
              >
                <span class="ai-pc-ref-chip-prefix" aria-hidden="true">@</span>
                <span class="ai-pc-ref-chip-text">{{ getAiPageContentUserRefFromMessage(msg)!.scopeLabel || '页面' }} · {{ getAiPageContentUserRefFromMessage(msg)!.fieldLabel }}</span>
                <span class="ai-pc-ref-chip-snippet">「{{ truncatePcRefSnippet(getAiPageContentUserRefFromMessage(msg)!.quoted) }}」</span>
              </button>
              <div v-if="pcRefDetailOpenForMessageId === msg.id" class="ai-pc-ref-detail">
                <div class="ai-pc-ref-detail-label">引用原文</div>
                <div class="ai-pc-ref-detail-body">{{ getAiPageContentUserRefFromMessage(msg)!.quoted }}</div>
              </div>
              <div
                v-if="getAiPageContentUserRefFromMessage(msg)!.requirement"
                class="ai-pc-user-ref-requirement"
              >{{ getAiPageContentUserRefFromMessage(msg)!.requirement }}</div>
              <div v-else class="ai-pc-user-ref-requirement is-placeholder">未填写说明，将按上下文润色该片段</div>
            </div>
            <div
              v-else-if="msg.role === 'user' && getProjectCreateUserRefFromMessage(msg)"
              class="chat-message-user-pc-ref"
            >
              <button
                type="button"
                class="ai-pc-ref-chip ai-pc-ref-chip--in-chat"
                :aria-expanded="pcRefDetailOpenForMessageId === msg.id ? 'true' : 'false'"
                :title="pcRefDetailOpenForMessageId === msg.id ? '点击收起引用原文' : '点击查看完整引用原文'"
                @click.stop="togglePcRefDetail(msg.id)"
              >
                <span class="ai-pc-ref-chip-prefix" aria-hidden="true">@</span>
                <span class="ai-pc-ref-chip-text">创建项目 · {{ getProjectCreateUserRefFromMessage(msg)!.fieldLabel }}</span>
                <span class="ai-pc-ref-chip-snippet">「{{ truncatePcRefSnippet(getProjectCreateUserRefFromMessage(msg)!.quoted) }}」</span>
              </button>
              <div v-if="pcRefDetailOpenForMessageId === msg.id" class="ai-pc-ref-detail">
                <div class="ai-pc-ref-detail-label">引用原文</div>
                <div class="ai-pc-ref-detail-body">{{ getProjectCreateUserRefFromMessage(msg)!.quoted }}</div>
              </div>
              <div
                v-if="getProjectCreateUserRefFromMessage(msg)!.requirement"
                class="ai-pc-user-ref-requirement"
              >{{ getProjectCreateUserRefFromMessage(msg)!.requirement }}</div>
              <div v-else class="ai-pc-user-ref-requirement is-placeholder">未填写说明，将按上下文润色该片段</div>
            </div>
            <div
              v-else-if="msg.role === 'user' && msg.content"
              class="chat-message-user-plain"
            >
              <div v-html="renderUserChatMessage(msg.content, msg.atTokens)"></div>
              <div
                v-if="smartDocMentionDetailKey === msg.id"
                class="ai-smart-doc-mention-detail"
              >
                <div class="ai-smart-doc-mention-detail-label">引用详情</div>
                <div class="ai-smart-doc-mention-detail-body">{{ smartDocMentionDetailText }}</div>
              </div>
            </div>
            <div
              v-if="msg.role === 'assistant' && typingContentMap[msg.id] && !isComponentRecommendMessage(msg) && !isComponentBomImportMessage(msg) && !isCalculatorFormulaGenerateMessage(msg) && !isSmartDocSkillDraftMessage(msg) && !isSmartDocSkillImportMessage(msg) && !isSmartDocTemplateDraftMessage(msg) && !isSmartDocSaveAsFromFileMessage(msg) && !isSmartDocTemplateGenerateMessage(msg) && !isBomGenerateMessage(msg) && !isBomCostEstimateMessage(msg) && !isTrReviewMessage(msg)"
              v-html="renderMarkdown(renderReplyText(stripCreateProjectGapNotice(typingContentMap[msg.id])))"
            ></div>
            <div
              v-else-if="msg.role === 'assistant' && resolveAssistantMessageDisplayContent(msg) && !isComponentRecommendMessage(msg) && !isComponentBomImportMessage(msg) && !isCalculatorFormulaGenerateMessage(msg) && !isSmartDocSkillDraftMessage(msg) && !isSmartDocSkillImportMessage(msg) && !isSmartDocTemplateDraftMessage(msg) && !isSmartDocSaveAsFromFileMessage(msg) && !isSmartDocTemplateGenerateMessage(msg) && !isBomGenerateMessage(msg) && !isTrReviewMessage(msg)"
              class="chat-message-selectable"
              data-ai-quote-scope="AI回复"
              data-ai-quote-field="ai-reply"
              data-ai-quote-label="AI 回复"
              data-ai-quote-editable="false"
              data-ai-quote-position="AI 对话区助手回复正文"
              v-html="renderChatMessage(resolveAssistantMessageDisplayContent(msg))"
            ></div>
            <div
              v-if="msg.role === 'assistant' && getSmartDocMoveAction(msg)"
              class="smart-doc-output-actions"
            >
              <button
                type="button"
                class="smart-doc-move-project-btn"
                :disabled="smartDocMovingDocId === getSmartDocMoveAction(msg)!.documentId"
                @click.stop="onMoveSmartDocToProject(msg)"
              >
                {{ smartDocMovingDocId === getSmartDocMoveAction(msg)!.documentId ? '移入中…' : '移入项目工作台' }}
              </button>
              <span
                v-if="getSmartDocMoveAction(msg)!.suggestedProjectName"
                class="smart-doc-move-hint"
              >目标：{{ getSmartDocMoveAction(msg)!.suggestedProjectName }}</span>
            </div>
            <div v-if="msg.attachments?.length" class="chat-message-attachments">
              <template v-for="(att, idx) in msg.attachments" :key="idx">
                <div
                  v-if="att.type === 'image'"
                  class="chat-attachment-thumb chat-attachment-image"
                  @click="openImagePreview(attachmentFullUrl(att.url))"
                >
                  <img :src="attachmentFullUrl(att.url)" :alt="att.name" />
                </div>
                <div
                  v-else
                  class="chat-attachment-thumb chat-attachment-doc"
                  role="button"
                  tabindex="0"
                  :title="att.name"
                  @click.stop="onChatAttachmentClick(att)"
                  @keydown.enter.prevent="onChatAttachmentClick(att)"
                >
                  <span class="chat-attachment-doc-icon">📄</span>
                  <span class="chat-attachment-doc-name">{{ att.name }}</span>
                </div>
              </template>
            </div>
            <div v-if="msg.role === 'assistant' && isComponentRecommendMessage(msg)" class="component-recommend-block">
              <div class="component-recommend-title">推荐结果统计</div>
              <p class="component-recommend-stats">{{ componentRecommendStatsLine(msg) }}</p>
              <div v-if="sessionActionMessageActionsVisible(msg)" class="bom-generate-footer">
                <button
                  type="button"
                  class="bom-generate-btn"
                  title="回退到会话之前的状态"
                  @click.stop="rollbackSessionActionFromMessage(msg)"
                >
                  回退
                </button>
                <button
                  type="button"
                  class="bom-generate-btn bom-generate-btn--primary"
                  title="放弃上次结果并重新执行"
                  @click.stop="retrySessionActionFromMessage(msg)"
                >
                  重试
                </button>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isComponentAutoFillMessage(msg)" class="bom-generate-block">
              <p class="bom-generate-stats">{{ componentAutoFillDialogLine(msg) }}</p>
              <p v-if="componentAutoFillProgressLine(msg)" class="bom-generate-progress">
                {{ componentAutoFillProgressLine(msg) }}
              </p>
              <ul v-if="componentAutoFillSteps(msg).length" class="calc-formula-name-list">
                <li v-for="(s, i) in componentAutoFillSteps(msg)" :key="i">
                  {{ s.icon || '·' }} {{ s.title }}：{{ s.text }}
                </li>
              </ul>
              <div v-if="componentAutoFillActionsVisible(msg)" class="bom-generate-footer">
                <span v-if="componentAutoFillStatus(msg) === 'running'" class="bom-generate-done">
                  自动填充进行中…
                </span>
                <span v-else-if="componentAutoFillStatus(msg) === 'complete'" class="bom-generate-done">
                  自动填充已完成
                </span>
                <span v-else-if="componentAutoFillStatus(msg) === 'failed'" class="bom-generate-done">
                  自动填充失败
                </span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isComponentBomImportMessage(msg)" class="bom-generate-block">
              <p class="bom-generate-stats">{{ componentBomImportDialogLine(msg) }}</p>
              <p v-if="componentBomImportPreviewLine(msg)" class="bom-generate-progress">
                {{ componentBomImportPreviewLine(msg) }}
              </p>
              <ComponentBomImportReviewBlock
                v-if="componentBomImportStatus(msg) === 'pending' && componentBomImportReviewData(msg)"
                :new-items="componentBomImportReviewData(msg)!.newItems"
                :overlap-identical="componentBomImportReviewData(msg)!.overlapIdentical"
                :overlap-conflicts="componentBomImportReviewData(msg)!.overlapConflicts"
                :similar-items="componentBomImportReviewData(msg)!.similarItems"
                :decisions="componentBomImportDecisionsForMessage(msg.id)"
                @update:decisions="(d) => setComponentBomImportDecisions(msg.id, d)"
              />
              <p v-if="aiLoading && componentBomImportProgressText" class="bom-generate-progress">
                {{ componentBomImportProgressText }}
              </p>
              <div v-if="componentBomImportActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="componentBomImportStatus(msg) === 'pending'">
                  <button
                    type="button"
                    class="bom-generate-btn bom-generate-btn--primary"
                    :disabled="componentBomImportSaving"
                    @click.stop="acceptComponentBomImport(msg)"
                  >
                    {{ componentBomImportSaving ? '写入中…' : '写入公司部件库' }}
                  </button>
                  <button type="button" class="bom-generate-btn" @click.stop="cancelComponentBomImport(msg)">
                    取消
                  </button>
                </template>
                <span v-else-if="componentBomImportStatus(msg) === 'saved'" class="bom-generate-done">
                  已写入部件库，可在部件详情页点击「自动填充」补全资料
                </span>
                <span v-else-if="componentBomImportStatus(msg) === 'cancelled'" class="bom-generate-done">
                  已取消本次 BOM 导入
                </span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isCalculatorFormulaGenerateMessage(msg)" class="bom-generate-block">
              <div v-if="msg.content" class="calc-formula-gen-reply" v-html="renderChatMessage(msg.content)"></div>
              <p class="bom-generate-stats">{{ calculatorFormulaGenerateLine(msg) }}</p>
              <ul v-if="calculatorFormulaGenerateNames(msg).length" class="calc-formula-name-list">
                <li v-for="(n, i) in calculatorFormulaGenerateNames(msg)" :key="i">{{ n }}</li>
              </ul>
              <div v-if="calculatorFormulaGenerateActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="calculatorFormulaGenerateStatus(msg) === 'pending'">
                  <button
                    type="button"
                    class="bom-generate-btn bom-generate-btn--primary"
                    :disabled="calculatorFormulaGenerateSaving"
                    @click.stop="acceptCalculatorFormulaGenerate(msg)"
                  >
                    {{ calculatorFormulaGenerateSaving ? '生成中…' : '开始生成' }}
                  </button>
                  <button type="button" class="bom-generate-btn" @click.stop="cancelCalculatorFormulaGenerate(msg)">
                    取消
                  </button>
                </template>
                <span v-else-if="calculatorFormulaGenerateStatus(msg) === 'generating'" class="bom-generate-done">
                  正在生成公式卡片…
                </span>
                <span v-else-if="calculatorFormulaGenerateStatus(msg) === 'done'" class="bom-generate-done">
                  公式卡片已生成
                </span>
                <span v-else-if="calculatorFormulaGenerateStatus(msg) === 'cancelled'" class="bom-generate-done">
                  已取消本次公式生成
                </span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isSmartDocSkillDraftMessage(msg)" class="bom-generate-block">
              <div v-if="msg.content" class="calc-formula-gen-reply" v-html="renderChatMessage(msg.content)"></div>
              <p class="bom-generate-stats">{{ smartDocSkillDraftLine(msg) }}</p>
              <ul v-if="smartDocSkillDraftMeta(msg).length" class="calc-formula-name-list">
                <li v-for="(n, i) in smartDocSkillDraftMeta(msg)" :key="i">{{ n }}</li>
              </ul>
              <div v-if="smartDocSkillDraftActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="smartDocSkillDraftStatus(msg) === 'pending'">
                  <button
                    type="button"
                    class="bom-generate-btn bom-generate-btn--primary"
                    :disabled="smartDocSkillDraftSaving"
                    @click.stop="acceptSmartDocSkillDraft(msg)"
                  >
                    {{ smartDocSkillDraftSaving ? '保存中…' : '保存到我的技能' }}
                  </button>
                  <button
                    type="button"
                    class="bom-generate-btn"
                    :disabled="smartDocSkillDraftSaving"
                    @click.stop="startUsingSmartDocSkillDraft(msg)"
                  >
                    {{ smartDocSkillDraftSaving ? '处理中…' : '开始使用' }}
                  </button>
                </template>
                <span v-else-if="smartDocSkillDraftStatus(msg) === 'saving'" class="bom-generate-done">
                  正在保存…
                </span>
                <span v-else-if="smartDocSkillDraftStatus(msg) === 'done'" class="bom-generate-done">
                  已保存到我的技能
                </span>
                <span v-else-if="smartDocSkillDraftStatus(msg) === 'cancelled'" class="bom-generate-done">
                  已取消
                </span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isSmartDocSkillImportMessage(msg)" class="bom-generate-block">
              <div v-if="msg.content" class="calc-formula-gen-reply" v-html="renderChatMessage(msg.content)"></div>
              <p class="bom-generate-stats">{{ smartDocSkillImportLine(msg) }}</p>
              <ul v-if="smartDocSkillImportMeta(msg).length" class="calc-formula-name-list">
                <li v-for="(n, i) in smartDocSkillImportMeta(msg)" :key="i">{{ n }}</li>
              </ul>
              <div v-if="smartDocSkillImportActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="smartDocSkillImportStatus(msg) === 'parsing'">
                  <span class="bom-generate-done">正在解析并安装技能包…</span>
                </template>
                <template v-else-if="smartDocSkillImportStatus(msg) === 'done'">
                  <button
                    type="button"
                    class="bom-generate-btn bom-generate-btn--primary"
                    @click.stop="startUsingImportedSmartDocSkill(msg)"
                  >
                    开始使用
                  </button>
                  <span class="bom-generate-done">已导入为我的技能（草稿）</span>
                </template>
                <span v-else-if="smartDocSkillImportStatus(msg) === 'failed'" class="bom-generate-done">
                  导入失败，请检查文件后重试
                </span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isSmartDocTemplateDraftMessage(msg)" class="bom-generate-block">
              <div v-if="msg.content" class="calc-formula-gen-reply" v-html="renderChatMessage(msg.content)"></div>
              <p class="bom-generate-stats">{{ smartDocTemplateDraftLine(msg) }}</p>
              <ul v-if="smartDocTemplateDraftMeta(msg).length" class="calc-formula-name-list">
                <li v-for="(n, i) in smartDocTemplateDraftMeta(msg)" :key="i">{{ n }}</li>
              </ul>
              <div v-if="smartDocTemplateDraftActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="smartDocTemplateDraftStatus(msg) === 'pending'">
                  <button
                    type="button"
                    class="bom-generate-btn bom-generate-btn--primary"
                    :disabled="smartDocTemplateDraftSaving"
                    @click.stop="acceptSmartDocTemplateDraft(msg)"
                  >
                    {{ smartDocTemplateDraftSaving ? '保存中…' : '保存到我的模版' }}
                  </button>
                  <button
                    type="button"
                    class="bom-generate-btn"
                    :disabled="smartDocTemplateDraftSaving"
                    @click.stop="startUsingSmartDocTemplateDraft(msg)"
                  >
                    {{ smartDocTemplateDraftSaving ? '处理中…' : '开始使用' }}
                  </button>
                </template>
                <span v-else-if="smartDocTemplateDraftStatus(msg) === 'saving'" class="bom-generate-done">
                  正在保存…
                </span>
                <span v-else-if="smartDocTemplateDraftStatus(msg) === 'done'" class="bom-generate-done">
                  已保存到我的模版
                </span>
                <span v-else-if="smartDocTemplateDraftStatus(msg) === 'cancelled'" class="bom-generate-done">
                  已取消
                </span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isSmartDocSaveAsFromFileMessage(msg)" class="bom-generate-block">
              <div v-if="msg.content" class="calc-formula-gen-reply" v-html="renderChatMessage(msg.content)"></div>
              <p class="bom-generate-stats">{{ smartDocSaveAsFromFileLine(msg) }}</p>
              <ul v-if="smartDocSaveAsFromFileMeta(msg).length" class="calc-formula-name-list">
                <li v-for="(n, i) in smartDocSaveAsFromFileMeta(msg)" :key="i">{{ n }}</li>
              </ul>
              <div v-if="smartDocSaveAsFromFileActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="smartDocSaveAsFromFileStatus(msg) === 'wizard_opened'">
                  <span class="bom-generate-done">请在提炼向导中确认结构与占位后保存</span>
                </template>
                <template v-else-if="smartDocSaveAsFromFileStatus(msg) === 'pending'">
                  <span class="bom-generate-done">正在准备提炼向导…</span>
                </template>
                <span v-else-if="smartDocSaveAsFromFileStatus(msg) === 'done'" class="bom-generate-done">
                  已保存到我的模版
                </span>
                <span v-else-if="smartDocSaveAsFromFileStatus(msg) === 'failed'" class="bom-generate-done">
                  处理失败，请重试
                </span>
              </div>
            </div>
            <div
              v-if="msg.role === 'assistant' && isSmartDocTemplateGenerateMessage(msg)"
              class="bom-generate-block"
            >
              <div
                v-if="smartDocTemplateGenerateDisplayContent(msg)"
                class="calc-formula-gen-reply"
                v-html="renderChatMessage(smartDocTemplateGenerateDisplayContent(msg))"
              ></div>
              <ul
                v-if="smartDocTemplateGenerateReviewItems(msg).length"
                class="tpl-gen-review-list"
              >
                <li
                  v-for="(item, idx) in smartDocTemplateGenerateReviewItems(msg)"
                  :key="`${item.label}-${idx}`"
                >
                  <button
                    type="button"
                    class="tpl-gen-review-item"
                    @click.stop="locateSmartDocTemplateReviewItem(item)"
                  >
                    <span class="tpl-gen-review-item__idx">{{ idx + 1 }}.</span>
                    <span class="tpl-gen-review-item__label">
                      <template v-if="item.category">{{ item.category }} · </template>{{ item.label }}
                    </span>
                    <span class="tpl-gen-review-item__action">定位</span>
                  </button>
                </li>
              </ul>
              <div v-if="smartDocTemplateGenerateActionsVisible(msg)" class="bom-generate-footer">
                <span v-if="smartDocTemplateGenerateStatus(msg) === 'generating'" class="bom-generate-done">
                  生成进行中，可在右侧查看实时预览
                </span>
                <span v-else-if="smartDocTemplateGenerateStatus(msg) === 'opening'" class="bom-generate-done">
                  正在打开预览…
                </span>
                <span v-else-if="smartDocTemplateGenerateStatus(msg) === 'collecting'" class="bom-generate-done">
                  请在下方补充信息后继续发送
                </span>
                <span v-else-if="smartDocTemplateGenerateStatus(msg) === 'failed'" class="bom-generate-done">
                  生成失败，可点击「重试」继续（无需重新 @模板）
                </span>
                <span v-else-if="smartDocTemplateGenerateStatus(msg) === 'opened'" class="bom-generate-done">
                  可在预览中直接修改，或继续对话说明修改要求
                </span>
                <button
                  v-if="smartDocTemplateGenerateStatus(msg) === 'failed' && sessionActionMessageActionsVisible(msg)"
                  type="button"
                  class="bom-generate-btn"
                  style="margin-left: 8px"
                  @click.stop="retrySessionActionFromMessage(msg)"
                >
                  重试
                </button>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isBomCostEstimateMessage(msg)" class="bom-generate-block">
              <div v-if="bomCostEstimateActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="bomCostEstimateStatus(msg) === 'pending'">
                  <button
                    type="button"
                    class="bom-generate-btn bom-generate-btn--primary"
                    :disabled="bomCostSaving"
                    @click.stop="acceptBomCostEstimate(msg)"
                  >
                    {{ bomCostSaving ? '保存中…' : '保存到 BOM 表' }}
                  </button>
                </template>
                <span v-else-if="bomCostEstimateStatus(msg) === 'saved'" class="bom-generate-done">已保存到数据库</span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isBomGenerateMessage(msg)" class="bom-generate-block">
              <p
                class="bom-generate-stats"
                :class="{ 'bom-generate-stats--warn': bomGenerateStatus(msg) === 'unsupported' }"
              >
                {{ bomGenerateDialogLine(msg) }}
              </p>
              <p v-if="aiLoading && bomGenerateProgressText" class="bom-generate-progress">
                {{ bomGenerateProgressText }}
              </p>
              <div v-if="bomGenerateActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="bomGenerateStatus(msg) === 'pending'">
                  <button
                    type="button"
                    class="bom-generate-btn bom-generate-btn--primary"
                    :disabled="bomGenerateSaving"
                    @click.stop="acceptBomGenerate(msg)"
                  >
                    {{ bomGenerateSaving ? '保存中…' : '保存' }}
                  </button>
                  <button type="button" class="bom-generate-btn" @click.stop="cancelBomGenerate(msg)">
                    取消
                  </button>
                </template>
                <span v-else-if="bomGenerateStatus(msg) === 'saved'" class="bom-generate-done">已保存到 BOM 表</span>
                <span v-else-if="bomGenerateStatus(msg) === 'cancelled'" class="bom-generate-done">已取消本次生成</span>
              </div>
            </div>
            <div
              v-if="msg.role === 'assistant' && isDrawingReviewMessage(msg)"
              class="bom-generate-block"
            >
              <div v-if="drawingReviewActionsVisible(msg)" class="bom-generate-footer">
                <button
                  type="button"
                  class="bom-generate-btn bom-generate-btn--primary"
                  @click.stop="viewDrawingReviewResult(msg)"
                >
                  查看审核结果
                </button>
              </div>
            </div>
            <div
              v-if="msg.role === 'assistant' && isBomDrawingReviewExistingMessage(msg)"
              class="bom-generate-block"
            >
              <div v-if="bomDrawingReviewExistingActionsVisible(msg)" class="bom-generate-footer">
                <button
                  type="button"
                  class="bom-generate-btn bom-generate-btn--primary"
                  @click.stop="viewBomDrawingReviewExisting(msg)"
                >
                  查看报告
                </button>
                <button type="button" class="bom-generate-btn" @click.stop="rerunBomDrawingReviewExisting(msg)">
                  重新审核
                </button>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isTrReviewMessage(msg)" class="tr-review-report-card">
              <div class="tr-review-report-head">
                <h3>TR 审核报告已生成</h3>
                <span>{{ trReviewStatus(msg) === 'pushed' ? '已提交审核' : '审核中' }}</span>
              </div>
              <p class="tr-review-report-desc">基于当前阶段交付物、需求清单、BOM 单与知识空间规则文档生成审核建议。</p>
              <div class="tr-review-report-summary">
                <div><b>审核依据</b><span>{{ trReviewRuleDocumentTitle(msg) }}</span></div>
                <div><b>Checklist</b><span>{{ trReviewChecklistSummaryText(msg) }}</span></div>
                <div v-if="trReviewKeyMissing(msg)"><b>重点缺失</b><span>{{ trReviewKeyMissing(msg) }}</span></div>
              </div>
              <div class="tr-review-report-actions">
                <button type="button" class="bom-generate-btn bom-generate-btn--primary" @click.stop="viewTrReviewReport(msg)">
                  查看报告
                </button>
              </div>
              <p v-if="trReviewStatus(msg) === 'pending' && trReviewCanPush(msg)" class="tr-review-hint">
                {{
                  trReviewRecommendPass(msg)
                    ? '可提交给指定审核人；对方可在项目详情「TR评审」按钮（红点）中审核，并收到通知。'
                    : 'AI 建议不通过，确认后可提交给指定审核人进行人工审核。'
                }}
              </p>
              <div v-if="trReviewActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="trReviewStatus(msg) === 'pending'">
                  <button
                    v-if="trReviewCanResubmit(msg)"
                    type="button"
                    class="bom-generate-btn"
                    :disabled="trReviewPushing || aiLoading"
                    @click.stop="retryTrReview(msg)"
                  >
                    {{ trReviewPushing ? '处理中…' : '重新评审' }}
                  </button>
                  <button
                    v-if="trReviewCanPush(msg)"
                    type="button"
                    class="bom-generate-btn"
                    :class="trReviewRecommendPass(msg) ? 'bom-generate-btn--primary' : 'bom-generate-btn--danger'"
                    :disabled="trReviewPushing"
                    @click.stop="acceptTrReviewPush(msg)"
                  >
                    {{
                      trReviewPushing
                        ? '提交中…'
                        : '提交审核'
                    }}
                  </button>
                </template>
                <span v-else-if="trReviewStatus(msg) === 'pushed'" class="bom-generate-done">
                  已提交给指定审核人，请提醒对方点击「TR评审」处理
                </span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && isProjectIntroRewriteMessage(msg)" class="project-intro-rewrite-block">
              <p v-if="projectIntroRewriteStatus(msg) === 'adopted'" class="project-intro-rewrite-hint">
                已根据项目资料生成并保存项目简介，可在左侧「项目信息」查看；如需恢复请点击「回退」。
              </p>
              <div v-if="projectIntroRewriteActionsVisible(msg)" class="bom-generate-footer">
                <template v-if="projectIntroRewriteStatus(msg) === 'adopted'">
                  <span class="bom-generate-done">已回填并保存项目简介</span>
                  <button
                    type="button"
                    class="bom-generate-btn"
                    :disabled="projectIntroRewriting"
                    @click.stop="revertProjectIntroRewrite(msg)"
                  >
                    回退
                  </button>
                </template>
                <span v-else-if="projectIntroRewriteStatus(msg) === 'reverted'" class="bom-generate-done">
                  已回退为帮写前的项目简介
                </span>
              </div>
            </div>
            <div v-if="msg.role === 'assistant' && wbsGenerateActionsVisible(msg)" class="bom-generate-footer">
              <template v-if="wbsGenerateStatus(msg) === 'completed'">
                <button
                  type="button"
                  class="bom-generate-btn"
                  :disabled="!canRollbackWbsFromMessage(msg) || aiLoading || isWbsGenerateBusyForMessage(msg)"
                  :title="wbsRollbackDisabledTitle(msg)"
                  @click.stop="rollbackWbsFromMessage(msg)"
                >
                  回退
                </button>
                <button
                  type="button"
                  class="bom-generate-btn bom-generate-btn--primary"
                  :disabled="aiLoading || isWbsGenerateBusyForMessage(msg)"
                  @click.stop="regenerateWbsFromMessage(msg)"
                >
                  重新生成
                </button>
              </template>
              <template v-else-if="wbsGenerateStatus(msg) === 'failed'">
                <button
                  type="button"
                  class="bom-generate-btn bom-generate-btn--primary"
                  :disabled="aiLoading || isWbsGenerateBusyForMessage(msg)"
                  @click.stop="regenerateWbsFromMessage(msg)"
                >
                  重新生成
                </button>
              </template>
              <span v-else-if="wbsGenerateStatus(msg) === 'reverted'" class="bom-generate-done">
                已回退本次 WBS 生成
              </span>
            </div>
            <!-- agent_complete 导航按钮 -->
            <div v-if="msg.role === 'assistant' && isAgentCompleteResult(msg.structuredResult)" class="agent-nav-footer">
              <button
                type="button"
                class="bom-generate-btn bom-generate-btn--primary"
                @click="handleAgentNavClick(msg)"
              >
                {{ getAgentCompleteNav(msg.structuredResult)?.label || '查看结果' }} &rarr;
              </button>
            </div>

            <div
              v-if="msg.role === 'assistant' && isWbsTaskReassignConfirm(msg)"
              class="bom-generate-actions"
            >
              <button
                type="button"
                class="bom-generate-btn"
                :disabled="aiLoading"
                @click.stop="declineWbsTaskReassign(msg)"
              >
                暂不重分配
              </button>
              <button
                type="button"
                class="bom-generate-btn bom-generate-btn--primary"
                :disabled="aiLoading"
                @click.stop="acceptWbsTaskReassign(msg)"
              >
                开始重分配
              </button>
            </div>

            <div v-if="msg.role === 'assistant' && isDocumentDeepReviewMessage(msg)" class="doc-deep-review-block">
              <div v-if="getDocumentDeepReviewFindings(msg).length" class="doc-deep-review-findings">
                <div class="doc-deep-review-findings-title">结构化批注（一诺）</div>
                <div
                  v-for="f in getDocumentDeepReviewFindings(msg)"
                  :key="f.id"
                  class="doc-deep-review-item"
                >
                  <div class="doc-deep-review-item-head">
                    <span class="doc-deep-review-badge">{{ documentDeepReviewCategoryLabel(f.category, f.categories) }}</span>
                  </div>
                  <div class="doc-deep-review-row">
                    <span class="doc-deep-review-k">原文</span>
                    <span class="doc-deep-review-v">{{ f.anchor_text }}</span>
                  </div>
                  <div class="doc-deep-review-row">
                    <span class="doc-deep-review-k">建议</span>
                    <span class="doc-deep-review-v">{{ f.suggested_replacement }}</span>
                  </div>
                </div>
              </div>
              <div v-if="documentDeepReviewActionsVisible(msg)" class="doc-deep-review-footer">
                <template v-if="documentDeepReviewStatus(msg) === 'pending'">
                  <button
                    type="button"
                    class="doc-deep-review-btn doc-deep-review-btn--primary"
                    @click.stop="acceptDocumentDeepReview(msg)"
                  >
                    全部采纳
                  </button>
                  <button type="button" class="doc-deep-review-btn" @click.stop="revertOrDismissDocumentDeepReview(msg)">
                    回退
                  </button>
                </template>
                <template v-else-if="documentDeepReviewStatus(msg) === 'adopted'">
                  <span class="doc-deep-review-done">已合并到文档</span>
                  <button type="button" class="doc-deep-review-btn" @click.stop="revertOrDismissDocumentDeepReview(msg)">
                    回退
                  </button>
                </template>
                <span v-else-if="documentDeepReviewStatus(msg) === 'reverted'" class="doc-deep-review-done">已回退正文</span>
                <span v-else-if="documentDeepReviewStatus(msg) === 'cancelled'" class="doc-deep-review-done">已放弃本次建议</span>
              </div>
            </div>
            <div
              v-if="msg.role === 'assistant' && isPendingAiPageContentFieldEditMessage(msg)"
              class="pc-field-edit-footer"
            >
              <div class="pc-field-edit-footer-actions">
                <button type="button" class="pc-field-edit-btn pc-field-edit-btn--primary" @click.stop="acceptPageContentFieldEdit(msg)">
                  采纳
                </button>
                <button type="button" class="pc-field-edit-btn" @click.stop="rejectPageContentFieldEdit(msg)">
                  拒绝
                </button>
              </div>
            </div>
            <div
              v-else-if="msg.role === 'assistant' && aiPageContentFieldEditDoneLabel(msg)"
              class="pc-field-edit-done-row"
            >{{ aiPageContentFieldEditDoneLabel(msg) }}</div>
            <div
              v-else-if="msg.role === 'assistant' && isPendingProjectCreateFieldEditMessage(msg)"
              class="pc-field-edit-footer"
            >
              <div class="pc-field-edit-footer-actions">
                <button type="button" class="pc-field-edit-btn pc-field-edit-btn--primary" @click.stop="acceptProjectCreateFieldEdit(msg)">
                  采纳
                </button>
                <button type="button" class="pc-field-edit-btn" @click.stop="rejectProjectCreateFieldEdit(msg)">
                  拒绝
                </button>
              </div>
            </div>
            <div
              v-else-if="msg.role === 'assistant' && projectCreateFieldEditDoneLabel(msg)"
              class="pc-field-edit-done-row"
            >{{ projectCreateFieldEditDoneLabel(msg) }}</div>
            <div v-if="msg.role === 'assistant' && sorRequirementActionsVisible(msg)" class="bom-generate-footer">
              <button
                type="button"
                class="bom-generate-btn"
                @click.stop="rollbackSorRequirementFromMessage(msg)"
              >
                回退
              </button>
              <button
                type="button"
                class="bom-generate-btn bom-generate-btn--primary"
                @click.stop="retrySorRequirementFromMessage(msg)"
              >
                重试
              </button>
            </div>
            <div v-if="msg.role === 'assistant' && sessionActionMessageActionsVisible(msg) && !isComponentRecommendMessage(msg)" class="bom-generate-footer">
              <button
                type="button"
                class="bom-generate-btn"
                title="回退到会话之前的状态"
                @click.stop="rollbackSessionActionFromMessage(msg)"
              >
                回退
              </button>
              <button
                type="button"
                class="bom-generate-btn bom-generate-btn--primary"
                title="放弃上次结果并重新执行"
                @click.stop="retrySessionActionFromMessage(msg)"
              >
                重试
              </button>
            </div>
          </div>
          <div
            v-if="isTaskThinkingVisible && liveThoughtSteps.length"
            class="chat-message system ai-message-thought ai-message-thought--live"
          >
            <div class="ai-thought-toggle ai-thought-toggle--static">
              <span>思考步骤</span>
              <span>进行中</span>
            </div>
            <div class="ai-thought-preview ai-thought-preview-clip">
              <div
                v-for="step in liveThoughtSteps"
                :key="step.key"
                class="ai-thought-step"
                :class="{ 'is-running': step.icon === '⏳' }"
              >
                <div class="ai-thought-step-head">
                  <strong>{{ step.title }}</strong>
                </div>
                <div class="ai-thought-step-body">{{ step.text }}</div>
              </div>
            </div>
          </div>
          <div
            v-else-if="isTaskThinkingVisible"
            class="chat-message system chat-message-thinking"
          >
            <p>
              正在连接一诺
              <span class="dot">.</span><span class="dot">.</span><span class="dot">.</span>
            </p>
          </div>
        </div>

        <!-- 项目选择器挂到 body，避免全屏一诺时侧栏被 chrome-hidden 裁切导致「卡住」 -->
        <Teleport to="body">
          <div
            v-if="projectSelectionVisible"
            class="project-selection-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="project-selection-title"
            @click.self="dismissProjectSelection"
          >
            <div class="project-selection-panel project-selection-panel--modal" @click.stop>
              <div class="project-selection-header">
                <div>
                  <div id="project-selection-title" class="project-selection-title">选择项目后继续提问</div>
                  <div class="project-selection-tip">{{ projectPromptMessage }}</div>
                </div>
                <button type="button" class="project-selection-close" @click="dismissProjectSelection">×</button>
              </div>
              <div class="project-selection-list">
                <button
                  v-for="project in projectSelectionList"
                  :key="project.id"
                  type="button"
                  class="project-selection-item"
                  :class="{ active: selectedProjectId === project.id }"
                  @click="selectedProjectId = project.id"
                >
                  <div class="project-selection-item-main">
                    <div class="project-selection-item-name">{{ project.name }}</div>
                    <div class="project-selection-item-meta">
                      <span>角色：{{ project.role || '成员' }}</span>
                      <span>进度：{{ Math.round(project.progress ?? 0) }}%</span>
                    </div>
                  </div>
                </button>
              </div>
              <div class="project-selection-actions">
                <button type="button" class="project-selection-confirm" :disabled="!selectedProjectId || projectSelectionLoading" @click="confirmProjectSelection">
                  确认并发送
                </button>
              </div>
            </div>
          </div>
        </Teleport>

        <!-- 输入区域：支持拖拽上传，附件缩略图在上方，发送/附件按钮悬浮在输入框右下角 -->
        <!-- 输入区域：支持拖拽上传，附件缩略图在上方，发送/附件按钮悬浮在输入框右下角 -->
        <div
          class="ai-input-area"
          :class="{
            'is-resize-edge': isOnTopEdge,
            'has-queue-strip': scopeActiveQueueTasks.length > 0,
          }"
          @mousemove="onInputAreaMouseMove"
          @mouseleave="onInputAreaMouseLeave"
          @mousedown="onInputAreaMouseDown"
        >
          <!-- 已选附件：缩略图/文档图标+文件名，支持删除；上传中显示进度与取消 -->
          <div v-if="attachmentList.length" class="ai-attachment-strip">
            <div
              v-for="item in attachmentList"
              :key="item.id"
              class="ai-attachment-item"
              :class="{
                'is-doc': item.type === 'doc',
                'is-uploading': item.progress < 100 && !item.error,
                'is-error': item.error,
              }"
            >
              <template v-if="item.type === 'image'">
                <img
                  v-if="item.objectUrl"
                  :src="item.objectUrl"
                  class="ai-attachment-thumb"
                  alt=""
                />
                <span v-else class="ai-attachment-placeholder">图片</span>
              </template>
              <template v-else>
                <span class="ai-attachment-doc-icon">📄</span>
                <span class="ai-attachment-name">{{ item.name }}</span>
              </template>
              <div v-if="item.progress < 100 && !item.error" class="ai-attachment-progress-wrap">
                <div class="ai-attachment-progress" :style="{ width: item.progress + '%' }"></div>
                <button
                  type="button"
                  class="ai-attachment-cancel"
                  title="取消上传"
                  @click="cancelUpload(item)"
                >
                  ✕
                </button>
              </div>
              <button
                v-else
                type="button"
                class="ai-attachment-remove"
                title="删除"
                @click="removeAttachment(item)"
              >
                ✕
              </button>
            </div>
          </div>
          <div
            v-if="smartDocAi.active && smartDocAi.execution"
            class="ai-smart-doc-execution"
            :data-status="smartDocAi.execution.status"
          >
            <div class="ai-smart-doc-execution-head">
              <strong>生成状态：{{ smartDocExecutionStatusLabel }}</strong>
              <button
                v-if="smartDocCanCancelExecution"
                type="button"
                class="ai-smart-doc-exec-btn"
                @click="onSmartDocCancelExecution"
              >
                取消
              </button>
            </div>
            <div
              v-if="smartDocAi.execution.resultHtml"
              class="ai-smart-doc-preview"
              v-html="smartDocAi.execution.resultHtml"
            />
            <div v-if="smartDocAi.execution.citations?.length" class="ai-smart-doc-citations">
              <div class="ai-smart-doc-citations-title">引用 Citation</div>
              <button
                v-for="c in smartDocAi.execution.citations"
                :key="c.index"
                type="button"
                class="ai-smart-doc-citation"
                @click="onSmartDocCitation(c)"
              >
                [{{ c.index }}] {{ c.title }}
              </button>
            </div>
            <p v-if="smartDocAi.execution.errorMessage" class="ai-smart-doc-error">
              {{ smartDocAi.execution.errorMessage }}
            </p>
            <div v-if="smartDocAi.execution.status === 'completed'" class="ai-smart-doc-apply">
              <button type="button" class="ai-smart-doc-exec-btn primary" @click="onSmartDocApply(false)">
                写入当前文档
              </button>
              <button type="button" class="ai-smart-doc-exec-btn" @click="onSmartDocApply(true)">
                新建文档
              </button>
            </div>
          </div>
          <div
            v-if="pageContentEditPending && currentRouteName !== 'ProjectCreate'"
            class="ai-pc-ref-composer-row"
          >
            <button
              type="button"
              class="ai-pc-ref-chip ai-pc-ref-chip--composer ai-pc-ref-chip--clickable"
              :title="pageContentEditPending.quoted"
              @click="onPageContentRefChipClick(pageContentEditPending)"
            >
              <span class="ai-pc-ref-chip-prefix" aria-hidden="true">@</span>
              <span class="ai-pc-ref-chip-text">{{ pageContentEditPending.scopeLabel }} · {{ pageContentEditPending.fieldLabel }}</span>
              <span class="ai-pc-ref-chip-snippet">「{{ truncatePcRefSnippet(pageContentEditPending.quoted) }}」</span>
            </button>
            <button type="button" class="ai-pc-ref-remove" title="移除引用" @click="clearPageContentFieldRef">×</button>
          </div>
          <div
            v-if="projectCreateFieldEditPending && currentRouteName === 'ProjectCreate'"
            class="ai-pc-ref-composer-row"
          >
            <button
              type="button"
              class="ai-pc-ref-chip ai-pc-ref-chip--composer"
              :title="projectCreateFieldEditPending.quoted"
              @click.prevent
            >
              <span class="ai-pc-ref-chip-prefix" aria-hidden="true">@</span>
              <span class="ai-pc-ref-chip-text">创建项目 · {{ projectCreateFieldEditPending.fieldLabel }}</span>
              <span class="ai-pc-ref-chip-snippet">「{{ truncatePcRefSnippet(projectCreateFieldEditPending.quoted) }}」</span>
            </button>
            <button type="button" class="ai-pc-ref-remove" title="移除引用" @click="clearProjectCreateFieldRef">×</button>
          </div>
          <div v-if="scopeActiveQueueTasks.length" class="ai-task-queue-strip">
            <div
              v-for="task in scopeActiveQueueTasks"
              :key="task.id"
              class="ai-task-queue-item"
              :class="{
                'is-running': task.status === 'running',
                'is-queued': task.status === 'queued' && !taskQueue.isTaskQueueDeferred(task.id),
                'is-paused': task.status === 'paused' || taskQueue.isTaskQueueDeferred(task.id),
              }"
            >
              <span class="ai-task-queue-indicator" aria-hidden="true">
                <span v-if="task.status === 'running'" class="ai-task-queue-spinner" />
                <span v-else class="ai-task-queue-dot" />
              </span>
              <span class="ai-task-queue-label" :title="task.label">{{ task.label }}</span>
              <span class="ai-task-queue-status">{{
                task.status === 'running'
                  ? '执行中'
                  : task.status === 'paused' || taskQueue.isTaskQueueDeferred(task.id)
                    ? '已暂停'
                    : '排队中'
              }}</span>
              <button
                v-if="task.kind === 'project_create' && task.status === 'running'"
                type="button"
                class="ai-task-queue-stop"
                title="终止项目生成"
                aria-label="终止项目生成"
                @click="onCancelQueueTask(task.id)"
              >
                终止生成
              </button>
              <button
                v-if="task.status === 'queued' && scopeQueueHeld && !scopeRunningTask && !scopePausedTask"
                type="button"
                class="ai-task-queue-continue"
                title="继续执行排队任务"
                @click="onContinueScopeQueue"
              >
                继续
              </button>
            </div>
          </div>
          <div
            v-if="smartDocAi.active && !aiMessages.length"
            class="ai-smart-doc-quick-prompts"
          >
            <button
              v-for="item in smartDocQuickPrompts"
              :key="item.key"
              type="button"
              class="ai-smart-doc-quick-prompt"
              @click="onSmartDocQuickPrompt(item.key)"
            >
              {{ item.label }}
            </button>
          </div>
          <div
            class="ai-input-inner"
            :style="{ height: inputInnerHeightPx }"
          >
            <SmartDocAtMentionMenu
              v-if="smartDocAi.active"
              :open="smartDocAtMentionOpen"
              :items="smartDocAtMentionItems"
              :active-index="smartDocAtMentionIndex"
              :loading="smartDocAtMentionLoading"
              :empty-text="smartDocAtMentionEmpty"
              @pick="onSmartDocAtMentionPick"
              @hover="(i) => (smartDocAtMentionIndex = i)"
            />
            <div class="ai-input-options">
              <!-- 智能文档：+ 在左下角，位于模型选择左侧 -->
              <SmartDocComposerPlusMenu
                v-if="smartDocAi.active"
                :workspace-id="smartDocAi.workspaceId"
                :selected-skill-id="smartDocAi.selectedSkillId"
                @upload="fileInputRef?.click()"
                @select="onSmartDocSkillPicked"
                @create="beginSmartDocSkillAuthoring('create')"
                @import="beginSmartDocSkillPackageImport"
              />
              <AiComposerDropdown
                v-model="llmProvider"
                :options="providerDropdownOptions"
                :disabled="!allowUserModelSelection"
                title="大模型类型"
              />
              <AiComposerDropdown
                v-if="showRunModeDropdown"
                v-model="aiRunMode"
                :options="runModeDropdownOptions"
                title="AI 运行模式"
              />
            </div>
            <div
              class="ai-textarea-stack"
              :class="{
                'has-smart-doc-mention': smartDocAi.active,
                'is-composing': smartDocAi.active && aiInputComposing,
              }"
            >
              <div
                v-if="smartDocAi.active"
                ref="aiTextareaHighlightRef"
                class="ai-textarea-highlight"
                aria-hidden="true"
                v-html="smartDocInputHighlightHtml"
              />
              <textarea
                ref="aiTextareaRef"
                v-model="aiInput"
                class="ai-textarea"
                :class="{
                  'ai-textarea--mention-overlay': smartDocAi.active,
                  'is-composing': smartDocAi.active && aiInputComposing,
                }"
                :placeholder="smartDocAi.active ? '输入消息，或键入 @ 引用技能 / 模板 / 项目' : inputPlaceholder"
                @paste="onAiInputPaste"
                @input="onSmartDocAiInput"
                @scroll="onSmartDocTextareaScroll"
                @mousemove="onSmartDocTextareaMouseMove"
                @mouseleave="onSmartDocTextareaMouseLeave"
                @compositionstart="onAiInputCompositionStart"
                @compositionupdate="onAiInputCompositionUpdate"
                @compositionend="onAiInputCompositionEnd"
                @keydown="onSmartDocAiKeydown"
                @keydown.enter.exact.prevent="onAiInputEnter"
                @keydown.enter.shift.stop
              ></textarea>
              <div
                v-if="smartDocComposerMentionHover"
                class="ai-smart-doc-mention-hover"
                :style="smartDocComposerMentionHoverStyle"
                @mouseenter="clearSmartDocMentionHoverHide"
                @mouseleave="scheduleSmartDocMentionHoverHide"
              >
                <div class="ai-smart-doc-mention-hover-detail">{{ smartDocComposerMentionHover.detail }}</div>
                <button
                  type="button"
                  class="ai-smart-doc-mention-hover-remove"
                  title="移除引用"
                  @click.stop="onSmartDocComposerMentionHoverRemove"
                >删除</button>
              </div>
            </div>
            <div v-if="pendingImagePreview" class="ai-preview-wrap">
              <img :src="pendingImagePreview" class="ai-preview-img" alt="preview" />
              <button class="ai-preview-close" @click="pendingImageBase64=null;pendingImagePreview=''" title="remove">x</button>
            </div>
            <div class="ai-input-actions">
              <input
                ref="fileInputRef"
                type="file"
                multiple
                :accept="CHAT_ATTACHMENT_ACCEPT"
                class="ai-file-input"
                @change="onFileSelect"
              />
              <input
                ref="skillPackageInputRef"
                type="file"
                accept=".zip,.json,application/zip,application/json,text/plain"
                class="ai-file-input"
                @change="onSmartDocSkillPackageFileSelect"
              />
              <button
                v-if="!smartDocAi.active"
                type="button"
                class="ai-attach-btn"
                :title="CHAT_ATTACHMENT_HINT"
                @click="fileInputRef?.click()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
              <!-- 流式中 / 任务执行中：停止（方块图标，非 X） -->
              <button
                v-if="isStreaming || showComposerTaskStop"
                type="button"
                class="ai-send-btn ai-send-btn--stop"
                title="停止当前任务"
                aria-label="停止当前任务"
                @click="onComposerStopClick"
              >
                <span class="ai-send-stop-icon" aria-hidden="true" />
              </button>
              <!-- 未在流式中：正常发送按钮。回退/重试只出现在对应会话消息上，不替换发送。 -->
              <button
                v-else
                type="button"
                class="ai-send-btn"
                :disabled="!canSend || isTaskLoadingVisible"
                @click="sendAiMessage"
              >
                <svg v-if="!isTaskLoadingVisible" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                <span v-else class="ai-loading-wave"><i></i><i></i><i></i></span>
              </button>
            </div>
          </div>
        </div>
        </template>
      </div>
    </div>
    <ImageLightbox
      :visible="!!previewImageUrl"
      :src="previewImageUrl || ''"
      @close="closeImagePreview"
    />
    <TrReviewReportDialog
      v-model:visible="trReviewReportVisible"
      :project-name="trReviewReportPayload.projectName"
      :verdict-label="trReviewReportPayload.verdictLabel"
      :recommend-pass="trReviewReportPayload.recommendPass"
      :reason="trReviewReportPayload.reason"
      :rule-document-title="trReviewReportPayload.ruleDocumentTitle"
      :key-missing-summary="trReviewReportPayload.keyMissingSummary"
      :checklist-summary="trReviewReportPayload.checklistSummary"
      :checklist-items="trReviewReportPayload.checklistItems"
      :improvement-suggestions="trReviewReportPayload.improvementSuggestions"
    />
    <SkillStudioDialog
      v-if="smartDocAi.active && smartDocSkillStudioOpen"
      :key="`skill-studio-${smartDocAi.openSkillStudioRequest}-${smartDocAi.skillStudioInitialMode}`"
      :project-id="smartDocAi.workspaceId"
      :initial-mode="smartDocAi.skillStudioInitialMode"
      @close="smartDocSkillStudioOpen = false"
      @select="onSmartDocSkillStudioSelect"
      @create-via-chat="onSmartDocSkillStudioCreateViaChat"
      @optimize-via-chat="onSmartDocSkillStudioOptimizeViaChat"
    />
  </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, inject, type Ref } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Dashboard 全屏一诺时隐藏侧栏 UI（fixed 不受外层 clip 影响，须在此组件内压制） */
    chromeHidden?: boolean;
    /** 嵌入创建页等布局：占满父容器，不参与右侧悬浮与宽度拖拽 */
    embeddedLayout?: boolean;
  }>(),
  { chromeHidden: false, embeddedLayout: false },
);
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import AiComposerDropdown from '@/components/AiComposerDropdown.vue';
import AiProjectTodoBar from '@/components/AiProjectTodoBar.vue';
import ComponentBomImportReviewBlock from '@/components/ComponentBomImportReviewBlock.vue';
import SmartDocComposerPlusMenu from '@/components/smart-document/SmartDocComposerPlusMenu.vue';
import SkillStudioDialog from '@/components/smart-document/SkillStudioDialog.vue';
import ImageLightbox from '@/components/ImageLightbox.vue';
import SmartDocAtMentionMenu, {
  type SmartDocAtMenuItem,
} from '@/components/smart-document/SmartDocAtMentionMenu.vue';
import type {
  DocumentSkillDetail,
  DocumentSkillListItem,
  SkillExecutionCitation,
  SmartDocAtKind,
} from '@/types/documentWorkbench';
import {
  SMART_DOC_INLINE_MENTION_RE,
  SMART_DOC_PROJECT_NAME_DISPLAY_MAX,
  buildSmartDocProjectDisplayLabel,
  buildSmartDocProjectOnlyLabel,
  buildSmartDocSkillOrTemplateLabel,
  smartDocAtTypeLabel,
  truncateSmartDocSegment,
  type SmartDocAtToken,
} from '@/types/documentWorkbench';
import {
  getDocumentTemplates,
  getDocumentWorkbenchSnapshot,
  getWorkbenchDocumentDetail,
  createBlankDocument,
  updateWorkbenchDocumentContent,
  moveWorkbenchDocumentToProject,
  resolveSmartDocOutputIntent,
  importDocumentSkill,
  importDocumentSkillPackageFile,
} from '@/api/documentWorkbenchApi';
import {
  useAiAssistantStore,
  type ChatMessage,
  type ChatMessageAttachment,
  type ChatMessageAtToken,
} from '@/stores/useAiAssistantStore';
import { useAiTaskQueueStore, type AiTaskKind, type AiTaskLifecycle, type AiTaskRunContext, isTaskCancelledError, TaskCancelledError } from '@/stores/useAiTaskQueueStore';
import { useSorAnalyzeControlStore } from '@/stores/useSorAnalyzeControlStore';
import { useAiSessionNotifyStore } from '@/stores/useAiSessionNotifyStore';
import { saveProjectDetailEngineeringTab } from '@/utils/projectDetailTabState';
import {
  isProjectAiRoute,
  resolveAiConversationScenario,
  resolveAiPageContextKey,
  resolveProjectIdFromRoute,
  resolveProjectSpaceScenario,
  isSmartDocumentScenario,
} from '@/utils/aiSessionContext';
import { sessionTitleCacheKey } from '@/utils/aiContextGuards';
import { shouldCompactAiHeaderActions } from '@/utils/aiHeaderCompact';
import {
  confirmAiSessionRetry,
  confirmAiSessionRollback,
  confirmPendingRevertBeforeSend,
  AI_SESSION_CONFIRM_MODAL_CLASS,
  AI_SESSION_CONFIRM_Z_INDEX,
  isAiSessionActionActive,
  nextAiSessionGeneration,
  stoppedDiscardMessage,
  type AiSessionAction,
  type PendingTerminatedRevert,
} from '@/utils/aiSessionControls';
import { useAppShellStore } from '@/stores/useAppShellStore';
import {
  actionUrlForTabKey,
  notifyPageTabCompletionOrBell,
  projectDashboardTabKey,
  routeToTabKey,
  trackPageTabRunningTask,
} from '@/utils/pageTabCompletionNotify';
import { useAuthStore } from '@/stores/useAuthStore';
import { getUserSessionScopeSuffix } from '@/utils/userSessionScope';
import { parseConversationContent } from '@/utils/conversationHistoryContent';
import { usePermissionStore } from '@/stores/usePermissionStore';
import { renderMarkdown } from '@/utils/markdown';
import {
  getMessageThoughtSteps,
  streamEventToThoughtStep,
  mergeStreamingThoughtSteps,
  buildFinalThoughtJson,
  extractStructuredFromFinalEvent,
  thoughtStepsToJson,
  type ThoughtStep,
  shouldShowInlineThought,
} from '@/utils/aiThought';
import { mapSorAnalyzeStepsToThought, type SorAnalyzeStepStatus } from '@/utils/sorAnalyzeAiSteps';
import {
  buildWbsGenerateSummary,
  mapWbsGenerateProgressToThought,
  wbsGenerateThoughtDone,
  wbsGenerateThoughtInitial,
} from '@/utils/wbsGenerateAiSteps';
import type { LlmReassignScope, ProjectWbsSnapshot } from '@/api/projectApi';
import {
  getProjectWbsGenerateContext,
  getProjectWbsSnapshot,
  pollProjectWbsGenerateTask,
  reassignTasksByLlm,
  restoreProjectWbsSnapshot,
  saveProjectFields,
  saveProjectWbsGenerateContext,
} from '@/api/projectApi';
import {
  clearDialogueState,
  hoursConfirmPrompt,
  hoursRulesPrompt,
  isHoursConfirmReply,
  isUseDefaultHoursReply,
  isWbsConfirmReply,
  loadDialogueState,
  parseHoursConfigRowsFromText,
  saveDialogueState,
  shouldStartHoursDialogue,
  startAwaitWbsConfirm,
  summarizeHoursRows,
  wbsConfirmPrompt,
} from '@/utils/wbsHoursDialogue';
import {
  notifyProjectWbsTasksChanged,
  subscribeProjectWbsTasksManuallyEdited,
} from '@/utils/wbsTaskEvents';
import { notifyProjectInfoChanged } from '@/utils/projectInfoEvents';
import { updateProjectDashboardTabLabel } from '@/utils/projectPageTab';
import { isProjectWbsGenerateLocked } from '@/utils/projectWbsGenerateLock';
import {
  clearWbsGenerateRollback,
  getWbsGenerateRollback,
  isProjectWbsManuallyEditedSince,
  markProjectWbsManuallyEdited,
  rememberWbsGenerateRollback,
  wbsManualEditEpoch,
} from '@/utils/wbsGenerateSession';
import {
  clampSessionTitle,
  extractSessionTitleFromStreamEvent,
  extractSessionTitleFromThought,
  fallbackSessionTitle,
  inferTaskSessionTitle,
  normalizeMessageForTitleSuggest,
} from '@/utils/aiSessionTitle';
import {
  formatProjectCreateNoAccessAssistantReply,
  formatProjectCreateNoRouteAccessNotice,
  hasProjectCreateNoRouteAccessNotice,
  isStructuredProjectCreateReady,
  stripCreateProjectGapNotice,
} from '@/utils/projectCreateReadiness';
import { canAccessProjectCreateRoute } from '@/utils/projectCreateRouteAccess';
import { runProjectAutofillIntroSequence } from '@/utils/projectAutofillTransition';
import {
  buildProjectCreateFieldEditPrompt,
  resolveFieldEditDualAnswer,
} from '@/utils/projectCreateFieldEditParse';
import {
  getProjectCreateFieldEditFromMessage,
  getProjectCreateUserRefFromMessage,
  isPendingProjectCreateFieldEditMessage,
  projectCreateFieldEditDoneLabel,
} from '@/utils/projectCreateMessagePayloads';
import { useProjectCreateDraftStore } from '@/stores/useProjectCreateDraftStore';
import {
  isExplicitNewProjectIntent,
  shouldForkProjectCreateConversation,
  shouldContinueProjectCreateConversation,
  type ProjectCreateConversationMode,
} from '@/utils/projectCreateConversationMode';
import { useProjectCreateFieldApplyStore } from '@/stores/useProjectCreateFieldApplyStore';
import { useProjectCreateStructuredMergeStore } from '@/stores/useProjectCreateStructuredMergeStore';
import { useProjectCreateAiContextStore } from '@/stores/useProjectCreateAiContextStore';
import { useProjectCreateFieldEditPreviewStore } from '@/stores/useProjectCreateFieldEditPreviewStore';
import { useAiPageContentApplyStore } from '@/stores/useAiPageContentApplyStore';
import { useAiPageContentEditPreviewStore } from '@/stores/useAiPageContentEditPreviewStore';
import { useSmartDocumentAiStore } from '@/stores/useSmartDocumentAiStore';
import {
  isCreateSkillIntent,
  useSmartDocumentSkillAuthorStore,
} from '@/stores/useSmartDocumentSkillAuthorStore';
import {
  isCreateTemplateIntent,
  useSmartDocumentTemplateAuthorStore,
} from '@/stores/useSmartDocumentTemplateAuthorStore';
import {
  isImportableTemplateFile,
  useSmartDocumentSaveAsFromFileStore,
  type SmartDocumentSaveAsFromFileStructured,
} from '@/stores/useSmartDocumentSaveAsFromFileStore';
import {
  displayTemplateSlotLabel,
  useSmartDocumentTemplateGenerateStore,
} from '@/stores/useSmartDocumentTemplateGenerateStore';
import type {
  SmartDocumentSkillDraftStructured,
  SmartDocumentSkillImportStructured,
  SmartDocumentTemplateDraftStructured,
  SmartDocumentTemplateGenerateStructured,
  TemplateGenerationReviewItem,
} from '@/types/documentWorkbench';
import { formatApiErrorMessage, isIgnorableAuthError } from '@/api/http';
import { sanitizeFieldEditUserDisplayText } from '@/utils/projectCreateFieldEditParse';
import { clearPageContentHighlights, highlightPageContentQuote } from '@/utils/aiPageContentHighlight';
import {
  buildAiPageContentFieldEditPrompt,
  isPageContentDiscussIntent,
  resolveAiPageContentFieldEditDualAnswer,
} from '@/utils/aiPageContentFieldEdit';
import { isCrossBlockQuote } from '@/utils/htmlTextAnchor';
import {
  aiPageContentFieldEditDoneLabel,
  getAiPageContentFieldEditFromMessage,
  getAiPageContentUserRefFromMessage,
  isActionableAiPageContentFieldEdit,
  isPendingAiPageContentFieldEditMessage,
  resolveAiPageContentLocateOptions,
  resolveAssistantMessageDisplayContent,
} from '@/utils/aiPageContentMessagePayloads';
import { fetchProjectCreateReferenceKnowledgeHits } from '@/utils/projectCreateReferenceKnowledge';

const emit = defineEmits<{
  (e: 'collapse-change', collapsed: boolean): void;
  (e: 'width-change', width: number): void;
}>();
import {
  navigateToProjectBomDrawingReview,
  parseBomDrawingReviewReportNav,
  stashBomDrawingReviewOpenIntent,
} from '@/utils/bomDrawingReviewNavigate';
import { getProjectDashboardData } from '@/api/dashboardApi';
import { getAccessibleProjects, listMemberPickerDefaultScope, listProjectMemberCandidates, type ProjectAccessOption } from '@/api/projectApi';
import {
  consumeAiChatStream,
  fetchAiChatOptions,
  postAgentChat,
  type AgentChatRequest,
  type AiChatOptionsResponse,
  type AiRunModeId,
  type ConversationSummary,
  type ChatMessageDTO,
  saveAgentConversation,
  getAgentConversationList,
  getAgentConversationDetail,
  renameAgentConversation,
  suggestAgentSessionTitle,
  deleteAgentConversation,
} from '@/api/agentApi';
import { pollChatDrawingReviewResult, submitChatDrawingReviewTask, uploadChatFile, listProjectDocuments } from '@/api/fileApi';
import { ensureUploadLimits } from '@/api/uploadLimitsApi';
import {
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_HINT,
  isChatImageFile,
  validateChatAttachmentFiles,
} from '@/utils/chatAttachmentRules';
import {
  extractObjectKeyFromFileAccessUrl,
  isImageAttachmentUrl,
  isIssueDocumentPreviewable,
  issueAttachmentFileName,
  issueAttachmentFullUrl,
  sanitizeIssueDownloadFileName,
} from '@/utils/issueAttachment';
import { downloadAccessUrlFile } from '@/utils/downloadEncryptedFile';
import {
  isProjectDocumentInnerTabActive,
  requestOpenProjectInnerTab,
} from '@/utils/projectInnerTabNav';
import type { BomGenerateResponse } from '@/api/bomApi';
import type { ComponentRecommendResponse, ComponentRecommendRow, ComponentRecommendTask } from '@/api/componentRecommendApi';
import type { FormulaGenerateStructured } from '@/types/calculatorFormula';
import { CALCULATOR_FORMULA_IMPORT_SCENARIO, CALCULATOR_GENERAL_SCENARIO } from '@/types/calculatorFormula';
import {
  buildCalcSessionPrefix,
  calcScenarioForFormulaId,
  calcSessionTitle,
  isCalculatorCalcScenario,
  loadCalcSessionMeta,
  saveCalcSessionMeta,
  type CalcFormulaSessionMeta,
} from '@/utils/calculatorAiSession';
import { useCalculatorFormulaImportStore } from '@/stores/useCalculatorFormulaImportStore';
import {
  analyzeComponentBomImport,
  buildDefaultOverlapDecisions,
  confirmComponentBomImport,
  getComponentAutoFillStatus,
  overlapDecisionsToPayload,
  startComponentAutoFill,
  type ComponentAutoFillStatus,
  type ComponentBomImportAnalyzeResult,
  type ComponentBomImportOverlapConflict,
} from '@/api/componentBomImportApi';
import { deepReviewDocument } from '@/api/projectReviewApi';
import TrReviewReportDialog from '@/components/review/TrReviewReportDialog.vue';
import type {
  TrReviewChecklistItemResult,
  TrReviewChecklistSummary,
} from '@/api/projectApi';
import {
  pollDesignReviewResult,
  submitDesignReviewTask,
  uploadDrawing,
  thoughtStepsFromReviewPoll,
  type DesignReviewResultData,
} from '@/api/aiReviewApi';
import { setAiReviewDone, setAiReviewPolling } from '@/utils/aiReviewTaskStore';
import { applyDocumentReviewFindingsToHtml } from '@/utils/htmlToReviewMarkdown';
import { dedupeDeepReviewFindings, deepReviewErrorLabel } from '@/utils/documentReviewComment';

interface AiTag {
  label: string;
  text: string;
}

/** 附件项：上传中 progress<100，完成有 objectUrl(图)、name；真实上传后带 token/accessUrl 供 Agent 使用 */
interface AttachmentItem {
  id: string;
  file: File;
  name: string;
  /** 后端转换后的内部文件名，界面仍展示原始 name */
  processingName?: string;
  processingObjectKey?: string;
  processingUrl?: string;
  type: 'image' | 'doc';
  progress: number;
  objectUrl?: string;
  /** 上传成功后后端返回的脱敏访问 Token */
  token?: string;
  /** 脱敏访问链接，供 AI Agent 拉取文件 */
  accessUrl?: string;
  /** MinIO 对象键（上传响应返回，避免前端解 JWT 损坏中文文件名） */
  objectKey?: string;
  error?: string;
  cancel?: () => void;
}

const route = useRoute();
const router = useRouter();
const store = useAiAssistantStore();
const taskQueue = useAiTaskQueueStore();
const sorAnalyzeControlStore = useSorAnalyzeControlStore();
const sessionNotify = useAiSessionNotifyStore();
/** 与 Layout / Dashboard 共用：展开态全屏一诺输入区 */
const shellStore = useAppShellStore();
/** 智能文档工作台：Skill / @ 提示词 / 写回确认 */
const smartDocAi = useSmartDocumentAiStore();
const smartDocSkillAuthor = useSmartDocumentSkillAuthorStore();
const smartDocTemplateAuthor = useSmartDocumentTemplateAuthorStore();
const smartDocSaveAsFromFile = useSmartDocumentSaveAsFromFileStore();
const smartDocTemplateGenerate = useSmartDocumentTemplateGenerateStore();
const smartDocSkillStudioOpen = ref(false);
const smartDocSkillDraftSaving = ref(false);
const skillPackageInputRef = ref<HTMLInputElement | null>(null);
const smartDocSkillImporting = ref(false);
const smartDocTemplateDraftSaving = ref(false);
const smartDocSaveAsFromFileSaving = ref(false);
const smartDocTemplateGenerateOpening = ref(false);

/** 智能文档 @ 联想 */
const aiTextareaRef = ref<HTMLTextAreaElement | null>(null);
const aiTextareaHighlightRef = ref<HTMLElement | null>(null);
/** 中文等 IME 组字中：Enter 用于上屏，不应触发发送 */
const aiInputComposing = ref(false);
/** composition 期间强制刷新高亮层，避免 v-model 滞后导致光标错位 */
const aiInputCompositionTick = ref(0);
const smartDocAtMentionOpen = ref(false);
const smartDocAtMentionItems = ref<SmartDocAtMenuItem[]>([]);
const smartDocAtMentionIndex = ref(0);
const smartDocAtMentionLoading = ref(false);
const smartDocAtMentionEmpty = ref('无匹配项');
const smartDocAtMentionAtIndex = ref(-1);
type SmartDocAtStage = 'category' | 'skill' | 'template' | 'project' | 'project_doc';
const smartDocAtMentionStage = ref<SmartDocAtStage>('category');
const smartDocAtMentionProjectId = ref('');
const smartDocAtMentionProjectName = ref('');
/** Enter 选中联想后抑制同一次按键触发的发送（同元素多 listener 竞态） */
let smartDocSuppressEnterSend = false;
let smartDocAtMentionSeq = 0;
let smartDocProjectsCache: ProjectAccessOption[] | null = null;
const smartDocProjectDocsCache = new Map<string, Array<{ id: string; name: string }>>();

const smartDocQuickPrompts = [
  { key: 'skill' as const, label: '@技能' },
  { key: 'project' as const, label: '@项目' },
  { key: 'template' as const, label: '@模板' },
];

const SMART_DOC_AT_CATEGORIES: Array<{
  key: string;
  name: string;
  category: 'skill' | 'template' | 'project';
  aliases: string[];
}> = [
  { key: 'cat-skill', name: '技能', category: 'skill', aliases: ['技能', 'skill'] },
  { key: 'cat-template', name: '模板', category: 'template', aliases: ['模板', '模版', '模版库', 'template'] },
  { key: 'cat-project', name: '项目', category: 'project', aliases: ['项目', 'project'] },
];

const SMART_DOC_MENTION_RE = new RegExp(SMART_DOC_INLINE_MENTION_RE.source, 'gi');

function escapeSmartDocHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatSmartDocMentionDetail(
  token?: Pick<SmartDocAtToken | ChatMessageAtToken, 'kind' | 'name'> | null,
  fallbackLabel = '',
): string {
  if (token?.name) {
    const kind = String(token.kind || '').toLowerCase();
    const typeLabel =
      kind === 'skill' || kind === 'template' || kind === 'project'
        ? smartDocAtTypeLabel(kind as SmartDocAtKind)
        : (kind || '引用');
    const fullName = String(token.name).replace(/｜/g, ' / ');
    return `${typeLabel}：${fullName}`;
  }
  return String(fallbackLabel || '').replace(/｜/g, ' / ');
}

function snapshotSmartDocAtTokensForMessage(): ChatMessageAtToken[] | undefined {
  if (!smartDocAi.atTokens.length) return undefined;
  return smartDocAi.atTokens.map((t) => ({
    kind: t.kind,
    name: t.name,
    label: t.label,
    refId: t.refId,
  }));
}

function removeSmartDocComposerMention(tokenId: string) {
  const token = smartDocAi.atTokens.find((t) => t.id === tokenId);
  if (!token) return;
  const label = token.label || '';
  if (label && aiInput.value.includes(label)) {
    let next = aiInput.value.replace(label, '');
    next = next.replace(/[ \t]{2,}/g, ' ').replace(/ \n/g, '\n').replace(/\n /g, '\n');
    aiInput.value = next;
  }
  smartDocAi.removeAtToken(tokenId);
  if (smartDocComposerMentionHover.value?.tokenId === tokenId) {
    smartDocComposerMentionHover.value = null;
  }
  if (token.kind === 'template' && smartDocTemplateGenerate.active && !smartDocAi.hasTemplateAtToken) {
    smartDocTemplateGenerate.endFlow();
  }
  void nextTick(() => {
    focusComposer();
    onSmartDocTextareaScroll();
  });
}

type SmartDocComposerMentionHover = {
  tokenId: string;
  detail: string;
  top: number;
  left: number;
};

const smartDocComposerMentionHover = ref<SmartDocComposerMentionHover | null>(null);
let smartDocMentionHoverHideTimer: ReturnType<typeof setTimeout> | null = null;

const smartDocComposerMentionHoverStyle = computed(() => {
  const h = smartDocComposerMentionHover.value;
  if (!h) return {};
  return {
    top: `${h.top}px`,
    left: `${h.left}px`,
  };
});

function clearSmartDocMentionHoverHide() {
  if (smartDocMentionHoverHideTimer) {
    clearTimeout(smartDocMentionHoverHideTimer);
    smartDocMentionHoverHideTimer = null;
  }
}

function scheduleSmartDocMentionHoverHide() {
  clearSmartDocMentionHoverHide();
  smartDocMentionHoverHideTimer = setTimeout(() => {
    smartDocComposerMentionHover.value = null;
    smartDocMentionHoverHideTimer = null;
  }, 160);
}

/** 高亮层在 textarea 下方时，临时穿透命中 mention 节点（供悬停详情） */
function hitSmartDocMentionFromPoint(clientX: number, clientY: number): HTMLElement | null {
  const mirror = aiTextareaHighlightRef.value;
  const ta = aiTextareaRef.value;
  if (!mirror || !ta) return null;
  const prevTa = ta.style.pointerEvents;
  const prevMirror = mirror.style.pointerEvents;
  ta.style.pointerEvents = 'none';
  mirror.style.pointerEvents = 'auto';
  const hit = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
  ta.style.pointerEvents = prevTa;
  mirror.style.pointerEvents = prevMirror;
  const mention = hit?.closest?.('.ai-smart-doc-mention') as HTMLElement | null;
  if (!mention || !mirror.contains(mention)) return null;
  return mention;
}

function showSmartDocComposerMentionHover(mention: HTMLElement) {
  clearSmartDocMentionHoverHide();
  const tokenId = mention.getAttribute('data-token-id') || '';
  const token = tokenId
    ? smartDocAi.atTokens.find((t) => t.id === tokenId)
    : smartDocAi.atTokens.find((t) => t.label === mention.textContent);
  const detail = formatSmartDocMentionDetail(token, mention.textContent || '');
  const stack = mention.closest('.ai-textarea-stack') as HTMLElement | null;
  if (!stack) return;
  const mentionRect = mention.getBoundingClientRect();
  const stackRect = stack.getBoundingClientRect();
  smartDocComposerMentionHover.value = {
    tokenId: token?.id || tokenId,
    detail,
    top: mentionRect.bottom - stackRect.top + 4,
    left: Math.max(0, mentionRect.left - stackRect.left),
  };
}

function onSmartDocTextareaMouseMove(event: MouseEvent) {
  if (!smartDocAi.active || aiInputComposing.value) return;
  const mention = hitSmartDocMentionFromPoint(event.clientX, event.clientY);
  if (!mention) {
    scheduleSmartDocMentionHoverHide();
    return;
  }
  showSmartDocComposerMentionHover(mention);
}

function onSmartDocTextareaMouseLeave(event: MouseEvent) {
  const related = event.relatedTarget as HTMLElement | null;
  if (related?.closest?.('.ai-smart-doc-mention-hover')) return;
  scheduleSmartDocMentionHoverHide();
}

function onSmartDocComposerMentionHoverRemove() {
  const tokenId = smartDocComposerMentionHover.value?.tokenId;
  if (!tokenId) {
    smartDocComposerMentionHover.value = null;
    return;
  }
  removeSmartDocComposerMention(tokenId);
}

function collectSmartDocMentionRanges(text: string): Array<{ start: number; end: number; text: string }> {
  const ranges: Array<{ start: number; end: number; text: string }> = [];
  const overlaps = (start: number, end: number) =>
    ranges.some((r) => !(end <= r.start || start >= r.end));
  const pushUnique = (start: number, end: number, label: string) => {
    if (start < 0 || end <= start || overlaps(start, end)) return;
    ranges.push({ start, end, text: label });
  };
  const labels = [...smartDocAi.atTokens.map((t) => t.label).filter(Boolean)]
    .sort((a, b) => b.length - a.length);
  for (const label of labels) {
    let from = 0;
    while (from < text.length) {
      const idx = text.indexOf(label, from);
      if (idx < 0) break;
      pushUnique(idx, idx + label.length, label);
      from = idx + label.length;
    }
  }
  const re = new RegExp(SMART_DOC_INLINE_MENTION_RE.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    pushUnique(match.index, match.index + match[0].length, match[0]);
  }
  return ranges.sort((a, b) => a.start - b.start);
}

const smartDocInputHighlightHtml = computed(() => {
  if (!smartDocAi.active) return '';
  // composition 期间 v-model 可能滞后，直接读 DOM，保证高亮宽度与光标一致
  void aiInputCompositionTick.value;
  const text = (aiInputComposing.value && aiTextareaRef.value)
    ? aiTextareaRef.value.value
    : (aiInput.value || '');
  let html = '';
  let last = 0;
  for (const range of collectSmartDocMentionRanges(text)) {
    html += escapeSmartDocHtml(text.slice(last, range.start));
    const token = smartDocAi.atTokens.find((t) => t.label === range.text);
    const title = escapeSmartDocHtml(formatSmartDocMentionDetail(token, range.text));
    const tokenIdAttr = token?.id ? ` data-token-id="${escapeSmartDocHtml(token.id)}"` : '';
    // 仅改颜色的 span，不引入 margin/padding，避免与 textarea 测宽不一致
    html += `<span class="ai-smart-doc-mention"${tokenIdAttr} title="${title}">${escapeSmartDocHtml(range.text)}</span>`;
    last = range.end;
  }
  html += escapeSmartDocHtml(text.slice(last));
  // 保留 \n + pre-wrap，与 textarea 换行算法一致；勿整段换成 <br>（易导致光标错位）
  if (text.endsWith('\n')) {
    html += '\u200b';
  }
  return html;
});


watch(
  () => smartDocAi.openSkillStudioRequest,
  (n, prev) => {
    if (smartDocAi.active && n > (prev || 0)) {
      if (smartDocAi.skillStudioInitialMode === 'create') {
        void beginSmartDocSkillAuthoring('create');
        return;
      }
      if (smartDocAi.skillStudioInitialMode === 'import') {
        beginSmartDocSkillPackageImport();
        return;
      }
      smartDocSkillStudioOpen.value = true;
    }
  },
);

watch(
  () => smartDocAi.attachTemplateRequest,
  (n, prev) => {
    if (!smartDocAi.active || !(n > (prev || 0))) return;
    const pending = smartDocAi.pendingAttachTemplate;
    if (!pending?.id || !pending?.name) return;
    smartDocAi.pendingAttachTemplate = null;
    const label = buildSmartDocSkillOrTemplateLabel('template', pending.name);
    // 去掉输入区已有的 @模板/模版 引用（含半成品 @模板），保持单模版
    aiInput.value = stripSmartDocTemplateMentionsFromText(aiInput.value);
    smartDocAi.addAtRef({
      kind: 'template',
      name: pending.name,
      refId: pending.id,
      label,
    });
    insertSmartDocMentionText(label);
    // 新模版引用时结束旧生成会话
    if (smartDocTemplateGenerate.active) {
      smartDocTemplateGenerate.endFlow();
    }
    void nextTick(() => {
      focusComposer();
      onSmartDocTextareaScroll();
    });
  },
);

const smartDocExecutionStatusLabel = computed(() => {
  const map: Record<string, string> = {
    waiting: '等待中',
    retrieving: '检索中',
    generating: '生成中',
    completed: '已完成（待确认）',
    failed: '失败',
    cancelled: '已取消',
  };
  const status = smartDocAi.execution?.status || '';
  return map[status] || status;
});

const smartDocCanCancelExecution = computed(() =>
  ['waiting', 'retrieving', 'generating'].includes(String(smartDocAi.execution?.status || '')),
);

function onSmartDocSkillChange(id: string) {
  smartDocAi.setSkillId(id);
}

/** 清除输入区内全部模版引用（含半成品 @模板 / @模板- ） */
function stripSmartDocTemplateMentionsFromText(text: string): string {
  let s = String(text || '');
  // 先按已登记 token 的完整 label 精确删除
  for (const token of smartDocAi.atTokens) {
    if (token.kind !== 'template' || !token.label) continue;
    const label = token.label;
    let idx = s.indexOf(label);
    while (idx >= 0) {
      s = `${s.slice(0, idx)}${s.slice(idx + label.length)}`;
      idx = s.indexOf(label);
    }
  }
  s = s
    // @模板-「名称」或未闭合的「
    .replace(/@(?:模板|模版|模版库|template)\s*-\s*「[^」]*」?/gi, ' ')
    // @模板-名称（无书名号）
    .replace(/@(?:模板|模版|模版库|template)\s*-[^\s@]*/gi, ' ')
    // 半成品：仅 @模板 / @模版 / @template
    .replace(/@(?:模板|模版|模版库|template)(?=\s|$|@)/gi, ' ')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/^\s+/, '')
    .replace(/\s+$/, '');
  return s;
}

function insertSmartDocMentionText(label: string, atIndex?: number, cursorEnd?: number) {
  const el = aiTextareaRef.value;
  const text = aiInput.value;
  const start = atIndex ?? el?.selectionStart ?? text.length;
  const end = cursorEnd ?? el?.selectionEnd ?? text.length;
  const before = text.slice(0, start);
  const after = text.slice(end);
  const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
  const mention = `${needsLeadingSpace ? ' ' : ''}${label}${/\s$/.test(label) ? '' : ' '}`;
  aiInput.value = `${before}${mention}${after.replace(/^\s+/, '')}`;
  const pos = before.length + mention.length;
  void nextTick(() => {
    el?.focus();
    el?.setSelectionRange(pos, pos);
    onSmartDocTextareaScroll();
  });
}

function onSmartDocSkillPicked(skill: DocumentSkillListItem) {
  const skillLabel = buildSmartDocSkillOrTemplateLabel('skill', skill.name);
  const result = smartDocAi.addAtRef({
    kind: 'skill',
    name: skill.name,
    refId: skill.id,
    label: skillLabel,
  });
  if (!result.ok && result.message) {
    ElMessage.info(result.message);
    return;
  }
  insertSmartDocMentionText(skillLabel);
  void smartDocAi.reloadSkills();
  ElMessage.success(`已选择技能：${skill.name}`);
}

function openSmartDocSkillStudio(mode: 'list' | 'create' | 'import' = 'list') {
  if (mode === 'create') {
    void beginSmartDocSkillAuthoring('create');
    return;
  }
  if (mode === 'import') {
    beginSmartDocSkillPackageImport();
    return;
  }
  smartDocAi.requestOpenSkillStudio(mode);
  smartDocSkillStudioOpen.value = true;
}

/** 上传本地技能：直接弹出文件选择器（支持 zip / json），不打开 Skill 工作室 */
function beginSmartDocSkillPackageImport() {
  if (!smartDocAi.active) {
    ElMessage.warning('请先进入智能文档工作台');
    return;
  }
  if (smartDocSkillImporting.value) {
    ElMessage.info('正在解析技能包，请稍候');
    return;
  }
  if (skillPackageInputRef.value) {
    skillPackageInputRef.value.value = '';
    skillPackageInputRef.value.click();
  }
}

async function onSmartDocSkillPackageFileSelect(ev: Event) {
  const input = ev.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  await importSmartDocSkillPackageInChat(file);
}

async function importSmartDocSkillPackageInChat(file: File) {
  const workspaceId = smartDocAi.workspaceId;
  if (!workspaceId) {
    ElMessage.warning('缺少工作区，无法导入技能');
    return;
  }
  const lower = file.name.toLowerCase();
  const isZip = lower.endsWith('.zip');
  const isJson = lower.endsWith('.json') || lower.endsWith('.txt');
  if (!isZip && !isJson) {
    ElMessage.warning('请选择标准 Skill ZIP 包，或兼容的 JSON 文件');
    return;
  }

  smartDocSkillImporting.value = true;
  addSessionMessage('user', `上传本地技能：${file.name}`);
  const structured: SmartDocumentSkillImportStructured = {
    kind: 'smart_document_skill_import',
    status: 'parsing',
    fileName: file.name,
  };
  const assistantMsg = addSessionMessage(
    'assistant',
    `正在解析技能包「${file.name}」…`,
    undefined,
    undefined,
    structured as unknown as Record<string, unknown>,
  );
  await nextTick();
  scrollAiToBottom();

  try {
    const result = isZip
      ? await importDocumentSkillPackageFile(workspaceId, file)
      : await importDocumentSkill(workspaceId, {
          packageContent: await file.text(),
        });
    const skill = result.skill;
    const warnings = result.warnings || [];
    const doneStructured: SmartDocumentSkillImportStructured = {
      kind: 'smart_document_skill_import',
      status: 'done',
      fileName: file.name,
      skillId: skill.id,
      code: skill.code,
      name: skill.name,
      category: skill.category,
      description: skill.description,
      hasStandardPackage: skill.hasStandardPackage,
      acceptanceCaseCount: skill.acceptanceCaseCount,
      warnings,
    };
    const warnText = warnings.length
      ? `\n\n校验提示（${warnings.length}）：\n${warnings.map((w) => `- ${w}`).join('\n')}`
      : '';
    updateSessionMessage(assistantMsg.id, {
      content: `已成功导入技能「${skill.name}」（草稿）。可点击「开始使用」将其加入当前对话。${warnText}`,
      structuredResult: doneStructured as unknown as Record<string, unknown>,
    });
    await smartDocAi.reloadSkills();
    await persistConversation();
    ElMessage.success(`已导入技能：${skill.name}`);
  } catch (e) {
    const errMsg = formatApiErrorMessage(e);
    const failedStructured: SmartDocumentSkillImportStructured = {
      kind: 'smart_document_skill_import',
      status: 'failed',
      fileName: file.name,
      errorMessage: errMsg,
    };
    updateSessionMessage(assistantMsg.id, {
      content: `导入技能包「${file.name}」失败：${errMsg}`,
      structuredResult: failedStructured as unknown as Record<string, unknown>,
    });
    await persistConversation();
    ElMessage.error(errMsg);
  } finally {
    smartDocSkillImporting.value = false;
    await nextTick();
    scrollAiToBottom();
  }
}

function bindSmartDocSkillAuthorContext() {
  smartDocSkillAuthor.bindPanelContext({
    scenario: conversationScenario.value,
    sessionId: currentSessionId.value,
  });
}

async function beginSmartDocSkillAuthoring(
  mode: 'create' | 'optimize' = 'create',
  sourceSkillId?: string,
) {
  smartDocSkillStudioOpen.value = false;
  if (smartDocTemplateAuthor.active) {
    smartDocTemplateAuthor.endTemplateAuthoring();
  }
  bindSmartDocSkillAuthorContext();
  await smartDocSkillAuthor.beginSkillAuthoring({
    mode,
    sourceSkillId: sourceSkillId || (mode === 'optimize' ? smartDocAi.selectedSkillId : undefined),
  });
  await nextTick();
  scrollAiToBottom();
}

function bindSmartDocTemplateAuthorContext() {
  smartDocTemplateAuthor.bindPanelContext({
    scenario: conversationScenario.value,
    sessionId: currentSessionId.value,
  });
}

async function beginSmartDocTemplateAuthoring() {
  if (smartDocSkillAuthor.active) {
    smartDocSkillAuthor.endSkillAuthoring();
  }
  bindSmartDocTemplateAuthorContext();
  await smartDocTemplateAuthor.beginTemplateAuthoring();
  await nextTick();
  scrollAiToBottom();
}

function onSmartDocSkillStudioCreateViaChat() {
  void beginSmartDocSkillAuthoring('create');
}

function onSmartDocSkillStudioOptimizeViaChat(skillId: string) {
  void beginSmartDocSkillAuthoring('optimize', skillId);
}

function onSmartDocSkillStudioSelect(skill: DocumentSkillDetail | DocumentSkillListItem) {
  smartDocSkillStudioOpen.value = false;
  const skillLabel = buildSmartDocSkillOrTemplateLabel('skill', skill.name);
  const result = smartDocAi.addAtRef({
    kind: 'skill',
    name: skill.name,
    refId: skill.id,
    label: skillLabel,
  });
  if (!result.ok && result.message) {
    ElMessage.info(result.message);
    return;
  }
  insertSmartDocMentionText(skillLabel);
  void smartDocAi.reloadSkills();
  ElMessage.success(`已选择技能：${skill.name}`);
}

function closeSmartDocAtMention() {
  smartDocAtMentionOpen.value = false;
  smartDocAtMentionItems.value = [];
  smartDocAtMentionIndex.value = 0;
  smartDocAtMentionLoading.value = false;
  smartDocAtMentionAtIndex.value = -1;
  smartDocAtMentionStage.value = 'category';
  smartDocAtMentionProjectId.value = '';
  smartDocAtMentionProjectName.value = '';
}

/** 已完成的引用后已继续输入，不再作为当前联想 */
function isCompletedSmartDocMentionQuery(query: string): boolean {
  const skillOrTpl = query.match(/^(技能|模版库|模版|模板|skill|template)-「[^」]+」/i)
    || query.match(/^(技能|模版库|模版|模板|skill|template)-([^\s@「]+)/i);
  if (skillOrTpl) {
    return /^\s/.test(query.slice(skillOrTpl[0].length));
  }
  // 双段文档引用
  const projectFull = query.match(/^(项目|project)-「[^」]+」-「[^」]+」/i)
    || query.match(/^(项目|project)-([^\s@「]+)-([^\s@「]+)/i);
  if (projectFull) {
    return /^\s/.test(query.slice(projectFull[0].length));
  }
  // 单段项目级引用（全库）：@项目-「名」 后跟空格则视为完成；后跟 - 则继续文档联想
  const projectOnly = query.match(/^(项目|project)-「[^」]+」/i)
    || query.match(/^(项目|project)-([^\s@「\-]+)/i);
  if (projectOnly) {
    const rest = query.slice(projectOnly[0].length);
    if (rest.startsWith('-')) return false;
    return /^\s/.test(rest);
  }
  return false;
}

function parseSmartDocAtQuery(text: string, cursor: number): { atIndex: number; query: string } | null {
  const before = text.slice(0, Math.max(0, cursor));
  let searchFrom = 0;
  let activeAt = -1;
  while (searchFrom < before.length) {
    const i = before.indexOf('@', searchFrom);
    if (i < 0) break;
    const query = before.slice(i + 1);
    if (!/\n/.test(query) && !isCompletedSmartDocMentionQuery(query)) {
      activeAt = i;
    }
    searchFrom = i + 1;
  }
  if (activeAt < 0) return null;
  const query = before.slice(activeAt + 1);
  if (/\n/.test(query)) return null;
  return { atIndex: activeAt, query };
}

type SmartDocAtMatch = {
  stage: SmartDocAtStage;
  filter: string;
  projectId?: string;
  projectName?: string;
};

function readSmartDocMentionSegment(input: string): {
  value: string;
  rest: string;
  closed: boolean;
} {
  const t = input.trimStart();
  if (!t) return { value: '', rest: '', closed: false };
  if (t.startsWith('「')) {
    const end = t.indexOf('」');
    if (end < 0) {
      return { value: t.slice(1), rest: '', closed: false };
    }
    return { value: t.slice(1, end), rest: t.slice(end + 1), closed: true };
  }
  const dash = t.indexOf('-');
  if (dash < 0) {
    return { value: t, rest: '', closed: false };
  }
  return { value: t.slice(0, dash), rest: t.slice(dash), closed: true };
}

function findProjectByMentionSegment(
  segment: string,
  projects: ProjectAccessOption[],
): ProjectAccessOption | undefined {
  const s = segment.trim();
  if (!s) return undefined;
  const exact = projects.find((p) => p.name === s);
  if (exact) return exact;
  const insensitive = projects.find((p) => p.name.toLowerCase() === s.toLowerCase());
  if (insensitive) return insensitive;
  const byTrunc = projects.filter(
    (p) => truncateSmartDocSegment(p.name, SMART_DOC_PROJECT_NAME_DISPLAY_MAX) === s,
  );
  if (byTrunc.length === 1) return byTrunc[0];
  return undefined;
}

async function matchSmartDocAtCategory(query: string): Promise<SmartDocAtMatch> {
  const raw = query.trimStart();
  const lower = raw.toLowerCase();
  for (const cat of SMART_DOC_AT_CATEGORIES) {
    for (const alias of cat.aliases) {
      const al = alias.toLowerCase();
      if (lower === al) {
        return { stage: cat.category, filter: '' };
      }
      if (
        lower.startsWith(`${al} `)
        || lower.startsWith(`${al}-`)
        || lower.startsWith(`${al}：`)
        || lower.startsWith(`${al}:`)
      ) {
        const rest = raw.slice(alias.length).replace(/^[\s\-：:]+/, '');
        if (cat.category === 'project') {
          return resolveProjectAtMatch(rest);
        }
        return { stage: cat.category, filter: rest.replace(/^「/, '').replace(/」$/, '') };
      }
      if (/[\u4e00-\u9fff]/.test(alias) && lower.startsWith(al) && lower.length > al.length) {
        const rest = raw.slice(alias.length);
        if (cat.category === 'project') {
          return resolveProjectAtMatch(rest);
        }
        return { stage: cat.category, filter: rest.replace(/^「/, '').replace(/」$/, '') };
      }
    }
  }
  return { stage: 'category', filter: raw };
}

async function resolveProjectAtMatch(rest: string): Promise<SmartDocAtMatch> {
  const trimmed = rest.trim();
  if (!trimmed) {
    return { stage: 'project', filter: '' };
  }
  const first = readSmartDocMentionSegment(trimmed);
  if (!first.closed) {
    return { stage: 'project', filter: first.value };
  }
  let after = first.rest;
  if (after.startsWith('-')) {
    after = after.slice(1);
  } else if (!after.trim()) {
    return { stage: 'project', filter: first.value };
  } else {
    return { stage: 'project', filter: first.value };
  }

  const projects = await loadSmartDocAccessibleProjects();
  const hit = findProjectByMentionSegment(first.value, projects);
  if (!hit) {
    return { stage: 'project', filter: first.value };
  }
  const second = readSmartDocMentionSegment(after);
  return {
    stage: 'project_doc',
    filter: second.value,
    projectId: String(hit.id),
    projectName: hit.name,
  };
}

function filterByKeyword<T extends { name: string }>(list: T[], keyword: string): T[] {
  const k = keyword.trim().toLowerCase();
  if (!k) return list;
  return list.filter((item) => item.name.toLowerCase().includes(k));
}

async function loadSmartDocAccessibleProjects(): Promise<ProjectAccessOption[]> {
  if (!smartDocProjectsCache) {
    smartDocProjectsCache = await getAccessibleProjects({ page: 1, size: 200 });
  }
  return smartDocProjectsCache;
}

async function loadSmartDocProjectDocuments(projectId: string): Promise<Array<{ id: string; name: string }>> {
  const cached = smartDocProjectDocsCache.get(projectId);
  if (cached) return cached;
  const docs: Array<{ id: string; name: string }> = [];
  const seen = new Set<string>();

  // 1) 项目资料附件（用户有权限查看的立项文档）
  const numericId = Number(projectId);
  if (Number.isFinite(numericId) && numericId > 0) {
    try {
      const bundle = await listProjectDocuments(numericId);
      for (const f of bundle.files || []) {
        const id = String(f.objectKey || f.id || '').trim();
        const name = String(f.fileName || '').trim();
        if (!id || !name || seen.has(id)) continue;
        seen.add(id);
        docs.push({ id, name });
      }
    } catch {
      /* 附件列表失败时继续尝试工作台文档 */
    }
  }

  // 2) 智能文档工作台文档（若有）
  try {
    const snap = await getDocumentWorkbenchSnapshot(projectId);
    for (const d of snap.documents || []) {
      const id = String(d.id || '').trim();
      const name = String(d.name || '').trim();
      if (!id || !name || seen.has(id)) continue;
      seen.add(id);
      docs.push({ id, name });
    }
  } catch {
    /* ignore */
  }

  smartDocProjectDocsCache.set(projectId, docs);
  return docs;
}

async function buildSmartDocAtMentionItems(
  matched: SmartDocAtMatch,
): Promise<{ items: SmartDocAtMenuItem[]; empty: string }> {
  const { stage, filter } = matched;
  if (stage === 'category') {
    const k = filter.trim().toLowerCase();
    const cats = SMART_DOC_AT_CATEGORIES.filter((c) => {
      if (!k) return true;
      return c.name.toLowerCase().includes(k)
        || c.aliases.some((a) => a.toLowerCase().includes(k) || k.includes(a.toLowerCase()));
    });
    return {
      items: cats.map((c) => ({
        key: c.key,
        kind: 'category' as const,
        kindLabel: '',
        name: `@${c.name}`,
        refId: c.category,
        category: c.category,
      })),
      empty: '无匹配类型',
    };
  }
  if (stage === 'skill') {
    if (!smartDocAi.skills.length) {
      await smartDocAi.reloadSkills();
    }
    const list = filterByKeyword(
      smartDocAi.skills.map((s) => ({ id: s.id, name: s.name })),
      filter,
    );
    return {
      items: list.map((s) => ({
        key: `skill-${s.id}`,
        kind: 'skill' as const,
        kindLabel: '技能',
        name: s.name,
        refId: s.id,
      })),
      empty: filter ? '无匹配技能' : '暂无技能，可先与 AI 对话创建',
    };
  }
  if (stage === 'template') {
    const scopes: Array<'mine' | 'recent' | 'enterprise' | 'project'> = ['mine', 'recent', 'enterprise'];
    if (!smartDocAi.personalWorkspace) scopes.push('project');
    const seen = new Set<string>();
    const templates: SmartDocAtMenuItem[] = [];
    for (const scope of scopes) {
      try {
        const res = await getDocumentTemplates({
          projectId: smartDocAi.workspaceId || '0',
          scope,
          keyword: filter.trim() || undefined,
          status: 'published',
        });
        for (const t of res.templates || []) {
          if (seen.has(t.id)) continue;
          if (t.status && t.status !== 'published') continue;
          seen.add(t.id);
          templates.push({
            key: `tpl-${t.id}`,
            kind: 'template',
            kindLabel: '模板',
            name: t.title,
            refId: t.id,
          });
        }
      } catch {
        /* ignore */
      }
    }
    const filtered = filterByKeyword(
      templates.map((t) => ({ id: t.refId, name: t.name, item: t })),
      filter,
    ).map((x) => x.item);
    return {
      items: filtered,
      empty: filter ? '无匹配模板' : '暂无模板',
    };
  }
  if (stage === 'project_doc') {
    const projectId = matched.projectId || '';
    const projectName = matched.projectName || '';
    if (!projectId) {
      return { items: [], empty: '请先选择有权限的项目' };
    }
    const docs = await loadSmartDocProjectDocuments(projectId);
    const list = filterByKeyword(docs, filter);
    const allItem: SmartDocAtMenuItem = {
      key: `doc-all-${projectId}`,
      kind: 'project_all',
      kindLabel: '检索',
      name: '全部可读资料（自动检索）',
      refId: projectId,
      projectId,
      projectName,
    };
    return {
      items: [
        ...(filter.trim() ? [] : [allItem]),
        ...list.map((d) => ({
          key: `doc-${projectId}-${d.id}`,
          kind: 'project_doc' as const,
          kindLabel: '文档',
          name: d.name,
          refId: d.id,
          projectId,
          projectName,
        })),
      ],
      empty: filter ? '无匹配项目文档' : '该项目暂无文档',
    };
  }
  try {
    const projects = await loadSmartDocAccessibleProjects();
    const list = filterByKeyword(
      projects.map((p) => ({ id: String(p.id), name: p.name })),
      filter,
    );
    return {
      items: list.map((p) => ({
        key: `project-${p.id}`,
        kind: 'project' as const,
        kindLabel: '项目',
        name: p.name,
        refId: p.id,
      })),
      empty: filter ? '无匹配项目' : '暂无可访问项目',
    };
  } catch {
    return { items: [], empty: '加载项目失败，请重试' };
  }
}

async function refreshSmartDocAtMention() {
  if (!smartDocAi.active) {
    closeSmartDocAtMention();
    return;
  }
  pruneSmartDocAtTokensByInput();
  const el = aiTextareaRef.value;
  const text = aiInput.value;
  const cursor = el?.selectionStart ?? text.length;
  const parsed = parseSmartDocAtQuery(text, cursor);
  if (!parsed) {
    closeSmartDocAtMention();
    return;
  }
  const matched = await matchSmartDocAtCategory(parsed.query);
  smartDocAtMentionAtIndex.value = parsed.atIndex;
  smartDocAtMentionStage.value = matched.stage;
  smartDocAtMentionProjectId.value = matched.projectId || '';
  smartDocAtMentionProjectName.value = matched.projectName || '';
  smartDocAtMentionOpen.value = true;
  const seq = ++smartDocAtMentionSeq;
  smartDocAtMentionLoading.value = true;
  try {
    const { items, empty } = await buildSmartDocAtMentionItems(matched);
    if (seq !== smartDocAtMentionSeq) return;
    smartDocAtMentionItems.value = items;
    smartDocAtMentionEmpty.value = empty;
    smartDocAtMentionIndex.value = 0;
  } catch {
    if (seq !== smartDocAtMentionSeq) return;
    smartDocAtMentionItems.value = [];
    smartDocAtMentionEmpty.value = '加载失败，请重试';
  } finally {
    if (seq === smartDocAtMentionSeq) {
      smartDocAtMentionLoading.value = false;
    }
  }
}

function pruneSmartDocAtTokensByInput() {
  const text = aiInput.value || '';
  // 发送前会清空输入区并触发 @input；空文本时不剪枝，避免刚选的 @模板 token 被误删后落到客服意图
  if (!text.trim()) return;
  // 输入区仍有任意 @模板 提及时，保留模版 token（展示名可能被截断，exact label 对不上）
  const hasAnyTemplateMention = /@(?:模板|模版|模版库|template)\s*-/i.test(text);
  for (const token of [...smartDocAi.atTokens]) {
    if (token.kind === 'template' && hasAnyTemplateMention) {
      continue;
    }
    // 输入区仍有任意 @技能 提及时，保留技能 token（展示名可能被截断）
    if (token.kind === 'skill' && /@(?:技能|Skill|skill)\s*-/i.test(text)) {
      continue;
    }
    // 输入区仍有任意 @项目 提及时，保留项目 token
    if (token.kind === 'project' && /@项目\s*-/i.test(text)) {
      continue;
    }
    const candidates = [token.label, token.name].filter(Boolean);
    if (token.kind === 'template') {
      candidates.push(
        `@模板-「${token.name}」`,
        `@模板-${token.name}`,
        `@模版-${token.name}`,
      );
    } else if (token.kind === 'skill') {
      candidates.push(`@技能-「${token.name}」`, `@技能-${token.name}`);
    } else if (token.kind === 'project') {
      const projectName = token.name.includes('｜') ? token.name.split('｜')[0] : token.name;
      candidates.push(
        buildSmartDocProjectOnlyLabel(projectName),
        `@项目-「${projectName}」`,
        `@项目-${projectName}`,
      );
      if (token.name.includes('｜')) {
        const docName = token.name.split('｜').slice(1).join('｜');
        candidates.push(buildSmartDocProjectDisplayLabel(projectName, docName));
      }
    }
    if (!candidates.some((c) => c && text.includes(c))) {
      smartDocAi.removeAtToken(token.id);
    }
  }
}

/** 当前路由是否为智能文档工作台（不含 sticky active） */
function isOnSmartDocumentWorkbenchRoute(): boolean {
  const name = String(route.name || '');
  return name === 'SmartDocumentWorkbench' || name === 'SmartDocumentWorkbenchHome';
}

/** 是否在智能文档工作台上下文（以当前路由为准，避免 keep-alive 残留 active 劫持项目页对话） */
function isSmartDocumentWorkbenchContext(): boolean {
  return isOnSmartDocumentWorkbenchRoute();
}

function textHasSmartDocTemplateMention(text: string): boolean {
  const t = String(text || '');
  return /@(?:模板|模版|模版库|template)\s*-\s*「[^」]+」/i.test(t)
    || /@(?:模板|模版|模版库|template)\s*-[^\s@「]+/i.test(t);
}

function textHasSmartDocSkillMention(text: string): boolean {
  const t = String(text || '');
  return /@(?:技能|Skill|skill)\s*-\s*「[^」]+」/i.test(t)
    || /@(?:技能|Skill|skill)\s*-[^\s@「]+/i.test(t);
}

function extractSmartDocTemplateMentionName(text: string): string {
  const t = String(text || '');
  const m1 = t.match(/@(?:模板|模版|模版库|template)\s*-\s*「([^」]+)」/i);
  if (m1?.[1]) return m1[1].trim();
  const m2 = t.match(/@(?:模板|模版|模版库|template)\s*-([^\s@「]+)/i);
  return (m2?.[1] || '').trim();
}

function extractSmartDocSkillMentionName(text: string): string {
  const t = String(text || '');
  const m1 = t.match(/@(?:技能|Skill|skill)\s*-\s*「([^」]+)」/i);
  if (m1?.[1]) return m1[1].trim();
  const m2 = t.match(/@(?:技能|Skill|skill)\s*-([^\s@「]+)/i);
  return (m2?.[1] || '').trim();
}

function textHasSmartDocProjectMention(text: string): boolean {
  const t = String(text || '');
  return /@项目\s*-\s*「[^」]+」/.test(t) || /@项目\s*-[^\s@「]+/.test(t);
}

function extractSmartDocProjectMentionName(text: string): string {
  const t = String(text || '');
  const m1 = t.match(/@项目\s*-\s*「([^」]+)」/);
  if (m1?.[1]) return m1[1].trim();
  const m2 = t.match(/@项目\s*-([^\s@「]+)/);
  return (m2?.[1] || '').trim();
}

function matchProjectByMentionName(
  mentioned: string,
  projects: Array<{ id: string | number; name: string }>,
): { id: string; name: string } | null {
  const m = mentioned.trim();
  if (!m || !projects.length) return null;
  const exact = projects.find((p) => (p.name || '').trim() === m);
  if (exact) return { id: String(exact.id), name: exact.name || m };
  const parts = m.split(/…|\.\.\./);
  const head = (parts[0] || '').trim();
  const tail = (parts.length > 1 ? parts[parts.length - 1] : '').trim();
  const fuzzy = projects.find((p) => {
    const name = (p.name || '').trim();
    if (!name) return false;
    if (head && name.startsWith(head) && (!tail || name.endsWith(tail))) return true;
    if (head.length >= 4 && name.includes(head.slice(0, Math.min(12, head.length)))) return true;
    const label = buildSmartDocProjectOnlyLabel(name);
    return label.includes(m) || m.includes(name.slice(0, 8));
  });
  return fuzzy ? { id: String(fuzzy.id), name: fuzzy.name || m } : null;
}

/** 发送前：若输入含 @项目 但 token 丢失，则按名称回填，确保 Skill/模版检索打到正确项目 */
async function ensureSmartDocProjectTokenFromInput(text: string): Promise<boolean> {
  ensureSmartDocWorkspaceBound({ force: isOnSmartDocumentWorkbenchRoute() || textHasSmartDocProjectMention(text) });
  if (smartDocAi.atTokens.some((t) => t.kind === 'project' && /^\d+/.test(String(t.refId || '')))) {
    return true;
  }
  if (!textHasSmartDocProjectMention(text)) return false;

  const mentioned = extractSmartDocProjectMentionName(text);
  if (!mentioned) return false;

  try {
    const projects = await loadSmartDocAccessibleProjects();
    const hit = matchProjectByMentionName(
      mentioned,
      projects.map((p) => ({ id: String(p.id), name: p.name })),
    );
    if (hit) {
      const label = buildSmartDocProjectOnlyLabel(hit.name);
      smartDocAi.addAtRef({
        kind: 'project',
        name: hit.name,
        refId: hit.id,
        label,
      });
      return true;
    }
  } catch {
    // fall through
  }
  ElMessage.warning(`未找到项目「${mentioned}」，请从 @项目 列表重新选择`);
  return false;
}

function ensureSmartDocWorkspaceBound(opts?: { force?: boolean }) {
  if (smartDocAi.active) return;
  // 项目详情等普通页不应因 @项目 回填 / resolveChatProjectId 副作用激活智能文档模式，
  // 否则后续每条消息都会被劫持成「智能文档对话」。
  if (!opts?.force && !isOnSmartDocumentWorkbenchRoute()) return;
  let workspaceId = '';
  // 仅智能文档 / 项目路由下才把 params.id 当作工作区；避免 AI 需求详情的需求 ID 被当成项目 ID
  if (isOnSmartDocumentWorkbenchRoute() || isProjectAiRoute(route)) {
    const rawId = route.params.id;
    const id = Array.isArray(rawId) ? rawId[0] : rawId;
    workspaceId = id != null && /^\d+$/.test(String(id)) ? String(id) : '';
  }
  // 非工作台路由时，回退当前 AI 面板选中的项目
  if (!workspaceId && projectId.value > 0) {
    workspaceId = String(projectId.value);
  }
  if (!workspaceId) workspaceId = '0';
  smartDocAi.bindWorkspace({
    workspaceId,
    personalWorkspace: workspaceId === '0',
  });
}

function matchTemplateByMentionName(
  mentioned: string,
  templates: Array<{ id: string; title?: string }>,
): { id: string; title: string } | null {
  const m = mentioned.trim();
  if (!m || !templates.length) return null;
  const exact = templates.find((t) => (t.title || '').trim() === m);
  if (exact) return { id: exact.id, title: exact.title || m };

  // 截断展示：头…尾（兼容 ...）
  const parts = m.split(/…|\.\.\./);
  const head = (parts[0] || '').trim();
  const tail = (parts.length > 1 ? parts[parts.length - 1] : '').trim();
  const fuzzy = templates.find((t) => {
    const title = (t.title || '').trim();
    if (!title) return false;
    if (head && title.startsWith(head) && (!tail || title.endsWith(tail))) return true;
    if (head.length >= 6 && title.includes(head.slice(0, Math.min(12, head.length)))) return true;
    const label = buildSmartDocSkillOrTemplateLabel('template', title);
    return label.includes(m) || m.includes(title.slice(0, 8));
  });
  return fuzzy ? { id: fuzzy.id, title: fuzzy.title || m } : null;
}

/** 发送前：若输入含 @模板 但 token 丢失，则按名称回填，确保进入模版生成编排 */
async function ensureSmartDocTemplateTokenFromInput(text: string): Promise<boolean> {
  ensureSmartDocWorkspaceBound({ force: true });
  if (smartDocAi.hasTemplateAtToken) return true;
  if (smartDocTemplateGenerate.canContinue) return true;
  if (!textHasSmartDocTemplateMention(text)) return false;

  const mentioned = extractSmartDocTemplateMentionName(text);
  if (!mentioned) return false;

  const keyword = mentioned.split(/…|\.\.\./)[0]?.trim() || mentioned;
  try {
    const workspaceId = smartDocAi.workspaceId || '0';
    const scopes: Array<'available' | 'mine' | 'project' | 'enterprise' | 'recent'> = [
      'available',
      'mine',
      'project',
      'enterprise',
      'recent',
    ];
    for (const scope of scopes) {
      const res = await getDocumentTemplates({
        projectId: workspaceId,
        scope,
        keyword: keyword || undefined,
        status: 'published',
      });
      const hit = matchTemplateByMentionName(mentioned, res.templates || []);
      if (hit) {
        const label = buildSmartDocSkillOrTemplateLabel('template', hit.title);
        smartDocAi.addAtRef({
          kind: 'template',
          name: hit.title,
          refId: hit.id,
          label,
        });
        return true;
      }
    }
  } catch {
    // fall through
  }
  ElMessage.warning('未识别到有效模版引用，请从模版库「使用」或通过 @模板 重新选择');
  return false;
}

function shouldRouteToSmartDocTemplateGenerate(text: string): boolean {
  // 只要有 @模板 引用（token / 文案 / 续轮），就走模版生成编排，不强制要求当前在工作台页
  return smartDocAi.hasTemplateAtToken
    || smartDocTemplateGenerate.canContinue
    || textHasSmartDocTemplateMention(text);
}

function matchSkillByMentionName(
  mentioned: string,
  skills: Array<{ id: string; name?: string; code?: string }>,
): { id: string; name: string } | null {
  const m = mentioned.trim();
  if (!m || !skills.length) return null;
  const exact = skills.find((s) => (s.name || '').trim() === m);
  if (exact) return { id: exact.id, name: exact.name || m };

  const byCode = skills.find((s) => (s.code || '').trim() === m);
  if (byCode) return { id: byCode.id, name: byCode.name || m };

  // 截断展示：头…尾
  const parts = m.split(/…|\.\.\./);
  const head = (parts[0] || '').trim();
  const tail = (parts.length > 1 ? parts[parts.length - 1] : '').trim();
  const fuzzy = skills.find((s) => {
    const name = (s.name || '').trim();
    if (!name) return false;
    if (head && name.startsWith(head) && (!tail || name.endsWith(tail))) return true;
    if (head.length >= 4 && name.includes(head.slice(0, Math.min(12, head.length)))) return true;
    const label = buildSmartDocSkillOrTemplateLabel('skill', name);
    return label.includes(m) || m.includes(name.slice(0, 8));
  });
  return fuzzy ? { id: fuzzy.id, name: fuzzy.name || m } : null;
}

/** 发送前：若输入含 @技能 但 token/selectedSkillId 丢失，则按名称回填 */
async function ensureSmartDocSkillTokenFromInput(text: string): Promise<boolean> {
  ensureSmartDocWorkspaceBound({ force: true });
  if (smartDocAi.selectedSkillId) return true;
  if (smartDocAi.hasSkillAtToken) {
    const token = smartDocAi.skillAtTokens[0];
    if (token?.refId) {
      smartDocAi.setSkillId(token.refId);
      return true;
    }
  }
  if (!textHasSmartDocSkillMention(text)) return false;

  const mentioned = extractSmartDocSkillMentionName(text);
  if (!mentioned) return false;

  try {
    if (!smartDocAi.skills.length) {
      await smartDocAi.reloadSkills();
    }
    let hit = matchSkillByMentionName(mentioned, smartDocAi.skills);
    if (!hit) {
      // 列表可能过期，强制刷新后再匹配一次
      await smartDocAi.reloadSkills();
      hit = matchSkillByMentionName(mentioned, smartDocAi.skills);
    }
    if (hit) {
      const label = buildSmartDocSkillOrTemplateLabel('skill', hit.name);
      smartDocAi.addAtRef({
        kind: 'skill',
        name: hit.name,
        refId: hit.id,
        label,
      });
      return true;
    }
  } catch {
    // fall through
  }
  ElMessage.warning(`未找到技能「${mentioned}」，请从 + → 技能 中重新选择`);
  return false;
}

function shouldRouteToSmartDocSkillExecution(text: string): boolean {
  return !!smartDocAi.selectedSkillId
    || smartDocAi.hasSkillAtToken
    || textHasSmartDocSkillMention(text);
}

function replaceSmartDocAtQuery(insert: string, opts?: { trailingDash?: boolean; trailingSpace?: boolean }) {
  const atIndex = smartDocAtMentionAtIndex.value;
  const trailingSpace = opts?.trailingSpace !== false && !opts?.trailingDash;
  const suffix = opts?.trailingDash ? '-' : (trailingSpace ? ' ' : '');
  if (atIndex < 0) {
    insertSmartDocMentionText(insert.endsWith('-') || insert.endsWith(' ') ? insert : `${insert}${suffix}`);
    return;
  }
  const el = aiTextareaRef.value;
  const text = aiInput.value;
  const cursor = el?.selectionStart ?? text.length;
  const before = text.slice(0, atIndex);
  const after = text.slice(cursor);
  const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
  const core = insert.replace(/[\s-]+$/, '');
  const mention = `${needsLeadingSpace ? ' ' : ''}${core}${suffix}`;
  aiInput.value = `${before}${mention}${after.replace(/^\s+/, '')}`;
  const pos = before.length + mention.length;
  void nextTick(() => {
    el?.focus();
    el?.setSelectionRange(pos, pos);
    onSmartDocTextareaScroll();
  });
}

function onSmartDocAtMentionPick(item: SmartDocAtMenuItem) {
  if (item.kind === 'category' && item.category) {
    const name = item.category === 'skill' ? '技能' : item.category === 'project' ? '项目' : '模板';
    const atIndex = smartDocAtMentionAtIndex.value;
    const el = aiTextareaRef.value;
    const text = aiInput.value;
    const cursor = el?.selectionStart ?? text.length;
    if (atIndex >= 0) {
      const insert = `@${name}`;
      aiInput.value = `${text.slice(0, atIndex)}${insert}${text.slice(cursor)}`;
      void nextTick(() => {
        const pos = atIndex + insert.length;
        el?.focus();
        el?.setSelectionRange(pos, pos);
        void refreshSmartDocAtMention();
      });
    }
    return;
  }
  if (item.kind === 'project' || item.kind === 'project_all') {
    const projectId = item.kind === 'project_all' ? (item.projectId || item.refId) : item.refId;
    const projectName = item.kind === 'project_all'
      ? (item.projectName || item.name)
      : item.name;
    const label = buildSmartDocProjectOnlyLabel(projectName);
    const result = smartDocAi.addAtRef({
      kind: 'project',
      name: projectName,
      refId: projectId,
      label,
    });
    if (!result.ok) {
      if (result.message) ElMessage.info(result.message);
      return;
    }
    replaceSmartDocAtQuery(label, { trailingSpace: true });
    closeSmartDocAtMention();
    return;
  }
  if (item.kind === 'project_doc') {
    const projectName = item.projectName || smartDocAtMentionProjectName.value || '项目';
    const label = buildSmartDocProjectDisplayLabel(projectName, item.name);
    const result = smartDocAi.addAtRef({
      kind: 'project',
      name: `${projectName}｜${item.name}`,
      refId: `${item.projectId || ''}:${item.refId}`,
      label,
    });
    if (!result.ok) {
      if (result.message) ElMessage.info(result.message);
      return;
    }
    replaceSmartDocAtQuery(label, { trailingSpace: true });
    closeSmartDocAtMention();
    return;
  }
  const kind = item.kind as SmartDocAtKind;
  if (kind !== 'skill' && kind !== 'template') return;
  const label = buildSmartDocSkillOrTemplateLabel(kind, item.name);
  if (kind === 'template') {
    // 单模版：清掉全部旧模版引用（含当前半成品 @模板 查询），再插入新引用
    const atIndex = smartDocAtMentionAtIndex.value;
    const el = aiTextareaRef.value;
    const cursor = el?.selectionStart ?? aiInput.value.length;
    let text = aiInput.value;
    if (atIndex >= 0 && atIndex <= cursor) {
      text = `${text.slice(0, atIndex)}${text.slice(cursor)}`;
    }
    aiInput.value = stripSmartDocTemplateMentionsFromText(text);
    smartDocAtMentionAtIndex.value = -1;
    const result = smartDocAi.addAtRef({
      kind: 'template',
      name: item.name,
      refId: item.refId,
      label,
    });
    if (!result.ok) {
      if (result.message) ElMessage.info(result.message);
      return;
    }
    insertSmartDocMentionText(label);
    closeSmartDocAtMention();
    if (smartDocTemplateGenerate.active) {
      smartDocTemplateGenerate.endFlow();
    }
    return;
  }
  const result = smartDocAi.addAtRef({
    kind,
    name: item.name,
    refId: item.refId,
    label,
  });
  if (!result.ok) {
    if (result.message) ElMessage.info(result.message);
    return;
  }
  replaceSmartDocAtQuery(label, { trailingSpace: true });
  closeSmartDocAtMention();
}

function onSmartDocQuickPrompt(kind: 'skill' | 'project' | 'template') {
  const name = kind === 'skill' ? '技能' : kind === 'project' ? '项目' : '模板';
  const insert = `@${name}`;
  const text = aiInput.value;
  const el = aiTextareaRef.value;
  const cursor = el?.selectionStart ?? text.length;
  const before = text.slice(0, cursor);
  const after = text.slice(cursor);
  const needsSpace = before.length > 0 && !/\s$/.test(before);
  aiInput.value = `${before}${needsSpace ? ' ' : ''}${insert}${after}`;
  const pos = before.length + (needsSpace ? 1 : 0) + insert.length;
  void nextTick(() => {
    el?.focus();
    el?.setSelectionRange(pos, pos);
    void refreshSmartDocAtMention();
  });
}

function onSmartDocTextareaScroll() {
  const src = aiTextareaRef.value;
  const mirror = aiTextareaHighlightRef.value;
  if (!src || !mirror) return;
  mirror.scrollTop = src.scrollTop;
  mirror.scrollLeft = src.scrollLeft;
}

function onSmartDocAiInput() {
  if (!smartDocAi.active) return;
  onSmartDocTextareaScroll();
  void refreshSmartDocAtMention();
}

function findSmartDocMentionRangeForBackspace(
  text: string,
  cursor: number,
): { start: number; end: number; text: string } | null {
  for (const range of collectSmartDocMentionRanges(text)) {
    if (cursor > range.start && cursor <= range.end) {
      return range;
    }
    if (cursor === range.end + 1 && text[range.end] === ' ') {
      return { start: range.start, end: range.end + 1, text: range.text };
    }
  }
  return null;
}

/**
 * 项目引用分层回退（「」包裹，兼容旧写法）：
 * @项目-「项目」-「文档」 → @项目-「项目」
 * @项目-「项目」- → @项目-
 * @项目-「项目」 → @项目-
 */
function stepBackSmartDocProjectMention(text: string, cursor: number): {
  nextText: string;
  pos: number;
} | null {
  const before = text.slice(0, cursor);
  const after = text.slice(cursor);

  const fullWrapped = before.match(/@(?:项目|project)-「[^」]+」-「[^」]+」(\s*)$/i);
  if (fullWrapped && fullWrapped.index != null) {
    const start = fullWrapped.index;
    const projectSeg = fullWrapped[0].match(/「[^」]+」/)?.[0] || '「项目」';
    const replacement = `@项目-${projectSeg}`;
    return {
      nextText: `${before.slice(0, start)}${replacement}${after.replace(/^\s+/, '')}`,
      pos: start + replacement.length,
    };
  }

  const projectOnlyWrapped = before.match(/@(?:项目|project)-「[^」]+」(\s*)$/i);
  if (projectOnlyWrapped && projectOnlyWrapped.index != null) {
    const start = projectOnlyWrapped.index;
    const replacement = '@项目-';
    return {
      nextText: `${before.slice(0, start)}${replacement}${after.replace(/^\s+/, '')}`,
      pos: start + replacement.length,
    };
  }

  const midWrapped = before.match(/@(?:项目|project)-「[^」]+」-$/i);
  if (midWrapped && midWrapped.index != null) {
    const start = midWrapped.index;
    const replacement = '@项目-';
    return {
      nextText: `${before.slice(0, start)}${replacement}${after}`,
      pos: start + replacement.length,
    };
  }

  const fullLegacy = before.match(/@(?:项目|project)-([^\s@「]+)-([^\s@「]+)(\s*)$/i);
  if (fullLegacy && fullLegacy.index != null) {
    const start = fullLegacy.index;
    const replacement = `@项目-${fullLegacy[1]}`;
    return {
      nextText: `${before.slice(0, start)}${replacement}${after.replace(/^\s+/, '')}`,
      pos: start + replacement.length,
    };
  }
  const midLegacy = before.match(/@(?:项目|project)-([^\s@「]+)-$/i);
  if (midLegacy && midLegacy.index != null) {
    const start = midLegacy.index;
    const replacement = '@项目-';
    return {
      nextText: `${before.slice(0, start)}${replacement}${after}`,
      pos: start + replacement.length,
    };
  }

  const range = findSmartDocMentionRangeForBackspace(text, cursor);
  if (range) {
    const m = range.text.match(/^@(?:项目|project)-(「[^」]+」)-「[^」]+」$/i)
      || range.text.match(/^@(?:项目|project)-([^\s@「]+)-([^\s@「]+)$/i);
    if (m) {
      const projectPart = m[1];
      const replacement = projectPart.startsWith('「')
        ? `@项目-${projectPart}`
        : `@项目-${projectPart}`;
      return {
        nextText: `${text.slice(0, range.start)}${replacement}${text.slice(range.end).replace(/^\s+/, '')}`,
        pos: range.start + replacement.length,
      };
    }
    const only = range.text.match(/^@(?:项目|project)-(「[^」]+」)$/i)
      || range.text.match(/^@(?:项目|project)-([^\s@「]+)$/i);
    if (only) {
      const replacement = '@项目-';
      return {
        nextText: `${text.slice(0, range.start)}${replacement}${text.slice(range.end).replace(/^\s+/, '')}`,
        pos: range.start + replacement.length,
      };
    }
  }
  return null;
}

function deleteSmartDocMentionAtCursor(): boolean {
  const el = aiTextareaRef.value;
  if (!el) return false;
  const text = aiInput.value;
  const cursor = el.selectionStart ?? 0;
  const selEnd = el.selectionEnd ?? 0;
  if (cursor !== selEnd) return false;

  const stepped = stepBackSmartDocProjectMention(text, cursor);
  if (stepped) {
    aiInput.value = stepped.nextText;
    pruneSmartDocAtTokensByInput();
    void nextTick(() => {
      el.focus();
      el.setSelectionRange(stepped.pos, stepped.pos);
      onSmartDocTextareaScroll();
      void refreshSmartDocAtMention();
    });
    return true;
  }

  const range = findSmartDocMentionRangeForBackspace(text, cursor);
  if (!range) return false;
  aiInput.value = `${text.slice(0, range.start)}${text.slice(range.end)}`;
  const pos = range.start;
  pruneSmartDocAtTokensByInput();
  void nextTick(() => {
    el.focus();
    el.setSelectionRange(pos, pos);
    onSmartDocTextareaScroll();
    void refreshSmartDocAtMention();
  });
  return true;
}

function onSmartDocAiKeydown(e: KeyboardEvent) {
  if (!smartDocAi.active) return;
  if (e.key === 'Enter' && !e.shiftKey && smartDocAtMentionOpen.value) {
    e.preventDefault();
    e.stopPropagation();
    smartDocSuppressEnterSend = true;
    const len = smartDocAtMentionItems.value.length;
    if (len) {
      const item = smartDocAtMentionItems.value[smartDocAtMentionIndex.value]
        || smartDocAtMentionItems.value[0];
      if (item) onSmartDocAtMentionPick(item);
    }
    return;
  }
  if (smartDocAtMentionOpen.value) {
    const len = smartDocAtMentionItems.value.length;
    if (e.key === 'ArrowDown' && len) {
      e.preventDefault();
      smartDocAtMentionIndex.value = (smartDocAtMentionIndex.value + 1) % len;
      return;
    }
    if (e.key === 'ArrowUp' && len) {
      e.preventDefault();
      smartDocAtMentionIndex.value = (smartDocAtMentionIndex.value - 1 + len) % len;
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      closeSmartDocAtMention();
      return;
    }
    if (e.key === 'Tab' && len) {
      e.preventDefault();
      smartDocSuppressEnterSend = true;
      const item = smartDocAtMentionItems.value[smartDocAtMentionIndex.value]
        || smartDocAtMentionItems.value[0];
      if (item) onSmartDocAtMentionPick(item);
      return;
    }
  }
  if (e.key === 'Backspace') {
    if (deleteSmartDocMentionAtCursor()) {
      e.preventDefault();
    }
  }
}

async function onSmartDocCancelExecution() {
  try {
    streamAbortController.value?.abort();
    await smartDocAi.cancelExecution();
  } catch (e) {
    ElMessage.error(formatApiErrorMessage(e));
  }
}

async function onSmartDocApply(createNew: boolean) {
  try {
    const docId = await smartDocAi.applyExecution(createNew);
    if (docId) {
      ElMessage.success(createNew ? '已创建新文档' : '已写入当前文档');
    }
  } catch (e) {
    ElMessage.error(formatApiErrorMessage(e));
  }
}

function onSmartDocCitation(c: SkillExecutionCitation) {
  smartDocAi.emitCitation(c);
  ElMessage.info(`引用 [${c.index}] ${c.title}：${c.snippet?.slice(0, 80) || ''}`);
}

async function sendSmartDocumentSkillApplyTargetClarification(text: string) {
  const displayText = text.trim();
  if (!displayText) return;
  const { action } = beginAiSessionAction({
    kind: 'other',
    label: '确认文档写入目标',
    discard: async () => {
      smartDocAi.clearPendingApplyTargetClarification();
    },
    retry: () => sendSmartDocumentSkillApplyTargetClarification(displayText),
  });
  activeSessionAction.value = action;
  addSessionMessage('user', displayText);
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  isStreaming.value = true;
  aiLoading.value = true;
  currentThoughtDraft.value = [
    { key: 'sd_apply_target', icon: '⏳', title: '确认写入目标', text: '正在理解您是要新建还是修改当前文档…' },
  ];
  try {
    const resolve = await smartDocAi.resolveAndApplyExecution(displayText);
    currentThoughtDraft.value = [
      { key: 'sd_apply_target', icon: '✅', title: '确认写入目标', text: '已完成判定' },
    ];
    if (resolve.status === 'applied') {
      const followUps = smartDocAi.pendingFollowUpQuestions || [];
      const lines: string[] = [
        resolve.createNew
          ? '已确认为新建文档，并写入工作台（可预览；需要 Word 时请用「导出 DOCX」）。'
          : '已确认为修改当前文档，并完成写入（可预览；需要 Word 时请用「导出 DOCX」）。',
      ];
      if (followUps.length) {
        lines.push('');
        lines.push('以下信息仍可进一步完善，请直接回复补充：');
        followUps.slice(0, 5).forEach((q, i) => {
          lines.push(`${i + 1}. ${q}`);
        });
      } else {
        lines.push('如需继续修改，可直接说明修订意见。');
      }
      addSessionMessage(
        'assistant',
        lines.join('\n'),
        undefined,
        thoughtStepsToJson(currentThoughtDraft.value),
      );
      return;
    }
    if (resolve.status === 'clarify') {
      addSessionMessage(
        'assistant',
        resolve.clarifyQuestion
          || '仍无法确认写入目标。请明确回复「新建文档」或「修改当前文档」。',
        undefined,
        thoughtStepsToJson(currentThoughtDraft.value),
      );
      return;
    }
    addSessionMessage('assistant', resolve.message || '无法确认写入目标，请重试。');
  } catch (e) {
    addSessionMessage('assistant', formatApiErrorMessage(e));
  } finally {
    isThinking.value = false;
    isStreaming.value = false;
    currentThoughtDraft.value = [];
    aiLoading.value = false;
    await nextTick();
    scrollAiToBottom();
  }
}

async function sendSmartDocumentIntelligentMessage(
  text: string,
  attachments: ChatMessageAttachment[] = [],
) {
  const displayText = text.trim() || '(智能文档对话)';
  await ensureSmartDocProjectTokenFromInput(displayText);

  const projectToken = smartDocAi.atTokens.find(
    (t) => t.kind === 'project' && /^\d+$/.test(String(t.refId || '').trim()),
  );
  const mentionedProjectId = projectToken?.refId ? String(projectToken.refId).trim() : '';
  const mentionedProjectName = projectToken?.name || '';

  // 对话检索可用 @项目；文档默认落「当前智能文档工作台」，不自动写入 @项目工作台
  const chatProjectId = mentionedProjectId
    || (Number(smartDocAi.workspaceId) > 0 ? String(smartDocAi.workspaceId) : '')
    || (await resolveChatProjectId(displayText));
  const workspaceIdForDoc = String(smartDocAi.workspaceId || '0');

  const pendingClarify = smartDocAi.pendingOutputIntentClarification;
  const isClarifyFollowUp = Boolean(pendingClarify?.originalText);

  return runInAiTaskQueue({
    kind: 'chat',
    label: isClarifyFollowUp ? '确认文档意图' : '智能文档对话',
    run: async (boundSessionId, ctx) => {
      const scenario = conversationScenario.value;
      const atTokensSnap = snapshotSmartDocAtTokensForMessage();
      const userMsg = addSessionMessage(
        'user',
        displayText,
        attachments.length > 0 ? attachments : undefined,
        undefined,
        undefined,
        boundSessionId,
        scenario,
      );
      if (atTokensSnap?.length) {
        updateSessionMessage(userMsg.id, { atTokens: atTokensSnap }, boundSessionId, scenario);
      }
      smartDocAi.clearAtTokens();
      await nextTick();
      scrollAiToBottom();

      try {
        const intentBody = {
          userMessage: isClarifyFollowUp
            ? `【原指令】\n${pendingClarify!.originalText}\n\n【用户补充】\n${displayText}`
            : displayText,
          hasCurrentDocument: Boolean(smartDocAi.documentId),
          currentDocumentId: smartDocAi.documentId || undefined,
          currentDocumentTitle: smartDocAi.documentTitle || undefined,
          previousClarifyQuestion: pendingClarify?.previousClarifyQuestion,
          mentionedProjectName: mentionedProjectName
            || pendingClarify?.mentionedProjectName
            || undefined,
        };
        const intent = await resolveSmartDocOutputIntent(workspaceIdForDoc, intentBody);
        const decision = String(intent.decision || 'clarify').trim();

        if (decision === 'clarify' || !['create_document', 'merge_current', 'revise_current', 'qa'].includes(decision)) {
          const q = (intent.clarifyQuestion || '').trim()
            || '请问是新建工作台文档、合入/修正当前文档，还是仅在对话中回答？';
          smartDocAi.pendingOutputIntentClarification = {
            originalText: pendingClarify?.originalText || displayText,
            previousClarifyQuestion: q,
            mentionedProjectId: mentionedProjectId || pendingClarify?.mentionedProjectId,
            mentionedProjectName: mentionedProjectName || pendingClarify?.mentionedProjectName,
          };
          const asst = addSessionMessage('assistant', q, undefined, undefined, undefined, boundSessionId, scenario);
          updateSessionMessage(asst.id, {
            structuredResult: { smartDocOutputIntent: { decision: 'clarify', question: q } },
          }, boundSessionId, scenario);
          await persistConversation(undefined, boundSessionId);
          return;
        }

        smartDocAi.clearPendingOutputIntentClarification();

        // 仅问答：不落文档（必须把 answer 写回消息正文，否则只剩思考步骤）
        if (decision === 'qa') {
          const streamed = await runStreamingAiChat({
            conversationId: boundSessionId,
            message: displayText,
            projectId: chatProjectId || undefined,
            sessionId: boundSessionId,
            routeName: currentRouteName.value,
            title: '智能文档问答',
            ...buildAgentChatContextWrapper(
              chatProjectId
                ? { projectId: chatProjectId, project_id: chatProjectId }
                : {},
            ),
          }, boundSessionId, { signal: ctx?.signal });
          const qaAnswer = (streamed.answer || '').trim() || '暂时无法回答，请稍后再试或补充说明。';
          currentAssistantId.value = streamed.assistantId;
          await nextTick();
          updateSessionMessage(streamed.assistantId, {
            content: qaAnswer,
            structuredResult: streamed.structuredResult,
          }, boundSessionId, scenario);
          startTypewriter(streamed.assistantId);
          isThinking.value = false;
          await waitForTypewriter(streamed.assistantId);
          await persistConversation(undefined, boundSessionId);
          return;
        }

        const genLabel = decision === 'create_document'
          ? '生成工作台文档'
          : decision === 'merge_current'
            ? '合入当前文档'
            : '修正当前文档';
        const streamed = await runStreamingAiChat({
          conversationId: boundSessionId,
          message: displayText,
          projectId: chatProjectId || undefined,
          sessionId: boundSessionId,
          routeName: currentRouteName.value,
          title: genLabel,
          ...buildAgentChatContextWrapper(
            chatProjectId
              ? { projectId: chatProjectId, project_id: chatProjectId }
              : {},
          ),
        }, boundSessionId, { signal: ctx?.signal });

        const answer = (streamed.answer || '').trim();
        if (!answer) {
          updateSessionMessage(streamed.assistantId, {
            content: '未能生成内容，请稍后重试。',
          }, boundSessionId, scenario);
          await persistConversation(undefined, boundSessionId);
          return;
        }

        const docKind = resolveSmartDocDeliverableKind(pendingClarify?.originalText || displayText);
        const today = new Date();
        const dateTag = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const defaultTitle = (intent.suggestedTitle || '').trim()
          || `${docKind}-${dateTag}`;
        const reportHtml = `<article class="aipd-project-report"><h1>${defaultTitle}</h1>${renderMarkdown(answer)}</article>`;

        let confirm = '';
        let structured: Record<string, unknown> | null = null;

        if (decision === 'create_document') {
          const created = await createBlankDocument(workspaceIdForDoc, defaultTitle);
          const docId = created.documentId;
          await updateWorkbenchDocumentContent(docId, reportHtml);
          smartDocAi.documentTitle = created.title || defaultTitle;
          smartDocAi.markDocumentApplied(docId, workspaceIdForDoc);
          const moveProjectId = mentionedProjectId
            || pendingClarify?.mentionedProjectId
            || '';
          const moveProjectName = mentionedProjectName
            || pendingClarify?.mentionedProjectName
            || '';
          const canOfferMove = workspaceIdForDoc === '0' && /^\d+$/.test(moveProjectId) && Number(moveProjectId) > 0;
          confirm = `已生成并保存到智能文档工作台「${created.title || defaultTitle}」。`
            + (canOfferMove
              ? `如需放入项目「${moveProjectName || moveProjectId}」工作台，请点击下方按钮。`
              : (workspaceIdForDoc === '0'
                ? '如需移入某项目工作台，可先 @项目 后再点「移入项目工作台」。'
                : ''));
          structured = {
            smartDocOutput: {
              kind: 'created',
              documentId: docId,
              title: created.title || defaultTitle,
              workspaceId: workspaceIdForDoc,
              suggestedProjectId: canOfferMove ? moveProjectId : '',
              suggestedProjectName: canOfferMove ? moveProjectName : '',
              offerMoveToProject: workspaceIdForDoc === '0',
            },
          };
        } else if (decision === 'merge_current' && smartDocAi.documentId) {
          const detail = await getWorkbenchDocumentDetail(smartDocAi.documentId);
          const prev = String(detail.contentHtml || '').trim() || '<p></p>';
          await updateWorkbenchDocumentContent(smartDocAi.documentId, `${prev}<hr/>${reportHtml}`);
          smartDocAi.markDocumentApplied(smartDocAi.documentId, workspaceIdForDoc);
          confirm = `已将内容合入当前文档「${smartDocAi.documentTitle || '未命名'}」。`;
          structured = {
            smartDocOutput: { kind: 'merged', documentId: smartDocAi.documentId },
          };
        } else if (decision === 'revise_current' && smartDocAi.documentId) {
          await updateWorkbenchDocumentContent(smartDocAi.documentId, reportHtml);
          smartDocAi.markDocumentApplied(smartDocAi.documentId, workspaceIdForDoc);
          confirm = `已按你的要求修正当前文档「${smartDocAi.documentTitle || '未命名'}」。`;
          structured = {
            smartDocOutput: { kind: 'revised', documentId: smartDocAi.documentId },
          };
        } else {
          // 缺少当前文档时降级为新建
          const created = await createBlankDocument(workspaceIdForDoc, defaultTitle);
          await updateWorkbenchDocumentContent(created.documentId, reportHtml);
          smartDocAi.documentTitle = created.title || defaultTitle;
          smartDocAi.markDocumentApplied(created.documentId, workspaceIdForDoc);
          confirm = `当前未打开文档，已改为新建「${created.title || defaultTitle}」。`;
          structured = {
            smartDocOutput: {
              kind: 'created',
              documentId: created.documentId,
              title: created.title || defaultTitle,
              workspaceId: workspaceIdForDoc,
              offerMoveToProject: workspaceIdForDoc === '0',
              suggestedProjectId: mentionedProjectId || '',
              suggestedProjectName: mentionedProjectName || '',
            },
          };
        }

        updateSessionMessage(streamed.assistantId, {
          content: confirm,
          structuredResult: structured,
        }, boundSessionId, scenario);
        await persistConversation(undefined, boundSessionId);
        ElMessage.success(confirm);
      } catch (e: unknown) {
        if (isTaskCancelledError(e)) return;
        console.error(e);
        const msg =
          e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
            ? String((e as { message: string }).message).trim()
            : '';
        ElMessage.error(msg || '智能文档处理失败，请稍后重试');
      } finally {
        aiLoading.value = false;
        isThinking.value = false;
        if (currentAssistantId.value) clearTypewriter(currentAssistantId.value);
        currentAssistantId.value = null;
      }
    },
  });
}

/** @deprecated 兼容旧名 */
async function sendSmartDocumentDeliverableMessage(
  text: string,
  attachments: ChatMessageAttachment[] = [],
) {
  return sendSmartDocumentIntelligentMessage(text, attachments);
}

async function sendSmartDocumentSkillMessage(text: string) {
  const displayText = text.trim() || '(使用 Skill 生成)';
  // 发送前补齐 @项目 / @技能 token，再快照给执行接口（随后会清 UI token）
  await ensureSmartDocProjectTokenFromInput(displayText);
  await ensureSmartDocSkillTokenFromInput(displayText);
  const tokensForExec = smartDocAi.atTokens.map((t) => ({ ...t }));

  const { action } = beginAiSessionAction({
    kind: 'other',
    label: '智能文档生成',
    discard: async () => {
      try {
        await smartDocAi.cancelExecution();
      } catch {
        /* ignore */
      }
    },
    retry: () => sendSmartDocumentSkillMessage(displayText),
  });
  activeSessionAction.value = action;
  const atTokensSnap = tokensForExec.length
    ? tokensForExec.map((t) => ({
        kind: t.kind,
        name: t.name,
        label: t.label,
        refId: t.refId,
      }))
    : snapshotSmartDocAtTokensForMessage();
  const userMsg = addSessionMessage('user', displayText);
  if (atTokensSnap?.length) {
    updateSessionMessage(userMsg.id, { atTokens: atTokensSnap });
  }
  // 发送后清掉输入区引用 chip，避免生成过程中残留（执行使用上方快照）
  smartDocAi.clearAtTokens();
  await nextTick();
  scrollAiToBottom();

  isThinking.value = true;
  isStreaming.value = true;
  streamStopped.value = false;
  currentThoughtDraft.value = [
    { key: 'sd_prepare', icon: '⏳', title: '准备执行', text: '正在读取技能与引用上下文…' },
  ];
  const abort = new AbortController();
  streamAbortController.value = abort;
  aiLoading.value = true;

  const progressTimer = window.setTimeout(() => {
    if (abort.signal.aborted) return;
    currentThoughtDraft.value = [
      { key: 'sd_prepare', icon: '✅', title: '准备执行', text: '上下文已就绪' },
      { key: 'sd_generate', icon: '⏳', title: '生成内容', text: '正在按技能生成文档草稿…' },
    ];
    void nextTick().then(() => scrollAiToBottom());
  }, 400);

  try {
    const result = await smartDocAi.runSkillExecution(displayText, {
      signal: abort.signal,
      tokens: tokensForExec,
    });
    if (abort.signal.aborted || streamStopped.value) {
      addSessionMessage('assistant', '已终止本次任务。');
      return;
    }
    if (!result.ok) {
      if (result.message === '已终止') {
        addSessionMessage('assistant', '已终止本次任务。');
        return;
      }
      if (result.message?.includes('Skill') || result.message?.includes('技能')) {
        openSmartDocSkillStudio('list');
        ElMessage.warning(result.message);
      } else {
        ElMessage.error(result.message || 'Skill 执行失败');
      }
      addSessionMessage('assistant', result.message || 'Skill 执行失败');
      return;
    }
    const exec = result.execution;
    currentThoughtDraft.value = [
      { key: 'sd_prepare', icon: '✅', title: '准备执行', text: '上下文已就绪' },
      { key: 'sd_generate', icon: '✅', title: '生成内容', text: '草稿已生成' },
    ];
    let content = '';
    if (exec?.status === 'completed') {
      // 落库目标由模型判定：新建 / 改当前 / 意图不清则多轮澄清（禁止前端关键词模糊匹配）
      let appliedDocId: string | null = null;
      let appliedAsNew = false;
      let clarifyQuestion = '';
      try {
        smartDocAi.execution = exec;
        const resolve = await smartDocAi.resolveAndApplyExecution(displayText);
        if (resolve.status === 'applied') {
          appliedDocId = resolve.documentId || null;
          appliedAsNew = !!resolve.createNew;
        } else if (resolve.status === 'clarify') {
          clarifyQuestion = resolve.clarifyQuestion || '';
          smartDocAi.execution = exec;
        } else if (resolve.message) {
          console.warn('resolve apply target failed', resolve.message);
        }
      } catch (e) {
        console.warn('auto apply skill result failed', e);
      }
      const citeCount = exec.citations?.length || 0;
      const followUps = exec.followUpQuestions || smartDocAi.pendingFollowUpQuestions || [];
      const lines: string[] = [];
      if (clarifyQuestion) {
        lines.push('文档草稿已生成，写入目标尚不明确。');
        if (citeCount > 0) {
          lines.push(`已引用项目资料 ${citeCount} 条。`);
        }
        lines.push('');
        lines.push(clarifyQuestion);
        content = lines.join('\n');
      } else if (appliedDocId) {
        lines.push(
          appliedAsNew
            ? `已根据技能生成文档并创建为新文档（可在工作台预览；需要 Word 时请用「导出 DOCX」）。`
            : `已根据技能生成并写入当前文档（可在工作台预览；需要 Word 时请用「导出 DOCX」）。`,
        );
        if (citeCount > 0) {
          lines.push(`已引用项目资料 ${citeCount} 条。`);
        }
        if (followUps.length) {
          lines.push('');
          lines.push('以下信息仍可进一步完善，请直接回复补充（将进入多轮修订）：');
          followUps.slice(0, 5).forEach((q, i) => {
            lines.push(`${i + 1}. ${q}`);
          });
        } else {
          lines.push('如需继续修改，可直接说明修订意见。');
        }
        content = lines.join('\n');
      } else {
        lines.push('已生成文档草稿（见下方预览）。可点击「写入当前文档」或「新建文档」。');
        if (citeCount > 0) {
          lines.push(`已引用项目资料 ${citeCount} 条。`);
        }
        lines.push('如需继续修改，可直接说明修订意见。');
        smartDocAi.execution = exec;
        content = lines.join('\n');
      }
    } else if (exec?.status === 'failed') {
      content = exec.errorMessage || 'Skill 执行失败';
    } else if (exec?.status === 'cancelled') {
      content = '已终止本次任务。';
    } else {
      content = `Skill 状态：${exec?.status || 'unknown'}`;
    }
    addSessionMessage(
      'assistant',
      content,
      undefined,
      thoughtStepsToJson(currentThoughtDraft.value),
      {
        smartDocumentExecutionId: exec?.executionId,
        status: exec?.status,
        followUpQuestions: exec?.followUpQuestions,
      },
    );
  } finally {
    window.clearTimeout(progressTimer);
    isThinking.value = false;
    isStreaming.value = false;
    if (streamAbortController.value === abort) {
      streamAbortController.value = null;
    }
    currentThoughtDraft.value = [];
    aiLoading.value = false;
    await nextTick();
    scrollAiToBottom();
  }
}

function currentPageTabKey(): string {
  if (shellStore.activePageTabKey) {
    return shellStore.activePageTabKey;
  }
  if (currentRouteName.value === 'ProjectDashboard' && projectId.value > 0) {
    return projectDashboardTabKey(projectId.value);
  }
  return routeToTabKey({
    name: String(route.name ?? ''),
    params: { ...route.params },
    query: { ...route.query },
  });
}

function notifyAiDialogueCompleted(tabKey: string, conversationId?: string) {
  void notifyPageTabCompletionOrBell({
    tabKey,
    title: 'AI 对话已完成',
    content: '点击查看对话内容',
    actionUrl: actionUrlForTabKey(tabKey),
    mergeKey: conversationId ? `AI_CHAT:${conversationId}` : undefined,
    type: 'AI_TASK_COMPLETED',
  });
}
/** Layout 拉取的项目名称，用于项目内 AI 标题（避免仅显示「项目 N 会话」） */
const shellProjectName = inject<Ref<string>>('aipdShellProjectName', ref(''));
const authStore = useAuthStore();
const permissionStore = usePermissionStore();
const projectCreateDraftStore = useProjectCreateDraftStore();
const projectCreateFieldApplyStore = useProjectCreateFieldApplyStore();
const projectCreateStructuredMergeStore = useProjectCreateStructuredMergeStore();
const projectCreateFieldEditPreviewStore = useProjectCreateFieldEditPreviewStore();
const aiPageContentApplyStore = useAiPageContentApplyStore();
const aiPageContentEditPreviewStore = useAiPageContentEditPreviewStore();
const projectCreateAiContextStore = useProjectCreateAiContextStore();

type PageContentEditPending = {
  fieldPath: string;
  fieldLabel: string;
  quoted: string;
  positionDescription: string;
  scopeLabel: string;
  cellKey?: string;
  paragraphIndex?: number;
  paragraphLabel?: string;
  /** modify：片段改写并采纳到页面；discuss：仅围绕引用内容对话 */
  mode: 'modify' | 'discuss';
};
const pageContentEditPending = ref<PageContentEditPending | null>(null);

type ProjectCreateFieldEditPending = {
  fieldPath: string;
  fieldLabel: string;
  quoted: string;
  positionDescription: string;
};
const projectCreateFieldEditPending = ref<ProjectCreateFieldEditPending | null>(null);
/** 用户消息内 @ 引用：展开查看完整原文 */
const pcRefDetailOpenForMessageId = ref<string | null>(null);
/** 会话区智能文档 @ 引用详情（messageId） */
const smartDocMentionDetailKey = ref<string | null>(null);
const smartDocMentionDetailText = ref('');

type DocumentDeepReviewFindingRow = {
  id: string;
  anchor_text: string;
  suggested_replacement: string;
  category?: string;
  categories?: string[];
  rationale?: string;
  paragraph_label?: string;
};
type DocumentDeepReviewCallbacks = {
  getDocumentHtml: () => string;
  applyMerged: (html: string) => void;
  captureSnapshot: () => void;
  revert: () => void;
};
const documentDeepReviewContext = ref<{
  messageId: string;
  findings: DocumentDeepReviewFindingRow[];
  callbacks: DocumentDeepReviewCallbacks;
} | null>(null);

type BomGenerateCallbacks = {
  onSave: () => void | Promise<void>;
  onCancel: () => void;
};
const bomGenerateContext = ref<{
  messageId: string;
  callbacks: BomGenerateCallbacks;
} | null>(null);
const bomGenerateSaving = ref(false);

type BomDrawingReviewExistingCallbacks = {
  onViewReport: () => void | Promise<void>;
  onRerun: () => void | Promise<void>;
};
const bomDrawingReviewExistingContext = ref<{
  messageId: string;
  callbacks: BomDrawingReviewExistingCallbacks;
} | null>(null);

const drawingReviewContext = ref<{
  messageId: string;
  onViewReport: () => void | Promise<void>;
} | null>(null);

const bomCostEstimateContext = ref<{
  messageId: string;
  callbacks: { onSave: () => void | Promise<void> };
} | null>(null);

const bomCostSaving = ref(false);

const trReviewPushing = ref(false);
const trReviewReportVisible = ref(false);
const trReviewProjectName = ref('');
const trReviewReportPayload = ref<{
  projectName?: string;
  verdictLabel?: string;
  recommendPass?: boolean | null;
  reason?: string;
  ruleDocumentTitle?: string;
  keyMissingSummary?: string;
  checklistSummary?: TrReviewChecklistSummary | null;
  checklistItems?: TrReviewChecklistItemResult[];
  improvementSuggestions?: string[];
}>({});
const trReviewContext = ref<{
  messageId: string;
  callbacks: {
    onPush: () => void | Promise<void>;
    onResubmit?: () => Promise<TrReviewSubmitResult>;
  };
} | null>(null);

const projectIntroRewriting = ref(false);
const projectIntroRewriteContext = ref<{
  messageId: string;
  previousDescription: string;
  callbacks: {
    onApply: (description: string) => void | Promise<void>;
    onRevert: (previousDescription: string) => void | Promise<void>;
  };
} | null>(null);
const sorRequirementContext = ref<{ messageId: string } | null>(null);

function isProjectIntroRewriteMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'project_intro_rewrite');
}

function projectIntroRewriteStructured(msg: ChatMessage): Record<string, unknown> | null {
  const sr = msg.structuredResult;
  return sr && typeof sr === 'object' ? (sr as Record<string, unknown>) : null;
}

function projectIntroRewriteStatus(msg: ChatMessage): string | null {
  const sr = projectIntroRewriteStructured(msg);
  if (!sr || sr.kind !== 'project_intro_rewrite') return null;
  return String(sr.status || 'pending');
}

function projectIntroRewriteActionsVisible(msg: ChatMessage) {
  const s = projectIntroRewriteStatus(msg);
  return s === 'adopted' || s === 'reverted';
}

function isSorRequirementMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'sor_requirement_analyze');
}

function sorRequirementStatus(msg: ChatMessage): string | null {
  if (!isSorRequirementMessage(msg)) return null;
  const sr = msg.structuredResult as Record<string, unknown>;
  return String(sr.status || '');
}

function sorRequirementActionsVisible(msg: ChatMessage) {
  const status = sorRequirementStatus(msg);
  const action = activeSessionAction.value;
  return (status === 'completed' || status === 'failed')
    && sorRequirementContext.value?.messageId === msg.id
    && action?.kind === 'sor_requirement_analyze'
    && !action.discarded
    && !scopeRunningTask.value
    && !scopePausedTask.value
    && !isStreaming.value
    && !aiLoading.value;
}

async function rollbackSorRequirementFromMessage(msg: ChatMessage) {
  if (!sorRequirementActionsVisible(msg)) {
    ElMessage.warning('无法回退：需求解析操作上下文已失效，请重新发起解析。');
    return;
  }
  await onRollbackSessionAction();
}

async function retrySorRequirementFromMessage(msg: ChatMessage) {
  if (!sorRequirementActionsVisible(msg)) {
    ElMessage.warning('无法重试：需求解析操作上下文已失效，请重新发起解析。');
    return;
  }
  await onRetrySessionAction();
}

async function revertProjectIntroRewrite(msg: ChatMessage) {
  const ctx = projectIntroRewriteContext.value;
  if (!ctx || ctx.messageId !== msg.id) {
    ElMessage.warning('无法回退：操作上下文已失效（例如已刷新页面），请手动恢复项目简介。');
    return;
  }
  projectIntroRewriting.value = true;
  try {
    await ctx.callbacks.onRevert(ctx.previousDescription);
    const scenario = conversationScenario.value;
    const sid = effectiveSessionId();
    store.updateMessage(scenario, msg.id, {
      structuredResult: {
        ...(msg.structuredResult as object),
        kind: 'project_intro_rewrite',
        status: 'reverted',
      },
    }, sid);
    projectIntroRewriteContext.value = null;
    ElMessage.success('已回退为帮写前的项目简介');
    void persistConversation();
  } catch (e: unknown) {
    const err = e as { message?: string };
    ElMessage.error(err?.message || '回退失败');
  } finally {
    projectIntroRewriting.value = false;
  }
}

function isTrReviewMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'tr_review');
}

function trReviewStructured(msg: ChatMessage): Record<string, unknown> | null {
  const sr = msg.structuredResult;
  return sr && typeof sr === 'object' ? (sr as Record<string, unknown>) : null;
}

function trReviewStatus(msg: ChatMessage): string | null {
  const sr = trReviewStructured(msg);
  if (!sr || sr.kind !== 'tr_review') return null;
  return String(sr.status || 'pending');
}

function trReviewRecommendPass(msg: ChatMessage): boolean {
  const sr = trReviewStructured(msg);
  return Boolean(sr?.recommendPass ?? sr?.recommend_pass);
}

function trReviewVerdictLabel(msg: ChatMessage): string {
  const sr = trReviewStructured(msg);
  const label = String(sr?.verdictLabel ?? sr?.verdict_label ?? '').trim();
  if (label) return label;
  return trReviewRecommendPass(msg) ? '通过' : '不通过';
}

function trReviewReason(msg: ChatMessage): string {
  const sr = trReviewStructured(msg);
  const reason = String(sr?.reason ?? '').trim();
  return reason || '—';
}

function trReviewPotentialImpact(msg: ChatMessage): string {
  const sr = trReviewStructured(msg);
  return String(sr?.potentialImpact ?? sr?.potential_impact ?? '').trim();
}

function trReviewImprovementSuggestions(msg: ChatMessage): string[] {
  const sr = trReviewStructured(msg);
  const raw = sr?.improvementSuggestions ?? sr?.improvement_suggestions;
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  const text = String(raw ?? '').trim();
  return text ? [text] : [];
}

function trReviewRuleDocumentTitle(msg: ChatMessage): string {
  const sr = trReviewStructured(msg);
  return String(sr?.ruleDocumentTitle ?? sr?.rule_document_title ?? '未选择TP评审规则文档').trim()
    || '未选择TP评审规则文档';
}

function trReviewKeyMissing(msg: ChatMessage): string {
  const sr = trReviewStructured(msg);
  return String(sr?.keyMissingSummary ?? sr?.key_missing_summary ?? '').trim();
}

function trReviewChecklistSummaryText(msg: ChatMessage): string {
  const sr = trReviewStructured(msg);
  const summary = (sr?.checklistSummary ?? sr?.checklist_summary) as TrReviewChecklistSummary | undefined;
  if (!summary || typeof summary !== 'object') return '—';
  return `共 ${summary.total ?? 0} 项，已提交 ${summary.submitted ?? 0} 项，未具备 ${summary.missing ?? 0} 项，无法判断 ${summary.undetermined ?? 0} 项。`;
}

function trReviewChecklistItems(msg: ChatMessage): TrReviewChecklistItemResult[] {
  const sr = trReviewStructured(msg);
  const raw = sr?.checklistItems ?? sr?.checklist_items;
  return Array.isArray(raw) ? (raw as TrReviewChecklistItemResult[]) : [];
}

function viewTrReviewReport(msg: ChatMessage) {
  trReviewReportPayload.value = {
    projectName: trReviewProjectName.value,
    verdictLabel: trReviewVerdictLabel(msg),
    recommendPass: trReviewRecommendPass(msg),
    reason: trReviewReason(msg),
    ruleDocumentTitle: trReviewRuleDocumentTitle(msg),
    keyMissingSummary: trReviewKeyMissing(msg),
    checklistSummary: (trReviewStructured(msg)?.checklistSummary
      ?? trReviewStructured(msg)?.checklist_summary) as TrReviewChecklistSummary | undefined,
    checklistItems: trReviewChecklistItems(msg),
    improvementSuggestions: trReviewImprovementSuggestions(msg),
  };
  trReviewReportVisible.value = true;
}

function formatTrReviewDialogText(res: TrReviewSubmitResult, verdictLabel: string): string {
  const lines = [`AI 评审结论：${verdictLabel}`, `评审依据与结论说明：${res.reason?.trim() || '—'}`];
  const impact = res.potentialImpact?.trim();
  if (impact) {
    lines.push(`可能造成的影响：${impact}`);
  }
  const suggestions = (res.improvementSuggestions ?? []).map((s) => String(s ?? '').trim()).filter(Boolean);
  if (suggestions.length) {
    lines.push(`改进建议：${suggestions.map((s, i) => `${i + 1}. ${s}`).join(' ')}`);
  }
  return lines.join('\n');
}

function trReviewCanPush(msg: ChatMessage): boolean {
  const sr = trReviewStructured(msg);
  return Boolean(sr?.canPushToNextPhase ?? sr?.can_push_to_next_phase);
}

function trReviewCanResubmit(msg: ChatMessage): boolean {
  const ctx = trReviewContext.value;
  return trReviewStatus(msg) === 'pending' && !!ctx?.callbacks.onResubmit && ctx.messageId === msg.id;
}

function trReviewActionsVisible(msg: ChatMessage) {
  const s = trReviewStatus(msg);
  if (s === 'pushed') return true;
  return s === 'pending' && (trReviewCanPush(msg) || trReviewCanResubmit(msg));
}

async function retryTrReview(msg: ChatMessage) {
  const ctx = trReviewContext.value;
  if (!ctx || ctx.messageId !== msg.id || !ctx.callbacks.onResubmit) {
    ElMessage.warning('请从项目详情「TR 评审」重新发起');
    return;
  }
  try {
    await ElMessageBox.confirm(
      '将依据最新需求、文档与问题清单重新分析，并替换本条结论。是否继续？',
      '重新 TR 评审',
      { type: 'warning', confirmButtonText: '重新评审', cancelButtonText: '取消' },
    );
  } catch {
    return;
  }
  trReviewPushing.value = true;
  try {
    const res = await ctx.callbacks.onResubmit();
    const scenario = conversationScenario.value;
    const sid = effectiveSessionId();
    const structured = trReviewStructuredFromResult(res, res.pushed ? 'pushed' : 'pending');
    const dialogText = formatTrReviewDialogText(res, structured.verdictLabel);
    store.updateMessage(scenario, msg.id, {
      content: dialogText,
      structuredResult: structured,
    }, sid);
    if (structured.canPushToNextPhase) {
      trReviewContext.value = { messageId: msg.id, callbacks: ctx.callbacks };
    }
    expandedThoughtIds.value.add(msg.id);
    void persistConversation();
    ElMessage.success('已根据最新资料重新完成 TR 评审');
  } catch (e: unknown) {
    const err = e as { message?: string };
    ElMessage.error(err?.message || '重新评审失败');
  } finally {
    trReviewPushing.value = false;
  }
}

function trReviewReviewerDisplayName(msg: ChatMessage): string {
  const sr = trReviewStructured(msg);
  const name1 = String(sr?.reviewer1Name ?? sr?.reviewer_1_name ?? '').trim();
  const name2 = String(sr?.reviewer2Name ?? sr?.reviewer_2_name ?? '').trim();
  if (name1 && name2) return `${name1}、${name2}`;
  if (name1) return name1;
  if (name2) return name2;
  return '审核人';
}

async function acceptTrReviewPush(msg: ChatMessage) {
  const ctx = trReviewContext.value;
  if (!ctx || ctx.messageId !== msg.id) {
    ElMessage.warning('无法提交审核：请重新发起 TR 评审后再试。');
    return;
  }
  if (!trReviewRecommendPass(msg)) {
    const reviewer = trReviewReviewerDisplayName(msg);
    try {
      await ElMessageBox.confirm(
        `AI审核不通过，是否提交给${reviewer}审核`,
        '提交审核',
        {
          type: 'warning',
          confirmButtonText: '提交审核',
          cancelButtonText: '取消',
          confirmButtonClass: 'el-button--danger',
        },
      );
    } catch {
      return;
    }
  } else {
    const reviewer = trReviewReviewerDisplayName(msg);
    try {
      await ElMessageBox.confirm(
        `是否提交给${reviewer}审核？`,
        '提交审核',
        {
          type: 'info',
          confirmButtonText: '提交审核',
          cancelButtonText: '取消',
        },
      );
    } catch {
      return;
    }
  }
  trReviewPushing.value = true;
  try {
    await ctx.callbacks.onPush();
    const scenario = conversationScenario.value;
    const sid = effectiveSessionId();
    store.updateMessage(scenario, msg.id, {
      structuredResult: {
        ...(msg.structuredResult as object),
        kind: 'tr_review',
        status: 'pushed',
        canPushToNextPhase: false,
        can_push_to_next_phase: false,
      },
    }, sid);
    expandedThoughtIds.value.add(msg.id);
    void persistConversation();
    ElMessage.success(`已提交给${trReviewReviewerDisplayName(msg)}审核`);
  } catch (e: unknown) {
    const err = e as { message?: string };
    ElMessage.error(err?.message || '提交审核失败');
  } finally {
    trReviewPushing.value = false;
  }
}

function isComponentRecommendMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'component_recommend');
}

type ComponentBomImportReviewPayload = {
  newItems: ComponentBomImportAnalyzeResult['newItems'];
  overlapIdentical: ComponentBomImportAnalyzeResult['overlapIdentical'];
  overlapConflicts: ComponentBomImportOverlapConflict[];
  similarItems: ComponentBomImportAnalyzeResult['similarItems'];
};

const componentBomImportContext = ref<{
  messageId: string;
  sessionId: string;
  callbacks: { onSaved: (componentIds: number[]) => void };
} | null>(null);
const componentBomImportDecisionsByMessageId = ref<Record<string, Record<string, string>>>({});
const componentBomImportSaving = ref(false);
const componentBomImportProgressText = ref('');

const calculatorFormulaImportStore = useCalculatorFormulaImportStore();
const calculatorFormulaGenerateSaving = ref(false);
/** 公式导入/创建期间由 store 驱动，避免与其它任务的 aiLoading 互相覆盖 */
const formulaImportBusyOwned = ref(false);

function isCalculatorFormulaGenerateMessage(msg: ChatMessage) {
  const sr = msg.structuredResult as FormulaGenerateStructured | null | undefined;
  return sr?.kind === 'calculator_formula_generate';
}

function calculatorFormulaGenerateStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as FormulaGenerateStructured | null | undefined;
  return sr?.status ?? null;
}

function calculatorFormulaGenerateLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as FormulaGenerateStructured | null | undefined;
  const count = sr?.formulaCount ?? 0;
  if (sr?.status === 'done') return `已成功生成 ${count} 个公式卡片`;
  if (sr?.status === 'cancelled') return '已取消公式生成';
  if (sr?.status === 'generating') return `正在生成 ${count} 个公式卡片…`;
  return count > 0 ? `共 ${count} 个公式待生成，确认后将在左侧展示卡片` : '公式方案已就绪';
}

function calculatorFormulaGenerateNames(msg: ChatMessage): string[] {
  const sr = msg.structuredResult as FormulaGenerateStructured | null | undefined;
  return sr?.formulaNames ?? [];
}

function calculatorFormulaGenerateActionsVisible(msg: ChatMessage) {
  const s = calculatorFormulaGenerateStatus(msg);
  return s === 'pending' || s === 'generating' || s === 'done' || s === 'cancelled';
}

async function acceptCalculatorFormulaGenerate(msg: ChatMessage) {
  calculatorFormulaGenerateSaving.value = true;
  try {
    await calculatorFormulaImportStore.confirmGenerate(msg.id);
  } finally {
    calculatorFormulaGenerateSaving.value = false;
  }
}

function cancelCalculatorFormulaGenerate(msg: ChatMessage) {
  calculatorFormulaImportStore.cancelGenerate(msg.id);
}

function isSmartDocSkillDraftMessage(msg: ChatMessage) {
  const sr = msg.structuredResult as SmartDocumentSkillDraftStructured | null | undefined;
  return sr?.kind === 'smart_document_skill_draft';
}

function smartDocSkillDraftStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as SmartDocumentSkillDraftStructured | null | undefined;
  return sr?.status ?? null;
}

function smartDocSkillDraftLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as SmartDocumentSkillDraftStructured | null | undefined;
  if (sr?.status === 'done') return `技能「${sr.name || ''}」已保存`;
  if (sr?.status === 'cancelled') return '已取消本次保存，可继续对话修改';
  if (sr?.status === 'saving') return '正在保存到我的技能…';
  return sr?.mode === 'optimize' ? '优化草稿已就绪，确认后将更新该技能' : '技能草稿已就绪，确认后保存到我的技能';
}

function smartDocSkillDraftMeta(msg: ChatMessage): string[] {
  const sr = msg.structuredResult as SmartDocumentSkillDraftStructured | null | undefined;
  if (!sr) return [];
  const lines: string[] = [];
  if (sr.name) lines.push(`名称：${sr.name}`);
  if (sr.code) lines.push(`编码：${sr.code}`);
  if (sr.category) lines.push(`分类：${sr.category}`);
  if (sr.description) lines.push(`说明：${sr.description}`);
  if (sr.promptText) {
    const summary = sr.promptText.length > 120 ? `${sr.promptText.slice(0, 120)}…` : sr.promptText;
    lines.push(`Prompt：${summary}`);
  }
  if (sr.sopText) {
    const summary = sr.sopText.length > 80 ? `${sr.sopText.slice(0, 80)}…` : sr.sopText;
    lines.push(`SOP：${summary}`);
  }
  return lines;
}

function smartDocSkillDraftActionsVisible(msg: ChatMessage) {
  const s = smartDocSkillDraftStatus(msg);
  return s === 'pending' || s === 'saving' || s === 'done' || s === 'cancelled';
}

async function acceptSmartDocSkillDraft(msg: ChatMessage) {
  smartDocSkillDraftSaving.value = true;
  try {
    bindSmartDocSkillAuthorContext();
    await smartDocSkillAuthor.confirmSave(msg.id);
    await persistConversation();
    await nextTick();
    scrollAiToBottom();
  } finally {
    smartDocSkillDraftSaving.value = false;
  }
}

/** 保存技能 → 新会话 → 自动 @引用该技能 */
async function startUsingSmartDocSkillDraft(msg: ChatMessage) {
  smartDocSkillDraftSaving.value = true;
  try {
    bindSmartDocSkillAuthorContext();
    const skill = await smartDocSkillAuthor.startUsing(msg.id);
    if (!skill) return;
    await persistConversation();
    await handleNewConversation();
    smartDocAi.clearAtTokens();
    const skillLabel = buildSmartDocSkillOrTemplateLabel('skill', skill.name);
    smartDocAi.addAtRef({
      kind: 'skill',
      name: skill.name,
      refId: skill.id,
      label: skillLabel,
    });
    aiInput.value = `${skillLabel} `;
    ElMessage.success(`已开始使用：${skill.name}`);
    await nextTick();
    focusComposer();
    onSmartDocTextareaScroll();
  } finally {
    smartDocSkillDraftSaving.value = false;
  }
}

function isSmartDocSkillImportMessage(msg: ChatMessage) {
  const sr = msg.structuredResult as SmartDocumentSkillImportStructured | null | undefined;
  return sr?.kind === 'smart_document_skill_import';
}

function smartDocSkillImportStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as SmartDocumentSkillImportStructured | null | undefined;
  return sr?.status ?? null;
}

function smartDocSkillImportLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as SmartDocumentSkillImportStructured | null | undefined;
  if (sr?.status === 'parsing') return `正在解析「${sr.fileName || '技能包'}」…`;
  if (sr?.status === 'failed') return `导入失败：${sr.errorMessage || '未知错误'}`;
  if (sr?.status === 'done') return `技能「${sr.name || ''}」已导入为草稿`;
  return '本地技能导入';
}

function smartDocSkillImportMeta(msg: ChatMessage): string[] {
  const sr = msg.structuredResult as SmartDocumentSkillImportStructured | null | undefined;
  if (!sr) return [];
  const lines: string[] = [];
  if (sr.fileName) lines.push(`文件：${sr.fileName}`);
  if (sr.name) lines.push(`名称：${sr.name}`);
  if (sr.code) lines.push(`编码：${sr.code}`);
  if (sr.category) lines.push(`分类：${sr.category}`);
  if (sr.description) lines.push(`说明：${sr.description}`);
  if (sr.hasStandardPackage) lines.push('标准包：已就绪');
  if (sr.acceptanceCaseCount != null && sr.acceptanceCaseCount > 0) {
    lines.push(`验收用例：${sr.acceptanceCaseCount} 条`);
  }
  if (sr.warnings?.length) {
    lines.push(`校验提示：${sr.warnings.length} 条`);
    sr.warnings.slice(0, 5).forEach((w) => lines.push(`· ${w}`));
  }
  return lines;
}

function smartDocSkillImportActionsVisible(msg: ChatMessage) {
  const s = smartDocSkillImportStatus(msg);
  return s === 'parsing' || s === 'done' || s === 'failed';
}

async function startUsingImportedSmartDocSkill(msg: ChatMessage) {
  const sr = msg.structuredResult as SmartDocumentSkillImportStructured | null | undefined;
  if (!sr?.skillId || !sr.name) {
    ElMessage.warning('导入结果不完整，请重新上传');
    return;
  }
  smartDocAi.clearAtTokens();
  const skillLabel = buildSmartDocSkillOrTemplateLabel('skill', sr.name);
  const result = smartDocAi.addAtRef({
    kind: 'skill',
    name: sr.name,
    refId: sr.skillId,
    label: skillLabel,
  });
  if (!result.ok && result.message) {
    ElMessage.info(result.message);
    return;
  }
  aiInput.value = `${skillLabel} `;
  await smartDocAi.reloadSkills();
  ElMessage.success(`已选择技能：${sr.name}`);
  await nextTick();
  focusComposer();
  onSmartDocTextareaScroll();
}

function isSmartDocTemplateDraftMessage(msg: ChatMessage) {
  const sr = msg.structuredResult as SmartDocumentTemplateDraftStructured | null | undefined;
  return sr?.kind === 'smart_document_template_draft';
}

function smartDocTemplateDraftStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as SmartDocumentTemplateDraftStructured | null | undefined;
  return sr?.status ?? null;
}

function smartDocTemplateDraftLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as SmartDocumentTemplateDraftStructured | null | undefined;
  if (sr?.status === 'done') return `模版「${sr.title || ''}」已保存`;
  if (sr?.status === 'cancelled') return '已取消本次保存，可继续对话修改';
  if (sr?.status === 'saving') return '正在保存到我的模版…';
  return '模版草稿已就绪，确认后保存到我的模版（Private Draft）';
}

function smartDocTemplateDraftMeta(msg: ChatMessage): string[] {
  const sr = msg.structuredResult as SmartDocumentTemplateDraftStructured | null | undefined;
  if (!sr) return [];
  const lines: string[] = [];
  if (sr.title) lines.push(`名称：${sr.title}`);
  if (sr.documentType) lines.push(`类型：${sr.documentType}`);
  if (sr.description) lines.push(`说明：${sr.description}`);
  if (sr.outlineChapters?.length) {
    lines.push(`大纲：${sr.outlineChapters.join(' / ')}`);
  }
  return lines;
}

function smartDocTemplateDraftActionsVisible(msg: ChatMessage) {
  const s = smartDocTemplateDraftStatus(msg);
  return s === 'pending' || s === 'saving' || s === 'done' || s === 'cancelled';
}

async function acceptSmartDocTemplateDraft(msg: ChatMessage) {
  smartDocTemplateDraftSaving.value = true;
  try {
    bindSmartDocTemplateAuthorContext();
    await smartDocTemplateAuthor.confirmSave(msg.id);
    await persistConversation();
    await nextTick();
    scrollAiToBottom();
  } finally {
    smartDocTemplateDraftSaving.value = false;
  }
}

async function startUsingSmartDocTemplateDraft(msg: ChatMessage) {
  smartDocTemplateDraftSaving.value = true;
  try {
    bindSmartDocTemplateAuthorContext();
    const result = await smartDocTemplateAuthor.startUsing(msg.id);
    if (!result) return;
    await persistConversation();
    await nextTick();
    scrollAiToBottom();
  } finally {
    smartDocTemplateDraftSaving.value = false;
  }
}

function isSmartDocSaveAsFromFileMessage(msg: ChatMessage) {
  const sr = msg.structuredResult as SmartDocumentSaveAsFromFileStructured | null | undefined;
  return sr?.kind === 'smart_document_save_as_from_file';
}

function smartDocSaveAsFromFileStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as SmartDocumentSaveAsFromFileStructured | null | undefined;
  return sr?.status ?? null;
}

function smartDocSaveAsFromFileLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as SmartDocumentSaveAsFromFileStructured | null | undefined;
  if (sr?.status === 'done') return `模版「${sr.templateName || ''}」已保存`;
  if (sr?.status === 'wizard_opened') return '已打开模版提炼向导，请完成人工审核';
  if (sr?.status === 'failed') return sr.errorMessage || '文件导入创建模版失败';
  if (sr?.summary) return sr.summary;
  return '正在准备模版提炼向导…';
}

function smartDocSaveAsFromFileMeta(msg: ChatMessage): string[] {
  const sr = msg.structuredResult as SmartDocumentSaveAsFromFileStructured | null | undefined;
  if (!sr) return [];
  const lines: string[] = [];
  if (sr.fileName) lines.push(`文件：${sr.fileName}`);
  if (sr.templateName) lines.push(`名称：${sr.templateName}`);
  if (sr.documentId) lines.push(`文档 ID：${sr.documentId}`);
  return lines;
}

function smartDocSaveAsFromFileActionsVisible(msg: ChatMessage) {
  const s = smartDocSaveAsFromFileStatus(msg);
  return s === 'pending' || s === 'wizard_opened' || s === 'done' || s === 'failed';
}

function bindSmartDocSaveAsFromFileContext() {
  smartDocSaveAsFromFile.bindPanelContext({
    scenario: conversationScenario.value,
    sessionId: currentSessionId.value,
  });
}

async function confirmSmartDocSaveAsFromFile(msg: ChatMessage) {
  smartDocSaveAsFromFileSaving.value = true;
  try {
    bindSmartDocSaveAsFromFileContext();
    await smartDocSaveAsFromFile.confirmSave(msg.id);
    await persistConversation();
    await nextTick();
    scrollAiToBottom();
  } finally {
    smartDocSaveAsFromFileSaving.value = false;
  }
}

function isSmartDocTemplateGenerateMessage(msg: ChatMessage) {
  const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
  return sr?.kind === 'smart_document_template_generate';
}

/** 优先展示交付摘要（打开后仍保留），否则用气泡正文 */
function smartDocTemplateGenerateDisplayContent(msg: ChatMessage): string {
  const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
  const delivery = (sr?.deliverySummary || '').trim();
  const content = (msg.content || '').trim();
  const status = sr?.status || '';
  if ((status === 'opened' || status === 'opening' || status === 'completed') && delivery) {
    // opening 时 content 可能带「正在打开…」，优先用 delivery + content 若 content 含摘要
    if (status === 'opening' && content.includes(delivery.slice(0, Math.min(20, delivery.length)))) {
      return content;
    }
    if (status === 'opened' && content.includes('已在右侧打开预览')) {
      return content;
    }
    if (status === 'opened' || status === 'completed') {
      return content.length >= delivery.length ? content : delivery;
    }
  }
  return content;
}

function smartDocTemplateGenerateReviewItems(msg: ChatMessage): TemplateGenerationReviewItem[] {
  const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
  const items = sr?.reviewChecklist;
  if (!Array.isArray(items) || !items.length) return [];
  return items.filter((it) => it && String(it.label || '').trim());
}

function locateSmartDocTemplateReviewItem(item: TemplateGenerationReviewItem) {
  const needle = String(item.searchText || item.label || '').trim();
  if (!needle) {
    ElMessage.info('该项暂无可定位文案');
    return;
  }
  // 优先定位标签本身，其次尝试 [待确认] 邻近
  const ok = highlightPageContentQuote(needle)
    || highlightPageContentQuote(`${needle} [待确认]`)
    || highlightPageContentQuote('[待确认]');
  if (!ok) {
    ElMessage.info('未在预览中定位到对应位置，可手动在右侧查找该字段');
  }
}

function smartDocTemplateGenerateStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
  return sr?.status ?? null;
}

function smartDocTemplateGenerateLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
  if (sr?.status === 'failed') return sr.errorMessage || '模版生成失败';
  if (sr?.missingFields?.length) {
    return `待补充：${sr.missingFields.map(displayTemplateSlotLabel).join('、')}`;
  }
  return '请在下方补充必要信息后继续发送';
}

function smartDocTemplateGenerateHasPending(msg: ChatMessage): boolean {
  const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
  return (sr?.missingFields?.length || 0) > 0;
}

function smartDocTemplateGenerateMeta(msg: ChatMessage): string[] {
  const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
  if (!sr) return [];
  const lines: string[] = [];
  if (sr.templateTitle) lines.push(`模版：${sr.templateTitle}`);
  if (sr.intentSummary) lines.push(`意图：${sr.intentSummary}`);
  if (sr.outlineSummary) lines.push(`大纲：${sr.outlineSummary}`);
  if (sr.validationIssueCount != null && sr.validationIssueCount > 0) {
    lines.push(`结构问题：${sr.validationIssueCount} 项`);
  }
  if (sr.pendingConfirmCount != null && sr.pendingConfirmCount > 0) {
    lines.push(`待确认：${sr.pendingConfirmCount} 处`);
  }
  if (sr.qualitySummary) lines.push(`质检：${sr.qualitySummary}`);
  const slots = sr.filledSlots || {};
  const filledKeys = Object.keys(slots);
  if (filledKeys.length) {
    lines.push(
      `已填：${filledKeys
        .slice(0, 6)
        .map((k) => `${displayTemplateSlotLabel(k)}=${slots[k]}`)
        .join('；')}${filledKeys.length > 6 ? '…' : ''}`,
    );
  }
  if (sr.missingFields?.length) {
    lines.push(`缺少：${sr.missingFields.map(displayTemplateSlotLabel).join('、')}`);
  }
  if (sr.documentTitle) lines.push(`文档：${sr.documentTitle}`);
  return lines;
}

function smartDocTemplateGenerateActionsVisible(msg: ChatMessage) {
  const s = smartDocTemplateGenerateStatus(msg);
  return s === 'collecting'
    || s === 'failed'
    || s === 'generating'
    || s === 'opening'
    || s === 'opened'
    || s === 'completed';
}

async function openSmartDocTemplateGenerate(msg: ChatMessage) {
  smartDocTemplateGenerateOpening.value = true;
  try {
    smartDocTemplateGenerate.bindPanelContext({
      scenario: conversationScenario.value,
      sessionId: currentSessionId.value,
    });
    const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
    if (sr?.generationId && sr.generationId !== smartDocTemplateGenerate.generationId) {
      // 卡片可能来自历史消息：尽量用卡片上的 generationId
      smartDocTemplateGenerate.generationId = sr.generationId;
      smartDocTemplateGenerate.status = 'completed';
      smartDocTemplateGenerate.templateId = sr.templateId || '';
      smartDocTemplateGenerate.templateTitle = sr.templateTitle || '';
    }
    await smartDocTemplateGenerate.confirmOpen(msg.id, sr?.deliverySummary || msg.content || undefined);
    await persistConversation();
    await nextTick();
    scrollAiToBottom();
  } finally {
    smartDocTemplateGenerateOpening.value = false;
  }
}

function reopenSmartDocTemplateGenerate(msg: ChatMessage) {
  const sr = msg.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
  const docId = sr?.documentId || smartDocTemplateGenerate.documentId;
  if (!docId) {
    void openSmartDocTemplateGenerate(msg);
    return;
  }
  // 触发工作台选中并展示
  smartDocAi.lastAppliedDocumentId = '';
  void nextTick(() => {
    smartDocAi.lastAppliedDocumentId = docId;
  });
}

async function sendSmartDocumentTemplateGenerateMessage(
  text: string,
  messageAttachments?: ChatMessageAttachment[],
) {
  const displayText = text.trim() || '(使用模版生成)';
  smartDocTemplateGenerate.bindPanelContext({
    scenario: conversationScenario.value,
    sessionId: currentSessionId.value,
  });
  if (!smartDocAi.templateAtToken?.refId && !smartDocTemplateGenerate.templateId) {
    const lastGen = [...getSessionMessages()].reverse().find(
      (m) => m.role === 'assistant' && isSmartDocTemplateGenerateMessage(m),
    );
    const sr = lastGen?.structuredResult as SmartDocumentTemplateGenerateStructured | null | undefined;
    if (sr?.templateId) {
      smartDocTemplateGenerate.templateId = sr.templateId;
      smartDocTemplateGenerate.templateTitle = sr.templateTitle || smartDocTemplateGenerate.templateTitle;
      smartDocTemplateGenerate.active = true;
    }
  }
  const { action } = beginAiSessionAction({
    kind: 'other',
    label: '智能文档生成',
    discard: () => {
      smartDocTemplateGenerate.resetForRetry();
    },
    retry: () => sendSmartDocumentTemplateGenerateMessage(displayText, messageAttachments),
  });
  activeSessionAction.value = action;
  isThinking.value = true;
  isStreaming.value = true;
  streamStopped.value = false;
  currentThoughtDraft.value = [
    { key: 'intent_bind', icon: '⏳', title: '需求理解与变量绑定', text: '正在解析意图并绑定模版变量…' },
  ];
  const abort = new AbortController();
  streamAbortController.value = abort;
  aiLoading.value = true;
  try {
    smartDocTemplateGenerate.bindPanelContext({
      scenario: conversationScenario.value,
      sessionId: currentSessionId.value,
    });
    await smartDocTemplateGenerate.sendUserMessage(displayText, {
      signal: abort.signal,
      attachments: (messageAttachments || [])
        .filter((a) => a.objectKey && String(a.objectKey).trim())
        .map((a) => ({
          name: a.processingName || a.name,
          objectKey: String(a.processingObjectKey || a.objectKey),
          accessUrl: a.processingUrl || a.url,
          type: a.type,
        })),
      onThought: (steps) => {
        currentThoughtDraft.value = steps;
        void nextTick().then(() => scrollAiToBottom());
      },
    });
    if (!action.messageId) {
      const lastAssistant = [...getSessionMessages()].reverse().find((m) => m.role === 'assistant');
      if (lastAssistant) action.messageId = lastAssistant.id;
    }
    await persistConversation();
  } finally {
    isThinking.value = false;
    isStreaming.value = false;
    if (streamAbortController.value === abort) {
      streamAbortController.value = null;
    }
    currentThoughtDraft.value = [];
    aiLoading.value = false;
  }
  await nextTick();
  scrollAiToBottom();
}

function pickImportableTemplateAttachment(sourceAttachments?: AttachmentItem[]): File | null {
  if (!sourceAttachments?.length) return null;
  for (const item of sourceAttachments) {
    if (item.file && isImportableTemplateFile(item.file) && item.progress >= 100 && !item.error) {
      return item.file;
    }
  }
  return null;
}

function shouldRouteSmartDocFileToSaveAsTemplate(
  text: string,
  sourceAttachments?: AttachmentItem[],
): File | null {
  if (!smartDocAi.active) return null;
  const file = pickImportableTemplateAttachment(sourceAttachments);
  if (!file) return null;
  const t = text.trim();
  if (!t) {
    // 纯附件：未选技能且未引用模版时视为「用文件创建模版」
    if (smartDocAi.selectedSkillId || smartDocAi.hasTemplateAtToken) return null;
    return file;
  }
  if (isCreateTemplateIntent(t)) return file;
  if (/(?:导入|另存为|解析).{0,8}(?:模版|模板)|(?:模版|模板).{0,8}(?:导入|另存为)/i.test(t)) {
    return file;
  }
  if (/(?:创建|新建)/.test(t) && /(?:模版|模板)/.test(t)) return file;
  // 已在模版创作会话中上传附件 → 走文件导入另存为
  if (smartDocTemplateAuthor.active) return file;
  return null;
}

function componentBomImportReviewData(msg: ChatMessage): ComponentBomImportReviewPayload | null {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr || sr.kind !== 'component_bom_import') return null;
  const newItems = (sr.newItems ?? sr.new_items ?? []) as ComponentBomImportReviewPayload['newItems'];
  const overlapIdentical = (sr.overlapIdentical ??
    sr.overlap_identical ??
    []) as ComponentBomImportReviewPayload['overlapIdentical'];
  const overlapConflicts = (sr.overlapConflicts ??
    sr.overlap_conflicts ??
    []) as ComponentBomImportOverlapConflict[];
  const similarItems = (sr.similarItems ?? sr.similar_items ?? []) as ComponentBomImportReviewPayload['similarItems'];
  if (!newItems.length && !overlapConflicts.length && !similarItems.length && !overlapIdentical.length) {
    return null;
  }
  return { newItems, overlapIdentical, overlapConflicts, similarItems };
}

function componentBomImportDecisionsForMessage(messageId: string): Record<string, string> {
  return componentBomImportDecisionsByMessageId.value[messageId] ?? {};
}

function setComponentBomImportDecisions(messageId: string, decisions: Record<string, string>) {
  componentBomImportDecisionsByMessageId.value = {
    ...componentBomImportDecisionsByMessageId.value,
    [messageId]: decisions,
  };
}

function isComponentBomImportMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'component_bom_import');
}

function componentBomImportStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr || sr.kind !== 'component_bom_import') return null;
  return String(sr.status || 'pending');
}

function componentBomImportDialogLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  const fromSr = String(sr?.assistantReply ?? sr?.assistant_reply ?? '').trim();
  if (fromSr) return fromSr;
  return msg.content?.trim() || 'BOM 解析完成，请确认是否写入公司部件库。';
}

function componentBomImportPreviewLine(msg: ChatMessage): string {
  const data = componentBomImportReviewData(msg);
  if (data) {
    return (
      `新增 ${data.newItems.length} · 完全相同 ${data.overlapIdentical.length}（跳过）· ` +
      `重叠待确认 ${data.overlapConflicts.length} · 相似对比 ${data.similarItems.length}`
    );
  }
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  const total = Number(sr?.totalExtracted ?? sr?.total_extracted ?? 0);
  if (total > 0) {
    return `共分析 ${total} 条 BOM 记录（已对照当前部件库）。`;
  }
  return '';
}

function componentBomImportActionsVisible(msg: ChatMessage) {
  const s = componentBomImportStatus(msg);
  return s === 'pending' || s === 'saved' || s === 'cancelled';
}

async function acceptComponentBomImport(msg: ChatMessage) {
  const ctx = componentBomImportContext.value;
  if (!ctx || ctx.messageId !== msg.id) {
    ElMessage.warning('无法写入：请重新上传 BOM 后再试（可能已刷新页面）。');
    return;
  }
  const conflicts =
    componentBomImportReviewData(msg)?.overlapConflicts ??
    [];
  const decisions = componentBomImportDecisionsForMessage(msg.id);
  const unresolved = conflicts.filter((c) => !decisions[c.conflictId]);
  if (unresolved.length) {
    const init = buildDefaultOverlapDecisions(conflicts);
    setComponentBomImportDecisions(msg.id, { ...init, ...decisions });
  }

  componentBomImportSaving.value = true;
  try {
    const res = await confirmComponentBomImport({
      sessionId: ctx.sessionId,
      overlapDecisions: overlapDecisionsToPayload(
        componentBomImportDecisionsForMessage(msg.id),
      ),
    });
    const scenario = conversationScenario.value;
    const sid = effectiveSessionId();
    store.updateMessage(scenario, msg.id, {
      structuredResult: {
        ...(msg.structuredResult as object),
        kind: 'component_bom_import',
        status: 'saved',
        confirmMessage: res.message,
      },
    }, sid);
    expandedThoughtIds.value.add(msg.id);
    ctx.callbacks.onSaved(res.componentIds ?? []);
    ElMessage.success(res.message || '已写入部件库');
    void persistConversation();
  } catch (e: unknown) {
    const err = e as { message?: string };
    ElMessage.error(err?.message || '写入失败');
  } finally {
    componentBomImportSaving.value = false;
  }
}

function cancelComponentBomImport(msg: ChatMessage) {
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  store.updateMessage(scenario, msg.id, {
    structuredResult: {
      ...(msg.structuredResult as object),
      kind: 'component_bom_import',
      status: 'cancelled',
    },
  }, sid);
  if (componentBomImportContext.value?.messageId === msg.id) {
    componentBomImportContext.value = null;
  }
  ElMessage.info('已取消本次 BOM 导入');
  void persistConversation();
}

function isAgentCompleteResult(sr: unknown): sr is { kind: string; route: string; params: Record<string, string>; label: string } {
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'agent_complete');
}

function getAgentCompleteNav(sr: unknown): { route: string; params: Record<string, string>; label: string } | null {
  if (!isAgentCompleteResult(sr)) return null;
  const s = sr as Record<string, unknown>;
  return {
    route: String(s.route || ''),
    params: (s.params as Record<string, string>) || {},
    label: String(s.label || '查看结果'),
  };
}


function handleAgentNavClick(msg: any) {
  console.log("agent nav click", msg?.structuredResult);
  const sr = msg?.structuredResult ?? null;
  if (!sr || sr.kind !== 'agent_complete') { console.log("agent nav: invalid sr", sr); return; }
  const nav = getAgentCompleteNav(sr);
  console.log("agent nav: navigating", nav);
  if (nav) navigateAgentComplete(nav);
}

function navigateAgentComplete(nav: { route: string; params: Record<string, string> }) {
  const id = nav.params.id;
  const tab = nav.params.tab;
  // Save tab state before navigation
  if (tab && id) {
    try {
      saveProjectDetailEngineeringTab(Number(id), tab as 'bom' | 'components');
    } catch (e) { /* ignore */ }
  }
  const routeNameMap: Record<string, string> = {
    '/project/[id]/dashboard': 'ProjectDashboard',
    '/project/[id]/requirements': 'ProjectRequirements',
    '/project/[id]/wbs': 'ProjectWbsSnapshot',
  };
  const name = routeNameMap[nav.route];
  if (name && id) {
    // Check if already on the target page
    const currentRoute = router.currentRoute.value;
    if (currentRoute.name === name && String(currentRoute.params.id) === String(id)) {
      // Already here — reload to apply saved tab state
      window.location.reload();
    } else {
      router.push({ name, params: { id } });
    }
  } else if (nav.route === '/ai-review') {
    if (router.currentRoute.value.name === 'AiSmartReview') {
      window.location.reload();
    } else {
      router.push({ name: 'AiSmartReview' });
    }
  } else {
    let resolved = nav.route;
    for (const [key, value] of Object.entries(nav.params)) {
      resolved = resolved.replace('[' + key + ']', String(value));
    }
    router.push(resolved);
  }
}

function isComponentAutoFillMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'component_autofill');
}

function componentAutoFillStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr || sr.kind !== 'component_autofill') return null;
  return String(sr.status || 'running');
}

function componentAutoFillDialogLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  const fromSr = String(sr?.assistantReply ?? sr?.assistant_reply ?? '').trim();
  if (fromSr) return fromSr;
  return msg.content?.trim() || '正在联网检索并填充部件资料…';
}

function componentAutoFillProgressLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  return String(sr?.progressText ?? sr?.progress_text ?? '').trim();
}

function componentAutoFillSteps(msg: ChatMessage): Array<{ icon?: string; title?: string; text?: string }> {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  const steps = sr?.steps;
  return Array.isArray(steps) ? (steps as Array<{ icon?: string; title?: string; text?: string }>) : [];
}

function componentAutoFillActionsVisible(msg: ChatMessage) {
  const s = componentAutoFillStatus(msg);
  return s === 'running' || s === 'complete' || s === 'failed';
}

function patchComponentAutoFillMessage(
  messageId: string,
  status: ComponentAutoFillStatus,
) {
  const scenario = conversationScenario.value;
  const steps = (status.steps ?? []).map((s) => ({
    key: String(s.key ?? ''),
    icon: String(s.icon ?? '·'),
    title: String(s.title ?? ''),
    text: String(s.text ?? ''),
  }));
  const thought = JSON.stringify({ steps });
  const state =
    status.status === 'COMPLETE'
      ? 'complete'
      : status.status === 'FAILED'
        ? 'failed'
        : 'running';
  const dialogText =
    (status.assistantReply || status.progressText || '').trim() ||
    '正在联网检索并填充部件资料…';
  updateSessionMessage(messageId, {
    content: dialogText,
    thought,
    structuredResult: {
      kind: 'component_autofill',
      status: state,
      jobId: status.jobId,
      componentId: status.componentId,
      assistantReply: status.assistantReply ?? '',
      progressText: status.progressText ?? '',
      thumbnailImported: status.thumbnailImported ?? 0,
      documentsImported: status.documentsImported ?? 0,
      steps,
    },
  });
}

async function pollComponentAutoFillJob(
  messageId: string,
  jobId: string,
  timeoutMs = 300000,
) {
  const start = Date.now();
  let delay = 2000;
  while (Date.now() - start < timeoutMs) {
    const status = await getComponentAutoFillStatus(jobId);
    patchComponentAutoFillMessage(messageId, status);
    await nextTick();
    expandedThoughtIds.value.add(messageId);
    if (status.status === 'COMPLETE' || status.status === 'FAILED') {
      return status;
    }
    await new Promise((r) => window.setTimeout(r, delay));
    delay = Math.min(5000, Math.round(delay * 1.1));
  }
  throw new Error('自动填充超时，请稍后重试');
}

type RunComponentAutoFillArgs = {
  componentId: number;
  componentName?: string;
  componentCode?: string;
  onComplete?: (status: ComponentAutoFillStatus) => void;
};

async function runComponentAutoFill(args: RunComponentAutoFillArgs) {
  const label = args.componentName || args.componentCode || `部件 ${args.componentId}`;
  return runInAiTaskQueue({
    kind: 'component_autofill',
    label: `自动填充 ${label}`,
    run: async () => {
  mainTab.value = 'new';
  expandRightPanel();
  // 会话归属在任务入队时就已确定（taskBoundSessionId / taskBoundScenario）。
  // 任务执行期间不能再改写全局 conversationScenario：改写会让「写本地 store
  // 用的 scenario」和「persistConversation 默认参数取的 scenario」分叉，而后端唯一
  // 键是 (user_id, scenario, session_id)，同一个 sessionId 于是被拆成两条库记录。
  const sid = effectiveSessionId();
  const scenario = effectiveScenario();
  const userLine = `【自动填充】${label}`;
  addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  aiLoading.value = true;
  let assistantMsgId = '';
  try {
    const startRes = await startComponentAutoFill(args.componentId);
    const assistantMsg = addSessionMessage(
      'assistant',
      '已启动自动填充，正在联网检索部件信息…',
      undefined,
      undefined,
      {
        kind: 'component_autofill',
        status: 'running',
        jobId: startRes.jobId,
        componentId: args.componentId,
        progressText: '正在联网检索部件信息…',
        steps: [],
      },
      sid,
      scenario,
    );
    assistantMsgId = assistantMsg.id;
    await nextTick();
    expandedThoughtIds.value.add(assistantMsgId);
    const finalStatus = await pollComponentAutoFillJob(assistantMsgId, startRes.jobId);
    if (finalStatus.status === 'COMPLETE') {
      ElMessage.success('自动填充完成');
    } else {
      ElMessage.warning(finalStatus.assistantReply || finalStatus.progressText || '自动填充未完成');
    }
    args.onComplete?.(finalStatus);
    await persistConversation(undefined, sid, scenario);
    await maybeGenerateSessionTitleAfterFirstTurn(sid, scenario);
  } catch (e: unknown) {
    const err = e as { message?: string };
    const errText = err?.message || '自动填充失败';
    ElMessage.error(errText);
    if (assistantMsgId) {
      patchComponentAutoFillMessage(assistantMsgId, {
        jobId: '',
        componentId: args.componentId,
        status: 'FAILED',
        progressText: errText,
        assistantReply: errText,
      });
    } else {
      addSessionMessage('assistant', `自动填充失败：${errText}`, undefined, undefined, null, sid, scenario);
    }
    await persistConversation(undefined, sid, scenario);
  } finally {
    isThinking.value = false;
    aiLoading.value = false;
  }
    },
  });
}

function isBomGenerateMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'bom_generate');
}

function isBomDrawingReviewExistingMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(
    sr &&
    typeof sr === 'object' &&
    (sr as Record<string, unknown>).kind === 'bom_drawing_review_existing'
  );
}

function isDrawingReviewMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'drawing_review');
}

function drawingReviewStatus(msg: ChatMessage): string {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  return String(sr?.status ?? '');
}

function drawingReviewActionsVisible(msg: ChatMessage) {
  return drawingReviewStatus(msg) === 'completed';
}

function drawingReviewReportNavFromMessage(msg: ChatMessage) {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr) return null;
  return parseBomDrawingReviewReportNav(sr.reportNav ?? sr.report_nav);
}

async function openDrawingReviewReportFromMessage(msg: ChatMessage) {
  const nav = drawingReviewReportNavFromMessage(msg);
  if (!nav) {
    ElMessage.warning('操作已失效，请重新点击「图纸审核」');
    return;
  }
  stashBomDrawingReviewOpenIntent({ ...nav, action: 'view' });
  await navigateToProjectBomDrawingReview(router, nav.projectId);
}

async function viewDrawingReviewResult(msg: ChatMessage) {
  const ctx = drawingReviewContext.value;
  if (ctx && ctx.messageId === msg.id) {
    await ctx.onViewReport();
    return;
  }
  await openDrawingReviewReportFromMessage(msg);
}

function bomDrawingReviewExistingStatus(msg: ChatMessage): string {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  return String(sr?.status ?? 'pending');
}

function bomDrawingReviewExistingActionsVisible(msg: ChatMessage) {
  return bomDrawingReviewExistingStatus(msg) === 'pending';
}

function formatBomDrawingReviewTime(raw: string): string {
  const t = (raw || '').trim();
  if (!t) return '未知时间';
  const d = new Date(t.includes('T') ? t : t.replace(' ', 'T'));
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-');
  }
  return t;
}

async function viewBomDrawingReviewExisting(msg: ChatMessage) {
  const ctx = bomDrawingReviewExistingContext.value;
  if (ctx && ctx.messageId === msg.id) {
    await ctx.callbacks.onViewReport();
    return;
  }
  await openDrawingReviewReportFromMessage(msg);
}

async function rerunBomDrawingReviewExisting(msg: ChatMessage) {
  const ctx = bomDrawingReviewExistingContext.value;
  if (!ctx || ctx.messageId !== msg.id) {
    const nav = drawingReviewReportNavFromMessage(msg);
    if (nav) {
      await navigateToProjectBomDrawingReview(router, nav.projectId);
      ElMessage.info('请在该项目 BOM 页勾选图纸后点击「图纸审核」重新提交');
      return;
    }
    ElMessage.warning('操作已失效，请重新点击「图纸审核」');
    return;
  }
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  store.updateMessage(scenario, msg.id, {
    structuredResult: {
      ...(msg.structuredResult as object),
      kind: 'bom_drawing_review_existing',
      status: 'rerun',
    },
  }, sid);
  if (bomDrawingReviewExistingContext.value?.messageId === msg.id) {
    bomDrawingReviewExistingContext.value = null;
  }
  void persistConversation();
  await ctx.callbacks.onRerun();
}

function isBomCostEstimateMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'bom_cost_estimate');
}

function bomCostEstimateStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr || sr.kind !== 'bom_cost_estimate') return null;
  return String(sr.status || 'pending');
}

function bomCostEstimateActionsVisible(msg: ChatMessage) {
  const s = bomCostEstimateStatus(msg);
  return s === 'pending' || s === 'saved';
}

async function acceptBomCostEstimate(msg: ChatMessage) {
  const ctx = bomCostEstimateContext.value;
  if (!ctx || ctx.messageId !== msg.id) {
    ElMessage.warning('无法保存：请重新进行成本核算后再试。');
    return;
  }
  bomCostSaving.value = true;
  try {
    await ctx.callbacks.onSave();
    const scenario = conversationScenario.value;
    const sid = effectiveSessionId();
    store.updateMessage(scenario, msg.id, {
      structuredResult: {
        ...(msg.structuredResult as object),
        kind: 'bom_cost_estimate',
        status: 'saved',
      },
    }, sid);
    expandedThoughtIds.value.add(msg.id);
    ElMessage.success('BOM 成本已保存');
    void persistConversation();
  } catch (e: unknown) {
    const err = e as { message?: string };
    ElMessage.error(err?.message || '保存失败');
  } finally {
    bomCostSaving.value = false;
  }
}

function bomGenerateStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr || sr.kind !== 'bom_generate') return null;
  return String(sr.status || 'pending');
}

function bomGenerateDialogLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  const fromSr = String(sr?.assistantReply ?? sr?.assistant_reply ?? '').trim();
  if (fromSr) return fromSr;
  const total = Number(sr?.totalParts ?? sr?.total_parts ?? 0);
  const matched = Number(sr?.matchedCount ?? sr?.matched_count ?? 0);
  if (total > 0) {
    return matched > 0
      ? `一共拆解${total}个部件，并匹配${matched}个部件`
      : `一共拆解${total}个部件，已生成 BOM`;
  }
  return msg.content?.trim() || 'BOM 生成完成，请在下方表格查看并确认。';
}

function bomGenerateActionsVisible(msg: ChatMessage) {
  const s = bomGenerateStatus(msg);
  if (s === 'unsupported') return false;
  return s === 'pending' || s === 'saved' || s === 'cancelled';
}

async function acceptBomGenerate(msg: ChatMessage) {
  const ctx = bomGenerateContext.value;
  if (!ctx || ctx.messageId !== msg.id) {
    ElMessage.warning('无法保存：请重新生成 BOM 后再试（可能已刷新页面）。');
    return;
  }
  bomGenerateSaving.value = true;
  try {
    await ctx.callbacks.onSave();
    const scenario = conversationScenario.value;
    const sid = effectiveSessionId();
    store.updateMessage(scenario, msg.id, {
      structuredResult: {
        ...(msg.structuredResult as object),
        kind: 'bom_generate',
        status: 'saved',
      },
    }, sid);
    expandedThoughtIds.value.add(msg.id);
    ElMessage.success('BOM 表已保存');
    void persistConversation();
  } catch (e: unknown) {
    const err = e as { message?: string };
    ElMessage.error(err?.message || '保存失败');
  } finally {
    bomGenerateSaving.value = false;
  }
}

function cancelBomGenerate(msg: ChatMessage) {
  const ctx = bomGenerateContext.value;
  if (ctx?.messageId === msg.id) {
    ctx.callbacks.onCancel();
  }
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  store.updateMessage(scenario, msg.id, {
    structuredResult: {
      ...(msg.structuredResult as object),
      kind: 'bom_generate',
      status: 'cancelled',
    },
  }, sid);
  if (bomGenerateContext.value?.messageId === msg.id) {
    bomGenerateContext.value = null;
  }
  ElMessage.info('已取消本次 BOM 生成');
  void persistConversation();
}

function componentRecommendStatsLine(msg: ChatMessage): string {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  const totalReq = Number(sr?.totalRequirements ?? sr?.total_requirements ?? 0);
  const matched = Number(sr?.matchedCount ?? sr?.matched_count ?? sr?.totalMatched ?? sr?.total_matched ?? 0);
  const unique = Number(sr?.uniquePartCount ?? sr?.unique_part_count ?? 0);
  if (totalReq > 0 || matched > 0 || unique > 0) {
    const unmatched = Math.max(0, totalReq - matched);
    return `需求条目 ${totalReq} 条；有推荐 ${matched} 条；无推荐 ${unmatched} 条；推荐部件 ${unique} 种。完整清单已写入左侧「通用部件推荐」表格。`;
  }
  return '完整推荐清单已写入左侧「通用部件推荐」表格，请在该区域查看。';
}

function isDocumentDeepReviewMessage(msg: ChatMessage) {
  const sr = msg.structuredResult;
  return !!(sr && typeof sr === 'object' && (sr as Record<string, unknown>).kind === 'document_deep_review');
}
function getDocumentDeepReviewFindings(msg: ChatMessage): DocumentDeepReviewFindingRow[] {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  const raw = sr?.findings;
  if (!Array.isArray(raw)) return [];
  return raw.map((f: unknown, i: number) => {
    const o = f as Record<string, unknown>;
    return {
      id: String(o.id ?? i),
      anchor_text: String(o.anchor_text ?? o.anchorText ?? ''),
      suggested_replacement: String(o.suggested_replacement ?? o.suggestedReplacement ?? ''),
      category: String(o.category ?? ''),
      categories: Array.isArray(o.categories) ? (o.categories as unknown[]).map((c) => String(c)) : undefined,
      rationale: String(o.rationale ?? ''),
      paragraph_label: String(o.paragraph_label ?? ''),
    };
  });
}
function documentDeepReviewStatus(msg: ChatMessage): string | null {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr || sr.kind !== 'document_deep_review') return null;
  return String(sr.status || 'pending');
}
function documentDeepReviewActionsVisible(msg: ChatMessage) {
  const s = documentDeepReviewStatus(msg);
  return s === 'pending' || s === 'adopted' || s === 'reverted' || s === 'cancelled';
}
function documentDeepReviewCategoryLabel(cat: string, categories?: string[]) {
  const codes = categories?.length ? categories : [cat];
  return codes.map((c) => deepReviewErrorLabel(c)).filter(Boolean).join(' / ') || '审查';
}

async function acceptDocumentDeepReview(msg: ChatMessage) {
  const ctx = documentDeepReviewContext.value;
  if (!ctx || ctx.messageId !== msg.id) {
    ElMessage.warning('无法采纳：请重新发起深度审查后再试（可能已刷新页面）。');
    return;
  }
  ctx.callbacks.captureSnapshot();
  const html = ctx.callbacks.getDocumentHtml();
  const merged = applyDocumentReviewFindingsToHtml(html, ctx.findings);
  ctx.callbacks.applyMerged(merged);
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  store.updateMessage(scenario, msg.id, {
    structuredResult: {
      ...(msg.structuredResult as object),
      kind: 'document_deep_review',
      status: 'adopted',
    },
  }, sid);
  expandedThoughtIds.value.add(msg.id);
  ElMessage.success('已将审查建议合并到文档');
  await persistConversation();
}

function dismissDocumentDeepReview(msg: ChatMessage) {
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  store.updateMessage(scenario, msg.id, {
    structuredResult: {
      ...(msg.structuredResult as object),
      kind: 'document_deep_review',
      status: 'cancelled',
    },
  }, sid);
  if (documentDeepReviewContext.value?.messageId === msg.id) {
    documentDeepReviewContext.value = null;
  }
  void persistConversation();
}

function revertOrDismissDocumentDeepReview(msg: ChatMessage) {
  const status = documentDeepReviewStatus(msg);
  if (status === 'pending') {
    dismissDocumentDeepReview(msg);
    ElMessage.info('已放弃本次审查建议');
    return;
  }
  if (status !== 'adopted') return;
  const ctx = documentDeepReviewContext.value;
  if (!ctx || ctx.messageId !== msg.id) {
    ElMessage.warning('无法回退：审查操作上下文已失效（例如已刷新页面）。请手动恢复正文或重新发起深度审查。');
    return;
  }
  ctx.callbacks.revert();
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  store.updateMessage(scenario, msg.id, {
    structuredResult: {
      ...(msg.structuredResult as object),
      kind: 'document_deep_review',
      status: 'reverted',
    },
  }, sid);
  documentDeepReviewContext.value = null;
  ElMessage.success('已回退文档正文');
  void persistConversation();
}

type RunDocumentDeepReviewArgs = {
  projectId?: number;
  documentPath: string;
  documentTitle: string;
  documentMarkdown: string;
  projectContext: string;
  docType?: string;
  ruleSetId?: string;
  reviewCategories?: string[];
  corePurposes?: string[];
  knowledgeContext?: string;
  standardsContext?: string;
  consistencyContext?: string;
  requirementsContext?: string;
  projectBackground?: string;
  projectGoals?: string;
  documentDirection?: string;
  selectedKnowledgeBaseIds?: string[];
  selectedKnowledgeDocumentIds?: string[];
  selectedStandardsKnowledgeDocumentIds?: string[];
  conversationId?: string;
  tableStructuresSupplement?: string;
  /** 将 findings 写入文档评论面板（右侧批注） */
  onApplyDocumentComments?: (
    findings: Array<{
      anchor_text: string;
      category: string;
      rationale?: string;
      suggested_replacement?: string;
      paragraph_label?: string;
    }>,
  ) => number | Promise<number>;
  getDocumentHtml: () => string;
  applyMerged: (html: string) => void;
  captureSnapshot: () => void;
  revert: () => void;
};

const DEEP_REVIEW_LIVE_STEPS: ThoughtStep[] = [
  { key: 'preprocess', icon: '⏳', title: '文档预处理', text: '正在整理待审文档正文与审查范围…' },
  { key: 'logic_self', icon: '⏳', title: '逻辑自查', text: '结合项目背景与文档专业方向审查…' },
  { key: 'compliance', icon: '⏳', title: '规范符合性检查', text: '对照规范集与知识库条款…' },
  { key: 'consistency', icon: '⏳', title: '逻辑一致性检查', text: '比对需求、背景与参考文档…' },
  { key: 'aggregate', icon: '⏳', title: '汇总审查意见', text: '合并三层结论并生成批注…' },
];

function startDeepReviewLiveThoughtAnimation() {
  let idx = 0;
  currentThoughtDraft.value = [{ ...DEEP_REVIEW_LIVE_STEPS[0] }];
  const timer = window.setInterval(() => {
    idx = Math.min(DEEP_REVIEW_LIVE_STEPS.length - 1, idx + 1);
    currentThoughtDraft.value = DEEP_REVIEW_LIVE_STEPS.slice(0, idx + 1).map((s, i) => ({
      ...s,
      icon: i < idx ? '✅' : '⏳',
    }));
  }, 3200);
  return () => window.clearInterval(timer);
}

async function runDocumentDeepReview(args: RunDocumentDeepReviewArgs) {
  const { generation, action } = beginAiSessionAction({
    kind: 'document_deep_review',
    label: args.documentTitle || '文档深度审查',
    discard: () => {
      try {
        args.revert();
      } catch {
        /* ignore */
      }
    },
    retry: () => runDocumentDeepReview(args),
  });
  return runInAiTaskQueue({
    kind: 'document_deep_review',
    label: args.documentTitle || '文档深度审查',
    sessionAction: action,
    lifecycle: {
      onStop: () => action.discard(),
      onRollback: () => action.discard(),
    },
    run: async (_boundSessionId, ctx) => {
  mainTab.value = 'new';
  expandRightPanel();
  const sid = effectiveSessionId();
  const scenario = effectiveScenario();
  const userLine = `【深度审查】${args.documentTitle || args.documentPath}`;
  addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  aiLoading.value = true;
  args.captureSnapshot();
  const stopLiveThought = startDeepReviewLiveThoughtAnimation();
  try {
    const res = await deepReviewDocument({
      projectId: args.projectId,
      documentPath: args.documentPath,
      documentTitle: args.documentTitle,
      documentMarkdown: args.documentMarkdown,
      projectContext: args.projectContext,
      docType: args.docType,
      ruleSetId: args.ruleSetId,
      reviewCategories: args.reviewCategories,
      corePurposes: args.corePurposes,
      knowledgeContext: args.knowledgeContext,
      standardsContext: args.standardsContext,
      consistencyContext: args.consistencyContext,
      requirementsContext: args.requirementsContext,
      projectBackground: args.projectBackground,
      projectGoals: args.projectGoals,
      documentDirection: args.documentDirection,
      selectedKnowledgeBaseIds: args.selectedKnowledgeBaseIds,
      selectedKnowledgeDocumentIds: args.selectedKnowledgeDocumentIds,
      selectedStandardsKnowledgeDocumentIds: args.selectedStandardsKnowledgeDocumentIds,
      conversationId: args.conversationId,
      tableStructuresSupplement: args.tableStructuresSupplement,
    });
    if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
      throw new DOMException('已取消', 'AbortError');
    }
    stopLiveThought();
    currentThoughtDraft.value = [];
    const thought = JSON.stringify(res.thought ?? { steps: [] });
    const findings = dedupeDeepReviewFindings(
      (res.findings || []).map((f, idx) => ({
        id: String((f as { id?: string }).id ?? idx),
        anchor_text: String((f as { anchor_text?: string }).anchor_text ?? ''),
        suggested_replacement: String((f as { suggested_replacement?: string }).suggested_replacement ?? ''),
        category: String((f as { category?: string }).category ?? ''),
        categories: Array.isArray((f as { categories?: string[] }).categories)
          ? (f as { categories: string[] }).categories
          : undefined,
        rationale: String((f as { rationale?: string }).rationale ?? ''),
        paragraph_label: String((f as { paragraph_label?: string }).paragraph_label ?? ''),
      })),
    );
    // Older AI deployments cannot distinguish an empty review from an invalid model reply.
    const reviewStatus = res.review_status || 'failed';
    const failedPhases = res.failed_phases || [];
    const savedCount = reviewStatus !== 'failed' && args.onApplyDocumentComments
      ? await args.onApplyDocumentComments(findings)
      : null;
    const baseText = String(res.assistant_reply || res.summary || '').trim();
    const resultText = reviewStatus === 'failed'
      ? '本次审查未完成，未替换右侧原有审查点；请重试。'
      : savedCount == null
        ? '本次审查点尚未保存到文档。'
        : `本次${reviewStatus === 'partial' ? '部分审查' : '审查'}已保存 ${savedCount} 条审查点到文档右侧。`;
    const dialogText = [baseText, resultText].filter(Boolean).join('\n\n');
    if (reviewStatus === 'failed') ElMessage.warning('深度审查未完成，原有审查点已保留');
    const structured: Record<string, unknown> = {
      kind: 'document_deep_review',
      status: reviewStatus === 'failed' ? 'failed' : 'pending',
      reviewStatus,
      failedPhases,
      savedCount,
      findings,
    };
    const assistantMsg = addSessionMessage(
      'assistant',
      dialogText,
      undefined,
      thought,
      structured,
      sid,
      scenario,
    );
    await nextTick();
    expandedThoughtIds.value.add(assistantMsg.id);
    if (reviewStatus !== 'failed') {
      documentDeepReviewContext.value = {
        messageId: assistantMsg.id,
        findings,
        callbacks: {
          getDocumentHtml: args.getDocumentHtml,
          applyMerged: args.applyMerged,
          captureSnapshot: args.captureSnapshot,
          revert: args.revert,
        },
      };
    }
    await revealAssistantMessage(assistantMsg.id, sid, scenario);
    await persistConversation(undefined, sid, scenario);
    await maybeGenerateSessionTitleAfterFirstTurn(sid, scenario);
  } catch (e: unknown) {
    stopLiveThought();
    currentThoughtDraft.value = [];
    if (isTaskCancelledError(e) || !isAiSessionActionActive(action, generation)) {
      try {
        args.revert();
      } catch {
        /* ignore */
      }
      throw e;
    }
    const err = e as { message?: string };
    ElMessage.error(err?.message || '深度审查失败');
    addSessionMessage('assistant', `深度审查失败：${err?.message || '请稍后重试'}`, undefined, undefined, null, sid, scenario);
  } finally {
    isThinking.value = false;
    aiLoading.value = false;
  }
    },
  });
}

type RunComponentRecommendArgs = {
  projectId: number;
  projectName: string;
  conversationId: string;
  callRecommendApi: (opts?: { signal?: AbortSignal; onProgress?: (task: ComponentRecommendTask) => void }) => Promise<ComponentRecommendResponse>;
  onRowsReady: (rows: ComponentRecommendRow[]) => void;
  /** 丢弃推荐结果，恢复推荐前表格 */
  onDiscard?: () => void;
};

const COMPONENT_BOM_IMPORT_LIVE_STEPS: ThoughtStep[] = [
  { key: 'parse', icon: '⏳', title: '解析 BOM 文件', text: '正在将 Excel/PDF 转为文本…' },
  { key: 'load_catalog', icon: '⏳', title: '加载通用部件库', text: '正在整理库内编号、名称、规格、尺寸…' },
  { key: 'llm_match', icon: '⏳', title: '对照库分析', text: '划分新增、重叠与相似部件…' },
];

function startComponentBomImportLiveThought() {
  let idx = 0;
  currentThoughtDraft.value = [{ ...COMPONENT_BOM_IMPORT_LIVE_STEPS[0] }];
  const timer = window.setInterval(() => {
    idx = Math.min(COMPONENT_BOM_IMPORT_LIVE_STEPS.length - 1, idx + 1);
    currentThoughtDraft.value = COMPONENT_BOM_IMPORT_LIVE_STEPS.slice(0, idx + 1).map((s, i) => ({
      ...s,
      icon: i < idx ? '✅' : '⏳',
    }));
  }, 3200);
  return () => window.clearInterval(timer);
}

type RunComponentBomImportArgs = {
  file: File;
  fileLabel: string;
  conversationId: string;
  onSaved: (componentIds: number[]) => void;
};

async function runComponentBomImport(args: RunComponentBomImportArgs) {
  return runInAiTaskQueue({
    kind: 'component_bom_import',
    label: args.fileLabel || 'BOM 导入',
    run: async () => {
  mainTab.value = 'new';
  expandRightPanel();
  // 同 runComponentAutoFill：任务执行期间不改写全局 conversationScenario，
  // 否则同一个 sessionId 会被写成两个 scenario 下的两条库记录。
  const scenario = effectiveScenario();
  const sid = effectiveSessionId();
  const userLine = `【BOM 导入】${args.fileLabel || args.file.name}`;
  addSessionMessage('user', userLine, [
    { type: 'doc', name: args.file.name, url: args.file.name },
  ], undefined, undefined, sid, scenario);
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  aiLoading.value = true;
  componentBomImportProgressText.value = '正在解析 BOM 文件…';
  const stopLive = startComponentBomImportLiveThought();
  try {
    const res: ComponentBomImportAnalyzeResult = await analyzeComponentBomImport(
      args.file,
      args.conversationId,
    );
    stopLive();
    if (!res.success) {
      throw new Error(res.assistantReply || res.summary || 'BOM 解析失败');
    }
    const steps = (res.thought?.steps || []).map((s) => ({
      key: String(s.key ?? ''),
      icon: String(s.icon ?? '✅'),
      title: String(s.title ?? ''),
      text: String(s.text ?? ''),
    }));
    currentThoughtDraft.value = steps.length
      ? steps
      : COMPONENT_BOM_IMPORT_LIVE_STEPS.map((s) => ({ ...s, icon: '✅' }));
    const dialogText =
      (res.assistantReply || res.summary || '').trim() ||
      `已从 BOM 识别 ${res.totalExtracted ?? res.items?.length ?? 0} 条部件，请确认是否写入公司部件库。`;
    const structured: Record<string, unknown> = {
      kind: 'component_bom_import',
      status: 'pending',
      sessionId: res.sessionId,
      totalExtracted: res.totalExtracted ?? 0,
      assistantReply: dialogText,
      newItems: res.newItems ?? [],
      overlapIdentical: res.overlapIdentical ?? [],
      overlapConflicts: res.overlapConflicts ?? [],
      similarItems: res.similarItems ?? [],
      catalogTotal: res.catalogSnapshot?.total ?? 0,
    };
    const thought = JSON.stringify(res.thought ?? { steps });
    const assistantMsg = addSessionMessage('assistant', dialogText, undefined, thought, structured, sid, scenario);
    componentBomImportDecisionsByMessageId.value = {
      ...componentBomImportDecisionsByMessageId.value,
      [assistantMsg.id]: buildDefaultOverlapDecisions(res.overlapConflicts ?? []),
    };
    componentBomImportContext.value = {
      messageId: assistantMsg.id,
      sessionId: res.sessionId,
      callbacks: { onSaved: args.onSaved },
    };
    await nextTick();
    expandedThoughtIds.value.add(assistantMsg.id);
    await revealAssistantMessage(assistantMsg.id, sid, scenario);
    await persistConversation(undefined, sid, scenario);
  } catch (e: unknown) {
    stopLive();
    currentThoughtDraft.value = [];
    const err = e as { message?: string };
    ElMessage.error(err?.message || 'BOM 导入失败');
    addSessionMessage(
      'assistant',
      `BOM 导入失败：${err?.message || '请稍后重试'}`,
      undefined,
      undefined,
      null,
      sid,
      scenario,
    );
  } finally {
    componentBomImportProgressText.value = '';
    isThinking.value = false;
    aiLoading.value = false;
  }
    },
  });
}

const BOM_GENERATE_LIVE_STEPS: ThoughtStep[] = [
  { key: 'load_tree', icon: '⏳', title: '解析 STEP 装配', text: '正在从 3D 图纸解析装配层级与零件尺寸…' },
  { key: 'done', icon: '⏳', title: '生成 BOM', text: '正在将解析结果写入 BOM 表…' },
];

const BOM_2D_GENERATE_LIVE_STEPS: ThoughtStep[] = [
  { key: 'phase1', icon: '🔍', title: '定位总装图', text: '正在从多张 2D 图纸中识别总装图…' },
  { key: 'phase2', icon: '🌲', title: '逐级拆解', text: '正在按图纸编码递归匹配子级图纸…' },
  { key: 'phase3', icon: '📋', title: '生成 BOM 表', text: '正在汇总层级与零件参数…' },
];

type BomGenerateStreamContext = {
  onProgress?: (data: Record<string, unknown>) => void;
  onPartialRows?: (res: BomGenerateResponse) => void;
  signal?: AbortSignal;
};

type RunBomGenerateArgs = {
  projectId: number;
  projectName: string;
  conversationId: string;
  drawingLabel: string;
  /** 2d：自顶向下拆解 2D 图纸；3d：STEP 装配 + 部件库匹配（默认） */
  bomMode?: '2d' | '3d';
  callGenerateApi: (ctx?: BomGenerateStreamContext) => Promise<BomGenerateResponse>;
  onRowsReady: (res: BomGenerateResponse) => void;
  onSave: () => void | Promise<void>;
  onCancel: () => void;
};

const bomGenerateProgressText = ref('');

async function runBomGenerate(args: RunBomGenerateArgs) {
  const { generation, action } = beginAiSessionAction({
    kind: 'bom_generate',
    label: args.drawingLabel || '生成 BOM',
    discard: () => {
      try {
        args.onCancel();
      } catch {
        /* ignore */
      }
    },
    retry: () => runBomGenerate(args),
  });
  return runInAiTaskQueue({
    kind: 'bom_generate',
    label: args.drawingLabel || '生成 BOM',
    sessionAction: action,
    lifecycle: {
      onStop: () => action.discard(),
      onRollback: () => action.discard(),
    },
    run: async (_boundSessionId, ctx) => {
  mainTab.value = 'new';
  expandRightPanel();
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  const userLine = `【生成 BOM】${args.drawingLabel || args.projectName || `项目 ${args.projectId}`}`;
  addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  aiLoading.value = true;
  bomGenerateProgressText.value = '';
  const is2dBom = args.bomMode === '2d';
  const liveSteps = is2dBom ? BOM_2D_GENERATE_LIVE_STEPS : BOM_GENERATE_LIVE_STEPS;
  currentThoughtDraft.value = [{ ...liveSteps[0] }];
  let liveTimer: ReturnType<typeof window.setInterval> | null = null;
  if (!is2dBom) {
    let stepIdx = 0;
    liveTimer = window.setInterval(() => {
      stepIdx = Math.min(liveSteps.length - 1, stepIdx + 1);
      currentThoughtDraft.value = liveSteps.slice(0, stepIdx + 1).map((s, i) => ({
        ...s,
        icon: i < stepIdx ? '✅' : '⏳',
      }));
    }, 2600);
  }
  let streamIncremental = false;
  const stopLiveTimer = () => {
    if (liveTimer != null) {
      window.clearInterval(liveTimer);
      liveTimer = null;
    }
  };
  try {
    const res = await args.callGenerateApi({
      onProgress: (data) => {
        if (!isAiSessionActionActive(action, generation)) return;
        stopLiveTimer();
        streamIncremental = true;
        const thought = data.thought as { steps?: Array<{ key?: string; icon?: string; title?: string; text?: string }> } | undefined;
        const steps = (thought?.steps || []).map((s) => ({
          key: String(s.key ?? ''),
          icon: String(s.icon ?? '⏳'),
          title: String(s.title ?? ''),
          text: String(s.text ?? ''),
        }));
        if (steps.length) currentThoughtDraft.value = steps;
        const batch = Number(data.batch ?? 0);
        const batchTotal = Number(data.batch_total ?? data.batchTotal ?? 0);
        const matched = Number(data.matched_count ?? data.matchedCount ?? 0);
        const total = Number(data.total_parts ?? data.totalParts ?? 0);
        const drawingCount = Number(data.drawing_count ?? data.drawingCount ?? 0);
        if (is2dBom && drawingCount > 0) {
          bomGenerateProgressText.value = `已加载 ${drawingCount} 份 2D 图纸，正在多模态识别总装图…`;
        } else if (batch > 0 && batchTotal > 0 && matched > 0) {
          bomGenerateProgressText.value = `匹配进度：第 ${batch}/${batchTotal} 批（已匹配 ${matched}/${total}）`;
        } else if (total > 0) {
          bomGenerateProgressText.value = `已解析 ${total} 个零件，正在生成 BOM…`;
        }
      },
      onPartialRows: (partial) => {
        if (!isAiSessionActionActive(action, generation)) return;
        if (!is2dBom && (partial.rows?.length ?? 0) > 0) {
          streamIncremental = true;
          args.onRowsReady(partial);
        }
      },
      signal: ctx?.signal,
    });
    if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
      throw new DOMException('已取消', 'AbortError');
    }
    stopLiveTimer();
    bomGenerateProgressText.value = '';
    const structureSupported = res.structure_supported ?? res.structureSupported ?? true;
    const steps = (res.thought?.steps || []).map((s) => ({
      key: String(s.key ?? ''),
      icon: String(s.icon ?? '✅'),
      title: String(s.title ?? ''),
      text: String(s.text ?? ''),
    }));
    currentThoughtDraft.value = steps.length
      ? steps
      : liveSteps.map((s) => ({ ...s, icon: '✅' }));
    if (!structureSupported) {
      const dialogText =
        (res.assistant_reply || res.summary || '').trim() ||
        '当前图纸格式暂不支持自动拆解装配结构，请在 CAD 中导出 STEP 装配文件（.stp / .step）后重新上传。';
      const structured: Record<string, unknown> = {
        kind: 'bom_generate',
        status: 'unsupported',
        totalParts: 0,
        matchedCount: 0,
        assistantReply: dialogText,
      };
      const thought = JSON.stringify(res.thought ?? { steps });
      const assistantMsg = addSessionMessage('assistant', dialogText, undefined, thought, structured);
      await nextTick();
      expandedThoughtIds.value.add(assistantMsg.id);
      startTypewriter(assistantMsg.id);
      await waitForTypewriter(assistantMsg.id);
      await persistConversation();
      ElMessage.warning(dialogText);
      return;
    }
    if (!streamIncremental || (is2dBom && (res.rows?.length ?? 0) > 0)) {
      args.onRowsReady(res);
    }
    let autoSaved = false;
    try {
      await args.onSave();
      autoSaved = true;
    } catch (e: unknown) {
      const err = e as { message?: string };
      ElMessage.error(err?.message || '\u0042\u004f\u004d \u81ea\u52a8\u4fdd\u5b58\u5931\u8d25\uff0c\u8bf7\u70b9\u51fb\u4fdd\u5b58\u91cd\u8bd5');
    }
    const totalParts = res.total_parts ?? res.rows?.length ?? 0;
    const matchedCount = res.matched_count ?? 0;
    const dialogText =
      (res.assistant_reply || res.summary || '').trim() ||
      (is2dBom
        ? `已从 2D 图纸生成 BOM，共 ${totalParts} 行。`
        : matchedCount > 0
          ? `一共拆解${totalParts}个部件，并匹配${matchedCount}个部件`
          : `一共拆解${totalParts}个部件，已生成 BOM`);
    const structured: Record<string, unknown> = {
      kind: 'bom_generate',
      status: autoSaved ? 'saved' : 'pending',
      totalParts,
      matchedCount,
      assistantReply: dialogText,
    };
    const thought = JSON.stringify(res.thought ?? { steps });
    const assistantMsg = addSessionMessage('assistant', dialogText, undefined, thought, structured);
    bomGenerateContext.value = autoSaved
      ? null
      : {
          messageId: assistantMsg.id,
          callbacks: { onSave: args.onSave, onCancel: args.onCancel },
        };
    await nextTick();
    expandedThoughtIds.value.add(assistantMsg.id);
    await revealAssistantMessage(assistantMsg.id, sid, scenario);
    await persistConversation();
  } catch (e: unknown) {
    stopLiveTimer();
    bomGenerateProgressText.value = '';
    currentThoughtDraft.value = [];
    if (isTaskCancelledError(e) || !isAiSessionActionActive(action, generation)) {
      try {
        args.onCancel();
      } catch {
        /* ignore */
      }
      throw e;
    }
    const err = e as { message?: string };
    const msg = err?.message || '生成 BOM 失败';
    if (!msg.includes('流式响应未正常结束') && !msg.includes('分批匹配已完成')) {
      ElMessage.error(msg);
    } else if (msg.includes('分批匹配已完成')) {
      ElMessage.warning(msg);
    } else {
      ElMessage.warning('连接已中断，若表格已有数据请直接保存；否则请重试');
    }
    addSessionMessage('assistant', `生成 BOM 失败：${err?.message || '请稍后重试'}`, undefined, undefined, null);
  } finally {
    isThinking.value = false;
    aiLoading.value = false;
  }
    },
  });
}

const BOM_COST_LIVE_STEPS: ThoughtStep[] = [
  { key: 'load_rates', icon: '⏳', title: '读取费率清单', text: '正在解析成本知识库…' },
  { key: 'calc_rows', icon: '⏳', title: '逐行核算', text: '正在计算材料费与加工费…' },
  { key: 'aggregate', icon: '⏳', title: '汇总成本', text: '正在统计含税/未税总额…' },
];

type RunBomCostEstimateArgs = {
  projectId: number;
  projectName: string;
  conversationId: string;
  knowledgeBaseName: string;
  callEstimateApi: (opts?: { signal?: AbortSignal }) => Promise<import('@/api/bomApi').BomCostEstimateResponse>;
  onRowsReady: (res: import('@/api/bomApi').BomCostEstimateResponse) => void;
  onSave: () => void | Promise<void>;
  /** 丢弃成本列变更，恢复核价前 BOM */
  onDiscard?: () => void;
};

async function runBomCostEstimate(args: RunBomCostEstimateArgs) {
  const { generation, action } = beginAiSessionAction({
    kind: 'bom_cost_estimate',
    label: args.knowledgeBaseName || '成本核价',
    discard: () => {
      args.onDiscard?.();
    },
    retry: () => runBomCostEstimate(args),
  });
  return runInAiTaskQueue({
    kind: 'bom_cost_estimate',
    label: args.knowledgeBaseName || '成本核价',
    sessionAction: action,
    lifecycle: {
      onStop: () => action.discard(),
      onRollback: () => action.discard(),
    },
    run: async (_boundSessionId, ctx) => {
  mainTab.value = 'new';
  expandRightPanel();
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  const userLine = `【成本核算】${args.knowledgeBaseName || args.projectName || `项目 ${args.projectId}`}`;
  addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  aiLoading.value = true;
  currentThoughtDraft.value = [{ ...BOM_COST_LIVE_STEPS[0] }];
  let stepIdx = 0;
  const liveTimer = window.setInterval(() => {
    stepIdx = Math.min(BOM_COST_LIVE_STEPS.length - 1, stepIdx + 1);
    currentThoughtDraft.value = BOM_COST_LIVE_STEPS.slice(0, stepIdx + 1).map((s, i) => ({
      ...s,
      icon: i < stepIdx ? '✅' : '⏳',
    }));
  }, 2400);
  try {
    const res = await args.callEstimateApi({ signal: ctx?.signal });
    if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
      throw new DOMException('已取消', 'AbortError');
    }
    window.clearInterval(liveTimer);
    const steps = (res.thought?.steps || []).map((s) => ({
      key: String(s.key ?? ''),
      icon: String(s.icon ?? '✅'),
      title: String(s.title ?? ''),
      text: String(s.text ?? ''),
    }));
    const finalSteps = steps.length ? steps : BOM_COST_LIVE_STEPS.map((s) => ({ ...s, icon: '✅' }));
    currentThoughtDraft.value = finalSteps;
    args.onRowsReady(res);
    const detail = res.summaryDetail;
    const dialogText = (res.assistant_reply || res.summary || '').trim() || '成本核算完成，请查看下方明细。';
    const structured: Record<string, unknown> = {
      kind: 'bom_cost_estimate',
      status: 'pending',
      summaryDetail: detail,
      assistantReply: dialogText,
    };
    const thought = thoughtStepsToJson(finalSteps);
    const assistantMsg = addSessionMessage('assistant', dialogText, undefined, thought, structured);
    bomCostEstimateContext.value = {
      messageId: assistantMsg.id,
      callbacks: { onSave: args.onSave },
    };
    await nextTick();
    expandedThoughtIds.value.add(assistantMsg.id);
    await persistConversation();
  } catch (e: unknown) {
    window.clearInterval(liveTimer);
    currentThoughtDraft.value = [];
    if (isTaskCancelledError(e) || !isAiSessionActionActive(action, generation)) {
      args.onDiscard?.();
      throw e;
    }
    const err = e as { message?: string };
    ElMessage.error(err?.message || '成本核算失败');
    addSessionMessage('assistant', `成本核算失败：${err?.message || '请稍后重试'}`, undefined, undefined, null);
  } finally {
    isThinking.value = false;
    aiLoading.value = false;
  }
    },
  });
}

async function runComponentRecommend(args: RunComponentRecommendArgs) {
  const { generation, action } = beginAiSessionAction({
    kind: 'component_recommend',
    label: args.projectName || '部件推荐',
    discard: () => {
      args.onDiscard?.();
    },
    retry: () => runComponentRecommend(args),
  });
  return runInAiTaskQueue({
    kind: 'component_recommend',
    label: args.projectName || '部件推荐',
    sessionAction: action,
    lifecycle: {
      onStop: () => action.discard(),
      onRollback: () => action.discard(),
    },
    run: async (_boundSessionId, ctx) => {
  const originTabKey = currentPageTabKey();
  mainTab.value = 'new';
  expandRightPanel();
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  const userLine = `【通用部件推荐】${args.projectName || `项目 ${args.projectId}`}`;
  addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  aiLoading.value = true;
  currentThoughtDraft.value = [{ key: 'submit', icon: '⏳', title: '提交推荐任务', text: '正在提交推荐任务…' }];
  try {
    const res = await args.callRecommendApi({
      signal: ctx?.signal,
      onProgress: (task) => {
        if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) return;
        currentThoughtDraft.value = [{
          key: task.status,
          icon: task.status === 'completed' ? '✅' : '⏳',
          title: task.status === 'queued' ? '排队中' : task.status === 'running' ? '正在生成' : '任务已结束',
          text: task.message,
        }];
      },
    });
    if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
      throw new DOMException('已取消', 'AbortError');
    }
    const steps = (res.thought?.steps || []).map((s) => ({
      key: String(s.key ?? ''),
      icon: String(s.icon ?? '✅'),
      title: String(s.title ?? ''),
      text: String(s.text ?? ''),
    }));
    currentThoughtDraft.value = steps.length ? steps : [{ key: 'completed', icon: '✅', title: '推荐完成', text: '推荐清单已生成' }];
    const matchedRows = (res.rows || []).filter((r) => r.partNo && r.partNo !== '—');
    args.onRowsReady(matchedRows);
    const matchedCount = res.matched_count ?? 0;
    const totalReq = res.total_requirements ?? 0;
    const uniquePartCount =
      res.unique_part_count ??
      new Set(matchedRows.map((r) => (r.partNo ?? '').trim()).filter(Boolean)).size;
    // 统计文案由下方「推荐结果统计」结构化区块展示，不再写入对话正文
    const dialogText = '';
    const structured: Record<string, unknown> = {
      kind: 'component_recommend',
      totalRequirements: totalReq,
      matchedCount,
      uniquePartCount,
      rowCount: matchedRows.length,
    };
    const thought = JSON.stringify(res.thought ?? { steps });
    const assistantMsg = addSessionMessage('assistant', dialogText, undefined, thought, structured);
    await nextTick();
    expandedThoughtIds.value.add(assistantMsg.id);
    await revealAssistantMessage(assistantMsg.id, sid, scenario);
    await persistConversation();
    ElMessage.success(
      matchedCount > 0
        ? `已为 ${matchedCount} 条需求生成推荐，共 ${matchedRows.length} 行 / ${uniquePartCount} 种部件，详见左侧表格`
        : `无匹配推荐（共 ${totalReq} 条需求），请查看 AI 说明`,
    );
    void notifyPageTabCompletionOrBell({
      tabKey: originTabKey,
      title: '部件推荐已完成',
      content: '点击查看推荐结果',
      actionUrl: `/project/${args.projectId}/dashboard`,
      mergeKey: `AI_COMPONENT_RECOMMEND:${args.projectId}`,
      type: 'AI_TASK_COMPLETED',
    });
  } catch (e: unknown) {
    currentThoughtDraft.value = [];
    if (isTaskCancelledError(e) || !isAiSessionActionActive(action, generation)) {
      args.onDiscard?.();
      throw e;
    }
    const err = e as { message?: string };
    const message = err?.message || '推荐生成失败，请稍后重试';
    ElMessage.error(message);
    addSessionMessage('assistant', message, undefined, undefined, null);
    throw e;
  } finally {
    isThinking.value = false;
    aiLoading.value = false;
  }
    },
  });
}

type TrReviewSubmitResult = {
  recommendPass?: boolean | null;
  reason?: string | null;
  potentialImpact?: string | null;
  improvementSuggestions?: string[] | null;
  verdictLabel?: string | null;
  canPushToNextPhase?: boolean | null;
  pushed?: boolean | null;
  projectCloseReview?: boolean | null;
  ruleDocumentTitle?: string | null;
  knowledgeDocumentId?: string | null;
  knowledgeBaseId?: string | null;
  checklistSummary?: TrReviewChecklistSummary | null;
  checklistItems?: TrReviewChecklistItemResult[] | null;
  keyMissingSummary?: string | null;
  reviewer1UserId?: number | null;
  reviewer1Name?: string | null;
  reviewer2UserId?: number | null;
  reviewer2Name?: string | null;
};

type RunTrReviewArgs = {
  projectId: number;
  projectName: string;
  conversationId: string;
  initialResult?: TrReviewSubmitResult | null;
  callSubmitApi?: () => Promise<TrReviewSubmitResult>;
  onPush: () => Promise<TrReviewSubmitResult>;
  /** 丢弃未推送草稿并重新调用 submit（forceRetry） */
  onResubmit?: () => Promise<TrReviewSubmitResult>;
  knowledgeDocumentId?: string;
  knowledgeBaseId?: string;
};

const TR_REVIEW_LIVE_STEPS: ThoughtStep[] = [
  { key: 'collect', icon: '⏳', title: '汇总项目资料', text: '正在读取需求、交付物、问题清单与风险…' },
  { key: 'analyze', icon: '⏳', title: 'TR 评审分析', text: '大模型正在给出阶段门评审建议…' },
];

function trReviewStructuredFromResult(res: TrReviewSubmitResult, status: 'pending' | 'pushed') {
  const recommendPass = Boolean(res.recommendPass);
  const canPush = Boolean(res.canPushToNextPhase) && status === 'pending';
  const verdictLabel = res.verdictLabel || (recommendPass ? '通过' : '不通过');
  const improvementSuggestions = (res.improvementSuggestions ?? [])
    .map((s) => String(s ?? '').trim())
    .filter(Boolean);
  return {
    kind: 'tr_review',
    status,
    recommendPass,
    recommend_pass: recommendPass,
    verdictLabel,
    verdict_label: verdictLabel,
    reason: res.reason || '',
    potentialImpact: res.potentialImpact || '',
    potential_impact: res.potentialImpact || '',
    improvementSuggestions,
    improvement_suggestions: improvementSuggestions,
    canPushToNextPhase: canPush,
    can_push_to_next_phase: canPush,
    projectCloseReview: res.projectCloseReview ?? false,
    ruleDocumentTitle: res.ruleDocumentTitle || '',
    rule_document_title: res.ruleDocumentTitle || '',
    knowledgeDocumentId: res.knowledgeDocumentId || '',
    knowledge_document_id: res.knowledgeDocumentId || '',
    knowledgeBaseId: res.knowledgeBaseId || '',
    knowledge_base_id: res.knowledgeBaseId || '',
    checklistSummary: res.checklistSummary ?? null,
    checklist_summary: res.checklistSummary ?? null,
    checklistItems: res.checklistItems ?? [],
    checklist_items: res.checklistItems ?? [],
    keyMissingSummary: res.keyMissingSummary || '',
    key_missing_summary: res.keyMissingSummary || '',
    reviewer1UserId: res.reviewer1UserId ?? null,
    reviewer_1_user_id: res.reviewer1UserId ?? null,
    reviewer1Name: res.reviewer1Name || '',
    reviewer_1_name: res.reviewer1Name || '',
    reviewer2UserId: res.reviewer2UserId ?? null,
    reviewer_2_user_id: res.reviewer2UserId ?? null,
    reviewer2Name: res.reviewer2Name || '',
    reviewer_2_name: res.reviewer2Name || '',
  };
}

async function appendTrReviewAssistantMessage(
  scenario: string,
  res: TrReviewSubmitResult,
  callbacks: {
    onPush: () => Promise<TrReviewSubmitResult>;
    onResubmit?: () => Promise<TrReviewSubmitResult>;
  },
) {
  const status: 'pending' | 'pushed' = res.pushed ? 'pushed' : 'pending';
  const structured = trReviewStructuredFromResult(res, status);
  const dialogText = formatTrReviewDialogText(res, structured.verdictLabel);
  const assistantMsg = addSessionMessage('assistant', dialogText, undefined, undefined, structured);
  if (status === 'pending' && (structured.canPushToNextPhase || callbacks.onResubmit)) {
    trReviewContext.value = {
      messageId: assistantMsg.id,
      callbacks: { onPush: callbacks.onPush, onResubmit: callbacks.onResubmit },
    };
  }
  await nextTick();
  expandedThoughtIds.value.add(assistantMsg.id);
  startTypewriter(assistantMsg.id);
  await waitForTypewriter(assistantMsg.id);
  await persistConversation();
  return assistantMsg;
}

async function runTrReview(args: RunTrReviewArgs) {
  const { generation, action } = beginAiSessionAction({
    kind: 'tr_review',
    label: 'TR 评审',
    discard: () => {
      trReviewContext.value = null;
    },
    retry: () => runTrReview({ ...args, initialResult: null }),
  });
  return runInAiTaskQueue({
    kind: 'tr_review',
    label: 'TR 评审',
    sessionAction: action,
    lifecycle: {
      onStop: () => action.discard(),
      onRollback: () => action.discard(),
    },
    run: async (_boundSessionId, ctx) => {
  mainTab.value = 'new';
  expandRightPanel();
  trReviewProjectName.value = args.projectName || '';
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();

  if (args.initialResult && !args.callSubmitApi) {
    const userLine = `【TR 评审】${args.projectName || `项目 ${args.projectId}`}（待提交审核）`;
    addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
    await nextTick();
    scrollAiToBottom();
    await appendTrReviewAssistantMessage(scenario, args.initialResult, {
      onPush: args.onPush,
      onResubmit: args.onResubmit,
    });
    return;
  }

  const userLine = `【TR 评审】${args.projectName || `项目 ${args.projectId}`}`;
  addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  aiLoading.value = true;
  currentThoughtDraft.value = [{ ...TR_REVIEW_LIVE_STEPS[0] }];
  let stepIdx = 0;
  const liveTimer = window.setInterval(() => {
    stepIdx = Math.min(TR_REVIEW_LIVE_STEPS.length - 1, stepIdx + 1);
    currentThoughtDraft.value = TR_REVIEW_LIVE_STEPS.slice(0, stepIdx + 1).map((s, i) => ({
      ...s,
      icon: i < stepIdx ? '✅' : '⏳',
    }));
  }, 1800);
  try {
    if (!args.callSubmitApi) {
      throw new Error('缺少 TR 评审提交接口');
    }
    const res = await args.callSubmitApi();
    if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
      throw new DOMException('已取消', 'AbortError');
    }
    window.clearInterval(liveTimer);
    currentThoughtDraft.value = TR_REVIEW_LIVE_STEPS.map((s) => ({ ...s, icon: '✅' }));
    await appendTrReviewAssistantMessage(scenario, res, {
      onPush: args.onPush,
      onResubmit: args.onResubmit,
    });
    ElMessage.success('TR 评审分析完成，请查看 AI 对话框');
  } catch (e: unknown) {
    window.clearInterval(liveTimer);
    currentThoughtDraft.value = [];
    if (isTaskCancelledError(e) || !isAiSessionActionActive(action, generation)) {
      action.discard();
      throw e;
    }
    const err = e as { message?: string };
    ElMessage.error(err?.message || 'TR 评审提交失败');
    addSessionMessage('assistant', `TR 评审失败：${err?.message || '请稍后重试'}`, undefined, undefined, null);
  } finally {
    isThinking.value = false;
    aiLoading.value = false;
  }
    },
  });
}

type ProjectIntroRewriteSubmitResult = {
  projectDescription?: string | null;
};

type RunProjectIntroRewriteArgs = {
  projectId: number;
  projectName: string;
  conversationId: string;
  previousDescription: string;
  callSubmitApi: () => Promise<ProjectIntroRewriteSubmitResult>;
  onApply: (description: string) => void | Promise<void>;
  onRevert: (previousDescription: string) => void | Promise<void>;
};

const PROJECT_INTRO_REWRITE_LIVE_STEPS: ThoughtStep[] = [
  { key: 'collect', icon: '⏳', title: '汇总项目资料', text: '正在读取需求清单与文档空间归档资料…' },
  { key: 'generate', icon: '⏳', title: '生成项目简介', text: '大模型正在根据资料撰写项目简介…' },
];

async function runProjectIntroRewrite(args: RunProjectIntroRewriteArgs) {
  return runInAiTaskQueue({
    kind: 'project_intro_rewrite',
    label: '项目简介帮写',
    run: async () => {
      mainTab.value = 'new';
      expandRightPanel();
      const scenario = conversationScenario.value;
      const sid = effectiveSessionId();
      const userLine = `【项目简介帮写】${args.projectName || `项目 ${args.projectId}`}`;
      addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
      await nextTick();
      scrollAiToBottom();
      isThinking.value = true;
      aiLoading.value = true;
      currentThoughtDraft.value = [{ ...PROJECT_INTRO_REWRITE_LIVE_STEPS[0] }];
      let stepIdx = 0;
      const liveTimer = window.setInterval(() => {
        stepIdx = Math.min(PROJECT_INTRO_REWRITE_LIVE_STEPS.length - 1, stepIdx + 1);
        currentThoughtDraft.value = PROJECT_INTRO_REWRITE_LIVE_STEPS.slice(0, stepIdx + 1).map((s, i) => ({
          ...s,
          icon: i < stepIdx ? '✅' : '⏳',
        }));
      }, 1600);
      try {
        const res = await args.callSubmitApi();
        window.clearInterval(liveTimer);
        currentThoughtDraft.value = PROJECT_INTRO_REWRITE_LIVE_STEPS.map((s) => ({ ...s, icon: '✅' }));
        const description = String(res.projectDescription ?? '').trim();
        if (!description) {
          throw new Error('大模型未返回有效的项目简介');
        }
        await args.onApply(description);
        const structured: Record<string, unknown> = {
          kind: 'project_intro_rewrite',
          status: 'adopted',
          projectDescription: description,
          previousDescription: args.previousDescription,
        };
        const assistantMsg = addSessionMessage('assistant', description, undefined, undefined, structured);
        projectIntroRewriteContext.value = {
          messageId: assistantMsg.id,
          previousDescription: args.previousDescription,
          callbacks: {
            onApply: args.onApply,
            onRevert: args.onRevert,
          },
        };
        await nextTick();
        expandedThoughtIds.value.add(assistantMsg.id);
        startTypewriter(assistantMsg.id);
        await waitForTypewriter(assistantMsg.id);
        await persistConversation();
        ElMessage.success('项目简介已生成并保存');
      } catch (e: unknown) {
        window.clearInterval(liveTimer);
        currentThoughtDraft.value = [];
        const err = e as { message?: string };
        ElMessage.error(err?.message || '项目简介帮写失败');
        addSessionMessage(
          'assistant',
          `项目简介帮写失败：${err?.message || '请稍后重试'}`,
          undefined,
          undefined,
          null,
        );
      } finally {
        isThinking.value = false;
        aiLoading.value = false;
      }
    },
  });
}

type RunWbsGenerateArgs = {
  projectId: number;
  projectName: string;
  /** @deprecated 已改为异步任务轮询；保留兼容旧调用方 */
  callGenerateApi?: () => Promise<ProjectWbsSnapshot>;
  onSuccess?: (snapshot: ProjectWbsSnapshot) => void | Promise<void>;
  /** 停止后丢弃：若后端已落库则由调用方恢复/忽略 */
  onDiscard?: () => void | Promise<void>;
};

function wbsGenerateStructured(msg: ChatMessage): Record<string, unknown> | null {
  const sr = msg.structuredResult;
  if (!sr || typeof sr !== 'object') return null;
  const rec = sr as Record<string, unknown>;
  if (rec.kind !== 'wbs_generate') return null;
  return rec;
}

function wbsGenerateStatus(msg: ChatMessage): string | null {
  const sr = wbsGenerateStructured(msg);
  if (!sr) return null;
  return String(sr.status || '');
}

function wbsGenerateActionsVisible(msg: ChatMessage) {
  const s = wbsGenerateStatus(msg);
  return s === 'completed' || s === 'failed' || s === 'reverted';
}

function isWbsGenerateBusyForMessage(msg: ChatMessage) {
  const sr = wbsGenerateStructured(msg);
  const pid = Number(sr?.projectId);
  if (!(Number.isFinite(pid) && pid > 0)) return false;
  return isProjectWbsGenerateLocked(pid);
}

function wbsGenerateGeneratedAt(msg: ChatMessage): number {
  const sr = wbsGenerateStructured(msg);
  const raw = Number(sr?.generatedAt);
  if (Number.isFinite(raw) && raw > 0) return raw;
  const ctx = getWbsGenerateRollback(msg.id);
  return ctx?.generatedAt ?? 0;
}

function isWbsManuallyEditedAfterMessage(msg: ChatMessage): boolean {
  void wbsManualEditEpoch.value;
  const sr = wbsGenerateStructured(msg);
  const projectId = Number(sr?.projectId);
  if (!(Number.isFinite(projectId) && projectId > 0)) return false;
  return isProjectWbsManuallyEditedSince(projectId, wbsGenerateGeneratedAt(msg));
}

function canRollbackWbsFromMessage(msg: ChatMessage): boolean {
  if (wbsGenerateStatus(msg) !== 'completed') return false;
  if (isWbsManuallyEditedAfterMessage(msg)) return false;
  return !!getWbsGenerateRollback(msg.id);
}

function wbsRollbackDisabledTitle(msg: ChatMessage): string {
  if (wbsGenerateStatus(msg) !== 'completed') return '';
  if (isWbsManuallyEditedAfterMessage(msg)) {
    return '已手动编辑任务，无法回退本次生成结果';
  }
  if (!getWbsGenerateRollback(msg.id)) {
    return '回退上下文已失效（例如已刷新页面），请重新生成或手动调整任务';
  }
  return '回退到本次生成之前的状态';
}

async function restoreWbsAfterGenerate(projectId: number, previousContent: unknown | null) {
  await restoreProjectWbsSnapshot(projectId, previousContent);
  notifyProjectWbsTasksChanged(projectId);
}

async function rollbackWbsFromMessage(msg: ChatMessage) {
  const sr = wbsGenerateStructured(msg);
  const projectId = Number(sr?.projectId);
  if (!(Number.isFinite(projectId) && projectId > 0)) {
    ElMessage.warning('无法回退：缺少项目信息');
    return;
  }
  if (isWbsManuallyEditedAfterMessage(msg)) {
    ElMessage.warning('已手动编辑任务，无法回退本次生成结果');
    return;
  }
  const ctx = getWbsGenerateRollback(msg.id);
  if (!ctx) {
    ElMessage.warning('无法回退：操作上下文已失效（例如已刷新页面），请重新生成或手动调整任务。');
    return;
  }
  if (isProjectWbsGenerateLocked(projectId) || aiLoading.value) {
    ElMessage.warning('WBS 正在生成中，请稍后再试');
    return;
  }
  const ok = await confirmAiSessionRollback(`WBS 生成：${String(sr?.projectName || projectId)}`);
  if (!ok) return;
  try {
    await restoreWbsAfterGenerate(projectId, ctx.previousContent);
    const scenario = conversationScenario.value;
    const sid = effectiveSessionId();
    store.updateMessage(scenario, msg.id, {
      structuredResult: {
        ...(msg.structuredResult as object),
        kind: 'wbs_generate',
        status: 'reverted',
      },
    }, sid);
    clearWbsGenerateRollback(msg.id);
    if (activeSessionAction.value?.kind === 'wbs_generate' && !activeSessionAction.value.discarded) {
      activeSessionAction.value.discarded = true;
    }
    void persistConversation();
    ElMessage.success('已回退到本次生成之前的状态');
  } catch (e: unknown) {
    const err = e as { message?: string };
    ElMessage.error(err?.message || '回退失败');
  }
}

async function regenerateWbsFromMessage(msg: ChatMessage) {
  const sr = wbsGenerateStructured(msg);
  const projectId = Number(sr?.projectId);
  if (!(Number.isFinite(projectId) && projectId > 0)) {
    ElMessage.warning('无法重新生成：缺少项目信息');
    return;
  }
  if (isProjectWbsGenerateLocked(projectId) || aiLoading.value) {
    ElMessage.warning('WBS 正在生成中，请稍后再试');
    return;
  }
  const status = String(sr?.status || '');
  const manuallyEdited = isWbsManuallyEditedAfterMessage(msg);
  if (status === 'completed' || manuallyEdited) {
    try {
      await ElMessageBox.confirm(
        manuallyEdited
          ? '检测到您已手动编辑过任务计划。重新生成将覆盖当前 WBS 快照与任务计划，是否继续？'
          : '重新生成将覆盖当前 WBS 快照与任务计划，是否继续？',
        '重新生成确认',
        { type: 'warning', confirmButtonText: '继续重新生成', cancelButtonText: '取消' },
      );
    } catch {
      return;
    }
  }
  const projectName =
    String(sr?.projectName || '').trim() || `项目 ${projectId}`;
  try {
    await runWbsGenerate({ projectId, projectName });
  } catch {
    /* 失败提示已在 runWbsGenerate 内处理 */
  }
}

type RunWbsTaskReassignArgs = {
  projectId: number;
  projectName?: string;
  scope: LlmReassignScope;
  fromUserId?: number;
  changeReason?: string;
  addedMemberUserIds?: number[];
  removedMemberUserIds?: number[];
  changedPhaseCode?: string;
  previousPhaseOwnerUserId?: number;
  newPhaseOwnerUserId?: number;
  confirmFirst?: boolean;
  confirmText?: string;
  autoRun?: boolean;
};

function isWbsTaskReassignConfirm(msg: ChatMessage): boolean {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  return sr?.kind === 'wbs_task_reassign' && sr?.status === 'confirm';
}

async function promptOrRunWbsTaskReassign(args: RunWbsTaskReassignArgs) {
  const projectId = Number(args.projectId);
  if (!Number.isFinite(projectId) || projectId <= 0) {
    throw new Error('项目无效');
  }
  mainTab.value = 'new';
  expandRightPanel();
  // 新确认/新执行前丢弃上一次重分配会话操作，避免回退/重试仍挂在旧消息上
  if (activeSessionAction.value?.kind === 'wbs_task_reassign' && !activeSessionAction.value.discarded) {
    activeSessionAction.value.discarded = true;
  }
  if (args.confirmFirst && !args.autoRun) {
    const scenario = conversationScenario.value;
    const userLine = `【任务重分配】${args.projectName || `项目 ${projectId}`}`;
    addSessionMessage('user', userLine, undefined, undefined, undefined, undefined, scenario);
    addSessionMessage(
      'assistant',
      args.confirmText || '是否对未完成 WBS 任务做批量重分配？',
      undefined,
      undefined,
      {
        kind: 'wbs_task_reassign',
        status: 'confirm',
        projectId,
        projectName: args.projectName || '',
        scope: args.scope,
        fromUserId: args.fromUserId ?? null,
        changeReason: args.changeReason ?? null,
        addedMemberUserIds: args.addedMemberUserIds ?? [],
        removedMemberUserIds: args.removedMemberUserIds ?? [],
        changedPhaseCode: args.changedPhaseCode ?? null,
        previousPhaseOwnerUserId: args.previousPhaseOwnerUserId ?? null,
        newPhaseOwnerUserId: args.newPhaseOwnerUserId ?? null,
      },
      undefined,
      scenario,
    );
    await nextTick();
    scrollAiToBottom();
    return;
  }
  await runWbsTaskReassign(args);
}

async function acceptWbsTaskReassign(msg: ChatMessage) {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr || sr.kind !== 'wbs_task_reassign') return;
  if (sr.status !== 'confirm') return;
  updateSessionMessage(msg.id, {
    content: `${String(msg.content || '').trim()}\n\n已开始重分配…`.trim(),
    structuredResult: { ...sr, status: 'running' },
  });
  await runWbsTaskReassign({
    projectId: Number(sr.projectId),
    projectName: String(sr.projectName || ''),
    scope: String(sr.scope || 'FULL_OPEN_WBS') as LlmReassignScope,
    fromUserId: sr.fromUserId != null ? Number(sr.fromUserId) : undefined,
    changeReason: sr.changeReason != null ? String(sr.changeReason) : undefined,
    addedMemberUserIds: Array.isArray(sr.addedMemberUserIds)
      ? (sr.addedMemberUserIds as number[]).map(Number).filter((n) => n > 0)
      : undefined,
    removedMemberUserIds: Array.isArray(sr.removedMemberUserIds)
      ? (sr.removedMemberUserIds as number[]).map(Number).filter((n) => n > 0)
      : undefined,
    changedPhaseCode: sr.changedPhaseCode != null ? String(sr.changedPhaseCode) : undefined,
    previousPhaseOwnerUserId: sr.previousPhaseOwnerUserId != null
      ? Number(sr.previousPhaseOwnerUserId) : undefined,
    newPhaseOwnerUserId: sr.newPhaseOwnerUserId != null
      ? Number(sr.newPhaseOwnerUserId) : undefined,
    autoRun: true,
  });
}

function declineWbsTaskReassign(msg: ChatMessage) {
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  if (!sr) return;
  updateSessionMessage(msg.id, {
    content: `${msg.content || ''}\n\n已跳过本次任务批量重分配。`.trim(),
    structuredResult: { ...sr, status: 'declined' },
  });
}

async function runWbsTaskReassign(args: RunWbsTaskReassignArgs) {
  const projectId = Number(args.projectId);
  const shortName =
    (args.projectName || '').trim().length > 18
      ? `${(args.projectName || '').trim().slice(0, 18)}…`
      : (args.projectName || '').trim() || `项目 ${args.projectId}`;
  const { generation, action } = beginAiSessionAction({
    kind: 'wbs_task_reassign',
    label: `任务重分配：${shortName}`,
    discard: async () => {},
    retry: () => runWbsTaskReassign(args),
  });
  await runInAiTaskQueue({
    kind: 'wbs_task_reassign',
    label: `任务重分配：${shortName}`,
    sessionAction: action,
    run: async (boundSessionId, ctx) => {
      mainTab.value = 'new';
      expandRightPanel();
      const scenario = conversationScenario.value;
      const userLine = `【任务重分配】${args.projectName || `项目 ${args.projectId}`}（${args.scope}）`;
      addSessionMessage('user', userLine, undefined, undefined, undefined, boundSessionId, scenario);
      await nextTick();
      scrollAiToBottom();
      isThinking.value = true;
      aiLoading.value = true;
      currentThoughtDraft.value = [
        {
          key: 'reassign',
          icon: '⏳',
          title: '任务重分配',
          text: '正在根据成员与任务信息重分配处理人…',
        },
      ];
      try {
        const result = await reassignTasksByLlm(projectId, {
          scope: args.scope,
          fromUserId: args.fromUserId,
          useLlm: true,
          changeReason: args.changeReason,
          addedMemberUserIds: args.addedMemberUserIds,
          removedMemberUserIds: args.removedMemberUserIds,
          changedPhaseCode: args.changedPhaseCode,
          previousPhaseOwnerUserId: args.previousPhaseOwnerUserId,
          newPhaseOwnerUserId: args.newPhaseOwnerUserId,
        });
        if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
          throw new DOMException('已取消', 'AbortError');
        }
        currentThoughtDraft.value = [];
        const samples = (result.samples || []).slice(0, 5).join('\n');
        const content = [
          result.summary || '任务重分配已完成',
          samples ? `\n示例：\n${samples}` : '',
        ].join('');
        const resultMsg = addSessionMessage(
          'assistant',
          content,
          undefined,
          undefined,
          {
            kind: 'wbs_task_reassign',
            status: 'completed',
            ...result,
          },
          boundSessionId,
          scenario,
        );
        // 回退/重试必须挂在结果消息上（内容之后），不能落到确认气泡
        action.messageId = resultMsg.id;
        notifyProjectWbsTasksChanged(projectId);
      } catch (e: any) {
        if (isTaskCancelledError(e) || e?.name === 'AbortError') throw e;
        currentThoughtDraft.value = [];
        const failedMsg = addSessionMessage(
          'assistant',
          e?.message || '任务重分配失败',
          undefined,
          undefined,
          { kind: 'wbs_task_reassign', status: 'failed' },
          boundSessionId,
          scenario,
        );
        action.messageId = failedMsg.id;
        throw e;
      } finally {
        isThinking.value = false;
        aiLoading.value = false;
        currentThoughtDraft.value = [];
        scrollAiToBottom();
      }
    },
  });
}

async function runWbsGenerate(args: RunWbsGenerateArgs) {
  const projectId = Number(args.projectId);
  const shortName =
    (args.projectName || '').trim().length > 18
      ? `${(args.projectName || '').trim().slice(0, 18)}…`
      : (args.projectName || '').trim() || `项目 ${args.projectId}`;

  // REQ-04：不再因 needsConfirm 阻断生成；有模板且无有效工时规则时，生成后进入对话补充（R3）
  let hoursDialogueCtx: Awaited<ReturnType<typeof getProjectWbsGenerateContext>> | null = null;
  if (Number.isFinite(projectId) && projectId > 0) {
    try {
      hoursDialogueCtx = await getProjectWbsGenerateContext(projectId);
      if (
        shouldStartHoursDialogue({
          wbsTemplateRef: hoursDialogueCtx?.wbsTemplateRef,
          hoursPlan: hoursDialogueCtx?.hoursPlan,
          constraints: hoursDialogueCtx?.constraints as Record<string, string> | undefined,
        })
      ) {
        ElMessage.info('未读到有效工时规则，将先生成 WBS（Agent 默认工时），随后引导确认并补充规则');
      }
    } catch {
      /* 上下文拉取失败不阻断生成 */
    }
  }

  let previousContent: unknown | null = null;
  let previousExisted = false;
  if (Number.isFinite(projectId) && projectId > 0) {
    try {
      const before = await getProjectWbsSnapshot(projectId);
      previousExisted = !!before?.exists;
      previousContent = previousExisted ? (before.content ?? null) : null;
    } catch {
      previousExisted = false;
      previousContent = null;
    }
  }

  const executeOnce = async (): Promise<ProjectWbsSnapshot | undefined> => {
    const { generation, action } = beginAiSessionAction({
      kind: 'wbs_generate',
      label: `WBS 生成：${shortName}`,
      discard: async () => {
        if (Number.isFinite(projectId) && projectId > 0) {
          try {
            await restoreWbsAfterGenerate(projectId, previousContent);
          } catch {
            /* ignore restore errors on discard */
          }
        }
        await args.onDiscard?.();
      },
      retry: () => runWbsGenerate(args),
    });
    return runInAiTaskQueue({
      kind: 'wbs_generate',
      label: `WBS 生成：${shortName}`,
      sessionAction: action,
      lifecycle: {
        onStop: () => action.discard(),
        onRollback: () => action.discard(),
      },
      run: async (boundSessionId, ctx) => {
        const { setProjectWbsGenerateLock } = await import('@/utils/projectWbsGenerateLock');
        setProjectWbsGenerateLock(args.projectId, true);
        mainTab.value = 'new';
        expandRightPanel();
        const scenario = conversationScenario.value;
        const userLine = `【WBS 生成】${args.projectName || `项目 ${args.projectId}`}`;
        addSessionMessage('user', userLine, undefined, undefined, undefined, boundSessionId, scenario);
        await nextTick();
        scrollAiToBottom();
        isThinking.value = true;
        aiLoading.value = true;
        currentThoughtDraft.value = wbsGenerateThoughtInitial();
        try {
          const result = await pollProjectWbsGenerateTask(args.projectId, {
            signal: ctx?.signal,
            discardOnAbort: true,
            onProgress: (data) => {
              if (!isAiSessionActionActive(action, generation)) return;
              currentThoughtDraft.value = mapWbsGenerateProgressToThought(data.steps);
            },
          });
          if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
            throw new DOMException('已取消', 'AbortError');
          }
          currentThoughtDraft.value = wbsGenerateThoughtDone();
          const snapshot = result.snapshot;
          if (!snapshot?.exists) {
            throw new Error('WBS 生成未返回有效快照');
          }
          await args.onSuccess?.(snapshot);
          // 通知项目页侧栏刷新（即使调用方未处理 onSuccess）
          notifyProjectWbsTasksChanged(args.projectId);
          const summary = buildWbsGenerateSummary(
            args.projectName,
            snapshot,
            result.syncedTaskCount,
          );
          const generatedAt = Date.now();
          const structured: Record<string, unknown> = {
            kind: 'wbs_generate',
            status: 'completed',
            projectId: args.projectId,
            projectName: args.projectName,
            syncedTaskCount: result.syncedTaskCount ?? null,
            generatedAt,
            previousExisted,
          };
          const assistantMsg = addSessionMessage(
            'assistant',
            summary,
            undefined,
            thoughtStepsToJson(currentThoughtDraft.value),
            structured,
            boundSessionId,
            scenario,
          );
          rememberWbsGenerateRollback({
            messageId: assistantMsg.id,
            projectId: args.projectId,
            previousContent,
            previousExisted,
            generatedAt,
          });
          await nextTick();
          expandedThoughtIds.value.add(assistantMsg.id);
          currentThoughtDraft.value = [];
          isThinking.value = false;
          await revealAssistantMessage(assistantMsg.id, boundSessionId, scenario);
          await persistConversation(undefined, boundSessionId);
          ElMessage.success('WBS 已生成并同步到任务计划');
          // R3：已选模板且无有效工时规则 → 引导确认 WBS 再补规则
          if (
            hoursDialogueCtx &&
            shouldStartHoursDialogue({
              wbsTemplateRef: hoursDialogueCtx.wbsTemplateRef,
              hoursPlan: hoursDialogueCtx.hoursPlan,
              constraints: hoursDialogueCtx.constraints as Record<string, string> | undefined,
            })
          ) {
            const title = String(hoursDialogueCtx.wbsTemplateRef?.title || '').trim();
            startAwaitWbsConfirm(args.projectId, title || undefined);
            addSessionMessage(
              'assistant',
              wbsConfirmPrompt(title || undefined),
              undefined,
              undefined,
              {
                kind: 'wbs_hours_dialogue',
                phase: 'await_wbs_confirm',
                projectId: args.projectId,
              },
              boundSessionId,
              scenario,
            );
            await persistConversation(undefined, boundSessionId);
          }
          return snapshot;
        } catch (e: unknown) {
          currentThoughtDraft.value = [];
          if (isTaskCancelledError(e) || !isAiSessionActionActive(action, generation)) {
            await args.onDiscard?.();
            await persistConversation(undefined, boundSessionId);
            throw e;
          }
          const err = e as { message?: string };
          const msg = err?.message || 'WBS 生成失败';
          ElMessage.error(msg);
          addSessionMessage(
            'assistant',
            `WBS 生成失败：${msg}`,
            undefined,
            undefined,
            {
              kind: 'wbs_generate',
              status: 'failed',
              projectId: args.projectId,
              projectName: args.projectName,
            },
            boundSessionId,
            scenario,
          );
          await persistConversation(undefined, boundSessionId);
          throw e;
        } finally {
          setProjectWbsGenerateLock(args.projectId, false);
          isThinking.value = false;
          aiLoading.value = false;
          currentThoughtDraft.value = [];
        }
      },
    });
  };

  if (Number.isFinite(projectId) && projectId > 0) {
    const { runExclusiveProjectWbsGenerate } = await import('@/utils/projectWbsGenerateLock');
    return runExclusiveProjectWbsGenerate(projectId, executeOnce);
  }
  return executeOnce();
}

/** REQ-04 R3：确认 WBS → 补工时规则 → 带参回填 */
async function tryHandleWbsHoursDialogue(text: string): Promise<boolean> {
  if (projectId.value <= 0) return false;
  const state = loadDialogueState(projectId.value);
  if (!state || state.phase === 'idle' || state.phase === 'done') return false;

  if (state.phase === 'await_wbs_confirm') {
    if (!isWbsConfirmReply(text) && !isUseDefaultHoursReply(text)) return false;
    addSessionMessage('user', text);
    if (isUseDefaultHoursReply(text)) {
      clearDialogueState(projectId.value);
      addSessionMessage('assistant', '好的，已保留本次 Agent 默认工时。你可在 WBS / 任务计划中手动调整工时。');
      await persistConversation();
      return true;
    }
    state.phase = 'await_hours_rules';
    saveDialogueState(state);
    addSessionMessage('assistant', hoursRulesPrompt());
    await persistConversation();
    return true;
  }

  if (state.phase === 'await_hours_rules') {
    addSessionMessage('user', text);
    if (isUseDefaultHoursReply(text)) {
      clearDialogueState(projectId.value);
      addSessionMessage('assistant', '好的，已保留本次 Agent 默认工时。你可在 WBS / 任务计划中手动调整工时。');
      await persistConversation();
      return true;
    }
    const rows = parseHoursConfigRowsFromText(text);
    state.pendingRulesText = text;
    state.phase = 'await_hours_confirm';
    saveDialogueState(state);
    const summary = rows.length
      ? summarizeHoursRows(rows)
      : `（文字说明）\n${text.trim().slice(0, 800)}`;
    addSessionMessage('assistant', hoursConfirmPrompt(summary));
    await persistConversation();
    return true;
  }

  if (state.phase === 'await_hours_confirm') {
    if (!isHoursConfirmReply(text) && !isUseDefaultHoursReply(text)) {
      // 视为继续修改规则
      addSessionMessage('user', text);
      const rows = parseHoursConfigRowsFromText(text);
      state.pendingRulesText = text;
      saveDialogueState(state);
      const summary = rows.length
        ? summarizeHoursRows(rows)
        : `（文字说明）\n${text.trim().slice(0, 800)}`;
      addSessionMessage('assistant', hoursConfirmPrompt(summary));
      await persistConversation();
      return true;
    }
    addSessionMessage('user', text);
    if (isUseDefaultHoursReply(text)) {
      clearDialogueState(projectId.value);
      addSessionMessage('assistant', '好的，已保留本次 Agent 默认工时。');
      await persistConversation();
      return true;
    }
    try {
      const rows = parseHoursConfigRowsFromText(state.pendingRulesText || '');
      clearDialogueState(projectId.value);
      if (!rows.length) {
        addSessionMessage(
          'assistant',
          '已记录你的说明。当前未能解析出结构化「任务,基线天数」行，将保留 Agent 默认工时；你可在 WBS / 任务计划中手动调整，或按「任务名,天数」格式重新发起补充。',
        );
        await persistConversation();
        return true;
      }
      const ctx = await getProjectWbsGenerateContext(projectId.value);
      await saveProjectWbsGenerateContext(projectId.value, {
        ...ctx,
        hoursConfigRows: rows,
        hoursPlan: undefined,
        wbsTemplateRef: ctx.wbsTemplateRef,
      });
      addSessionMessage('assistant', '工时规则已确认，正在按规则请求 Agent 回填任务工时…');
      await persistConversation();
      await runWbsGenerate({
        projectId: projectId.value,
        projectName: dashboard.value?.project?.name || `项目 ${projectId.value}`,
      });
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || '回填失败';
      addSessionMessage('assistant', `工时回填失败：${msg}。你仍可手动调整任务工时，或稍后重试。`);
      await persistConversation();
    }
    return true;
  }

  return false;
}

type RunDrawingReviewArgs = {
  drawingLabel: string;
  userMessage?: string;
  userAttachments?: ChatMessageAttachment[];
  documentType?: string;
  options?: string;
  submitAndPoll: (onProgress: (data: DesignReviewResultData) => void, signal?: AbortSignal) => Promise<DesignReviewResultData>;
  /** 用户点击 AI 对话内「查看审核结果」时打开报告 */
  onOpenReport?: () => void | Promise<void>;
  /** 页面切换后仍可跳转打开报告（项目 BOM 图纸审核） */
  reportNav?: import('@/utils/bomDrawingReviewNavigate').BomDrawingReviewReportNav;
};

type PromptBomDrawingReviewExistingArgs = {
  drawingLabel: string;
  reviewedAt: string;
  reviewerName: string;
  onViewReport: () => void | Promise<void>;
  onRerun: () => void | Promise<void>;
  reportNav?: import('@/utils/bomDrawingReviewNavigate').BomDrawingReviewReportNav;
};

async function promptBomDrawingReviewExisting(args: PromptBomDrawingReviewExistingArgs) {
  return runInAiTaskQueue({
    kind: 'drawing_review',
    label: args.drawingLabel || '图纸审核',
    run: async () => {
  mainTab.value = 'new';
  expandRightPanel();
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  const userLine = `【图纸智审】${args.drawingLabel || '图纸'}`;
  addSessionMessage('user', userLine, undefined, undefined, undefined, sid, scenario);
  await nextTick();
  scrollAiToBottom();
  const timeLabel = formatBomDrawingReviewTime(args.reviewedAt);
  const name = (args.reviewerName || '').trim() || '未知';
  const dialogText = `该图纸已于 ${timeLabel} 由 ${name} 审核。`;
  const structured: Record<string, unknown> = {
    kind: 'bom_drawing_review_existing',
    status: 'pending',
    reviewedAt: args.reviewedAt,
    reviewerName: name,
    ...(args.reportNav ? { reportNav: args.reportNav } : {}),
  };
  const assistantMsg = addSessionMessage('assistant', dialogText, undefined, undefined, structured);
  bomDrawingReviewExistingContext.value = {
    messageId: assistantMsg.id,
    callbacks: { onViewReport: args.onViewReport, onRerun: args.onRerun },
  };
  await nextTick();
  expandedThoughtIds.value.add(assistantMsg.id);
  startTypewriter(assistantMsg.id);
  await waitForTypewriter(assistantMsg.id);
  await persistConversation();
    },
  });
}

async function runDrawingReview(args: RunDrawingReviewArgs) {
  const { generation, action } = beginAiSessionAction({
    kind: 'drawing_review',
    label: args.drawingLabel || '图纸智审',
    discard: () => {
      drawingReviewContext.value = null;
    },
    retry: () => runDrawingReview(args),
  });
  return runInAiTaskQueue({
    kind: 'drawing_review',
    label: args.drawingLabel || '图纸智审',
    sessionAction: action,
    lifecycle: {
      onStop: () => action.discard(),
      onRollback: () => action.discard(),
    },
    run: async (_boundSessionId, ctx) => {
  const originTabKey = currentPageTabKey();
  mainTab.value = 'new';
  expandRightPanel();
  const scenario = effectiveScenario();
  const sid = effectiveSessionId();
  const userLine = args.userMessage?.trim() || `【图纸智审】${args.drawingLabel || '图纸'}`;
  addSessionMessage(
    'user',
    userLine,
    args.userAttachments?.length ? args.userAttachments : undefined,
    undefined,
    undefined,
    sid,
    scenario,
  );
  await nextTick();
  scrollAiToBottom();
  isThinking.value = true;
  aiLoading.value = true;
  currentThoughtDraft.value = [
    { key: 'parse', icon: '⏳', title: '2D 图纸解析', text: '正在从图纸提取标注、表格与尺寸…' },
  ];
  try {
    const result = await args.submitAndPoll((data) => {
      if (!isAiSessionActionActive(action, generation)) return;
      const steps = thoughtStepsFromReviewPoll(data);
      if (steps.length) {
        currentThoughtDraft.value = steps.map((s) => ({
          key: String(s.key ?? ''),
          icon: String(s.icon ?? '⏳'),
          title: String(s.title ?? ''),
          text: String(s.text ?? data.message ?? ''),
        }));
      } else if (data.message) {
        const last = currentThoughtDraft.value[currentThoughtDraft.value.length - 1];
        if (last) last.text = data.message;
      }
    }, ctx?.signal);
    if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
      throw new DOMException('已取消', 'AbortError');
    }
    const dialogText =
      (result.user_reply || result.userReply || '').trim() ||
      (result.summary || '').trim() ||
      '图纸智审已完成。';
    const issueCount = result.issues?.length ?? 0;
    const structured: Record<string, unknown> = {
      kind: 'drawing_review',
      status: 'completed',
      ...(result.structured_result || result.structuredResult || {}),
      summary: result.summary,
      score: result.score,
      issueCount,
      issues: result.issues,
      ...(args.reportNav ? { reportNav: args.reportNav } : {}),
    };
    const thoughtPayload = result.thought?.steps?.length
      ? result.thought
      : { steps: currentThoughtDraft.value };
    const thought = JSON.stringify(thoughtPayload);
    const assistantMsg = addSessionMessage(
      'assistant',
      dialogText,
      undefined,
      thought,
      structured,
      sid,
      scenario,
    );
    await nextTick();
    expandedThoughtIds.value.add(assistantMsg.id);
    await revealAssistantMessage(assistantMsg.id, sid, scenario);
    await persistConversation(undefined, sid, scenario);
    ElMessage.success(issueCount > 0 ? `智审完成，共 ${issueCount} 条问题` : '智审完成，未发现明显问题');
    if (args.onOpenReport) {
      drawingReviewContext.value = {
        messageId: assistantMsg.id,
        onViewReport: args.onOpenReport,
      };
    }
    void notifyPageTabCompletionOrBell({
      tabKey: originTabKey,
      title: '图纸智审已完成',
      content: '点击查看评审结果',
      actionUrl: projectId.value > 0 ? `/project/${projectId.value}/dashboard` : actionUrlForTabKey(originTabKey),
      mergeKey: projectId.value > 0 ? `AI_DRAWING_REVIEW:${projectId.value}` : undefined,
      type: 'AI_TASK_COMPLETED',
    });
  } catch (e: unknown) {
    currentThoughtDraft.value = [];
    if (isTaskCancelledError(e) || !isAiSessionActionActive(action, generation)) {
      action.discard();
      throw e;
    }
    const err = e as { message?: string };
    const msg = err?.message || '图纸智审失败';
    ElMessage.error(msg);
    addSessionMessage(
      'assistant',
      `图纸智审失败：${msg}`,
      undefined,
      undefined,
      null,
      sid,
      scenario,
    );
  } finally {
    currentThoughtDraft.value = [];
    isThinking.value = false;
    aiLoading.value = false;
  }
    },
  });
}

function onPageContentRefChipClick(pending: PageContentEditPending) {
  const ok = highlightPageContentQuote(pending.quoted, pending.fieldPath, {
    paragraphIndex: pending.paragraphIndex,
    paragraphLabel: pending.paragraphLabel,
    cellKey: pending.cellKey,
  });
  if (!ok) ElMessage.info('未在页面中定位到引用原文，可能内容已变更');
}

function onPageContentUserRefChipClick(msg: ChatMessage) {
  const ref = getAiPageContentUserRefFromMessage(msg);
  if (!ref) return;
  const locateOpts = resolveAiPageContentLocateOptions(
    ref,
    getSessionMessages(currentSessionId.value, conversationScenario.value),
    msg.id,
  );
  const located = aiPageContentApplyStore.locateFieldQuote(ref.quoted, ref.fieldPath || '', locateOpts);
  const ok = located || highlightPageContentQuote(ref.quoted, ref.fieldPath, locateOpts);
  if (pcRefDetailOpenForMessageId.value === msg.id) {
    pcRefDetailOpenForMessageId.value = null;
  } else {
    pcRefDetailOpenForMessageId.value = msg.id;
  }
  if (!ok && pcRefDetailOpenForMessageId.value === msg.id) {
    ElMessage.info('未在页面中定位到引用原文，已展开查看完整内容');
  }
}

function togglePcRefDetail(messageId: string) {
  pcRefDetailOpenForMessageId.value = pcRefDetailOpenForMessageId.value === messageId ? null : messageId;
}

const isStreaming = ref(false);
const isStreamPaused = ref(false);
const streamStopped = ref(false);
const streamAbortController = ref<AbortController | null>(null);
const isThinking = ref(false);
const currentAssistantId = ref<string | null>(null);
const currentAssistantTarget = ref<HTMLElement | null>(null);
const collapsedThoughtIds = ref(new Set<string>());
const expandedThoughtIds = ref(new Set<string>());
const typingMessageIds = ref(new Set<string>());
const typingTimers = new Map<string, number>();
const typingDoneResolvers = new Map<string, () => void>();
const typingContentMap = ref<Record<string, string>>({});
const pendingProjectMessage = ref<{ text: string; attachments: ChatMessageAttachment[] } | null>(null);
const projectSelectionVisible = ref(false);
const projectSelectionLoading = ref(false);
const projectSelectionList = ref<ProjectAccessOption[]>([]);
const selectedProjectId = ref<number | null>(null);
const projectPromptMessage = ref('检测到该问题需要项目上下文，请先选择一个项目后再继续。');
const currentThoughtDraft = ref<ThoughtStep[]>([]);
const liveThoughtSteps = computed(() => currentThoughtDraft.value);

const panelLastAssistantId = computed(() => {
  const list = aiMessages.value;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i]?.role === 'assistant') return list[i].id;
  }
  return '';
});

function shouldShowPanelInlineThought(msg: ChatMessage): boolean {
  return shouldShowInlineThought(msg, {
    visible: isTaskThinkingVisible.value,
    stepCount: liveThoughtSteps.value.length,
    ownerMessageId: panelLastAssistantId.value,
  });
}
const currentSessionTitle = computed(() => {
  const resolved = resolveSessionTitle(currentSessionId.value);
  if (resolved) return resolved;
  if (currentRouteName.value === 'ProjectCreate') {
    return '项目创建';
  }
  if (hasProjectContext.value && projectId.value > 0) {
    const nm = shellProjectName.value.trim();
    if (nm) return `${nm}会话`;
    return `项目 ${projectId.value} 会话`;
  }
  if (conversationScenario.value === CALCULATOR_FORMULA_IMPORT_SCENARIO) {
    return importSessionTitle.value || '公式创建';
  }
  if (isCalculatorCalcScenario(conversationScenario.value)) {
    const meta =
      calcSessionMeta.value || loadCalcSessionMeta(conversationScenario.value);
    if (meta?.formulaName) return calcSessionTitle(meta.formulaName);
  }
  return '通用会话';
});

const calcSessionMeta = ref<CalcFormulaSessionMeta | null>(null);
const importSessionTitle = ref('');

/** 图片预览弹窗：当前预览的图片 URL，非空时显示弹窗 */
const previewImageUrl = ref<string | null>(null);

function attachmentFullUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('blob:')) return url;
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return url.startsWith('/') ? base + url : base + '/' + url.replace(/^\//, '');
}

function openImagePreview(url: string) {
  previewImageUrl.value = url;
}

function isDrawingAttachmentName(name: string): boolean {
  return /\.(pdf|dwg|dxf)$/i.test(String(name ?? '').trim());
}

function findDrawingAttachment(attachments: ChatMessageAttachment[]): ChatMessageAttachment | null {
  for (const att of attachments) {
    const name = att.name || issueAttachmentFileName(att.url);
    if (isDrawingAttachmentName(name)) return att;
  }
  return null;
}

function shouldRouteAttachmentToDrawingReview(text: string, attachments: ChatMessageAttachment[]): boolean {
  if (!findDrawingAttachment(attachments)) return false;
  const t = text.trim();
  if (!t || t === '(附带附件)' || t === '(请根据附件内容回复)') return true;
  return /图纸|drawing|cad|dwg|dxf|智审|评审|审核|图样|图号/i.test(t)
    && /分析|审查|评审|审核|问题|检查|看看|帮我|指出|发现|评估|诊断/i.test(t);
}

async function onChatAttachmentClick(att: ChatMessageAttachment) {
  const url = String(att.url ?? '').trim();
  if (!url) return;
  if (att.type === 'image' || isImageAttachmentUrl(url)) {
    openImagePreview(attachmentFullUrl(url));
    return;
  }
  const fileName = att.name || issueAttachmentFileName(url);
  if (isIssueDocumentPreviewable(url)) {
    const objectKey = extractObjectKeyFromFileAccessUrl(url);
    if (objectKey && projectId.value > 0) {
      requestOpenProjectInnerTab(
        {
          kind: 'document',
          projectId: projectId.value,
          objectKey,
          fileName,
        },
        { router, navigateIfNeeded: true },
      );
      return;
    }
    window.open(attachmentFullUrl(url), '_blank', 'noopener');
    return;
  }
  try {
    await ElMessageBox.confirm(
      `当前附件类型不支持在线预览，是否下载「${fileName}」？`,
      '下载附件',
      { type: 'info', confirmButtonText: '下载', cancelButtonText: '取消' },
    );
    await downloadAccessUrlFile(
      issueAttachmentFullUrl(url),
      sanitizeIssueDownloadFileName(fileName),
    );
  } catch {
    /* 用户取消 */
  }
}

function addQuoteToComposer(text: string) {
  const trimmed = text.trim();
  if (!trimmed) return;
  aiInput.value = aiInput.value ? `${aiInput.value}\n${trimmed}` : trimmed;
  void nextTick(() => focusComposer());
}

function closeImagePreview() {
  previewImageUrl.value = null;
}

function truncatePcRefSnippet(text: string, maxLen = 56): string {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
}

function getMessageCopyText(msg: ChatMessage): string {
  const pageRef = getAiPageContentUserRefFromMessage(msg);
  if (pageRef) {
    const tail = pageRef.requirement.trim() || '（按上下文润色）';
    return `[${pageRef.scopeLabel || '页面'} · ${pageRef.fieldLabel}] ${pageRef.quoted}\n\n${tail}`;
  }
  const ref = getProjectCreateUserRefFromMessage(msg);
  if (ref) {
    const tail = ref.requirement.trim() || '（按上下文润色）';
    return `[创建项目 · ${ref.fieldLabel}] ${ref.quoted}\n\n${tail}`;
  }
  const s = msg?.content?.trim();
  return s ?? '';
}

function clearPageContentFieldRef() {
  pageContentEditPending.value = null;
  clearPageContentHighlights();
}

function clearProjectCreateFieldRef() {
  projectCreateFieldEditPending.value = null;
}

async function copyMessageContent(text: string) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success('已复制到剪贴板');
  } catch {
    ElMessage.error('复制失败');
  }
}

function waitForTypewriter(messageId: string): Promise<void> {
  if (!typingMessageIds.value.has(messageId)) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    typingDoneResolvers.set(messageId, resolve);
  });
}

function isViewingActiveSession(sessionId: string, scenario: string): boolean {
  return (
    sessionId === currentSessionId.value
    && scenario === conversationScenario.value
    && mainTab.value === 'new'
    && !isCollapsed.value
    && document.visibilityState !== 'hidden'
  );
}

function clearCompletedTypingPreviews(sessionId = currentSessionId.value, scenario = conversationScenario.value) {
  for (const m of getSessionMessages(sessionId, scenario)) {
    if (m.role === 'assistant' && resolveAssistantMessageDisplayContent(m).trim()) {
      clearTypewriter(m.id);
    }
  }
}

async function revealAssistantMessage(messageId: string, sessionId: string, scenario: string) {
  if (isViewingActiveSession(sessionId, scenario)) {
    startTypewriter(messageId);
    await waitForTypewriter(messageId);
  } else {
    clearTypewriter(messageId);
  }
}

function startTypewriter(messageId: string) {
  typingMessageIds.value = new Set([...typingMessageIds.value, messageId]);
  const list = getSessionMessages();
  const msg = [...list].reverse().find((m) => m.id === messageId);
  const full = resolveAssistantMessageDisplayContent(msg ?? { id: messageId, role: 'assistant', content: '', createdAt: 0 });
  if (!msg) {
    typingMessageIds.value = new Set([...typingMessageIds.value].filter((id) => id !== messageId));
    const resolve = typingDoneResolvers.get(messageId);
    if (resolve) resolve();
    typingDoneResolvers.delete(messageId);
    return;
  }
  // 仅更新展示层 typingContentMap，不修改 store 中的完整正文，避免持久化时写入截断内容
  typingContentMap.value = { ...typingContentMap.value, [messageId]: '' };
  store.setAssistantTypingPreview(messageId, '');
  const chars = Array.from(full);
  let idx = 0;
  const step = () => {
    const next = Math.min(chars.length, idx + (chars.length <= 40 ? 1 : Math.max(1, Math.ceil(chars.length / 90))));
    const partial = chars.slice(0, next).join('');
    typingContentMap.value = { ...typingContentMap.value, [messageId]: partial };
    store.setAssistantTypingPreview(messageId, partial);
    idx = next;
    if (idx < chars.length) {
      const timer = window.setTimeout(step, chars.length <= 40 ? 120 : 24);
      typingTimers.set(messageId, timer);
    } else {
      typingMessageIds.value = new Set([...typingMessageIds.value].filter((id) => id !== messageId));
      typingTimers.delete(messageId);
      const nextMap = { ...typingContentMap.value };
      delete nextMap[messageId];
      typingContentMap.value = nextMap;
      store.clearAssistantTypingPreview(messageId);
      const resolve = typingDoneResolvers.get(messageId);
      if (resolve) resolve();
      typingDoneResolvers.delete(messageId);
    }
  };
  step();
}

function typewriterText(messageId: string): string {
  const list = getSessionMessages();
  const msg = [...list].reverse().find((m) => m.id === messageId);
  return msg?.content ?? '';
}

function clearTypewriter(messageId: string) {
  const timer = typingTimers.get(messageId);
  if (timer) window.clearTimeout(timer);
  typingTimers.delete(messageId);
  typingMessageIds.value = new Set([...typingMessageIds.value].filter((id) => id !== messageId));
  const next = { ...typingContentMap.value };
  delete next[messageId];
  typingContentMap.value = next;
  store.clearAssistantTypingPreview(messageId);
  const resolve = typingDoneResolvers.get(messageId);
  if (resolve) resolve();
  typingDoneResolvers.delete(messageId);
}

function removeUserMessage(messageId: string) {
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  const list = store.getMessages(scenario, sid);
  const idx = list.findIndex((m) => m.id === messageId);
  if (idx === -1 || list[idx].role !== 'user') return;
  const next = [...list];
  next.splice(idx, 1);
  if (idx < next.length && next[idx].role === 'assistant') {
    clearTypewriter(next[idx].id);
    next.splice(idx, 1);
  }
  store.setMessages(scenario, next, sid);
  void persistConversation().catch(() => {});
}

function renderReplyText(text: string): string {
  const safe = String(text ?? '');
  const projectName = projectSelectionList.value.find((p) => p.id === selectedProjectId.value)?.name;
  if (projectName && safe.includes(projectName)) {
    return safe.replace(
      projectName,
      `[项目: ${projectName}]`,
    );
  }
  return safe;
}

function escapeHtml(text: string) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function parseQuotedBlock(block: string) {
  const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
  const header = lines[0] || '';
  const body = lines.slice(1).join('\n').trim();
  const fileMatch = header.match(/^引用自\s+(.+?)(?:[｜|]|[:：])(.+)?$/);
  const source = fileMatch?.[1]?.trim() || '';
  const label = fileMatch?.[2]?.trim() || '';
  return {
    header,
    body: body || lines.slice(1).join('\n').trim() || header.replace(/^引用自\s+/, ''),
    fileLabel: source,
    summary: label ? `原文位置：${label} · 高亮摘要：${body || ''}`.trim() : (body || '').trim(),
  };
}

function renderQuotePillHtml(block: string) {
  const data = parseQuotedBlock(block);
  const fieldPath =
    data.fileLabel === '需求清单' ? 'requirements'
    : data.fileLabel === 'BOM单' ? 'bom'
    : data.fileLabel === '通用部件列表' ? 'component-library'
    : data.fileLabel === '项目信息' ? 'project-info'
    : data.fileLabel === 'AI回复' ? 'ai-reply'
    : (currentRouteName.value === 'ProjectDocumentViewer' || isProjectDocumentInnerTabActive())
      ? 'document-body'
    : data.fileLabel || '';
  return `<button type="button" class="quote-pill quote-pill--link" data-quote-pill="1" data-quote-locate="1" data-quote-file="${escapeHtml(data.fileLabel)}" data-quote-label="${escapeHtml(data.header)}" data-quote-text="${escapeHtml(data.body)}" data-quote-field="${escapeHtml(fieldPath)}">` +
    `<span class="quote-pill__icon">引用</span>` +
    `<span class="quote-pill__meta">` +
    `<span class="quote-pill__file">${escapeHtml(data.fileLabel || '引用')}</span>` +
    `<span class="quote-pill__summary">${escapeHtml(data.summary || data.body).replace(/\n/g, '<br />')}</span>` +
    `</span>` +
  `</button>`;
}

function renderUserChatMessage(text: string, atTokens?: ChatMessageAtToken[]) {
  const safe = renderReplyText(stripCreateProjectGapNotice(text));
  const re = /(引用自\s+[^\n]+\n[\s\S]*?)(?=\n\n|$)/g;
  let html = '';
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(safe)) !== null) {
    if (m.index > last) {
      html += renderUserTextWithSmartDocMentions(safe.slice(last, m.index), atTokens);
    }
    html += renderQuotePillHtml(m[1]);
    last = m.index + m[1].length;
  }
  if (last < safe.length) {
    html += renderUserTextWithSmartDocMentions(safe.slice(last), atTokens);
  }
  return html || renderUserTextWithSmartDocMentions(safe, atTokens);
}

function findSmartDocAtTokenForLabel(
  label: string,
  atTokens?: ChatMessageAtToken[],
): ChatMessageAtToken | undefined {
  if (!label) return undefined;
  const list = atTokens || [];
  const exact = list.find((t) => t.label === label);
  if (exact) return exact;
  // 截断展示名：用 name 前缀或 label 模糊匹配
  return list.find((t) => {
    if (t.label && (label.includes(t.label) || t.label.includes(label))) return true;
    const name = String(t.name || '').trim();
    if (!name) return false;
    const short = name.split(/…|\.\.\./)[0];
    return short && label.includes(short);
  });
}

function renderSmartDocMentionSpan(
  label: string,
  atTokens?: ChatMessageAtToken[],
): string {
  const token = findSmartDocAtTokenForLabel(label, atTokens);
  const detail = formatSmartDocMentionDetail(token, label);
  const fullName = token?.name ? String(token.name).replace(/｜/g, ' / ') : '';
  // 纯行内 span：避免 button/inline-block 占满一行，把后文挤到下一行
  return (
    `<span class="ai-smart-doc-mention ai-smart-doc-mention--chat"`
    + ` data-smart-doc-mention="1"`
    + ` data-mention-detail="${escapeHtml(detail)}"`
    + ` data-mention-fullname="${escapeHtml(fullName)}"`
    + ` title="${escapeHtml(detail)}">`
    + `${escapeHtml(label)}`
    + `</span>`
  );
}

/** 将用户消息中的 @引用 染成浅蓝行内样式（与输入框一致，避免 markdown <p> 换行） */
function renderUserTextWithSmartDocMentions(text: string, atTokens?: ChatMessageAtToken[]) {
  if (!text) return '';
  // 会话气泡内按行内混排：折叠换行，避免引用后正文被挤到下一行
  const normalized = String(text).replace(/[ \t]*\n+[ \t]*/g, ' ');
  const ranges: Array<{ start: number; end: number; text: string }> = [];
  const overlaps = (start: number, end: number) =>
    ranges.some((r) => !(end <= r.start || start >= r.end));
  const pushUnique = (start: number, end: number, label: string) => {
    if (start < 0 || end <= start || overlaps(start, end)) return;
    ranges.push({ start, end, text: label });
  };
  const labels = [...(atTokens || []).map((t) => t.label).filter(Boolean) as string[]]
    .sort((a, b) => b.length - a.length);
  for (const label of labels) {
    let from = 0;
    while (from < normalized.length) {
      const idx = normalized.indexOf(label, from);
      if (idx < 0) break;
      pushUnique(idx, idx + label.length, label);
      from = idx + label.length;
    }
  }
  const re = new RegExp(SMART_DOC_INLINE_MENTION_RE.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = re.exec(normalized))) {
    pushUnique(match.index, match.index + match[0].length, match[0]);
  }
  ranges.sort((a, b) => a.start - b.start);
  if (!ranges.length) return renderMarkdown(text);

  const renderPlainInline = (chunk: string) => escapeHtml(chunk);

  let html = '';
  let last = 0;
  for (const range of ranges) {
    if (range.start > last) {
      html += renderPlainInline(normalized.slice(last, range.start));
    }
    html += renderSmartDocMentionSpan(range.text, atTokens);
    last = range.end;
  }
  if (last < normalized.length) {
    html += renderPlainInline(normalized.slice(last));
  }
  return html;
}

function renderChatMessage(text: string) {
  const safe = renderReplyText(stripCreateProjectGapNotice(text));
  const re = /(引用自\s+[^\n]+\n[\s\S]*?)(?=\n\n|$)/g;
  let html = '';
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(safe)) !== null) {
    if (m.index > last) {
      html += renderMarkdown(safe.slice(last, m.index));
    }
    html += renderQuotePillHtml(m[1]);
    last = m.index + m[1].length;
  }
  if (last < safe.length) {
    html += renderMarkdown(safe.slice(last));
  }
  return html || renderMarkdown(safe);
}

function buildQuoteText(payload: { text: string; documentId?: string; documentName?: string; offset?: number }) {
  const fileLabel = payload.documentName || '文档';
  const relativePath = payload.documentId || '';
  const snippet = payload.text.trim();
  const head = relativePath ? `引用自 ${fileLabel}（${relativePath}）` : `引用自 ${fileLabel}`;
  return `${head}\n${snippet}`;
}

function appendQuoteToComposer(text: string) {
  const trimmed = text.trim();
  if (!trimmed) return;
  const next = aiInput.value.trim();
  aiInput.value = next ? `${next}\n\n${trimmed}` : trimmed;
  void nextTick(() => focusComposer());
}

function setComposerDraftMessage(text: string) {
  aiInput.value = text;
  void nextTick(() => focusComposer());
}

function setConversationTab(tab: 'new' | 'history') {
  mainTab.value = tab;
  if (tab === 'new') {
    sessionNotify.markRead(persistenceScenario.value, currentSessionId.value);
  }
}

const compactHistoryOpen = ref(false);
const compactHistoryWrapRef = ref<HTMLElement | null>(null);
const compactHistoryPopoverRef = ref<HTMLElement | null>(null);
const compactHistoryPopoverStyle = ref<Record<string, string>>({});

function updateCompactHistoryPopoverPosition() {
  const wrap = compactHistoryWrapRef.value;
  if (!wrap) return;
  const rect = wrap.getBoundingClientRect();
  const width = 280;
  const margin = 8;
  let left = rect.right - width;
  left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
  compactHistoryPopoverStyle.value = {
    position: 'fixed',
    top: `${Math.round(rect.bottom + 6)}px`,
    left: `${Math.round(left)}px`,
    width: `${width}px`,
    zIndex: '3200',
  };
}

function closeCompactHistoryPopover() {
  compactHistoryOpen.value = false;
}

function toggleCompactHistoryPopover() {
  if (compactHistoryOpen.value) {
    closeCompactHistoryPopover();
    return;
  }
  if (mainTab.value === 'history') {
    setConversationTab('new');
  }
  compactHistoryOpen.value = true;
  void nextTick(() => updateCompactHistoryPopoverPosition());
}

async function onCompactHistorySelect(item: ConversationSummary) {
  closeCompactHistoryPopover();
  setConversationTab('new');
  await loadHistoryItem(item, { userInitiated: true });
}

function onDocPointerDownForCompactHistory(e: PointerEvent) {
  if (!compactHistoryOpen.value) return;
  const target = e.target as Node | null;
  if (!target) return;
  if (compactHistoryWrapRef.value?.contains(target)) return;
  if (compactHistoryPopoverRef.value?.contains(target)) return;
  closeCompactHistoryPopover();
}

function isHistorySessionRunning(sessionId: string): boolean {
  return taskQueue.sessionHasQueuedOrRunning(persistenceScenario.value, sessionId);
}

function isHistorySessionUnread(item: ConversationSummary): boolean {
  return sessionNotify.isUnread(persistenceScenario.value, item.sessionId);
}

const historyHasUnread = computed(() => sessionNotify.hasUnreadInScenario(persistenceScenario.value));

function markTaskSessionUnreadIfNeeded(boundSessionId: string, scopeKey: string) {
  const viewingSameSession =
    boundSessionId === currentSessionId.value
    && scopeKey === persistenceScenario.value
    && mainTab.value === 'new';
  if (viewingSameSession) {
    sessionNotify.markRead(scopeKey, boundSessionId);
    return;
  }
  sessionNotify.markUnread(scopeKey, boundSessionId);
}

async function archiveCurrentSessionIfNeeded(
  sessionId = currentSessionId.value,
  scenario = conversationScenario.value,
) {
  const sid = String(sessionId || '').trim();
  const sc = String(scenario || '').trim();
  if (!sid || !sc) return;
  const msgs = getSessionMessages(sid, sc);
  if (msgs.length) {
    await persistConversation(undefined, sid, sc);
  }
}

async function activateSessionForCurrentPageContext(expectedGeneration = pageContextSwitchGen) {
  const expectedContext = capturePageContextSnapshot(expectedGeneration);
  visibleMessageCount.value = VISIBLE_MESSAGE_INITIAL;
  if (isCalculatorCalcScenario(conversationScenario.value)) {
    calcSessionMeta.value = loadCalcSessionMeta(conversationScenario.value);
  } else if (conversationScenario.value !== CALCULATOR_FORMULA_IMPORT_SCENARIO) {
    calcSessionMeta.value = null;
  }
  await loadConversationHistory({ applyLastSession: false, expectedContext });
  if (!isPageContextSnapshotCurrent(expectedContext)) return;
  const scenario = persistenceScenario.value;
  const convScenario = conversationScenario.value;
  const last = readLastSessionId(scenario);
  const localMsgs = last ? getSessionMessages(last, convScenario) : [];
  const localHasLast = last && localMsgs.length > 0;
  const historyHasLast = last && historyList.value.some((h) => h.sessionId === last);
  const localAssistantMissingContent = localMsgs.some(
    (m) => m.role === 'assistant' && !String(m.content ?? '').trim() && Boolean(m.thought?.trim()),
  );

  if (last && historyHasLast && (!localHasLast || localAssistantMissingContent)) {
    const item = historyList.value.find((h) => h.sessionId === last);
    if (item) {
      const restored = await loadHistoryItem(item, { notifyEmpty: false, expectedContext });
      if (restored) return;
    }
  }

  if (last && localHasLast) {
    currentSessionId.value = last;
    selectedSessionId.value = last;
    activeHistoryId.value = historyList.value.find((h) => h.sessionId === last)?.id ?? null;
  } else {
    currentSessionId.value = generateSessionId();
    selectedSessionId.value = currentSessionId.value;
    activeHistoryId.value = null;
    writeLastSessionId(scenario, currentSessionId.value);
  }
  mainTab.value = 'new';
  sessionNotify.markRead(scenario, currentSessionId.value);
  clearCompletedTypingPreviews(currentSessionId.value, convScenario);
}

function registerQuoteFocusTarget(payload: { documentId?: string; offset?: number; text?: string }) {
  const text = payload.text?.trim();
  if (!text) return;
  appendQuoteToComposer(text);
}

function handleInlineQuote(payload: { documentId?: string; documentName?: string; offset?: number; text?: string }) {
  const text = payload.text?.trim();
  if (!text) return;
  const quoted = buildQuoteText({
    text,
    documentId: payload.documentId,
    documentName: payload.documentName,
    offset: payload.offset,
  });
  setComposerDraftMessage(quoted);
  mainTab.value = 'new';
  expandRightPanel();
}

function onChatMessageClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  const mention = target?.closest?.('[data-smart-doc-mention="1"]') as HTMLElement | null;
  if (mention) {
    event.preventDefault();
    event.stopPropagation();
    const msgRoot = mention.closest?.('[data-msg-id]') as HTMLElement | null;
    const msgId = msgRoot?.getAttribute('data-msg-id') || '';
    const detail = mention.getAttribute('data-mention-detail') || mention.getAttribute('title') || '';
    const fullName = (mention.getAttribute('data-mention-fullname') || '').trim();
    const body = fullName && !detail.includes(fullName)
      ? `${detail}\n全名：${fullName}`
      : detail;
    if (smartDocMentionDetailKey.value === msgId && smartDocMentionDetailText.value === body) {
      smartDocMentionDetailKey.value = null;
      smartDocMentionDetailText.value = '';
    } else {
      smartDocMentionDetailKey.value = msgId;
      smartDocMentionDetailText.value = body;
    }
    return;
  }
  const pill = target?.closest?.('[data-quote-pill="1"]') as HTMLElement | null;
  if (!pill) return;
  event.preventDefault();
  event.stopPropagation();
  const text = pill.getAttribute('data-quote-text') || '';
  if (!text) return;
  const fieldPath = pill.getAttribute('data-quote-field') || undefined;
  const located = fieldPath
    ? aiPageContentApplyStore.locateFieldQuote(text, fieldPath)
    : false;
  const ok = located || highlightPageContentQuote(text, fieldPath);
  if (!ok) ElMessage.info('未在页面中定位到引用原文');
  shellStore.setDashboardExpandedAiComposer(false);
  expandRightPanel();
}

function isThoughtExpanded(messageId: string) {
  return expandedThoughtIds.value.has(messageId);
}

function toggleMessageThought(messageId: string) {
  const next = new Set(expandedThoughtIds.value);
  if (next.has(messageId)) next.delete(messageId);
  else next.add(messageId);
  expandedThoughtIds.value = next;
}

interface AiPanelSettingsV1 {
  v: 1;
  collapsed?: boolean;
  inputInnerHeight?: number;
  panelWidth?: number;
  mainTab?: 'new' | 'history';
  floatButton?: FloatButtonPosition;
  // 预留：后续可扩展其他设置（例如主题、快捷标签折叠等）
}

type FloatBtnEdge = 'left' | 'right' | 'top' | 'bottom';

interface FloatButtonPosition {
  edge: FloatBtnEdge;
  /** 贴左/右时为 top；贴顶/底时为 left */
  offset: number;
}

const FLOAT_BTN_SIZE = 48;
const FLOAT_BTN_EDGE_MARGIN = 16;
const FLOAT_BTN_HEADER_TOP = 72;
const FLOAT_DRAG_THRESHOLD = 5;
const DEFAULT_FLOAT_BTN: FloatButtonPosition = { edge: 'right', offset: FLOAT_BTN_HEADER_TOP };

function isFloatBtnEdge(v: unknown): v is FloatBtnEdge {
  return v === 'left' || v === 'right' || v === 'top' || v === 'bottom';
}

/** 左侧主栏占用宽度，悬浮球不可贴到侧栏下方 */
function floatBtnSidebarInset(): number {
  return shellStore.sidebarCollapsed
    ? shellStore.PRIMARY_RAIL_COLLAPSED_WIDTH
    : shellStore.PRIMARY_RAIL_WIDTH;
}

function floatBtnSafeLeftMin(): number {
  return floatBtnSidebarInset() + FLOAT_BTN_EDGE_MARGIN;
}

function clampFloatBtnPosition(pos: FloatButtonPosition): FloatButtonPosition {
  if (typeof window === 'undefined') return pos;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const m = FLOAT_BTN_EDGE_MARGIN;
  if (pos.edge === 'left' || pos.edge === 'right') {
    const minTop = Math.max(FLOAT_BTN_HEADER_TOP, m);
    const maxTop = Math.max(minTop, vh - FLOAT_BTN_SIZE - m);
    return { edge: pos.edge, offset: clamp(pos.offset, minTop, maxTop) };
  }
  const minLeft = floatBtnSafeLeftMin();
  const maxLeft = Math.max(minLeft, vw - FLOAT_BTN_SIZE - m);
  return { edge: pos.edge, offset: clamp(pos.offset, minLeft, maxLeft) };
}

function normalizeFloatBtnPosition(raw: Partial<FloatButtonPosition> | null | undefined): FloatButtonPosition {
  if (!raw) return { ...DEFAULT_FLOAT_BTN };
  const edge = isFloatBtnEdge(raw.edge) ? raw.edge : DEFAULT_FLOAT_BTN.edge;
  const offset =
    typeof raw.offset === 'number' && Number.isFinite(raw.offset) ? raw.offset : DEFAULT_FLOAT_BTN.offset;
  return clampFloatBtnPosition({ edge, offset });
}

function floatBtnPositionToStyle(pos: FloatButtonPosition): Record<string, string> {
  const m = FLOAT_BTN_EDGE_MARGIN;
  const style: Record<string, string> = {
    width: `${FLOAT_BTN_SIZE}px`,
    height: `${FLOAT_BTN_SIZE}px`,
    left: 'auto',
    right: 'auto',
    top: 'auto',
    bottom: 'auto',
  };
  switch (pos.edge) {
    case 'left':
      style.left = `${floatBtnSafeLeftMin()}px`;
      style.top = `${pos.offset}px`;
      break;
    case 'right':
      style.right = `${m}px`;
      style.top = `${pos.offset}px`;
      break;
    case 'top':
      style.top = `${Math.max(FLOAT_BTN_HEADER_TOP, m)}px`;
      style.left = `${pos.offset}px`;
      break;
    case 'bottom':
      style.bottom = `${m}px`;
      style.left = `${pos.offset}px`;
      break;
  }
  return style;
}

function getFloatBtnScreenPosition(pos: FloatButtonPosition): { left: number; top: number } {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720;
  const m = FLOAT_BTN_EDGE_MARGIN;
  switch (pos.edge) {
    case 'left':
      return { left: floatBtnSafeLeftMin(), top: pos.offset };
    case 'right':
      return { left: vw - FLOAT_BTN_SIZE - m, top: pos.offset };
    case 'top':
      return { left: pos.offset, top: Math.max(FLOAT_BTN_HEADER_TOP, m) };
    case 'bottom':
      return { left: pos.offset, top: vh - FLOAT_BTN_SIZE - m };
  }
}

function snapFloatBtnPosition(clientX: number, clientY: number): FloatButtonPosition {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const half = FLOAT_BTN_SIZE / 2;
  const distLeft = clientX;
  const distRight = vw - clientX;
  const distTop = clientY;
  const distBottom = vh - clientY;
  const minDist = Math.min(distLeft, distRight, distTop, distBottom);
  let edge: FloatBtnEdge;
  if (minDist === distLeft) edge = 'left';
  else if (minDist === distRight) edge = 'right';
  else if (minDist === distTop) edge = 'top';
  else edge = 'bottom';
  if (edge === 'left' || edge === 'right') {
    const minTop = Math.max(FLOAT_BTN_HEADER_TOP, FLOAT_BTN_EDGE_MARGIN);
    const maxTop = Math.max(minTop, vh - FLOAT_BTN_SIZE - FLOAT_BTN_EDGE_MARGIN);
    return clampFloatBtnPosition({ edge, offset: clamp(clientY - half, minTop, maxTop) });
  }
  const minLeft = floatBtnSafeLeftMin();
  const maxLeft = Math.max(minLeft, vw - FLOAT_BTN_SIZE - FLOAT_BTN_EDGE_MARGIN);
  return clampFloatBtnPosition({ edge, offset: clamp(clientX - half, minLeft, maxLeft) });
}

const SETTINGS_VERSION = 1 as const;
const settingsStorageKey = computed(() => {
  const u = authStore.userInfo?.userId;
  const t = authStore.userInfo?.tenantId;
  if (!u) return `aipd_ai_panel_settings_v${SETTINGS_VERSION}_guest`;
  return `aipd_ai_panel_settings_v${SETTINGS_VERSION}_t${t ?? '0'}_u${u}`;
});

function readPanelSettings(): AiPanelSettingsV1 | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(settingsStorageKey.value);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AiPanelSettingsV1;
    if (!parsed || parsed.v !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

const aiInput = ref('');
/** 按会话场景隔离输入框草稿，页签切换时互不污染 */
const composerDraftByScenario = ref<Record<string, string>>({});

function saveComposerDraftForScenario(scenario: string | null | undefined) {
  const key = String(scenario || '').trim();
  if (!key) return;
  composerDraftByScenario.value = {
    ...composerDraftByScenario.value,
    [key]: aiInput.value,
  };
}

function restoreComposerDraftForScenario(scenario: string | null | undefined) {
  const key = String(scenario || '').trim();
  aiInput.value = key ? (composerDraftByScenario.value[key] || '') : '';
  closeSmartDocAtMention();
}

function clearComposerDraftForCurrentScenario() {
  const key = String(persistenceScenario.value || '').trim();
  if (key) {
    composerDraftByScenario.value = {
      ...composerDraftByScenario.value,
      [key]: '',
    };
  }
  aiInput.value = '';
  closeSmartDocAtMention();
}
/** compositionend 后部分浏览器仍会立刻触发 keydown Enter，需短时忽略 */
let aiInputCompositionJustEnded = false;
let aiInputCompositionEndTimer: ReturnType<typeof setTimeout> | null = null;

function onAiInputCompositionStart() {
  aiInputComposing.value = true;
  aiInputCompositionTick.value += 1;
}

function onAiInputCompositionUpdate() {
  if (!aiInputComposing.value) return;
  aiInputCompositionTick.value += 1;
  if (smartDocAi.active) {
    onSmartDocTextareaScroll();
  }
}

function onAiInputCompositionEnd() {
  aiInputComposing.value = false;
  aiInputCompositionTick.value += 1;
  aiInputCompositionJustEnded = true;
  if (aiInputCompositionEndTimer) clearTimeout(aiInputCompositionEndTimer);
  aiInputCompositionEndTimer = setTimeout(() => {
    aiInputCompositionJustEnded = false;
    aiInputCompositionEndTimer = null;
  }, 80);
  if (smartDocAi.active) {
    void refreshSmartDocAtMention();
    void nextTick(() => onSmartDocTextareaScroll());
  }
}

function onAiInputEnter(e: KeyboardEvent) {
  if (
    aiInputComposing.value
    || aiInputCompositionJustEnded
    || e.isComposing
    || e.keyCode === 229
  ) {
    return;
  }
  if (smartDocSuppressEnterSend) {
    smartDocSuppressEnterSend = false;
    return;
  }
  if (smartDocAtMentionOpen.value) {
    return;
  }
  void sendAiMessage();
}

const aiLoading = ref(false);
watch(
  () => `${authStore.userInfo?.tenantId ?? ''}:${authStore.userInfo?.userId ?? ''}`,
  () => {
    // Rendering state belongs to the current account. Backend SOR jobs continue.
    isThinking.value = false;
    aiLoading.value = false;
    currentThoughtDraft.value = [];
    sorRequirementContext.value = null;
  },
  { flush: 'sync' },
);
let aiPanelTabRunEnd: (() => void) | null = null;

watch(
  () => aiLoading.value || isThinking.value || isStreaming.value,
  (running) => {
    if (running) {
      if (!aiPanelTabRunEnd) {
        aiPanelTabRunEnd = trackPageTabRunningTask(currentPageTabKey());
      }
    } else if (aiPanelTabRunEnd) {
      aiPanelTabRunEnd();
      aiPanelTabRunEnd = null;
    }
  },
);

const fileInputRef = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const panelDragDepth = ref(0);

function dataTransferHasFiles(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes('Files');
}

function onPanelDragEnter(e: DragEvent) {
  if (mainTab.value !== 'new' || !dataTransferHasFiles(e)) return;
  panelDragDepth.value += 1;
  isDragging.value = true;
}

function onPanelDragOver(e: DragEvent) {
  if (mainTab.value !== 'new' || !dataTransferHasFiles(e)) return;
  e.preventDefault();
  isDragging.value = true;
}

function onPanelDragLeave(e: DragEvent) {
  const body = e.currentTarget as HTMLElement;
  const related = e.relatedTarget as Node | null;
  if (related && body.contains(related)) return;
  panelDragDepth.value = Math.max(0, panelDragDepth.value - 1);
  if (panelDragDepth.value === 0) isDragging.value = false;
}

// 历史会话重命名（行内编辑）
const editingHistoryId = ref<number | null>(null);
const editingHistoryTitle = ref('');
const historyTitleInputRef = ref<HTMLInputElement | null>(null);

// 当前会话标题重命名（双击标题栏）
const editingSessionTitle = ref(false);
const editingSessionTitleDraft = ref('');
const sessionTitleInputRef = ref<HTMLInputElement | null>(null);
const aiHeaderRef = ref<HTMLElement | null>(null);
const aiHeaderTabsMeasureRef = ref<HTMLElement | null>(null);
const compactHeaderActions = ref(true);
let headerActionsResizeObserver: ResizeObserver | null = null;

function updateCompactHeaderActions() {
  const header = aiHeaderRef.value;
  const measure = aiHeaderTabsMeasureRef.value;
  if (!header || !measure) return;
  const leading = header.querySelector('.ai-expand-toggle-btn') as HTMLElement | null;
  const plusBtn = header.querySelector('.ai-new-session-btn') as HTMLElement | null;
  const minusBtn = header.querySelector('.ai-collapse-to-float-btn') as HTMLElement | null;
  const plusW = plusBtn?.offsetWidth ?? 0;
  const minusW = minusBtn?.offsetWidth ?? 0;
  const actionsWidth = plusW + minusW + (plusW && minusW ? 6 : 0);
  compactHeaderActions.value = shouldCompactAiHeaderActions({
    headerWidth: header.clientWidth,
    leadingWidth: leading?.offsetWidth ?? 0,
    expandedTabsWidth: measure.offsetWidth,
    roundActionsWidth: actionsWidth,
  });
}

const attachmentList = ref<AttachmentItem[]>([]);

// 面板宽度：支持拖拽左边界调整
const PANEL_WIDTH_MIN = 280;
const PANEL_WIDTH_MAX = 560;
/** 默认会话场景：项目协助；其他场景（文本生成、AI评审）由调用方单独指定 */
const DEFAULT_CONVERSATION_SCENARIO = 'project_assistance';
const panelWidth = ref(320);
const aiPanelWidthPx = computed(() => `${panelWidth.value}px`);
const RESIZE_LEFT_EDGE_THRESHOLD = 8;
const isOnLeftEdge = ref(false);
const isResizingWidth = ref(false);

function onPanelMouseMove(e: MouseEvent) {
  if (isResizingWidth.value) return;
  const el = e.currentTarget as HTMLElement | null;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  isOnLeftEdge.value = e.clientX - rect.left <= RESIZE_LEFT_EDGE_THRESHOLD;
}

function onPanelMouseLeave() {
  if (!isResizingWidth.value) isOnLeftEdge.value = false;
}

function onAsideMouseMove(e: MouseEvent) {
  if (props.embeddedLayout) return;
  onPanelMouseMove(e);
}

function onAsideMouseLeave() {
  if (props.embeddedLayout) return;
  onPanelMouseLeave();
}

function onPanelMouseDown(e: MouseEvent) {
  if (!isOnLeftEdge.value) return;
  e.preventDefault();
  startResizeWidth();
}

function onAsideMouseDown(e: MouseEvent) {
  if (props.embeddedLayout) return;
  onPanelMouseDown(e);
}

function startResizeWidth() {
  isResizingWidth.value = true;
  document.addEventListener('mousemove', onResizeWidthMove);
  document.addEventListener('mouseup', onResizeWidthEnd);
}

function onResizeWidthMove(e: MouseEvent) {
  // 面板右侧固定 right: 16px，因此通过鼠标 X 计算 width
  const rightX = window.innerWidth - 16;
  const next = Math.round(rightX - e.clientX);
  panelWidth.value = clamp(next, PANEL_WIDTH_MIN, PANEL_WIDTH_MAX);
  emit('width-change', panelWidth.value);
  schedulePersistPanelSettings();
}

function onResizeWidthEnd() {
  isResizingWidth.value = false;
  isOnLeftEdge.value = false;
  document.removeEventListener('mousemove', onResizeWidthMove);
  document.removeEventListener('mouseup', onResizeWidthEnd);
}

// 输入区内层高度（约 3 行），可拖拽上边界调整
const INPUT_HEIGHT_MIN = 72;
const INPUT_HEIGHT_MAX = 220;
const inputInnerHeight = ref(88);
const inputInnerHeightPx = computed(() => `${inputInnerHeight.value}px`);

/** 鼠标在 ai-input-area 上边框附近（约 10px）时视为可拖拽调整高度 */
const RESIZE_EDGE_THRESHOLD = 10;
const isOnTopEdge = ref(false);
const isResizing = ref(false);
const aiChatDisplayRef = ref<HTMLElement | null>(null);

let resizeStartY = 0;
let resizeStartH = 0;
let shouldKeepChatPinnedToBottom = false;
let pendingPinToBottomRaf = 0;
let pendingFollowChatRaf = 0;

function isChatDisplayPinnedToBottom(): boolean {
  const el = aiChatDisplayRef.value ?? (document.querySelector('.ai-chat-display') as HTMLElement | null);
  if (!el) return true;
  const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
  return distance <= 24; // 允许少量误差
}

function schedulePinChatToBottomIfNeeded() {
  if (!shouldKeepChatPinnedToBottom) return;
  if (pendingPinToBottomRaf) return;
  pendingPinToBottomRaf = window.requestAnimationFrame(async () => {
    pendingPinToBottomRaf = 0;
    await nextTick();
    scrollAiToBottom();
  });
}

/** 思考步骤 / 打字机等内容增高时，若用户仍贴在底部则跟随滚到底 */
function scheduleFollowChatToBottomIfPinned() {
  if (!isChatDisplayPinnedToBottom()) return;
  if (pendingFollowChatRaf) return;
  pendingFollowChatRaf = window.requestAnimationFrame(async () => {
    pendingFollowChatRaf = 0;
    await nextTick();
    scrollAiToBottom();
  });
}

function onInputAreaMouseMove(e: MouseEvent) {
  if (isResizing.value) return;
  isOnTopEdge.value = e.offsetY <= RESIZE_EDGE_THRESHOLD;
}

function onInputAreaMouseLeave() {
  if (!isResizing.value) isOnTopEdge.value = false;
}

function onInputAreaMouseDown(e: MouseEvent) {
  if (e.offsetY <= RESIZE_EDGE_THRESHOLD) {
    e.preventDefault();
    startResize(e);
  }
}

function startResize(e: MouseEvent) {
  isResizing.value = true;
  isOnTopEdge.value = true;
  resizeStartY = e.clientY;
  resizeStartH = inputInnerHeight.value;
  shouldKeepChatPinnedToBottom = isChatDisplayPinnedToBottom();
  document.addEventListener('mousemove', onResizeMove);
  document.addEventListener('mouseup', onResizeEnd);
}

function onResizeMove(e: MouseEvent) {
  const dy = e.clientY - resizeStartY;
  // 向上拖动（dy<0）应增大输入区高度；向下拖动应减小
  const next = Math.round(resizeStartH - dy);
  inputInnerHeight.value = Math.min(INPUT_HEIGHT_MAX, Math.max(INPUT_HEIGHT_MIN, next));
  schedulePinChatToBottomIfNeeded();
}

function onResizeEnd() {
  isResizing.value = false;
  isOnTopEdge.value = false;
  schedulePinChatToBottomIfNeeded();
  shouldKeepChatPinnedToBottom = false;
  document.removeEventListener('mousemove', onResizeMove);
  document.removeEventListener('mouseup', onResizeEnd);
}
const dashboard = ref<any | null>(null);
const isCollapsed = ref(false);
/** 用户主动收起为悬浮球时，阻止 SOR/任务回调等自动 expandRightPanel */
const suppressAutoExpand = ref(false);
const floatBtnPosition = ref<FloatButtonPosition>({ ...DEFAULT_FLOAT_BTN });
const floatBtnDragging = ref(false);
const floatBtnDragPreview = ref<{ left: number; top: number } | null>(null);
const floatDragDidMove = ref(false);
let floatDragPointerId: number | null = null;
let floatDragStart = { x: 0, y: 0, moved: false, startLeft: 0, startTop: 0 };

const floatBtnStyle = computed(() => {
  if (floatBtnDragPreview.value) {
    return {
      ...floatBtnPositionToStyle(floatBtnPosition.value),
      left: `${floatBtnDragPreview.value.left}px`,
      top: `${floatBtnDragPreview.value.top}px`,
      right: 'auto',
      bottom: 'auto',
    };
  }
  return floatBtnPositionToStyle(floatBtnPosition.value);
});

function syncFloatBtnPositionOnResize() {
  floatBtnPosition.value = clampFloatBtnPosition(floatBtnPosition.value);
}

function beginFloatBtnDrag(clientX: number, clientY: number) {
  const { left, top } = getFloatBtnScreenPosition(floatBtnPosition.value);
  floatDragStart = { x: clientX, y: clientY, moved: false, startLeft: left, startTop: top };
  floatBtnDragging.value = true;
  floatBtnDragPreview.value = { left, top };
}

function moveFloatBtnDrag(clientX: number, clientY: number) {
  const dx = clientX - floatDragStart.x;
  const dy = clientY - floatDragStart.y;
  if (Math.abs(dx) > FLOAT_DRAG_THRESHOLD || Math.abs(dy) > FLOAT_DRAG_THRESHOLD) {
    floatDragStart.moved = true;
  }
  if (!floatBtnDragPreview.value || typeof window === 'undefined') return;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const m = FLOAT_BTN_EDGE_MARGIN;
  floatBtnDragPreview.value = {
    left: clamp(floatDragStart.startLeft + dx, floatBtnSafeLeftMin(), vw - FLOAT_BTN_SIZE - m),
    top: clamp(floatDragStart.startTop + dy, FLOAT_BTN_HEADER_TOP, vh - FLOAT_BTN_SIZE - m),
  };
}

function finishFloatBtnDrag(clientX: number, clientY: number) {
  if (floatDragStart.moved && floatBtnDragPreview.value) {
    floatDragDidMove.value = true;
    const centerX = floatBtnDragPreview.value.left + FLOAT_BTN_SIZE / 2;
    const centerY = floatBtnDragPreview.value.top + FLOAT_BTN_SIZE / 2;
    floatBtnPosition.value = snapFloatBtnPosition(centerX, centerY);
    schedulePersistPanelSettings();
  }
  floatBtnDragging.value = false;
  floatBtnDragPreview.value = null;
  floatDragPointerId = null;
}

function onFloatBtnPointerDown(e: MouseEvent) {
  if (e.button !== 0) return;
  e.preventDefault();
  beginFloatBtnDrag(e.clientX, e.clientY);
  window.addEventListener('mousemove', onFloatBtnMouseMove);
  window.addEventListener('mouseup', onFloatBtnMouseUp);
}

function onFloatBtnMouseMove(e: MouseEvent) {
  moveFloatBtnDrag(e.clientX, e.clientY);
}

function onFloatBtnMouseUp(e: MouseEvent) {
  window.removeEventListener('mousemove', onFloatBtnMouseMove);
  window.removeEventListener('mouseup', onFloatBtnMouseUp);
  finishFloatBtnDrag(e.clientX, e.clientY);
}

function onFloatBtnTouchStart(e: TouchEvent) {
  if (e.touches.length !== 1) return;
  const touch = e.touches[0];
  floatDragPointerId = touch.identifier;
  beginFloatBtnDrag(touch.clientX, touch.clientY);
  window.addEventListener('touchmove', onFloatBtnTouchMove, { passive: false });
  window.addEventListener('touchend', onFloatBtnTouchEnd);
  window.addEventListener('touchcancel', onFloatBtnTouchEnd);
}

function onFloatBtnTouchMove(e: TouchEvent) {
  const touch = [...e.touches].find((t) => t.identifier === floatDragPointerId);
  if (!touch) return;
  e.preventDefault();
  moveFloatBtnDrag(touch.clientX, touch.clientY);
}

function onFloatBtnTouchEnd(e: TouchEvent) {
  const touch = [...e.changedTouches].find((t) => t.identifier === floatDragPointerId);
  window.removeEventListener('touchmove', onFloatBtnTouchMove);
  window.removeEventListener('touchend', onFloatBtnTouchEnd);
  window.removeEventListener('touchcancel', onFloatBtnTouchEnd);
  if (!touch) return;
  finishFloatBtnDrag(touch.clientX, touch.clientY);
}

function onFloatBtnClick() {
  if (floatDragDidMove.value) {
    floatDragDidMove.value = false;
    return;
  }
  toggleCollapse();
}

function cleanupFloatBtnDragListeners() {
  window.removeEventListener('mousemove', onFloatBtnMouseMove);
  window.removeEventListener('mouseup', onFloatBtnMouseUp);
  window.removeEventListener('touchmove', onFloatBtnTouchMove);
  window.removeEventListener('touchend', onFloatBtnTouchEnd);
  window.removeEventListener('touchcancel', onFloatBtnTouchEnd);
}
const historyList = ref<ConversationSummary[]>([]);
const activeHistoryId = ref<number | null>(null);
const selectedSessionId = ref<string | null>(null);
// 主 Tab：'new' 新建对话（默认）| 'history' 历史对话
const mainTab = ref<'new' | 'history'>('new');

watch(
  () => props.embeddedLayout,
  (v) => {
    if (v) isCollapsed.value = false;
  },
  { immediate: true },
);

const isApplyingPanelSettings = ref(false);
let persistSettingsTimer: number | null = null;

function applyPanelSettings(settings: AiPanelSettingsV1 | null) {
  if (!settings) return;
  isApplyingPanelSettings.value = true;
  try {
    if (typeof settings.collapsed === 'boolean') {
      isCollapsed.value = settings.collapsed;
      if (settings.collapsed) {
        suppressAutoExpand.value = true;
      }
    }
    if (typeof settings.inputInnerHeight === 'number' && Number.isFinite(settings.inputInnerHeight)) {
      inputInnerHeight.value = clamp(settings.inputInnerHeight, INPUT_HEIGHT_MIN, INPUT_HEIGHT_MAX);
    }
    if (typeof settings.panelWidth === 'number' && Number.isFinite(settings.panelWidth)) {
      panelWidth.value = clamp(settings.panelWidth, PANEL_WIDTH_MIN, PANEL_WIDTH_MAX);
    }
    if (settings.mainTab === 'new' || settings.mainTab === 'history') {
      mainTab.value = settings.mainTab;
    }
    floatBtnPosition.value = normalizeFloatBtnPosition(settings.floatButton);
  } finally {
    isApplyingPanelSettings.value = false;
  }
}

function persistPanelSettingsNow() {
  try {
    if (typeof localStorage === 'undefined') return;
    const payload: AiPanelSettingsV1 = {
      v: 1,
      collapsed: isCollapsed.value,
      inputInnerHeight: inputInnerHeight.value,
      panelWidth: panelWidth.value,
      mainTab: mainTab.value,
      floatButton: floatBtnPosition.value,
    };
    localStorage.setItem(settingsStorageKey.value, JSON.stringify(payload));
  } catch {
    // 忽略持久化异常（例如隐私模式、存储满等）
  }
}

function schedulePersistPanelSettings() {
  if (isApplyingPanelSettings.value) return;
  if (persistSettingsTimer != null) window.clearTimeout(persistSettingsTimer);
  persistSettingsTimer = window.setTimeout(() => {
    persistSettingsTimer = null;
    persistPanelSettingsNow();
  }, 200);
}

// setup 期间先应用一次（避免刷新/重新登录时闪烁）
applyPanelSettings(readPanelSettings());
emit('width-change', panelWidth.value);

// 简单的会话编号，便于传给后端做数据库持久化和历史管理
const currentSessionId = ref<string>(generateSessionId());

// 统一转为字符串，避免 route.name 为 Symbol 等导致判断失效
const currentRouteName = computed(() => String(route.name ?? ''));

const AI_RUN_MODE_STORAGE_KEY = 'aipd_ai_run_mode';
const AI_LLM_PROVIDER_STORAGE_KEY = 'aipd_ai_llm_provider';
const LLM_PROVIDER_AUTO = 'auto';

const AI_LONG_CYCLE_MODE = 'long_cycle';

const DEFAULT_CHAT_OPTIONS: AiChatOptionsResponse = {
  defaultProvider: 'deepseek',
  autoProvider: 'deepseek',
  allowUserModelSelection: false,
  defaultRunMode: 'flash',
  longCycleMode: AI_LONG_CYCLE_MODE,
  providers: [{ id: 'deepseek', label: 'DeepSeek', modes: ['flash', 'pro'] }],
};

const chatOptions = ref<AiChatOptionsResponse>(DEFAULT_CHAT_OPTIONS);
const aiRunMode = ref<AiRunModeId>(
  localStorage.getItem(AI_RUN_MODE_STORAGE_KEY) || DEFAULT_CHAT_OPTIONS.defaultRunMode || 'flash',
);
const llmProvider = ref(
  localStorage.getItem(AI_LLM_PROVIDER_STORAGE_KEY) || LLM_PROVIDER_AUTO,
);

const allowUserModelSelection = computed(
  () => chatOptions.value.allowUserModelSelection === true,
);

/** 手动选择 DeepSeek / 豆包等具体模型时才展示运行模式 */
const showRunModeDropdown = computed(
  () => allowUserModelSelection.value && llmProvider.value !== LLM_PROVIDER_AUTO,
);

const chatProviderOptions = computed(() => chatOptions.value.providers ?? DEFAULT_CHAT_OPTIONS.providers);

const resolvedProviderId = computed(() => {
  if (llmProvider.value === LLM_PROVIDER_AUTO || !allowUserModelSelection.value) {
    return chatOptions.value.autoProvider || chatOptions.value.defaultProvider || 'deepseek';
  }
  return llmProvider.value;
});

/** BOM 任务使用的 provider（Auto 时取 bomAutoProvider） */
const resolvedBomProviderId = computed(() => {
  if (llmProvider.value === LLM_PROVIDER_AUTO || !allowUserModelSelection.value) {
    return chatOptions.value.bomAutoProvider || chatOptions.value.autoProvider || chatOptions.value.defaultProvider || 'doubao';
  }
  return llmProvider.value;
});

/** BOM 任务使用的 runMode（Auto 时取 bomAutoRunMode；手动选豆包时默认 turbo） */
const resolvedBomRunMode = computed(() => {
  const bomDefault = chatOptions.value.bomAutoRunMode || 'turbo';
  if (llmProvider.value === LLM_PROVIDER_AUTO || !allowUserModelSelection.value) {
    return bomDefault;
  }
  if (llmProvider.value === 'doubao') {
    const doubaoModes = chatProviderOptions.value.find((p) => p.id === 'doubao')?.modes ?? [];
    const chatModes = new Set(['flash', 'pro', 'quick', 'professional', 'long_cycle']);
    if (chatModes.has(aiRunMode.value) || !doubaoModes.includes(aiRunMode.value)) {
      return doubaoModes.includes(bomDefault) ? bomDefault : 'turbo';
    }
  }
  return aiRunMode.value;
});

const visibleRunModes = computed(() => {
  const provider = chatProviderOptions.value.find((p) => p.id === resolvedProviderId.value);
  let providerModes = (provider?.modes?.length ? provider.modes : ['flash']).map((id) => ({
    id,
    label: id,
  }));
  // Auto 模式下合并 BOM 默认 provider 的档位（如豆包 turbo），避免选不到 turbo
  if (llmProvider.value === LLM_PROVIDER_AUTO || !allowUserModelSelection.value) {
    const bomProvider = chatProviderOptions.value.find((p) => p.id === resolvedBomProviderId.value);
    const bomModes = bomProvider?.modes ?? [];
    const seen = new Set(providerModes.map((m) => m.id));
    for (const id of bomModes) {
      if (!seen.has(id)) {
        providerModes.push({ id, label: id });
        seen.add(id);
      }
    }
    const bomDefault = chatOptions.value.bomAutoRunMode;
    if (bomDefault && !seen.has(bomDefault)) {
      providerModes.push({ id: bomDefault, label: bomDefault });
    }
  }
  const longCycle = chatOptions.value.longCycleMode || AI_LONG_CYCLE_MODE;
  return [...providerModes, { id: longCycle, label: longCycle }];
});

const runModeDropdownOptions = computed(() =>
  visibleRunModes.value.map((mode) => ({ id: mode.id, label: mode.label })),
);

const providerDropdownOptions = computed(() => {
  const autoOption = { id: LLM_PROVIDER_AUTO, label: '自动' };
  if (!allowUserModelSelection.value) {
    return [autoOption];
  }
  return [
    autoOption,
    ...chatProviderOptions.value.map((p) => ({ id: p.id, label: p.label })),
  ];
});

watch(aiRunMode, (value) => {
  try {
    localStorage.setItem(AI_RUN_MODE_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
});

watch(llmProvider, (value) => {
  try {
    localStorage.setItem(AI_LLM_PROVIDER_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
  if (value === 'doubao') {
    const bomDefault = chatOptions.value.bomAutoRunMode || 'turbo';
    const doubaoModes = chatProviderOptions.value.find((p) => p.id === 'doubao')?.modes ?? [];
    if (doubaoModes.includes(bomDefault)) {
      aiRunMode.value = bomDefault;
    }
  }
});

watch(visibleRunModes, (modes) => {
  if (!modes.some((m) => m.id === aiRunMode.value)) {
    aiRunMode.value = modes[0]?.id || chatOptions.value.defaultRunMode || 'flash';
  }
});

watch(resolvedProviderId, () => {
  const modes = visibleRunModes.value;
  if (!modes.some((m) => m.id === aiRunMode.value)) {
    aiRunMode.value = modes[0]?.id || chatOptions.value.defaultRunMode || 'flash';
  }
});

async function loadAiChatOptions() {
  try {
    const opts = await fetchAiChatOptions();
    if (opts?.providers?.length) {
      chatOptions.value = opts;
    }
    if (!allowUserModelSelection.value) {
      llmProvider.value = LLM_PROVIDER_AUTO;
      if (opts?.bomAutoRunMode) {
        aiRunMode.value = opts.bomAutoRunMode;
      }
      return;
    }
    const providerIds = new Set([
      LLM_PROVIDER_AUTO,
      ...(chatOptions.value.providers ?? []).map((p) => p.id),
    ]);
    if (!providerIds.has(llmProvider.value)) {
      llmProvider.value = LLM_PROVIDER_AUTO;
    }
  } catch {
    llmProvider.value = LLM_PROVIDER_AUTO;
  }
}

function buildAgentChatContextWrapper(extra?: Record<string, unknown>): { context: Record<string, unknown> } {
  const providerForRequest =
    !allowUserModelSelection.value || llmProvider.value === LLM_PROVIDER_AUTO
      ? LLM_PROVIDER_AUTO
      : llmProvider.value;
  const merged: Record<string, unknown> = {};
  const orgPayload = projectCreateAiContextStore.buildChatContextPayload(
    currentRouteName.value === 'ProjectCreate' ? currentProjectCreateDraftKey() : '',
  );
  if (orgPayload && typeof orgPayload === 'object') {
    Object.assign(merged, orgPayload as Record<string, unknown>);
  }
  if (extra && typeof extra === 'object') {
    Object.assign(merged, extra);
  }
  merged.allowProjectFinancialContext = permissionStore.hasRoles(['SUPER_ADMIN', 'ADMIN']);
  // 页面信号：顶层 routeName 只到 Java 后端，AI 服务读的是 context，须在此单独带上
  merged.pageRouteName = currentRouteName.value;
  merged.aiRunMode = aiRunMode.value;
  merged.llmProvider = providerForRequest;
  const smartDocPayload = smartDocAi.buildChatContextPayload();
  if (smartDocPayload) {
    Object.assign(merged, smartDocPayload);
  }
  return { context: merged };
}

/** 创建项目对话：预检索知识库并把 referenceSearchHits 并入 Agent context */
/** 从 Dashboard 发起「创建项目」时路由非 ProjectCreate，仍须附带约 2 级组织名单供模型匹配负责人 */
async function ensureOrgMemberRosterForCreateProjectAi() {
  const rows = projectCreateAiContextStore.orgMemberRoster;
  if (Array.isArray(rows) && rows.length > 0) return;
  try {
    const scope = await listMemberPickerDefaultScope();
    projectCreateAiContextStore.setOrgMemberRoster(
      (scope.members || []).map((m) => ({
        userId: m.userId,
        realName: m.realName || '',
        departmentName: m.departmentName ?? '',
        position: m.position ?? '',
        platformUserId: m.platformUserId ?? '',
      })),
    );
  } catch {
    /* 忽略：无名单时后端仍可用草稿逻辑，仅少自动推荐 */
  }
}

async function withProjectCreateReferenceContext(
  userText: string,
  extra?: Record<string, unknown>,
): Promise<{ context?: Record<string, unknown> } | Record<string, never>> {
  await ensureOrgMemberRosterForCreateProjectAi();
  const wrap = buildAgentChatContextWrapper(extra);
  const hits = await fetchProjectCreateReferenceKnowledgeHits(userText);
  const base: Record<string, unknown> =
    'context' in wrap && wrap.context && typeof wrap.context === 'object'
      ? { ...(wrap.context as Record<string, unknown>) }
      : {};
  if (extra && typeof extra === 'object') {
    Object.assign(base, extra);
  }
  if (!hits.length) {
    projectCreateAiContextStore.setLastReferenceSearchHits([]);
    if (!Object.keys(base).length) return {};
    return { context: base };
  }
  base.referenceSearchHits = hits;
  projectCreateAiContextStore.setLastReferenceSearchHits(hits);
  return { context: base };
}

const showPanel = computed(() => {
  // 登录页等不展示，其他认证页全部展示
  if (currentRouteName.value === 'Login' || currentRouteName.value === 'WeComCallback') {
    return false;
  }
  return true;
});

// 项目 AI 上下文只认当前路由中的项目 ID；无法解析时 fail-closed，不向后端发送空项目请求。
const hasProjectContext = computed(() => isProjectAiRoute(route));
const projectId = computed(() => resolveProjectIdFromRoute(route));

/**
 * 对话发送用的项目 ID：路由项目 → 智能文档工作区 → @项目 token。
 * 避免个人智能文档页仅 @ 了项目却仍以空 projectId 打到后端。
 */
async function resolveChatProjectId(text: string): Promise<string> {
  try {
    await ensureSmartDocProjectTokenFromInput(text);
  } catch {
    /* ignore */
  }
  const token = smartDocAi.atTokens.find(
    (t) => t.kind === 'project' && /^\d+$/.test(String(t.refId || '').trim()),
  );
  if (token?.refId) return String(token.refId).trim();

  const fromRoute = resolveProjectIdFromRoute(route);
  if (fromRoute > 0) return String(fromRoute);
  if (projectId.value > 0) return String(projectId.value);
  const ws = Number(smartDocAi.workspaceId || 0);
  if (Number.isFinite(ws) && ws > 0) return String(ws);
  const lastRef = String(smartDocAi.lastSkillProjectRefId || '').trim();
  if (/^\d+$/.test(lastRef)) return lastRef;
  return '';
}

/** 当前会话场景：按页面上下文划分（项目子页 / AI 工具 / 知识空间等各自独立） */
const conversationScenario = computed({
  get: () => {
    const cur = store.currentScenario;
    const name = currentRouteName.value;
    // 公式详情/分享页：沿用单公式计算场景
    const isCalcDetailPage = name === 'AiCalculatorDetail' || name === 'AiCalculatorFormulaShare';
    if (
      isCalcDetailPage && (
        isCalculatorCalcScenario(cur)
        || cur === CALCULATOR_FORMULA_IMPORT_SCENARIO
        || cur.startsWith(CALCULATOR_SCENARIO_PREFIX)
        || cur.startsWith('calc_')
      )
    ) {
      return cur;
    }
    // 计算器首页：保留通用场景与公式导入场景（导入/创建自定义公式依赖此键出卡）；
    // 仍剥离详情页遗留的单公式 calc_*，避免串台
    if (
      name === 'AiCalculator'
      && (cur === CALCULATOR_FORMULA_IMPORT_SCENARIO || cur === CALCULATOR_GENERAL_SCENARIO)
    ) {
      return cur;
    }
    if (name === 'Resources' || name === 'ResourceCreate' || name === 'ResourceDetail') {
      return COMPONENT_SCENARIO;
    }
    // 智能文档页：始终走独立场景（含技能创作子模式）
    // 勿用 sticky smartDocAi.active：keep-alive 切到项目页后仍会把会话场景锁在智能文档
    if (
      name === 'SmartDocumentWorkbench'
      || name === 'SmartDocumentWorkbenchHome'
    ) {
      return resolveAiConversationScenario(route, cur || DEFAULT_CONVERSATION_SCENARIO);
    }
    const fallback = isCalculatorCalcScenario(cur)
      || cur === CALCULATOR_FORMULA_IMPORT_SCENARIO
      || cur === CALCULATOR_GENERAL_SCENARIO
      || cur.startsWith(CALCULATOR_SCENARIO_PREFIX)
      || cur.startsWith('calc_') ? DEFAULT_CONVERSATION_SCENARIO : cur;
    return resolveAiConversationScenario(route, fallback || DEFAULT_CONVERSATION_SCENARIO);
  },
  set: (v: string) => {
    store.currentScenario = v && v.trim() ? v.trim() : DEFAULT_CONVERSATION_SCENARIO;
  },
});

const aiPageContextKey = computed(() => resolveAiPageContextKey(route));

type PageContextSnapshot = {
  generation: number;
  projectId: number;
  scenario: string;
  routeName: string;
  pageTabKey: string;
  operationId: string;
};

function capturePageContextSnapshot(generation = pageContextSwitchGen): PageContextSnapshot {
  return {
    generation,
    projectId: projectId.value,
    scenario: conversationScenario.value,
    routeName: currentRouteName.value,
    pageTabKey: currentPageTabKey(),
    operationId: genId(),
  };
}

function isPageContextSnapshotCurrent(snapshot: PageContextSnapshot): boolean {
  return snapshot.generation === pageContextSwitchGen
    && snapshot.projectId === projectId.value
    && snapshot.scenario === conversationScenario.value
    && snapshot.routeName === currentRouteName.value
    && snapshot.pageTabKey === currentPageTabKey();
}

const VISIBLE_MESSAGE_INITIAL = 50;
const VISIBLE_MESSAGE_STEP = 50;
/** 提交 AI 任务后若一直无任何返回，在此时间（毫秒）后中止并提示超时，默认 45 秒 */
const AI_TASK_TIMEOUT_MS = 45000;
const visibleMessageCount = ref(VISIBLE_MESSAGE_INITIAL);

/** 业务页短暂提示（不落 store，避免创建页与 Dashboard 共用 project_assistance 时串台） */
type EphemeralAssistantNotice = {
  id: string;
  content: string;
  sessionId: string;
  routeName: string;
  createdAt: number;
};
const ephemeralAssistantNotices = ref<EphemeralAssistantNotice[]>([]);

const aiMessages = computed(() => {
  // 始终显示当前页面 session 的消息（任务消息不混入其他页面）
  const list = getSessionMessages(currentSessionId.value, conversationScenario.value);
  const count = Math.min(visibleMessageCount.value, list.length);
  const persisted = list.slice(list.length - count);
  const routeName = currentRouteName.value;
  const sid = currentSessionId.value;
  const eph = ephemeralAssistantNotices.value
    .filter((n) => n.routeName === routeName && n.sessionId === sid)
    .map((n) => ({
      id: n.id,
      role: 'assistant' as const,
      content: n.content,
      createdAt: n.createdAt,
    }));
  return eph.length ? [...persisted, ...eph] : persisted;
});

watch(currentRouteName, (name, prev) => {
  if (prev === 'ProjectCreate' && name !== 'ProjectCreate') {
    ephemeralAssistantNotices.value = ephemeralAssistantNotices.value.filter(
      (n) => n.routeName !== 'ProjectCreate',
    );
  }
});

/** 持久化与列表查询使用的场景，与当前会话一致 */
const persistenceScenario = computed(() => conversationScenario.value || DEFAULT_CONVERSATION_SCENARIO);

/** 本地缓存的会话标题（LLM 生成或任务推断），key 为 scenario + sessionId */
const sessionTitleBySessionId = ref<Record<string, string>>({});
/** 已尝试生成/持久化标题的 scenario + sessionId */
const sessionTitlePersisted = ref(new Set<string>());

function readSessionTitleCache(sessionId: string, scenario = persistenceScenario.value): string {
  return sessionTitleBySessionId.value[sessionTitleCacheKey(sessionId, scenario)]?.trim() || '';
}

function cacheSessionTitle(sessionId: string, title: string, scenario = persistenceScenario.value) {
  sessionTitleBySessionId.value = {
    ...sessionTitleBySessionId.value,
    [sessionTitleCacheKey(sessionId, scenario)]: title,
  };
}

/** 队列任务执行期间绑定的 sessionId（与 UI 当前会话可能不同） */
const taskBoundSessionId = ref<string | null>(null);
/** 队列任务执行期间绑定的会话场景（切换页签后仍写入发起任务时的场景） */
const taskBoundScenario = ref<string | null>(null);

function effectiveSessionId(): string {
  return taskBoundSessionId.value ?? currentSessionId.value;
}

function effectiveScenario(): string {
  return taskBoundScenario.value ?? conversationScenario.value;
}

function effectivePersistenceScenario(): string {
  return taskBoundScenario.value ?? persistenceScenario.value;
}

/** 仅在与任务绑定场景一致时展示进行中的思考/加载态（避免切换页签后串台） */
const isTaskThinkingVisible = computed(
  () => isThinking.value
    && (!taskBoundScenario.value || taskBoundScenario.value === conversationScenario.value)
    && currentRouteName.value !== 'AiCalculatorDetail'
    && currentRouteName.value !== 'AiCalculatorFormulaShare',
);
const isTaskLoadingVisible = computed(
  () => aiLoading.value
    && (!taskBoundScenario.value || taskBoundScenario.value === conversationScenario.value)
    && currentRouteName.value !== 'AiCalculatorDetail'
    && currentRouteName.value !== 'AiCalculatorFormulaShare',
);

function getSessionMessages(sessionId = effectiveSessionId(), scenario = conversationScenario.value) {
  return store.getMessages(scenario, sessionId);
}

function addSessionMessage(
  role: 'user' | 'assistant',
  content: string,
  attachments?: ChatMessageAttachment[],
  thought?: string,
  structuredResult?: Record<string, unknown> | null,
  sessionId = effectiveSessionId(),
  scenario = effectiveScenario(),
) {
  const msg = store.addMessage(scenario, sessionId, role, content, attachments, thought, structuredResult);
  const action = activeSessionAction.value;
  if (action && !action.discarded && role === 'assistant' && !action.messageId) {
    action.messageId = msg.id;
  }
  return msg;
}

function updateSessionMessage(
  id: string,
  patch: Partial<ChatMessage>,
  sessionId = effectiveSessionId(),
  scenario = effectiveScenario(),
) {
  store.updateMessage(scenario, id, patch, sessionId);
}

function setSessionMessages(
  messages: ChatMessage[],
  sessionId = effectiveSessionId(),
  scenario = effectiveScenario(),
) {
  store.setMessages(scenario, messages, sessionId);
}

function clearSessionMessages(sessionId = effectiveSessionId(), scenario = effectiveScenario()) {
  store.clearConversation(scenario, sessionId);
}

function resolveSessionTitle(sessionId: string): string | null {
  const cached = readSessionTitleCache(sessionId);
  if (cached) return cached;
  const fromHistory = historyList.value.find((h) => h.sessionId === sessionId)?.title?.trim();
  if (fromHistory && fromHistory !== '对话') return fromHistory;
  return null;
}

async function applySessionTitle(sessionId: string, rawTitle: string, scenario = persistenceScenario.value) {
  const title = clampSessionTitle(rawTitle);
  if (!title || title === '对话') return;
  cacheSessionTitle(sessionId, title, scenario);
  if (sessionId === currentSessionId.value) {
    await persistConversation(title, sessionId, scenario);
  } else {
    try {
      const item = historyList.value.find((h) => h.sessionId === sessionId);
      if (item?.id) {
        await renameAgentConversation({ id: item.id, title });
        await loadConversationHistory();
      }
    } catch {
      /* ignore */
    }
  }
  sessionTitlePersisted.value.add(sessionTitleCacheKey(sessionId, scenario));
}

async function maybeGenerateSessionTitleAfterFirstTurn(
  sessionId: string,
  scenario = conversationScenario.value,
  thoughtRaw?: unknown,
) {
  const titleKey = sessionTitleCacheKey(sessionId, scenario);
  if (sessionTitlePersisted.value.has(titleKey)) return;
  const messages = getSessionMessages(sessionId, scenario);
  const userCount = messages.filter((m) => m.role === 'user').length;
  const assistantCount = messages.filter((m) => m.role === 'assistant').length;
  if (userCount < 1 || assistantCount < 1) return;
  const firstUser = messages.find((m) => m.role === 'user');
  const firstAssistant = messages.find((m) => m.role === 'assistant');
  const fromThought = extractSessionTitleFromThought(thoughtRaw);
  const fromTask = firstUser ? inferTaskSessionTitle(firstUser.content) : null;

  let title = fromThought || fromTask || null;
  // 流式意图路由未带标题、也非任务标签时：走后端 LLM 概括（覆盖各 scenario，不依赖 ai-service 意图节点）
  if (!title && firstUser?.content?.trim()) {
    try {
      const suggested = await suggestAgentSessionTitle({
        message: normalizeMessageForTitleSuggest(firstUser.content) || firstUser.content.trim(),
        scenario,
        routeName: currentRouteName.value || undefined,
        assistantHint: (firstAssistant?.content || '').trim().slice(0, 200) || undefined,
      });
      const clamped = clampSessionTitle(suggested);
      if (clamped && clamped !== '对话') title = clamped;
    } catch (e) {
      console.warn('LLM 会话标题生成失败，使用兜底截断', e);
    }
  }
  if (!title && firstUser) {
    title = fallbackSessionTitle(firstUser.content);
  }
  if (!title) return;
  // 必须把 scenario 传下去：applySessionTitle 默认取全局 persistenceScenario，
  // 与本次调用绑定的 scenario 不一致时会把同一个 sessionId 写成另一条库记录。
  await applySessionTitle(sessionId, title, scenario);
}

async function runInAiTaskQueue<T>(opts: {
  kind: AiTaskKind;
  label: string;
  sessionId?: string;
  sessionAction?: AiSessionAction;
  lifecycle?: AiTaskLifecycle;
  run: (boundSessionId: string, ctx?: AiTaskRunContext) => Promise<T>;
}): Promise<T> {
  if (!(await resolvePendingRevertBeforeSend())) {
    throw new TaskCancelledError();
  }
  const boundSessionId = opts.sessionId ?? currentSessionId.value;
  const scopeKey = persistenceScenario.value;
  const boundScenario = scopeKey;
  const isCurrent = boundSessionId === currentSessionId.value && boundScenario === conversationScenario.value;
  if (isCurrent) {
    aiLoading.value = true;
  }
  try {
    const result = await taskQueue.enqueue({
      scopeKey,
      sessionId: boundSessionId,
      kind: opts.kind,
      label: opts.label,
      lifecycle: opts.lifecycle,
      execute: async (ctx) => {
        // 只有任务真正开始执行时才成为当前动作；排队任务不能覆盖正在执行的动作。
        if (opts.sessionAction) {
          activeSessionAction.value = opts.sessionAction;
        }
        taskBoundSessionId.value = boundSessionId;
        taskBoundScenario.value = boundScenario;
        try {
          return await opts.run(boundSessionId, ctx);
        } finally {
          taskBoundSessionId.value = null;
          taskBoundScenario.value = null;
        }
      },
    });
    markTaskSessionUnreadIfNeeded(boundSessionId, scopeKey);
    return result;
  } catch (e) {
    markTaskSessionUnreadIfNeeded(boundSessionId, scopeKey);
    if (!isTaskCancelledError(e)) {
      throw e;
    }
    return undefined as T;
  } finally {
    if (isCurrent && !taskQueue.isScopeBusy(scopeKey)) {
      aiLoading.value = false;
      isThinking.value = false;
      currentThoughtDraft.value = [];
    }
  }
}

async function onCancelQueueTask(taskId: string) {
  const task = scopeActiveQueueTasks.value.find((t) => t.id === taskId);
  if (!task || (task.status !== 'running' && task.status !== 'paused' && task.status !== 'queued')) return;
  if (task.kind === 'project_create') {
    try {
      await ElMessageBox.confirm(
        '终止后将不会继续生成项目，确定终止吗？',
        '终止生成',
        {
          type: 'warning',
          // Element Plus recalculates zIndex on open; modalClass below forces the overlay above ExpandedAiComposer.
          zIndex: AI_SESSION_CONFIRM_Z_INDEX,
          modalClass: AI_SESSION_CONFIRM_MODAL_CLASS,
          confirmButtonText: '终止生成',
          cancelButtonText: '继续生成',
        },
      );
    } catch {
      return;
    }
  }
  if (task.kind === 'chat' || task.kind === 'project_create') {
    stopCurrentStream();
  }
  // 取消排队中的单项：仅中止，不自动回退
  if (task.status === 'running' || task.status === 'paused') {
    void softStopTask(task);
  } else {
    void taskQueue.stopTask(taskId);
  }
}

async function markActiveSessionDiscardedAndRestore() {
  const action = activeSessionAction.value;
  if (!action || action.discarded) return;
  action.discarded = true;
  try {
    await action.discard();
  } catch {
    /* ignore */
  }
}

/** 发送前快照，终止后写回输入框 */
const lastSubmittedComposerDraft = ref('');
const pendingComposerRestore = ref<string | null>(null);
const pendingTerminatedRevert = ref<PendingTerminatedRevert | null>(null);

function snapshotComposerDraft(text: string) {
  lastSubmittedComposerDraft.value = String(text ?? '');
}

function restoreComposerAfterStop() {
  const draft = lastSubmittedComposerDraft.value;
  if (!String(draft ?? '').trim()) return;
  pendingComposerRestore.value = draft;
  setComposerDraftMessage(draft);
}

function consumeRestoredComposerDraft(): string | null {
  const v = pendingComposerRestore.value;
  pendingComposerRestore.value = null;
  return v;
}

/**
 * 软终止：中止执行、暂缓排队、恢复输入；不自动回退项目修改。
 * 标记 action.discarded 以丢弃晚到结果，但保留 discard 供再次发送时确认。
 */
async function softStopTask(task: { id: string; label: string; kind: string; sessionId: string }) {
  const action = activeSessionAction.value;
  if (action && !action.discarded) {
    pendingTerminatedRevert.value = {
      label: action.label || task.label,
      discard: action.discard,
    };
    action.discarded = true;
  }
  await taskQueue.stopTask(task.id);
  // 仅对话类任务恢复输入框；工具任务（BOM 等）通常无用户输入草稿
  if (task.kind === 'chat' || task.kind === 'project_create') {
    restoreComposerAfterStop();
  }
  if (task.sessionId === currentSessionId.value) {
    isThinking.value = false;
    aiLoading.value = false;
    currentThoughtDraft.value = [];
    addSessionMessage('assistant', stoppedDiscardMessage(task.label), undefined, undefined, {
      kind: 'ai_session_control',
      status: 'stopped',
      taskKind: task.kind,
    });
    void persistConversation();
  }
  ElMessage.info(stoppedDiscardMessage(task.label));
}

async function onStopScopeTask() {
  const task = scopeRunningTask.value ?? scopePausedTask.value;
  if (!task) {
    if (isStreaming.value || streamAbortController.value || smartDocSkillAuthor.sending || smartDocTemplateAuthor.sending || smartDocTemplateGenerate.sending || smartDocAi.sending) {
      stopCurrentStream();
      restoreComposerAfterStop();
    }
    return;
  }
  if (task.kind === 'chat' || task.kind === 'project_create' || isStreaming.value) {
    stopCurrentStream();
  }
  await softStopTask(task);
}

async function onComposerStopClick() {
  await onStopScopeTask();
}

function onPauseScopeTask() {
  // 兼容旧入口：统一为停止（软终止，不自动回退）
  void onStopScopeTask();
}

/** 再次发送前：若终止后仍有可回退的项目修改，询问用户 */
async function resolvePendingRevertBeforeSend(): Promise<boolean> {
  const pending = pendingTerminatedRevert.value;
  if (!pending) return true;
  const choice = await confirmPendingRevertBeforeSend(pending.label);
  if (choice === 'abort') return false;
  if (choice === 'revert') {
    try {
      await pending.discard();
      ElMessage.success('已回退上次任务对项目的修改');
    } catch {
      /* ignore */
    }
  }
  pendingTerminatedRevert.value = null;
  return true;
}

function onResumeScopeTask() {
  const task = scopePausedTask.value;
  if (!task) return;
  taskQueue.resumeTask(task.id);
  if (task.sessionId === currentSessionId.value && taskBoundScenario.value === conversationScenario.value) {
    aiLoading.value = true;
    isThinking.value = true;
  }
}

async function onRollbackScopeTask() {
  await onRollbackSessionAction();
}

async function onRollbackSessionAction() {
  const action = activeSessionAction.value;
  const pending = pendingTerminatedRevert.value;
  const pausedOrRunning = scopePausedTask.value ?? scopeRunningTask.value;
  if (!action && !pausedOrRunning && !pending) return;
  const label = action?.label || pending?.label || pausedOrRunning?.label || '本次 AI 结果';
  const ok = await confirmAiSessionRollback(label);
  if (!ok) return;
  if (pending) {
    try {
      await pending.discard();
    } catch {
      /* ignore */
    }
    pendingTerminatedRevert.value = null;
  } else if (action && !action.discarded) {
    action.discarded = true;
    try {
      await action.discard();
    } catch {
      /* ignore */
    }
  }
  if (pausedOrRunning) {
    await taskQueue.rollbackTask(pausedOrRunning.id);
  }
  if (!pausedOrRunning || pausedOrRunning.sessionId === currentSessionId.value) {
    isThinking.value = false;
    aiLoading.value = false;
    currentThoughtDraft.value = [];
  }
  addSessionMessage('assistant', `已回退「${label}」，项目已恢复为会话之前的状态。`, undefined, undefined, {
    kind: 'ai_session_control',
    status: 'rolled_back',
  });
  void persistConversation();
  ElMessage.success('已回退到会话之前的状态');
}

async function onRetrySessionAction() {
  const action = activeSessionAction.value;
  if (!action) {
    ElMessage.warning('暂无可重试的任务，请从项目页重新发起');
    return;
  }
  const ok = await confirmAiSessionRetry(action.label);
  if (!ok) return;
  const running = scopeRunningTask.value ?? scopePausedTask.value;
  if (running) {
    action.discarded = true;
    try {
      await action.discard();
    } catch {
      /* ignore */
    }
    await taskQueue.stopTask(running.id);
  } else if (!action.discarded) {
    action.discarded = true;
    try {
      await action.discard();
    } catch {
      /* ignore */
    }
  }
  try {
    await action.retry();
  } catch (e: unknown) {
    if (!isTaskCancelledError(e)) {
      const err = e as { message?: string };
      ElMessage.error(err?.message || '重试失败');
    }
  }
}

function onContinueScopeQueue() {
  taskQueue.continueScopeQueue(persistenceScenario.value);
}

const activeSessionAction = ref<AiSessionAction | null>(null);

function beginAiSessionAction(opts: {
  kind: AiTaskKind;
  label: string;
  discard: () => void | Promise<void>;
  retry: () => Promise<void>;
}): { generation: number; action: AiSessionAction } {
  const generation = nextAiSessionGeneration();
  const action: AiSessionAction = {
    kind: opts.kind,
    label: opts.label,
    generation,
    discarded: false,
    discard: opts.discard,
    retry: opts.retry,
  };
  return { generation, action };
}

const scopeQueuedCount = computed(() => taskQueue.queuedCountForScope(persistenceScenario.value));
const scopeRunningTask = computed(() => taskQueue.runningTaskForScope(persistenceScenario.value));
const scopePausedTask = computed(() => taskQueue.pausedTaskForScope(persistenceScenario.value));
const scopeQueueHeld = computed(() => taskQueue.isScopeQueueHeld(persistenceScenario.value));
const scopeActiveQueueTasks = computed(() =>
  taskQueue.activeTasksForScope(persistenceScenario.value),
);
const showComposerTaskStop = computed(
  () =>
    (!!scopeRunningTask.value && !isStreaming.value)
    || (
      smartDocAi.active
      && (smartDocSkillAuthor.sending || smartDocTemplateAuthor.sending || smartDocTemplateGenerate.sending || smartDocAi.sending)
      && !isStreaming.value
    ),
);
const SESSION_ACTION_OWN_MESSAGE_BUTTON_KINDS = new Set<AiTaskKind>([
  'wbs_generate',
  'sor_requirement_analyze',
  'document_deep_review',
]);

/** 输入框始终保留发送；回退/重试只出现在对应会话消息上 */
const showComposerRetryRollback = computed(() => false);

function sessionActionMessageActionsVisible(msg: ChatMessage): boolean {
  const action = activeSessionAction.value;
  if (!action || action.discarded) return false;
  if (SESSION_ACTION_OWN_MESSAGE_BUTTON_KINDS.has(action.kind)) return false;
  if (pendingTerminatedRevert.value) return false;
  if (scopeRunningTask.value || scopePausedTask.value || isStreaming.value || aiLoading.value) {
    return false;
  }
  const sr = msg.structuredResult as Record<string, unknown> | null | undefined;
  // 任务重分配：回退/重试只出现在结果消息上，避免挂在确认气泡（位于正式回复之前）
  if (action.kind === 'wbs_task_reassign') {
    if (sr?.kind !== 'wbs_task_reassign') return false;
    const st = sr?.status;
    if (st !== 'completed' && st !== 'failed') return false;
  }
  if (action.messageId) return msg.id === action.messageId;
  if (sr?.kind !== action.kind) return false;
  const last = [...getSessionMessages()].reverse().find(
    (m) => m.role === 'assistant'
      && (m.structuredResult as Record<string, unknown> | null | undefined)?.kind === action.kind,
  );
  return last?.id === msg.id;
}

async function rollbackSessionActionFromMessage(msg: ChatMessage) {
  if (!sessionActionMessageActionsVisible(msg)) {
    ElMessage.warning('无法回退：操作上下文已失效，请重新发起任务。');
    return;
  }
  await onRollbackSessionAction();
}

async function retrySessionActionFromMessage(msg: ChatMessage) {
  if (!sessionActionMessageActionsVisible(msg)) {
    ElMessage.warning('无法重试：操作上下文已失效，请重新发起任务。');
    return;
  }
  await onRetrySessionAction();
}

const floatBtnWorking = computed(
  () =>
    isCollapsed.value
    && (isThinking.value || aiLoading.value || isStreaming.value || scopeActiveQueueTasks.value.length > 0),
);

const scopeQueueHint = computed(() => {
  const queued = scopeQueuedCount.value;
  const running = scopeRunningTask.value;
  if (!queued && !running) return '';
  if (running && queued) {
    return `正在执行「${running.label}」，另有 ${queued} 个任务排队中`;
  }
  if (running) return `正在执行：${running.label}`;
  return `${queued} 个任务排队中`;
});

const COMPONENT_SCENARIO = 'component_library';
const CALCULATOR_SCENARIO_PREFIX = 'calculator_';
const LAST_SESSION_KEY_PREFIX = 'aipd_ai_last_session_';
const LAST_NON_COMPONENT_SCENARIO_KEY = 'aipd_ai_last_non_component_scenario';

function lastSessionStorageKey(scenario: string): string {
  return `${LAST_SESSION_KEY_PREFIX}${getUserSessionScopeSuffix()}_${scenario}`;
}

function lastNonComponentScenarioStorageKey(): string {
  return `${LAST_NON_COMPONENT_SCENARIO_KEY}${getUserSessionScopeSuffix()}`;
}

function readLastSessionId(scenario: string): string | null {
  try {
    return localStorage.getItem(lastSessionStorageKey(scenario));
  } catch {
    return null;
  }
}

function writeLastSessionId(scenario: string, sessionId: string) {
  try {
    if (!scenario || !sessionId) return;
    localStorage.setItem(lastSessionStorageKey(scenario), sessionId);
  } catch {
    // ignore
  }
}

function readLastNonComponentScenario(): string | null {
  try {
    return localStorage.getItem(lastNonComponentScenarioStorageKey());
  } catch {
    return null;
  }
}

function writeLastNonComponentScenario(scenario: string) {
  try {
    if (!scenario) return;
    localStorage.setItem(lastNonComponentScenarioStorageKey(), scenario);
  } catch {
    // ignore
  }
}

// 测试阶段临时关闭后端会话持久化，避免数据库结构异常影响 AI 对话主链路
const ENABLE_CONVERSATION_PERSISTENCE = true;

/** 可发送：有文本或已上传完成的附件，或已挂起字段引用，且无正在上传项（AI 任务排队时不阻塞输入） */
const canSend = computed(() => {
  const hasPcFieldRef =
    !!projectCreateFieldEditPending.value &&
    currentRouteName.value === 'ProjectCreate';
  const hasPageFieldRef =
    !!pageContentEditPending.value &&
    currentRouteName.value !== 'ProjectCreate';
  const hasSmartDocSkill = smartDocAi.active && !!smartDocAi.selectedSkillId;
  const hasSmartDocTemplate = smartDocAi.hasTemplateAtToken
    || smartDocTemplateGenerate.canContinue
    || textHasSmartDocTemplateMention(aiInput.value);
  return (
    (aiInput.value.trim() ||
      attachmentList.value.some((a) => a.progress >= 100 && !a.error) ||
      hasPcFieldRef ||
      hasPageFieldRef ||
      hasSmartDocSkill ||
      hasSmartDocTemplate) &&
    attachmentList.value.every((a) => a.progress >= 100 || a.error != null)
  );
});

const sessionTabs = computed(() =>
  historyList.value.map((item) => ({
    id: item.id,
    sessionId: item.sessionId,
    title: item.title || '对话',
  })),
);

const lastAssistantMessage = computed(() => {
  const list = aiMessages.value;
  const last = [...list].reverse().find((m) => m.role === 'assistant');
  return last?.content.slice(0, 40);
});

// 检测是否在计算器场景
const isCalculatorScenario = computed(() => {
  const s = conversationScenario.value;
  return (
    s === CALCULATOR_FORMULA_IMPORT_SCENARIO
    || s === CALCULATOR_GENERAL_SCENARIO
    || s.startsWith(CALCULATOR_SCENARIO_PREFIX)
    || s.startsWith('calc_')
  );
});

// 获取当前计算器名称
const calculatorName = computed(() => {
  if (!isCalculatorScenario.value) return '';
  if (conversationScenario.value === CALCULATOR_FORMULA_IMPORT_SCENARIO) {
    return '公式创建';
  }
  const meta =
    calcSessionMeta.value || loadCalcSessionMeta(conversationScenario.value);
  if (meta?.formulaName) return meta.formulaName;
  const formulaId = conversationScenario.value.replace(CALCULATOR_SCENARIO_PREFIX, '');
  const builtinNames: Record<string, string> = {
    enclosure_heat_loss: '箱体漏热计算',
    dew_check: '凝露校核计算',
    heat_load: '热负荷计算',
    thermo_cycle: '热力计算',
    compressor_selection: '压缩机选型计算',
    condenser_sizing: '冷凝器选型计算',
    evaporator_sizing: '蒸发器选型计算',
    fan_sizing: '风扇选型计算',
  };
  return builtinNames[formulaId] || '自定义计算器';
});

const inputPlaceholder = computed(() => {
  if (conversationScenario.value === 'calculator_formula_import') {
    return '说明要生成全部或部分公式，或描述需要调整的内容';
  }
  if (isCalculatorScenario.value) {
    return `输入参数值进行${calculatorName.value}，或询问相关问题`;
  }
  if (smartDocAi.active) {
    return '输入消息，或键入 @ 引用技能 / 模板 / 项目';
  }
  if (currentRouteName.value === 'ProjectCreate') {
    if (projectCreateFieldEditPending.value) {
      return '补充修改说明（可留空，将按上下文润色引用片段）';
    }
    return '在左侧选中片段并点「修改」，或在此说明要如何调整';
  }
  if (pageContentEditPending.value) {
    return '补充修改说明（可留空，将按上下文润色引用片段）';
  }
  if (hasProjectContext.value) {
    return '基于当前项目，告诉我你需要什么帮助';
  }
  if (currentRouteName.value === 'Dashboard') {
    return '例如：帮我筛选需要重点关注的项目';
  }
  return '您需要我做什么呢？';
});


function toggleCollapsedThought(messageId: string) {
  const next = new Set(expandedThoughtIds.value);
  if (next.has(messageId)) next.delete(messageId);
  else next.add(messageId);
  expandedThoughtIds.value = next;
}

function normalizeIntentText(text: string): string {
  return String(text ?? '').toLowerCase().replace(/\s+/g, '');
}

function isWbsTaskCreationIntent(text: string): boolean {
  const t = normalizeIntentText(text);
  if (!t) return false;
  // This local shortcut writes before AI interpretation: accept only an affirmative
  // command at the start, never a creator relationship, query or mixed request.
  const routing = t.replace(/“[^”]*”|"[^"]*"|‘[^’]*’|'[^']*'/g, '');
  if (/(?:怎么|怎样|如何|哪里|在哪|为什么|为啥|什么|谁|哪些|多少|几个|查询|查看|检索|统计|列出|有没有|是否|吗|呢|？|\?)/.test(routing)) return false;
  if (/(?:不要|别|不用|不需要|无需|不能|禁止|勿)/.test(routing)) return false;
  if (/(?:并且|同时|然后|之后|顺便|接着|并|再)/.test(routing)) return false;
  const issueOnly = ['问题单', '新问题', '创建问题', '新建问题', '新增问题', 'createissue', 'newissue'];
  if (issueOnly.some((k) => t.includes(k))) return false;
  return /^(?:(?:请|麻烦)?(?:帮我|为我|给我|我想|我要|我需要)?)(?:创建|新建|新增|建)(?:一[个条项])?(?:wbs)?任务(?=[:：“"‘']|$)/.test(t)
    || /^(?:please)?(?:create(?:a|an)?task|newtask)[:：]/.test(t);
}

function extractWbsTaskDraftFromText(text: string): { title: string; description?: string; assigneeHint?: string } {
  const raw = String(text ?? '').trim();
  let title = raw
    .replace(/^(?:请|帮我|麻烦)?(?:创建|新建|新增)(?:一个|一条)?任务[:：\s]*/i, '')
    .replace(/^(?:任务标题|标题)[:：\s]*/i, '')
    .trim();
  if (!title) title = raw.slice(0, 80) || '未命名任务';
  const assigneeMatch = raw.match(/(?:处理人|负责人|指派给|交给)[:：\s]*([^\s，,。；;]+)/);
  const assigneeHint = assigneeMatch?.[1]?.trim();
  return {
    title: title.length > 120 ? `${title.slice(0, 117)}…` : title,
    description: raw,
    assigneeHint,
  };
}

async function tryCreateWbsTaskFromChat(text: string): Promise<boolean> {
  if (!isWbsTaskCreationIntent(text) || projectId.value <= 0) return false;
  const draft = extractWbsTaskDraftFromText(text);
  let assigneeUserIds: number[] | undefined;
  try {
    // 先写入用户消息，再回复
    addSessionMessage('user', text);
    const members = await listProjectMemberCandidates('');
    if (draft.assigneeHint) {
      const hint = draft.assigneeHint.toLowerCase();
      const hit = members.find((m) => {
        const name = (m.realName || '').toLowerCase();
        const pos = (m.position || '').toLowerCase();
        return name.includes(hint) || hint.includes(name) || (pos && pos.includes(hint));
      });
      if (hit?.userId) {
        assigneeUserIds = [hit.userId];
      } else {
        addSessionMessage(
          'assistant',
          `已识别创建任务「${draft.title}」，但处理人「${draft.assigneeHint}」不在可选成员中。请在侧栏新建任务时从项目成员中选择，或重新说明处理人姓名。`,
        );
        await persistConversation();
        return true;
      }
    }
    const { createWbsTask } = await import('@/api/taskApi');
    const { notifyProjectWbsTasksChanged } = await import('@/utils/wbsTaskEvents');
    const created = await createWbsTask(projectId.value, {
      title: draft.title,
      description: draft.description,
      assigneeUserIds,
      status: 'PENDING_CONFIRM',
    });
    addSessionMessage(
      'assistant',
      `已创建 WBS 任务 ${created.taskCode || ''}「${created.title}」，可在左侧任务计划中查看与编辑。`,
    );
    notifyProjectWbsTasksChanged(projectId.value);
    await persistConversation();
    return true;
  } catch (e: any) {
    addSessionMessage('assistant', `创建任务失败：${e?.message || '请稍后重试'}`);
    await persistConversation();
    return true;
  }
}

function isIssueOrTaskCreationIntent(text: string): boolean {
  const t = normalizeIntentText(text);
  if (!t) return false;
  const explicitNewProject = ['新项目', '新建项目', '创建项目', '立项', '开项目', '新增项目'];
  const issueMarkers = [
    '问题单',
    '新问题',
    '创建问题',
    '新建问题',
    '新增问题',
    '录问题',
    '提问题',
    '创建任务',
    '新建任务',
    '新增任务',
    'createissue',
    'newissue',
    'createtask',
  ];
  if (explicitNewProject.some((k) => t.includes(k)) && !issueMarkers.some((k) => t.includes(k))) {
    return false;
  }
  if (issueMarkers.some((k) => t.includes(k))) return true;
  return /(?:创建|新建|新增|帮我创建|帮我新建).{0,12}(?:问题|任务|issue|task)/.test(t);
}

function isCreateProjectIntent(text: string): boolean {
  if (isIssueOrTaskCreationIntent(text)) return false;
  if (isRenameExistingProjectIntent(text)) return false;
  const t = normalizeIntentText(text);
  // 疑问句是在咨询「怎么创建项目」的操作方法，应交给一诺知识库回答，不当作创建指令（与后端意图分类保持一致）
  if (/(?:怎么|怎样|如何|哪里|在哪|为什么|为啥|是什么|什么流程|什么步骤|吗|呢|？|\?)/.test(t)) return false;
  // 「生成项目周报/日报」「项目进度」等是查已有项目，勿因「生成项目」子串误判为立项
  if (isProjectStatusOrReportIntent(t)) {
    return false;
  }
  const createKeywords = [
    '创建项目',
    '新建项目',
    '新增项目',
    '立项',
    '创建一个项目',
    '新建一个项目',
    '做一个项目',
    '帮我做一个项目',
    '生成项目',
    '项目创建',
    '创建这个项目',
    '新项目',
  ];
  if (createKeywords.some((k) => t.includes(k))) return true;
  // 「做一个 X 项目」中间插词时字面量匹配不上，动词表与后端保持一致；
  // 不用裸「做」以免「做完了这个项目」被误判为创建。
  // 间隔上限 60：项目名在 DB/创建页表单的真实上限是 255，口语能说出的项目名不会超过 60 字；
  // 上限过窄会把长名称创建指令漏判成查询项目、误弹项目选择器（曾为 8，后对齐抽取截断调到 30 仍不够）
  return /(?:创建|新建|新增|立项|生成|做一个|做个).{0,60}(?:项目|项目方案|项目文档|项目计划)/.test(t) && !/(已有|现有|选择|查询|分析|查看).{0,8}项目/.test(t);
}

/**
 * 改已有项目名称（非新建）。「请将项目名称改成…」曾因含「项目名称」被当成创建补充而误进创建流程。
 */
function isRenameExistingProjectIntent(text: string): boolean {
  const t = normalizeIntentText(text);
  if (!t) return false;
  if (/(?:怎么|怎样|如何|哪里|在哪|为什么|为啥|是什么|什么流程|什么步骤|吗|呢|？|\?)/.test(t)) return false;
  // 明确新建口令优先，避免「创建项目，名称改成 XX」被当成改名
  if (/(?:创建|新建|新增|立项|开一个|做一个|帮我做一个|帮我创建|帮我新建).{0,24}项目/.test(t)
    && !/(?:改|换|修改|更改|更新).{0,8}(?:项目)?名称/.test(t)) {
    return false;
  }
  return /(?:请)?(?:将|把)?(?:本|当前|这个)?(?:项目的?)?(?:项目名称|项目名|名称).{0,8}(?:改成|改为|换成|修改为|更新为|变更为)/.test(t)
    || /(?:改|修改|更改|更新|变更)(?:一下)?(?:本|当前|这个)?(?:项目的?)?(?:项目名称|项目名)/.test(t)
    || /(?:项目名称|项目名).{0,6}(?:改成|改为|换成|修改为|更新为|变更为)/.test(t);
}

function extractRenamedProjectName(text: string): string {
  const raw = String(text ?? '').trim();
  const patterns = [
    /(?:将|把)\s*(?:本|当前|这个)?(?:项目的?)?(?:项目名称|项目名|名称)\s*(?:改成|改为|换成|修改为|更新为|变更为)\s*[「『《"'“‘]?([^」』》"'”’\n]{1,255})/,
    /(?:请将|请把)\s*(?:本|当前|这个)?(?:项目的?)?(?:项目名称|项目名|名称)\s*(?:改成|改为|换成|修改为|更新为|变更为)\s*[「『《"'“‘]?([^」』》"'”’\n]{1,255})/,
    /(?:项目名称|项目名|名称)\s*(?:改成|改为|换成|修改为|更新为|变更为)\s*[「『《"'“‘]?([^」』》"'”’\n]{1,255})/,
    /(?:改|修改|更改|更新)(?:一下)?(?:本|当前|这个)?(?:项目的?)?(?:项目名称|项目名)\s*(?:为|成|到)?\s*[「『《"'“‘]?([^」』》"'”’\n]{1,255})/,
  ];
  for (const re of patterns) {
    const m = raw.match(re);
    if (m?.[1]) {
      return m[1]
        .replace(/^[\s"'「『《“‘]+|[\s"'」』》。，！？!?：:；;]+$/g, '')
        .trim();
    }
  }
  return '';
}

async function tryRenameExistingProjectFromChat(text: string): Promise<boolean> {
  if (!isRenameExistingProjectIntent(text) || projectId.value <= 0) return false;
  // 创建页上的「改名称」仍走创建草稿增量，不在此落库
  if (currentRouteName.value === 'ProjectCreate') return false;
  const nextName = extractRenamedProjectName(text);
  addSessionMessage('user', text);
  if (!nextName) {
    addSessionMessage(
      'assistant',
      '已识别到要修改项目名称，但未解析出新名称。请再说一次，例如：请将项目名称改成「问界车载冰箱定制项目202612」。',
    );
    await persistConversation();
    return true;
  }
  try {
    await saveProjectFields(projectId.value, { name: nextName });
    updateProjectDashboardTabLabel(projectId.value, nextName);
    notifyProjectInfoChanged({ projectId: projectId.value, name: nextName });
    addSessionMessage(
      'assistant',
      `已将当前项目名称更新为「${nextName}」。左侧「项目信息」与页签标题会同步刷新。`,
    );
    await persistConversation();
    return true;
  } catch (e: any) {
    addSessionMessage('assistant', `修改项目名称失败：${e?.message || '请稍后重试'}`);
    await persistConversation();
    return true;
  }
}

/** 查已有项目状态/报告：即使上一轮在创建项目会话中，也不应再走「创建项目」队列 */
function isProjectStatusOrReportIntent(text: string): boolean {
  const t = normalizeIntentText(text);
  return /(?:周报|日报)/.test(t)
    || /(?:查询|查看|分析|统计).{0,8}项目/.test(t)
    || /项目(?:进度|风险|状态|资源|逾期|延期)/.test(t);
}

/** 智能文档场景：生成日报/周报并落成工作台文档 */
function isSmartDocDeliverableIntent(text: string): boolean {
  const t = normalizeIntentText(text);
  if (/@\s*模板|基于\s*模板|使用\s*模板/.test(t)) return false;
  const kindHit = /(?:周报|日报|会议纪要|纪要|总结|方案|报告)/.test(t);
  if (!kindHit) return false;
  return /(?:生成|写|起草|输出|帮我|整理|汇总|起草一份|输出一份)/.test(t)
    || /项目周报|项目日报|项目报告/.test(t);
}

function resolveSmartDocDeliverableKind(text: string): string {
  const t = normalizeIntentText(text);
  if (/周报/.test(t)) return '项目周报';
  if (/日报/.test(t)) return '项目日报';
  if (/会议纪要|纪要/.test(t)) return '会议纪要';
  if (/总结/.test(t)) return '项目总结';
  if (/方案/.test(t)) return '项目方案';
  if (/报告/.test(t)) return '项目报告';
  return '项目文档';
}

type SmartDocMoveAction = {
  documentId: string;
  suggestedProjectId?: string;
  suggestedProjectName?: string;
  offerMoveToProject?: boolean;
};

function getSmartDocMoveAction(msg: ChatMessage): SmartDocMoveAction | null {
  const raw = msg.structuredResult as Record<string, unknown> | null | undefined;
  const out = raw?.smartDocOutput as Record<string, unknown> | undefined;
  if (!out || out.kind !== 'created') return null;
  const documentId = String(out.documentId || '').trim();
  if (!documentId) return null;
  const ws = String(out.workspaceId ?? '0');
  // 个人智能文档工作台创建的文档，提供移入项目
  if (ws !== '0' && out.offerMoveToProject !== true) return null;
  return {
    documentId,
    suggestedProjectId: String(out.suggestedProjectId || '').trim() || undefined,
    suggestedProjectName: String(out.suggestedProjectName || '').trim() || undefined,
    offerMoveToProject: true,
  };
}

const smartDocMovingDocId = ref('');

async function onMoveSmartDocToProject(msg: ChatMessage) {
  const action = getSmartDocMoveAction(msg);
  if (!action?.documentId || smartDocMovingDocId.value) return;
  let projectId = action.suggestedProjectId || '';
  let projectName = action.suggestedProjectName || '';
  if (!/^\d+$/.test(projectId) || Number(projectId) <= 0) {
    try {
      const projects = await loadSmartDocAccessibleProjects();
      if (!projects.length) {
        ElMessage.warning('暂无可用项目，请先创建或加入项目');
        return;
      }
      if (projects.length === 1) {
        projectId = String(projects[0].id);
        projectName = projects[0].name;
        await ElMessageBox.confirm(
          `将文档移入项目「${projectName}」的工作台？`,
          '移入项目工作台',
          { type: 'info', confirmButtonText: '移入', cancelButtonText: '取消' },
        );
      } else {
        ElMessage.info('请先在输入框 @项目 指定目标，再点击「移入项目工作台」');
        return;
      }
    } catch (e) {
      if (e === 'cancel') return;
      ElMessage.error('加载项目列表失败');
      return;
    }
  } else {
    try {
      await ElMessageBox.confirm(
        `将文档移入项目「${projectName || projectId}」的工作台？`,
        '移入项目工作台',
        { type: 'info', confirmButtonText: '移入', cancelButtonText: '取消' },
      );
    } catch {
      return;
    }
  }
  smartDocMovingDocId.value = action.documentId;
  try {
    const moved = await moveWorkbenchDocumentToProject(action.documentId, projectId);
    ElMessage.success(`已移入项目工作台「${moved.title || projectName}」`);
    updateSessionMessage(msg.id, {
      content: `${msg.content}\n\n（已移入项目「${projectName || projectId}」工作台）`,
      structuredResult: {
        ...(msg.structuredResult || {}),
        smartDocOutput: {
          kind: 'moved',
          documentId: moved.documentId,
          title: moved.title,
          workspaceId: projectId,
          offerMoveToProject: false,
        },
      },
    });
    smartDocAi.markDocumentApplied(moved.documentId, projectId);
    await router.push({
      name: 'SmartDocumentWorkbench',
      params: { id: projectId },
      query: { documentId: moved.documentId },
    }).catch(() => undefined);
  } catch (e: unknown) {
    const m =
      e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
        ? String((e as { message: string }).message).trim()
        : '';
    ElMessage.error(m || '移入项目工作台失败');
  } finally {
    smartDocMovingDocId.value = '';
  }
}

/** 从助手 thought JSON 判断是否处于「创建新项目」会话（用于多轮补充信息，不要求再选已有项目） */
function thoughtJsonIndicatesCreateProject(thought: string | undefined): boolean {
  const raw = String(thought ?? '').trim();
  if (!raw) return false;
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    if (o.intent === 'CREATE_PROJECT') return true;
    if (o.create_project && typeof o.create_project === 'object') return true;
    const steps = o.steps;
    if (Array.isArray(steps)) {
      for (const s of steps) {
        if (!s || typeof s !== 'object') continue;
        const step = s as Record<string, unknown>;
        const key = String(step.key ?? '');
        const title = String(step.title ?? '');
        const text = String(step.text ?? '');
        if (key === 'intent' && text.includes('CREATE_PROJECT')) return true;
        if (title.includes('意图') && text.includes('CREATE_PROJECT')) return true;
      }
    }
  } catch {
    return /CREATE_PROJECT/i.test(raw);
  }
  return false;
}

function lastAssistantSuggestedCreateProject(): boolean {
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  const list = store.getMessages(scenario, sid);
  const lastAsst = [...list].reverse().find((m) => m.role === 'assistant');
  return thoughtJsonIndicatesCreateProject(lastAsst?.thought);
}

function currentProjectCreateDraftKey(): string {
  if (currentRouteName.value !== 'ProjectCreate') return '';
  const raw = route.query.draft;
  return String(Array.isArray(raw) ? raw[0] : raw ?? '').trim();
}

function historicalProjectContextRequested(text: string): boolean {
  const t = normalizeIntentText(text);
  const historicalMarker = /(?:以上|上述|上面|之前的?对话|历史会话|本次会话|这个会话|沿用)/.test(t);
  const relationWithContext = /(?:根据|基于|参考)/.test(t) && /(?:内容|对话|会话)/.test(t);
  return historicalMarker || relationWithContext;
}

function buildHistoricalProjectContext(sessionId: string, scenario: string): string {
  const rows = store.getMessages(scenario, sessionId)
    .filter((m) => String(m.content ?? '').trim())
    .slice(-12)
    .map((m) => {
      const role = m.role === 'user' ? '用户' : '一诺';
      const content = String(m.content ?? '').replace(/\s+/g, ' ').trim().slice(0, 800);
      return `${role}：${content}`;
    });
  return rows.join('\n').slice(0, 6000);
}

async function forkProjectCreateConversationForNewProject(text: string) {
  const scenario = conversationScenario.value;
  const sourceSessionId = currentSessionId.value;
  const sourceMessages = getSessionMessages(sourceSessionId, scenario);
  const sourceConversationContext = historicalProjectContextRequested(text)
    ? buildHistoricalProjectContext(sourceSessionId, scenario)
    : '';
  if (sourceMessages.length) {
    await persistConversation(undefined, sourceSessionId, scenario);
  }
  const sessionId = generateSessionId();
  currentSessionId.value = sessionId;
  selectedSessionId.value = sessionId;
  activeHistoryId.value = null;
  mainTab.value = 'new';
  visibleMessageCount.value = VISIBLE_MESSAGE_INITIAL;
  collapsedThoughtIds.value = new Set();
  writeLastSessionId(persistenceScenario.value, sessionId);
  return { sourceSessionId, sourceConversationContext, sessionId };
}

async function startProjectCreateNewProject(text: string, attachments: ChatMessageAttachment[]) {
  if (!canAccessProjectCreateRoute(permissionStore)) {
    await replyProjectCreateNoAccess(text, attachments);
    return;
  }
  const fromCreatePage = currentRouteName.value === 'ProjectCreate';
  const oldDraftKey = currentProjectCreateDraftKey();
  if (oldDraftKey) projectCreateDraftStore.autosaveLocal(oldDraftKey);
  const source = await forkProjectCreateConversationForNewProject(text);
  if (fromCreatePage) {
    const draftKey = projectCreateDraftStore.createDraft();
    await router.push({ name: 'ProjectCreate', query: { draft: draftKey } });
    if (currentRouteName.value !== 'ProjectCreate') {
      ElMessage.error('无法打开新的项目创建草稿');
      return;
    }
    await nextTick();
    await sendAiMessageWithProject('', text, attachments, {
      projectCreateMode: 'new_project',
      draftKey,
      sourceConversationId: source.sourceSessionId,
      sourceConversationContext: source.sourceConversationContext,
    });
    return;
  }
  await sendCreateProjectIntentResponse(text, attachments, {
    projectCreateMode: 'new_project',
    sessionId: source.sessionId,
    sourceConversationId: source.sourceSessionId,
    sourceConversationContext: source.sourceConversationContext,
  });
}

/**
 * 读取后端本轮在 thought 中下发的规范意图（IntentType）。
 * 前端关键词判定只做发送前的乐观分流，分流对错判的场景由后端意图当轮校正：
 * 优先取顶层 intent 字段，其次取 key='intent' 的思考步骤（text 形如「PROJECT_STATUS [low]」）。
 */
function extractBackendIntent(thought: string | undefined): string {
  const raw = String(thought ?? '').trim();
  if (!raw) return '';
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    if (typeof o.intent === 'string' && o.intent) return o.intent;
    const steps = o.steps;
    if (Array.isArray(steps)) {
      for (const st of steps) {
        if (!st || typeof st !== 'object') continue;
        const step = st as Record<string, unknown>;
        if (String(step.key ?? '') === 'intent') {
          return String(step.text ?? '').split(/[\s[]/)[0].trim();
        }
      }
    }
  } catch {
    /* thought 非 JSON 时返回空，不做关键词兜底（权威信号必须来自后端） */
  }
  return '';
}

/**
 * 用户在为「新项目」补充表单式描述（含项目名称等），且未同时表达查已有项目进度/风险时，
 * 避免仅因出现「项目」二字就要求从项目列表中选择。
 */
function isLikelyCreateProjectSupplementText(text: string): boolean {
  const t = normalizeIntentText(text);
  if (!t.includes('项目')) return false;
  // 「请将项目名称改成…」是改已有项目，不是创建补充
  if (isRenameExistingProjectIntent(t)) return false;
  const formMarkers = [
    '项目名称',
    '项目代号',
    '项目目标',
    '项目背景',
    '交付',
    '样件',
    '里程碑',
    'wbs',
    '需求补充',
    '预算',
    '人力',
    '功耗',
    '尺寸',
    '保温',
    '制冷',
    '节点',
    '仍在沟通',
    '未确定',
  ];
  const queryMarkers = [
    '进度',
    '完成情况',
    '延期',
    '风险',
    '日报',
    '周报',
    '查询',
    '查看',
    '分析已有',
    '当前阶段',
    '滞后',
    '逾期',
  ];
  const hasForm = formMarkers.some((k) => t.includes(k));
  const hasQuery = queryMarkers.some((k) => t.includes(k));
  if (hasForm && hasQuery) return false;
  return hasForm;
}

function isProjectIntent(text: string): boolean {
  if (isCreateProjectIntent(text)) return false;
  if (lastAssistantSuggestedCreateProject() || isLikelyCreateProjectSupplementText(text)) return false;
  const t = normalizeIntentText(text);
  // 项目数据词：问这些必须先有项目上下文，否则后端取不到数据
  const dataMarkers = ['风险', '进度', '完成时间', '延期', '资源', '日报', '高风险'];
  const hasDataMarker = dataMarkers.some((k) => t.includes(k));
  // 无数据词的疑问句是在咨询操作方法（如「怎么创建项目」），
  // 后端归客服、不需要项目上下文；仅凭「项目」二字弹项目选择器会打断客服问答
  if (!hasDataMarker && /(?:怎么|怎样|如何|哪里|在哪|为什么|为啥|是什么|什么流程|什么步骤|吗|呢|？|\?)/.test(t)) {
    return false;
  }
  return hasDataMarker || ['项目', 'project', 'ipd'].some((k) => t.includes(k));
}

async function loadProjectSelection() {
  projectSelectionLoading.value = true;
  try {
    projectSelectionList.value = await getAccessibleProjects({ page: 1, size: 200 });
  } catch {
    projectSelectionList.value = [];
  } finally {
    projectSelectionLoading.value = false;
  }
}

function dismissProjectSelection() {
  projectSelectionVisible.value = false;
  pendingProjectMessage.value = null;
  selectedProjectId.value = null;
}

async function promptProjectSelection(payload: { text: string; attachments: ChatMessageAttachment[] }) {
  pendingProjectMessage.value = payload;
  projectPromptMessage.value = '检测到该问题需要项目上下文，请先选择一个项目后再继续。';
  selectedProjectId.value = null;
  projectSelectionVisible.value = true;
  await loadProjectSelection();
  if (!projectSelectionList.value.length) {
    projectPromptMessage.value = '当前没有可选项目，请稍后再试或联系管理员开通项目权限。';
  }
}

function extractFieldEditUserRequirement(composerFullText: string): string {
  return String(composerFullText ?? '').trim();
}

function beginPageContentEdit(args: {
  fieldPath: string;
  fieldLabel: string;
  quoted: string;
  positionDescription: string;
  scopeLabel: string;
  cellKey?: string;
  paragraphIndex?: number;
  paragraphLabel?: string;
  mode?: 'modify' | 'discuss';
}) {
  aiPageContentEditPreviewStore.clearPreview();
  aiPageContentEditPreviewStore.clearLocateQuote();
  clearPageContentHighlights();
  shellStore.setDashboardExpandedAiComposer(false);
  pageContentEditPending.value = {
    fieldPath: args.fieldPath,
    fieldLabel: args.fieldLabel,
    quoted: args.quoted,
    positionDescription: args.positionDescription.trim(),
    scopeLabel: args.scopeLabel.trim() || '当前页面',
    cellKey: args.cellKey?.trim() || undefined,
    paragraphIndex: args.paragraphIndex,
    paragraphLabel: args.paragraphLabel?.trim() || undefined,
    mode: args.mode ?? 'modify',
  };
  aiInput.value = '';
  mainTab.value = 'new';
  if (isCollapsed.value) {
    isCollapsed.value = false;
    emit('collapse-change', false);
  }
  void nextTick(() => focusComposer());
}

function beginProjectCreateFieldEdit(args: {
  fieldPath: string;
  fieldLabel: string;
  quoted: string;
  positionDescription: string;
}) {
  projectCreateFieldEditPreviewStore.clearPreview();
  projectCreateFieldEditPending.value = {
    fieldPath: args.fieldPath,
    fieldLabel: args.fieldLabel,
    quoted: args.quoted,
    positionDescription: args.positionDescription.trim(),
  };
  aiInput.value = '';
  mainTab.value = 'new';
  void nextTick(() => focusComposer());
}

async function acceptPageContentFieldEdit(msg: ChatMessage) {
  const p = getAiPageContentFieldEditFromMessage(msg);
  if (!p || p.status !== 'pending' || !isActionableAiPageContentFieldEdit(p)) return;
  const original = String(p.original ?? '').trim() || String(p.quoted ?? '').trim();
  const globalQuoteScope = p.fieldPath === 'document-body'
    || isCrossBlockQuote(String(p.quoted ?? original), p.paragraphLabel);
  const ok = aiPageContentApplyStore.applyFieldReplace({
    fieldPath: p.fieldPath,
    original,
    revised: p.revised,
    cellKey: p.cellKey,
    paragraphIndex: globalQuoteScope ? undefined : p.paragraphIndex,
    paragraphLabel: globalQuoteScope ? undefined : p.paragraphLabel,
    quoted: p.quoted,
  });
  if (!ok) {
    ElMessage.error('未能应用修改，请确认页面仍包含所选原文');
    return;
  }
  const scenario = conversationScenario.value;
  const prev = msg.structuredResult && typeof msg.structuredResult === 'object' ? { ...msg.structuredResult } : {};
  updateSessionMessage(msg.id, {
    structuredResult: {
      ...prev,
      page_content_field_edit: { ...p, status: 'accepted' },
    },
  }, currentSessionId.value, scenario);
  ElMessage.success('已采纳修改');
  aiPageContentEditPreviewStore.clearPreview();
  await persistConversation();
}

async function rejectPageContentFieldEdit(msg: ChatMessage) {
  const p = getAiPageContentFieldEditFromMessage(msg);
  if (!p || p.status !== 'pending') return;
  const scenario = conversationScenario.value;
  const prev = msg.structuredResult && typeof msg.structuredResult === 'object' ? { ...msg.structuredResult } : {};
  updateSessionMessage(msg.id, {
    structuredResult: {
      ...prev,
      page_content_field_edit: { ...p, status: 'rejected' },
    },
  }, currentSessionId.value, scenario);
  ElMessage.info('已拒绝该修改建议');
  aiPageContentEditPreviewStore.clearPreview();
  await persistConversation();
}

async function acceptProjectCreateFieldEdit(msg: ChatMessage) {
  const p = getProjectCreateFieldEditFromMessage(msg);
  if (!p || p.status !== 'pending') return;
  const ok = projectCreateFieldApplyStore.applyFieldReplace({
    fieldPath: p.fieldPath,
    original: p.original,
    revised: p.revised,
  }, currentProjectCreateDraftKey());
  if (!ok) {
    ElMessage.error('未能应用修改，请确认左侧表单仍包含所选原文');
    return;
  }
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  const prev = msg.structuredResult && typeof msg.structuredResult === 'object' ? { ...msg.structuredResult } : {};
  store.updateMessage(scenario, msg.id, {
    structuredResult: {
      ...prev,
      project_create_field_edit: { ...p, status: 'accepted' },
    },
  }, sid);
  ElMessage.success('已采纳修改');
  projectCreateFieldEditPreviewStore.clearPreview();
  await persistConversation();
}

async function rejectProjectCreateFieldEdit(msg: ChatMessage) {
  const p = getProjectCreateFieldEditFromMessage(msg);
  if (!p || p.status !== 'pending') return;
  const scenario = conversationScenario.value;
  const sid = effectiveSessionId();
  const prev = msg.structuredResult && typeof msg.structuredResult === 'object' ? { ...msg.structuredResult } : {};
  store.updateMessage(scenario, msg.id, {
    structuredResult: {
      ...prev,
      project_create_field_edit: { ...p, status: 'rejected' },
    },
  }, sid);
  ElMessage.info('已拒绝该修改建议');
  projectCreateFieldEditPreviewStore.clearPreview();
  await persistConversation();
}

type StreamingChatResult = {
  assistantId: string;
  answer: string;
  thought: string;
  structuredResult: Record<string, unknown> | null;
  nextQuestions: string[];
};

async function runStreamingAiChat(
  chatPayload: AgentChatRequest,
  boundSessionId = effectiveSessionId(),
  options?: { deferSessionTitle?: boolean; signal?: AbortSignal; requestContext?: PageContextSnapshot },
): Promise<StreamingChatResult> {
  const originTabKey = options?.requestContext?.pageTabKey || currentPageTabKey();
  const scenario = options?.requestContext?.scenario || conversationScenario.value;
  const assistantId = genId();
  const assistantMsg = addSessionMessage('assistant', '', undefined, thoughtStepsToJson([]), null, boundSessionId, scenario);
  if (assistantMsg.id !== assistantId) {
    updateSessionMessage(assistantMsg.id, { id: assistantId }, boundSessionId, scenario);
  }
  currentAssistantId.value = assistantId;

  isThinking.value = true;
  aiLoading.value = true;
  isStreaming.value = true;
  streamStopped.value = false;
  currentThoughtDraft.value = [
    {
      key: 'bootstrap::running',
      icon: '⏳',
      title: '已提交请求',
      text: '正在连接一诺并准备处理…',
    },
  ];
  expandedThoughtIds.value = new Set([...expandedThoughtIds.value, assistantId]);

  await nextTick();
  scrollAiToBottom();

  const abortController = new AbortController();
  streamAbortController.value = abortController;
  const externalSignal = options?.signal;
  if (externalSignal) {
    if (externalSignal.aborted) {
      abortController.abort();
    } else {
      externalSignal.addEventListener(
        'abort',
        () => {
          streamStopped.value = true;
          abortController.abort();
        },
        { once: true },
      );
    }
  }

  let finalAnswer = '';
  let finalThought: unknown;
  let structuredResult: Record<string, unknown> | null = null;
  let nextQuestions: string[] = [];
  let capturedStreamSteps: ThoughtStep[] = [];
  let streamSessionTitle: string | null = null;

  try {
    await consumeAiChatStream(
      chatPayload,
      (event) => {
        if (options?.requestContext && !isPageContextSnapshotCurrent(options.requestContext)) {
          streamStopped.value = true;
          abortController.abort();
          return;
        }
        if (streamStopped.value) return;
        if (event.event === 'final') {
          finalAnswer = String(event.answer ?? '').trim();
          finalThought = event.thought;
          const extracted = extractStructuredFromFinalEvent(event);
          structuredResult = extracted.structuredResult ?? null;
          nextQuestions = extracted.nextQuestions ?? [];
          return;
        }
        const titleFromIntent = extractSessionTitleFromStreamEvent(event);
        if (titleFromIntent) streamSessionTitle = titleFromIntent;
        const step = streamEventToThoughtStep(event);
        if (!step) return;
        const merged = mergeStreamingThoughtSteps(currentThoughtDraft.value, step);
        currentThoughtDraft.value = merged;
        updateSessionMessage(assistantId, {
          thought: buildFinalThoughtJson(merged, undefined),
        }, boundSessionId, scenario);
        void nextTick(() => {
          requestAnimationFrame(() => scrollAiToBottom());
        });
      },
      { signal: abortController.signal },
    );
  } catch (e) {
    if (!streamStopped.value && !isTaskCancelledError(e)) {
      throw e;
    }
  } finally {
    capturedStreamSteps = [...currentThoughtDraft.value];
    isStreaming.value = false;
    streamAbortController.value = null;
    currentThoughtDraft.value = [];
  }

  if (options?.requestContext && !isPageContextSnapshotCurrent(options.requestContext)) {
    return {
      assistantId,
      answer: '',
      thought: '',
      structuredResult: null,
      nextQuestions: [],
    };
  }

  const thoughtJson = buildFinalThoughtJson(capturedStreamSteps, finalThought);
  updateSessionMessage(assistantId, {
    thought: thoughtJson,
    structuredResult,
  }, boundSessionId, scenario);

  notifyAiDialogueCompleted(originTabKey, chatPayload.conversationId);
  if (!options?.deferSessionTitle) {
    await maybeGenerateSessionTitleAfterFirstTurn(
      boundSessionId,
      scenario,
      streamSessionTitle ? { session_title: streamSessionTitle } : finalThought,
    );
  }

  return {
    assistantId,
    answer: finalAnswer,
    thought: thoughtJson,
    structuredResult,
    nextQuestions,
  };
}

function pageContentQuoteNeedsGlobalScope(args: {
  quoted: string;
  paragraphLabel?: string;
  fieldPath?: string;
}): boolean {
  if (args.fieldPath === 'document-body') return true;
  return isCrossBlockQuote(args.quoted, args.paragraphLabel);
}

async function sendPageContentFieldEditMessage(
  pending: PageContentEditPending,
  composerFullText: string,
  messageAttachments: ChatMessageAttachment[],
) {
  const scenario = conversationScenario.value;
  const boundSessionId = effectiveSessionId();
  const userRequirement = extractFieldEditUserRequirement(composerFullText);
  const userStructured: Record<string, unknown> = {
    page_content_user_ref: {
      fieldLabel: pending.fieldLabel,
      quoted: pending.quoted,
      requirement: userRequirement,
      scopeLabel: pending.scopeLabel,
      fieldPath: pending.fieldPath,
      cellKey: pending.cellKey,
      paragraphIndex: pending.paragraphIndex,
      paragraphLabel: pending.paragraphLabel,
    },
  };
  addSessionMessage(
    'user',
    '',
    messageAttachments.length > 0 ? messageAttachments : undefined,
    undefined,
    userStructured,
  );
  await nextTick();
  scrollAiToBottom();

  try {
    const promptBody = buildAiPageContentFieldEditPrompt(
      pending.quoted,
      pending.fieldLabel,
      userRequirement,
      {
        fieldPath: pending.fieldPath,
        positionDescription: pending.positionDescription,
        scopeLabel: pending.scopeLabel,
      },
    );
    const streamed = await runStreamingAiChat({
      conversationId: boundSessionId,
      message: promptBody,
      sessionId: boundSessionId,
      routeName: 'PageContentEdit',
      title: `${pending.scopeLabel} · 片段修改`,
      ...buildAgentChatContextWrapper({
        routeName: 'PageContentEdit',
        pageFieldEdit: true,
      }),
    }, boundSessionId, { deferSessionTitle: true });
    const assistantId = streamed.assistantId;
    const answer = streamed.answer || '';
    const { userText, edit: parsed } = resolveAiPageContentFieldEditDualAnswer(
      answer,
      streamed.thought,
      pending.quoted.trim(),
    );

    await nextTick();
    const globalQuoteScope = pageContentQuoteNeedsGlobalScope({
      quoted: pending.quoted,
      paragraphLabel: pending.paragraphLabel,
      fieldPath: pending.fieldPath,
    });
    const normalizedEdit = parsed
      ? {
          original: (() => {
            const q = pending.quoted.trim();
            if (pending.fieldPath === 'document-body' || globalQuoteScope) return q;
            const aiOriginal = String(parsed.original ?? '').trim();
            if (!aiOriginal) return q;
            return aiOriginal;
          })(),
          revised: String(parsed.revised ?? '').trim(),
        }
      : null;
    if (normalizedEdit && isActionableAiPageContentFieldEdit({
      fieldPath: pending.fieldPath,
      original: normalizedEdit.original,
      revised: normalizedEdit.revised,
      status: 'pending',
      quoted: pending.quoted.trim(),
    })) {
      aiPageContentEditPreviewStore.setPreview({
        fieldPath: pending.fieldPath,
        fieldLabel: pending.fieldLabel,
        original: normalizedEdit.original,
        revised: normalizedEdit.revised,
        messageId: assistantId,
        cellKey: pending.cellKey,
        paragraphIndex: globalQuoteScope ? undefined : pending.paragraphIndex,
        paragraphLabel: globalQuoteScope ? undefined : pending.paragraphLabel,
        quoted: pending.quoted.trim(),
      });
      const body =
        sanitizeFieldEditUserDisplayText(userText)
        || '已根据你的说明给出改写建议：请在页面中查看**淡蓝色背景的原文**与紧随其后的**红色改写**；满意请在本条消息末尾点击「采纳」应用到页面，否则点击「拒绝」。';
      updateSessionMessage(assistantId, {
        content: body,
        structuredResult: {
          page_content_field_edit: {
            fieldPath: pending.fieldPath,
            original: normalizedEdit.original,
            revised: normalizedEdit.revised,
            status: 'pending',
            cellKey: pending.cellKey,
            paragraphIndex: globalQuoteScope ? undefined : pending.paragraphIndex,
            paragraphLabel: globalQuoteScope ? undefined : pending.paragraphLabel,
            quoted: pending.quoted.trim(),
            replyText: body,
          },
        },
      }, boundSessionId, scenario);
    } else {
      const fallbackBody = sanitizeFieldEditUserDisplayText(userText || answer);
      updateSessionMessage(assistantId, {
        content: fallbackBody || '未能解析改写结果，请换一种说法再试。',
        structuredResult: null,
      }, boundSessionId, scenario);
      startTypewriter(assistantId);
      isThinking.value = false;
      await waitForTypewriter(assistantId);
    }
    isThinking.value = false;
    await maybeGenerateSessionTitleAfterFirstTurn(boundSessionId, scenario, streamed.thought);
    await persistConversation(undefined, boundSessionId, scenario);
  } catch (e) {
    console.error(e);
    ElMessage.error('请求失败，请稍后重试');
  } finally {
    aiLoading.value = false;
    isThinking.value = false;
    if (currentAssistantId.value) clearTypewriter(currentAssistantId.value);
    currentAssistantId.value = null;
  }
}

async function sendPageContentDiscussMessage(
  pending: PageContentEditPending,
  composerFullText: string,
  messageAttachments: ChatMessageAttachment[],
) {
  const scenario = conversationScenario.value;
  const boundSessionId = effectiveSessionId();
  const userRequirement = extractFieldEditUserRequirement(composerFullText);
  const userStructured: Record<string, unknown> = {
    page_content_user_ref: {
      fieldLabel: pending.fieldLabel,
      quoted: pending.quoted,
      requirement: userRequirement,
      scopeLabel: pending.scopeLabel,
      fieldPath: pending.fieldPath,
      cellKey: pending.cellKey,
      paragraphIndex: pending.paragraphIndex,
      paragraphLabel: pending.paragraphLabel,
    },
  };
  addSessionMessage(
    'user',
    '',
    messageAttachments.length > 0 ? messageAttachments : undefined,
    undefined,
    userStructured,
  );
  await nextTick();
  scrollAiToBottom();

  const where = pending.positionDescription || `${pending.scopeLabel} · ${pending.fieldLabel}`;
  const promptBody = [
    '【系统任务：页面引用内容问答】',
    '用户从业务页面选中了一段原文，并在此基础上提问或说明需求。请仅围绕引用片段与用户说明作答，不要切换到项目风险盘点、项目列表检索或其他无关任务。',
    '',
    `【引用来源】${pending.scopeLabel}`,
    `【引用位置】${where}`,
    '【引用原文】',
    pending.quoted,
    '',
    '【用户说明】',
    userRequirement || '请根据上下文说明该片段的含义或给出建议。',
  ].join('\n');

  try {
    const streamed = await runStreamingAiChat({
      conversationId: boundSessionId,
      message: promptBody,
      sessionId: boundSessionId,
      routeName: 'PageContentDiscuss',
      title: `${pending.scopeLabel} · 引用问答`,
    }, boundSessionId, { deferSessionTitle: true });
    const assistantId = streamed.assistantId;
    const answer = streamed.answer || '暂无回复。';
    updateSessionMessage(assistantId, { content: answer, structuredResult: streamed.structuredResult }, boundSessionId, scenario);
    startTypewriter(assistantId);
    isThinking.value = false;
    await waitForTypewriter(assistantId);
    await maybeGenerateSessionTitleAfterFirstTurn(boundSessionId, scenario, streamed.thought);
    await persistConversation(undefined, boundSessionId, scenario);
  } catch (e) {
    console.error(e);
    ElMessage.error('请求失败，请稍后重试');
  } finally {
    aiLoading.value = false;
    isThinking.value = false;
    if (currentAssistantId.value) clearTypewriter(currentAssistantId.value);
    currentAssistantId.value = null;
  }
}

async function sendProjectCreateFieldEditMessage(
  pending: ProjectCreateFieldEditPending,
  composerFullText: string,
  messageAttachments: ChatMessageAttachment[],
) {
  const scenario = conversationScenario.value;
  const userRequirement = extractFieldEditUserRequirement(composerFullText);
  const userStructured: Record<string, unknown> = {
    project_create_user_ref: {
      fieldLabel: pending.fieldLabel,
      quoted: pending.quoted,
      requirement: userRequirement,
    },
  };
  addSessionMessage(
    'user',
    '',
    messageAttachments.length > 0 ? messageAttachments : undefined,
    undefined,
    userStructured,
  );
  await nextTick();
  scrollAiToBottom();

  try {
    const promptBody = buildProjectCreateFieldEditPrompt(pending.quoted, pending.fieldLabel, userRequirement, {
      fieldPath: pending.fieldPath,
      positionDescription: pending.positionDescription,
    });
    const streamed = await runStreamingAiChat({
      conversationId: currentSessionId.value,
      message: promptBody,
      sessionId: currentSessionId.value,
      routeName: 'ProjectCreate',
      title: '项目创建 · 片段修改',
      ...buildAgentChatContextWrapper(messageAttachments.length > 0 ? { has_file: true, attachments: messageAttachments.map(a => ({ objectKey: a.processingObjectKey || a.objectKey, fileName: a.processingName || a.name, accessUrl: a.processingUrl || a.url })) } : undefined),
    });
    const assistantId = streamed.assistantId;
    const answer = streamed.answer || '';
    const { userText, edit: parsed } = resolveFieldEditDualAnswer(
      answer,
      streamed.thought,
      pending.quoted.trim(),
    );

    await nextTick();
    if (parsed) {
      projectCreateFieldEditPreviewStore.setPreview({
        fieldPath: pending.fieldPath,
        fieldLabel: pending.fieldLabel,
        original: parsed.original,
        revised: parsed.revised,
        messageId: assistantId,
      });
      const body =
        userText.trim()
        || '已根据你的说明给出改写建议：请在左侧「项目信息」对应字段下方查看**淡蓝色背景的原文**与紧随其后的**红色改写**；满意请在本条消息末尾点击「采纳」应用到表单，否则点击「拒绝」。';
      updateSessionMessage(assistantId, {
        content: body,
        structuredResult: {
          project_create_field_edit: {
            fieldPath: pending.fieldPath,
            original: parsed.original,
            revised: parsed.revised,
            status: 'pending',
          },
        },
      }, currentSessionId.value, scenario);
    } else {
      updateSessionMessage(assistantId, {
        content: answer.trim() || '未能解析改写结果，请换一种说法再试。',
        structuredResult: null,
      }, currentSessionId.value, scenario);
      startTypewriter(assistantId);
      isThinking.value = false;
      await waitForTypewriter(assistantId);
    }
    isThinking.value = false;
    await persistConversation();
  } catch (e) {
    console.error(e);
    ElMessage.error('请求失败，请稍后重试');
  } finally {
    aiLoading.value = false;
    isThinking.value = false;
    if (currentAssistantId.value) clearTypewriter(currentAssistantId.value);
    currentAssistantId.value = null;
  }
}

type CreateProjectResponseOptions = {
  projectCreateMode?: ProjectCreateConversationMode;
  draftKey?: string;
  sessionId?: string;
  sourceConversationId?: string;
  sourceConversationContext?: string;
};

/** 无创建权限时立即回复，避免进入漫长的创建项目多轮对话后才失败 */
async function replyProjectCreateNoAccess(
  text: string,
  attachments: ChatMessageAttachment[] = [],
  sessionId?: string,
) {
  const displayText = text || '(附带附件)';
  const boundSessionId = sessionId || currentSessionId.value;
  const scenario = conversationScenario.value;
  addSessionMessage(
    'user',
    displayText,
    attachments.length > 0 ? attachments : undefined,
    undefined,
    undefined,
    boundSessionId,
    scenario,
  );
  await nextTick();
  scrollAiToBottom();
  const assistantMsg = addSessionMessage(
    'assistant',
    formatProjectCreateNoAccessAssistantReply(),
    undefined,
    undefined,
    undefined,
    boundSessionId,
    scenario,
  );
  startTypewriter(assistantMsg.id);
  await waitForTypewriter(assistantMsg.id);
  await persistConversation(undefined, boundSessionId);
  projectPromptMessage.value = '';
}

async function sendCreateProjectIntentResponse(
  text: string,
  attachments: ChatMessageAttachment[] = [],
  options: CreateProjectResponseOptions = {},
) {
  if (!canAccessProjectCreateRoute(permissionStore)) {
    await replyProjectCreateNoAccess(text, attachments, options.sessionId);
    return;
  }
  const displayText = text || '(附带附件)';
  return runInAiTaskQueue({
    kind: 'project_create',
    label: '创建项目',
    sessionId: options.sessionId,
    run: async (boundSessionId, ctx) => {
  const scenario = conversationScenario.value;
  addSessionMessage('user', displayText, attachments.length > 0 ? attachments : undefined, undefined, undefined, boundSessionId, scenario);
  await nextTick();
  scrollAiToBottom();

  try {
    const streamed = await runStreamingAiChat({
      conversationId: boundSessionId,
      message: (() => {
        const raw = text || '(请根据附件内容回复)';
        if (raw.includes('三步确认')) return raw;
        return `${raw}\n\n【流程】请按基础信息 → 成员（未匹配组织目录只标记、可跳过、不建账号）→ 补充配置三步追问；用户明确确认前不要创建项目。完成后提示可「查看项目配置」。`;
      })(),
      sessionId: boundSessionId,
      routeName: 'Dashboard',
      title: '创建项目',
      ...(await withProjectCreateReferenceContext(text.trim() || '(附带附件)', {
        ...(options.projectCreateMode ? { projectCreateMode: options.projectCreateMode } : {}),
        ...(options.sourceConversationId ? { sourceConversationId: options.sourceConversationId } : {}),
        ...(options.sourceConversationContext ? { sourceConversationContext: options.sourceConversationContext } : {}),
      })),
    }, boundSessionId, { signal: ctx?.signal });
    const answer = streamed.answer || '我已开始帮你补齐项目结构。';
    const nextQuestions = streamed.nextQuestions ?? [];
    const structuredResult = streamed.structuredResult ?? null;
    const finalAnswer = nextQuestions.length ? `${answer}\n\n我先想确认：${nextQuestions[0]}` : answer;
    const assistantId = streamed.assistantId;
    currentAssistantId.value = assistantId;
    await nextTick();
    updateSessionMessage(assistantId, {
      content: finalAnswer,
      structuredResult: structuredResult,
    }, boundSessionId, scenario);
    startTypewriter(assistantId);
    isThinking.value = false;
    await waitForTypewriter(assistantId);
    await persistConversation(undefined, boundSessionId);
    if (structuredResult && typeof structuredResult === 'object' && isStructuredProjectCreateReady(structuredResult)) {
      if (!canAccessProjectCreateRoute(permissionStore)) {
        const listAfter = getSessionMessages(boundSessionId, scenario);
        const tailMsg = listAfter.find((m) => m.id === assistantId);
        if (tailMsg && !hasProjectCreateNoRouteAccessNotice(tailMsg.content)) {
          updateSessionMessage(assistantId, {
            content: (tailMsg.content || '') + formatProjectCreateNoRouteAccessNotice(),
          }, boundSessionId, scenario);
        }
        await persistConversation(undefined, boundSessionId);
      } else {
        await runProjectAutofillIntroSequence(shellStore, router, {
          structuredResult: structuredResult as Record<string, unknown>,
          answerText: finalAnswer,
          conversationId: boundSessionId,
        });
      }
    }
  } catch (e: unknown) {
    if (isTaskCancelledError(e)) return;
    console.error(e);
    const msg =
      e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
        ? String((e as { message: string }).message).trim()
        : '';
    ElMessage.error(msg || '创建项目对话请求失败，请稍后重试');
  } finally {
    aiLoading.value = false;
    isThinking.value = false;
    if (currentAssistantId.value) clearTypewriter(currentAssistantId.value);
    currentAssistantId.value = null;
  }
    },
  });
}

async function confirmProjectSelection() {
  if (!pendingProjectMessage.value || !selectedProjectId.value) return;
  const project = projectSelectionList.value.find((p) => p.id === selectedProjectId.value);
  projectSelectionVisible.value = false;
  const payload = pendingProjectMessage.value;
  pendingProjectMessage.value = null;
  if (project) {
    await sendAiMessageWithProject(String(project.id), payload.text, payload.attachments);
  }
}

async function sendAiMessageWithProject(
  projectId: string,
  text: string,
  attachments: ChatMessageAttachment[] = [],
  projectCreateOptions: CreateProjectResponseOptions = {},
  requestSnapshot?: PageContextSnapshot,
) {
  const displayText = text || '(附带附件)';
  const chatLabel = isProjectStatusOrReportIntent(displayText)
    ? (/周报/.test(displayText) ? '生成项目周报' : /日报/.test(displayText) ? '生成项目日报' : '项目咨询')
    : (displayText.length > 12 ? `${displayText.slice(0, 12)}…` : displayText);
  const snapshot = requestSnapshot ?? capturePageContextSnapshot();
  return runInAiTaskQueue({
    kind: 'chat',
    label: chatLabel || '对话',
    run: async (boundSessionId, ctx) => {
  if (!isPageContextSnapshotCurrent(snapshot)) return undefined as never;
  const scenario = snapshot.scenario;
  const agentProjectId = projectId;
  const imageB64 = pendingImageBase64 || (window as any).__aipdPendingImage || null;
  (window as any).__aipdPendingImage = null;
  pendingImageBase64 = null;
  pendingImagePreview.value = '';

  // 粘贴截图：生成 blob URL 加入到 attachments（和附件一样展示），持久化时过滤掉
  // 仅当消息没有图片附件时才创建（避免附件上传时重复）
  const hasImageAttachment = attachments.some(a => a.type === 'image');
  const displayAttachments = [...attachments];
  if (imageB64 && !hasImageAttachment) {
    try {
      const binary = atob(imageB64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'image/png' });
      displayAttachments.unshift({
        name: '截图.png',
        url: URL.createObjectURL(blob),
        type: 'image',
      });
    } catch { /* base64 decode failed — silently skip */ }
  }
  const userMsg = addSessionMessage('user', displayText, displayAttachments.length > 0 ? displayAttachments : undefined, undefined, undefined, boundSessionId, scenario);
  await nextTick();
  scrollAiToBottom();

  try {
    const onCreatePage = snapshot.routeName === 'ProjectCreate';
    const requestContext = {
      projectId: agentProjectId,
      currentProjectId: snapshot.projectId > 0 ? String(snapshot.projectId) : '',
      routeName: snapshot.routeName,
      pageTabKey: snapshot.pageTabKey,
      operationId: snapshot.operationId,
    };
    const ctxPayload = onCreatePage
      ? await withProjectCreateReferenceContext(text, {
          projectCreateMode: projectCreateOptions.projectCreateMode || 'update_current_draft',
          ...(projectCreateOptions.draftKey ? { projectCreateDraftKey: projectCreateOptions.draftKey } : {}),
          ...(projectCreateOptions.sourceConversationId ? { sourceConversationId: projectCreateOptions.sourceConversationId } : {}),
          ...(projectCreateOptions.sourceConversationContext ? { sourceConversationContext: projectCreateOptions.sourceConversationContext } : {}),
          ...requestContext,
        })
      : buildAgentChatContextWrapper({
          ...(attachments.length > 0 ? { has_file: true, attachments: attachments.map(a => ({ objectKey: a.processingObjectKey || a.objectKey, fileName: a.processingName || a.name, accessUrl: a.processingUrl || a.url })) } : {}),
          ...requestContext,
        });
    if (agentProjectId && ctxPayload && 'context' in ctxPayload && ctxPayload.context && typeof ctxPayload.context === 'object') {
      (ctxPayload.context as Record<string, unknown>).projectId = agentProjectId;
      (ctxPayload.context as Record<string, unknown>).project_id = agentProjectId;
    }
    const streamed = await runStreamingAiChat({
      conversationId: boundSessionId,
      message: text || '(请根据附件内容回复)',
      projectId: agentProjectId,
      operationId: snapshot.operationId,
      sessionId: boundSessionId,
      routeName: onCreatePage ? 'ProjectCreate' : 'Dashboard',
      imageBase64: imageB64 ?? undefined,
      title: onCreatePage ? '项目创建' : '项目咨询',
      ...ctxPayload,
    }, boundSessionId, { signal: ctx?.signal, requestContext: snapshot });
    if (!isPageContextSnapshotCurrent(snapshot)) return undefined as never;
    const answer = streamed.answer || '暂无回复内容。';
    const structuredResult = streamed.structuredResult ?? null;
    const assistantId = streamed.assistantId;
    const backendIntent = extractBackendIntent(streamed.thought);
    // 最小 C（弹窗改由后端意图驱动）：后端判定需要项目上下文而尚未选项目时，
    // 不展示本轮兜底回复，撤下本轮消息并弹出项目选择器，选中后经 pendingProjectMessage 重发。
    // 原先在发送前用前端关键词拦截，会把「创建一个…(长名称)…项目」这类创建指令误弹选择器
    if (!agentProjectId && !onCreatePage && backendIntent === 'PROJECT_STATUS') {
      const remaining = getSessionMessages(boundSessionId, scenario).filter(
        (m) => m.id !== assistantId && m.id !== userMsg.id,
      );
      setSessionMessages(remaining, boundSessionId, scenario);
      isThinking.value = false;
      await persistConversation(undefined, boundSessionId);
      await promptProjectSelection({ text: text || '(附带附件)', attachments });
      return;
    }
    currentAssistantId.value = assistantId;
    await nextTick();
    updateSessionMessage(assistantId, {
      content: answer,
      structuredResult: structuredResult,
    }, boundSessionId, scenario);
    startTypewriter(assistantId);
    isThinking.value = false;
    await waitForTypewriter(assistantId);
    await persistConversation(undefined, boundSessionId);
    if (onCreatePage && structuredResult && typeof structuredResult === 'object') {
      const sr = structuredResult as Record<string, unknown>;
      if (sr.shouldRefreshProjectForm !== false) {
        const applied = await projectCreateStructuredMergeStore.notifyStructuredMerge(
          sr,
          currentProjectCreateDraftKey(),
        );
        if (!applied) {
          updateSessionMessage(assistantId, {
            content: `${answer}\n\n⚠️ 本次修改未写入当前项目草稿，请确认当前打开的是目标创建页后重试。`,
          }, boundSessionId, scenario);
          await persistConversation(undefined, boundSessionId);
        }
      }
    }
    // B（创建流程自愈）：前端关键词漏判时以后端意图为权威——
    // 本轮实为创建项目且结构化字段就绪时，与创建通道同样进入创建页自动预填
    // 「改已有项目名称」即使被后端误标 CREATE_PROJECT，也不跳进创建页
    if (
      !onCreatePage &&
      backendIntent === 'CREATE_PROJECT' &&
      !isRenameExistingProjectIntent(text)
    ) {
      if (!canAccessProjectCreateRoute(permissionStore)) {
        if (!hasProjectCreateNoRouteAccessNotice(answer)) {
          updateSessionMessage(assistantId, {
            content: `${answer}${formatProjectCreateNoRouteAccessNotice()}`,
          }, boundSessionId, scenario);
          await persistConversation(undefined, boundSessionId);
        }
      } else if (
        structuredResult &&
        typeof structuredResult === 'object' &&
        isStructuredProjectCreateReady(structuredResult)
      ) {
        await runProjectAutofillIntroSequence(shellStore, router, {
          structuredResult: structuredResult as Record<string, unknown>,
          answerText: answer,
          conversationId: boundSessionId,
        });
      }
    }
  } catch (e: unknown) {
    if (isTaskCancelledError(e)) return;
    console.error(e);
    const msg =
      e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
        ? String((e as { message: string }).message).trim()
        : '';
    ElMessage.error(msg || 'AI 请求失败，请稍后重试');
  } finally {
    aiLoading.value = false;
    isThinking.value = false;
    if (currentAssistantId.value) clearTypewriter(currentAssistantId.value);
    currentAssistantId.value = null;
  }
    },
  });
}

function fillInput(text: string) {
  aiInput.value = text;
}

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
  emit('collapse-change', isCollapsed.value);
  if (isCollapsed.value) {
    suppressAutoExpand.value = true;
  } else {
    suppressAutoExpand.value = false;
    mainTab.value = 'new';
  }
  schedulePersistPanelSettings();
}

/** 收起右侧栏为右上角「AI」悬浮球（与全屏顶栏「−」共用） */
function collapseToFloatingButton() {
  if (isCollapsed.value) return;
  isCollapsed.value = true;
  suppressAutoExpand.value = true;
  emit('collapse-change', true);
  schedulePersistPanelSettings();
}

/** 保持悬浮态，阻止任务回调自动展开（如表格全屏时 AI 已为悬浮球） */
function pinFloatingAiPanel() {
  suppressAutoExpand.value = true;
}

const leadingToggleTitle = computed(() => '展开全屏对话');

/** 常态侧栏「<」→ 展开态（全屏对话）；若当前为收起态则先恢复侧栏再全屏 */
function onLeadingPanelToggleClick() {
  if (isCollapsed.value) {
    isCollapsed.value = false;
    suppressAutoExpand.value = false;
    emit('collapse-change', false);
  }
  shellStore.setDashboardExpandedAiComposer(true);
  schedulePersistPanelSettings();
}

async function handleNewConversation() {
  if (isCalculatorCalcScenario(conversationScenario.value)) {
    const meta =
      calcSessionMeta.value || loadCalcSessionMeta(conversationScenario.value);
    mainTab.value = 'new';
    await nextTick();
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
    await archiveAndStartNewSession({
      archiveTitle: meta ? calcSessionTitle(meta.formulaName) : undefined,
    });
    if (meta) {
      // 场景前缀只进本地 store，不落库；等用户问出第一句，正常对话流程会把它一起持久化。
      store.addMessage(conversationScenario.value, 'assistant', buildCalcSessionPrefix(meta));
    }
    return;
  }

  mainTab.value = 'new';
  await nextTick();
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
  await persistConversation(undefined, currentSessionId.value);

  clearSessionMessages(currentSessionId.value);
  clearComposerDraftForCurrentScenario();
  projectCreateFieldEditPending.value = null;
  pageContentEditPending.value = null;
  currentSessionId.value = generateSessionId();
  activeHistoryId.value = null;
  selectedSessionId.value = currentSessionId.value;
  visibleMessageCount.value = VISIBLE_MESSAGE_INITIAL;
  collapsedThoughtIds.value = new Set();
  writeLastSessionId(persistenceScenario.value, currentSessionId.value);
}

async function archiveAndStartNewSession(opts?: { archiveTitle?: string }) {
  const scenario = conversationScenario.value;
  const archiveSessionId = currentSessionId.value;
  const fullList = getSessionMessages(archiveSessionId, scenario);
  if (fullList.length) {
    await persistConversation(opts?.archiveTitle, archiveSessionId, scenario);
    clearSessionMessages(archiveSessionId, scenario);
  }
  clearComposerDraftForCurrentScenario();
  projectCreateFieldEditPending.value = null;
  pageContentEditPending.value = null;
  currentSessionId.value = generateSessionId();
  activeHistoryId.value = null;
  selectedSessionId.value = currentSessionId.value;
  visibleMessageCount.value = VISIBLE_MESSAGE_INITIAL;
  collapsedThoughtIds.value = new Set();
  writeLastSessionId(persistenceScenario.value, currentSessionId.value);
  try {
    await loadConversationHistory();
  } catch {
    /* ignore */
  }
}

async function initCalculatorFormulaSession(
  meta: CalcFormulaSessionMeta,
  options?: { copyFromImport?: boolean },
) {
  const scenario = calcScenarioForFormulaId(meta.formulaId);
  saveCalcSessionMeta(scenario, meta);
  calcSessionMeta.value = meta;
  store.currentScenario = scenario;

  await loadConversationHistory();

  const lastSid = readLastSessionId(scenario);
  let existing = lastSid ? store.getMessages(scenario, lastSid) : [];

  if (lastSid && existing.length === 0) {
    const item = historyList.value.find((h) => h.sessionId === lastSid);
    if (item) {
      await loadHistoryItem(item);
      existing = store.getMessages(scenario, lastSid);
    }
  }

  if (options?.copyFromImport && existing.length === 0) {
    const lastImportSid = readLastSessionId(CALCULATOR_FORMULA_IMPORT_SCENARIO);
    let importMsgs = lastImportSid
      ? store.getMessages(CALCULATOR_FORMULA_IMPORT_SCENARIO, lastImportSid)
      : [];
    if (!importMsgs.length) {
      importMsgs = store.getMessages(CALCULATOR_FORMULA_IMPORT_SCENARIO);
    }
    if (importMsgs.length) {
      const sid = generateSessionId();
      store.setMessages(
        scenario,
        importMsgs.map((m) => ({
          ...m,
          id: `${m.id}_calc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        })),
        sid,
      );
      currentSessionId.value = sid;
      selectedSessionId.value = sid;
      writeLastSessionId(scenario, sid);
      await persistConversation(calcSessionTitle(meta.formulaName));
      return;
    }
  }

  if (existing.length === 0) {
    currentSessionId.value = generateSessionId();
    selectedSessionId.value = currentSessionId.value;
    writeLastSessionId(scenario, currentSessionId.value);
    if (meta.welcomeMessage?.trim()) {
      // 引导语只进本地 store。这里原本紧跟一句 persistConversation，导致每次打开
      // 公式详情页、本地又没有会话时，就往库里塞一条正文只有引导语的会话。
      addSessionMessage('assistant', meta.welcomeMessage.trim());
    }
  } else if (lastSid) {
    currentSessionId.value = lastSid;
    selectedSessionId.value = lastSid;
  }
}

function setImportSessionTitle(title: string) {
  importSessionTitle.value = (title || '').trim();
}

function generateSessionId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

async function loadDashboardForPanel() {
  if (!hasProjectContext.value || !projectId.value) {
    dashboard.value = null;
    return;
  }
  try {
    dashboard.value = await getProjectDashboardData(projectId.value);
  } catch (e) {
    console.error('加载项目指挥舱数据失败(面板)', e);
    dashboard.value = null;
  }
}

/**
 * 会话持久化异常的提示节流：persistConversation 在流式回复过程中会被高频调用，
 * 出问题时若逐次弹窗会刷屏，10 秒内只提示一次。
 */
let lastConversationIssueNotifyAt = 0;
function notifyConversationIssue(message: string) {
  const now = Date.now();
  if (now - lastConversationIssueNotifyAt < 10_000) return;
  lastConversationIssueNotifyAt = now;
  ElMessage.warning(message);
}

let conversationHistoryLoadGen = 0;

async function loadConversationHistory(opts?: {
  applyLastSession?: boolean;
  expectedContext?: PageContextSnapshot;
}) {
  const gen = ++conversationHistoryLoadGen;
  if (!ENABLE_CONVERSATION_PERSISTENCE) {
    historyList.value = [];
    return;
  }
  if (!authStore.isAuthenticated || authStore.loggingOut) {
    historyList.value = [];
    return;
  }
  const scenario = persistenceScenario.value;
  const authScope = getUserSessionScopeSuffix();
  const applyLast = opts?.applyLastSession === true;
  try {
    if (applyLast) {
      const last = readLastSessionId(scenario);
      if (last && last !== currentSessionId.value) {
        currentSessionId.value = last;
      }
    }
    const list = await getAgentConversationList(scenario);
    if (
      gen !== conversationHistoryLoadGen
      || persistenceScenario.value !== scenario
      || (opts?.expectedContext && !isPageContextSnapshotCurrent(opts.expectedContext))
      || authScope !== getUserSessionScopeSuffix()
      || !authStore.isAuthenticated
      || authStore.loggingOut
    ) {
      return;
    }
    historyList.value = list;
    if (applyLast) {
      const current = sessionTabs.value.find((t) => t.sessionId === currentSessionId.value);
      if (!current && sessionTabs.value.length) {
        currentSessionId.value = sessionTabs.value[0].sessionId;
      }
      selectedSessionId.value = currentSessionId.value;
      writeLastSessionId(scenario, currentSessionId.value);
    }
  } catch (e) {
    if (gen !== conversationHistoryLoadGen || persistenceScenario.value !== scenario) {
      return;
    }
    if (
      authScope !== getUserSessionScopeSuffix()
      || !authStore.isAuthenticated
      || authStore.loggingOut
      || isIgnorableAuthError(e)
    ) return;
    console.error('加载会话历史失败', e);
    notifyConversationIssue('历史会话列表加载失败，列表可能不完整');
  }
}

function toMessageDtos(list: ChatMessage[]): ChatMessageDTO[] {
  return list.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    createdAt: m.createdAt,
    attachments: m.attachments?.filter(a => !a.url.startsWith('blob:')),
    thought: m.thought,
    structuredResult: m.structuredResult ?? null,
  }));
}

async function persistConversation(
  overrideTitle?: string,
  sessionId = effectiveSessionId(),
  scenario = effectivePersistenceScenario(),
) {
  if (!ENABLE_CONVERSATION_PERSISTENCE) {
    return;
  }
  const fullList = getSessionMessages(sessionId, scenario);
  if (!fullList.length) {
    return;
  }
  // 只有用户真的说过话，这段会话才值得在历史列表里占一条记录。各场景在会话开头
  // 注入的引导语（计算器的「请填写…参数」、组件库的「可点击 BOM 导入…」、AI 工具页的
  // 「我是 AI 计算器助手…」）是 UI 占位而不是对话内容；用户一次交互都没有就落库，
  // 历史列表会被刷满标题重复、正文只有一句引导语的空壳会话。
  // 已经在库里的会话不受此限制：用户删掉自己的消息后（removeUserMessage）剩下的
  // 可能只有 assistant 消息，这次更新必须照常写回，否则删除动作会丢。
  const hasUserMessage = fullList.some((m) => m.role === 'user');
  const alreadyInHistory = historyList.value.some((h) => h.sessionId === sessionId);
  if (!hasUserMessage && !alreadyInHistory) {
    return;
  }
  const titleKey = sessionTitleCacheKey(sessionId, scenario);
  // 仅任务标签可在落库前同步定标题；普通闲聊/问答留给 maybeGenerateSessionTitleAfterFirstTurn
  // 走 LLM，避免此处用首句截断并标记 persisted，导致 LLM 标题永远不覆盖。
  if (!overrideTitle && !sessionTitlePersisted.value.has(titleKey)) {
    const userCount = fullList.filter((m) => m.role === 'user').length;
    const assistantCount = fullList.filter((m) => m.role === 'assistant').length;
    if (userCount >= 1 && assistantCount >= 1) {
      const firstUser = fullList.find((m) => m.role === 'user');
      const taskTitle = firstUser ? inferTaskSessionTitle(firstUser.content) : null;
      if (taskTitle) {
        cacheSessionTitle(sessionId, taskTitle, scenario);
        sessionTitlePersisted.value.add(titleKey);
      }
    }
  }
  const messagesDto = toMessageDtos(fullList);
  const title =
    overrideTitle?.trim()
    || readSessionTitleCache(sessionId, scenario)
    || undefined;
  try {
    await saveAgentConversation({
      scenario,
      sessionId,
      routeName: currentRouteName.value,
      title,
      messages: messagesDto,
    });
  } catch (e) {
    console.error('保存会话失败', e);
    // 项目切换后这次保存属于旧场景的后台归档；失败不要用当前页的提示打断用户。
    if (scenario === persistenceScenario.value) {
      notifyConversationIssue('会话保存失败，本次对话可能不会出现在历史列表中');
    }
  }
  if (scenario === persistenceScenario.value) {
    try {
      await loadConversationHistory({ applyLastSession: false });
    } catch (e) {
      console.error('刷新会话列表失败', e);
    }
  }
}

async function loadHistoryItem(
  item: ConversationSummary,
  opts?: { notifyEmpty?: boolean; expectedContext?: PageContextSnapshot; userInitiated?: boolean },
): Promise<boolean> {
  if (!ENABLE_CONVERSATION_PERSISTENCE) {
    ElMessage.info('测试阶段已关闭历史会话加载');
    return false;
  }
  let expectedContext = opts?.expectedContext ?? capturePageContextSnapshot();
  try {
    const detail = await getAgentConversationDetail(item.id);
    if (!isPageContextSnapshotCurrent(expectedContext)) {
      if (!opts?.userInitiated) return false;
      // scenario 编码了项目与页签上下文：scenario 未变说明会话仍属于当前上下文，
      // 页签水合 / query 抖动造成的过期不应吞掉用户点击，重新捕获快照继续加载。
      if (expectedContext.scenario === persistenceScenario.value) {
        expectedContext = capturePageContextSnapshot();
      } else {
        ElMessage.warning('页面已切换，请重新打开该对话');
        void loadConversationHistory();
        return false;
      }
    }
    const parsed = parseConversationContent(detail.content);
    if (!parsed) {
      // 原来的 `if (!detail.content) return` 对 "[]" 不成立，会把空数组写进本地缓存，
      // 销毁这段对话在客户端仅存的那一份；读不出消息时必须原样保留并告知用户。
      // 项目切换时的自动恢复不要弹窗：空记录在并发加载下列表很容易对不上。
      if (opts?.notifyEmpty !== false) {
        ElMessage.warning('这条历史记录没有可恢复的消息内容，已保留当前对话');
      }
      return false;
    }
    const messages: ChatMessage[] = parsed.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      createdAt: m.createdAt,
      attachments: m.attachments,
      thought: m.thought,
      structuredResult: m.structuredResult ?? null,
    }));
    collapsedThoughtIds.value = new Set();
    const convScenario = expectedContext.scenario;
    setSessionMessages(messages, detail.sessionId, convScenario);
    if (detail.title?.trim()) {
      cacheSessionTitle(detail.sessionId, detail.title.trim(), convScenario);
      sessionTitlePersisted.value.add(sessionTitleCacheKey(detail.sessionId, convScenario));
    }
    currentSessionId.value = detail.sessionId;
    activeHistoryId.value = detail.id;
    selectedSessionId.value = detail.sessionId;
    writeLastSessionId(persistenceScenario.value, detail.sessionId);
    sessionNotify.markRead(persistenceScenario.value, detail.sessionId);
    mainTab.value = 'new';
    clearCompletedTypingPreviews(detail.sessionId, convScenario);
    isCollapsed.value = false;
    emit('collapse-change', false);
    schedulePersistPanelSettings();

    if (detail.routeName === 'ProjectCreate') {
      const sid = String(detail.sessionId || '').trim();
      let dk =
        projectCreateDraftStore.getDraftKeyBySessionId(sid)
        || projectCreateDraftStore.findDraftKeyBySessionIdStorageScan(sid);
      if (dk) {
        await router.push({ name: 'ProjectCreate', query: { draft: dk } });
        ElMessage.success('已打开项目创建草稿');
        return true;
      }
      const freshDk = projectCreateDraftStore.createDraft();
      await router.push({ name: 'ProjectCreate', query: { draft: freshDk } });
      ElMessage.info('未找到本地表单快照，已打开创建项目页并加载对话。可继续沟通或手动填写表单。');
    }
    return true;
  } catch (e) {
    console.error('加载会话详情失败', e);
    if (opts?.notifyEmpty !== false) {
      ElMessage.error('加载历史对话失败');
    }
    return false;
  }
}

function startHeaderTitleEdit() {
  editingSessionTitleDraft.value = currentSessionTitle.value;
  editingSessionTitle.value = true;
  void nextTick(() => {
    const el = sessionTitleInputRef.value;
    if (!el) return;
    el.focus();
    el.select();
  });
}

function cancelHeaderTitleEdit() {
  editingSessionTitle.value = false;
  editingSessionTitleDraft.value = '';
}

async function confirmHeaderTitleEdit() {
  if (!editingSessionTitle.value) return;
  const current = currentSessionTitle.value.trim();
  const nextTitle = editingSessionTitleDraft.value.trim();
  cancelHeaderTitleEdit();
  if (!nextTitle || nextTitle === current) return;
  await renameCurrentSessionTitle(nextTitle);
}

async function renameCurrentSessionTitle(rawTitle: string) {
  const sessionId = currentSessionId.value;
  const title = clampSessionTitle(rawTitle);
  try {
    await applySessionTitle(sessionId, title);
    const item = historyList.value.find((h) => h.sessionId === sessionId);
    if (item?.id) {
      await renameAgentConversation({ id: item.id, title });
      await loadConversationHistory();
    }
  } catch (e) {
    console.error('重命名会话标题失败', e);
    ElMessage.error('重命名会话标题失败');
  }
}

function startInlineRename(item: ConversationSummary) {
  editingHistoryId.value = item.id;
  editingHistoryTitle.value = (item.title || '对话').trim();
  void nextTick(() => {
    const el = historyTitleInputRef.value;
    if (!el) return;
    el.focus();
    el.select();
    // 将光标移动到文本末尾
    const len = el.value.length;
    el.setSelectionRange(len, len);
  });
}

function cancelInlineRename() {
  editingHistoryId.value = null;
  editingHistoryTitle.value = '';
}

async function confirmInlineRename(item: ConversationSummary) {
  const current = (item.title || '对话').trim();
  const nextTitle = editingHistoryTitle.value.trim();
  if (!nextTitle || nextTitle === current) {
    cancelInlineRename();
    return;
  }
  try {
    await renameAgentConversation({ id: item.id, title: nextTitle });
    await loadConversationHistory();
  } catch (e) {
    console.error('重命名会话失败', e);
    ElMessage.error('重命名会话失败');
  } finally {
    cancelInlineRename();
  }
}

async function confirmDelete(item: ConversationSummary): Promise<boolean> {
   
  const ok = window.confirm('确定要删除该会话吗？删除后不可恢复。');
  if (!ok) return false;
  try {
    await deleteAgentConversation(item.id);
    if (activeHistoryId.value === item.id) {
      activeHistoryId.value = null;
      clearSessionMessages(currentSessionId.value);
      aiInput.value = '';
    }
    await loadConversationHistory();
    return true;
  } catch (e) {
    console.error('删除会话失败', e);
    ElMessage.error('删除会话失败');
    return false;
  }
}

function selectSession(sessionId: string) {
  const item = historyList.value.find((h) => h.sessionId === sessionId);
  if (item) {
    void loadHistoryItem(item, { userInitiated: true });
  }
}

function handleSelectSession() {
  if (!selectedSessionId.value) return;
  selectSession(selectedSessionId.value);
}

function genId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function addFilesToAttachmentList(files: File[]) {
  const msg = validateChatAttachmentFiles(files, {
    total: attachmentList.value.length,
    images: attachmentList.value.filter((item) => item.type === 'image').length,
  });
  if (msg) {
    ElMessage.warning(msg);
    return;
  }
  for (const file of files) {
    const item: AttachmentItem = {
      id: genId(),
      file,
      name: file.name,
      type: isChatImageFile(file) ? 'image' : 'doc',
      progress: 0,
    };
    attachmentList.value.push(item);
    doUploadAttachment(item);
  }
}

function doUploadAttachment(item: AttachmentItem) {
  let cancelled = false;
  item.cancel = () => { cancelled = true; };
  const sessionId = currentSessionId.value ?? undefined;
  uploadChatFile(item.file, sessionId)
    .then((res) => {
      if (cancelled) return;
      const objectUrl = item.type === 'image' ? URL.createObjectURL(item.file) : undefined;
      const idx = attachmentList.value.findIndex((a) => a.id === item.id);
      if (idx >= 0) {
        attachmentList.value[idx] = {
          ...item,
          progress: 100,
          objectUrl,
          token: res.token,
          accessUrl: res.accessUrl,
          objectKey: res.objectKey,
          processingName: res.processingName || res.fileName,
          processingObjectKey: res.processingObjectKey || res.objectKey,
          processingUrl: res.processingAccessUrl || res.accessUrl,
        };
      }
    })
    .catch((err: { message?: string }) => {
      if (cancelled) return;
      const idx = attachmentList.value.findIndex((a) => a.id === item.id);
      if (idx >= 0) {
        attachmentList.value[idx] = {
          ...item,
          progress: 0,
          error: err?.message ?? '上传失败',
        };
      }
    });
  // 先给一个“上传中”进度
  item.progress = 20;
}

function onDrop(e: DragEvent) {
  panelDragDepth.value = 0;
  isDragging.value = false;
  const list = e.dataTransfer?.files;
  if (!list?.length) return;
  addFilesToAttachmentList(Array.from(list));
}

function onFileSelect(e: Event) {
  const input = e.target as HTMLInputElement;
  const list = input.files;
  if (!list?.length) return;
  addFilesToAttachmentList(Array.from(list));
  input.value = '';
}

function cancelUpload(item: AttachmentItem) {
  item.cancel?.();
  item.progress = 0;
  item.error = '已取消';
}

function removeAttachment(item: AttachmentItem) {
  if (item.objectUrl && item.objectUrl.startsWith('blob:')) URL.revokeObjectURL(item.objectUrl);
  if (item.id.startsWith('paste_')) pendingImageBase64 = null;
  if (item.id.startsWith('paste_')) (window as any).__aipdPendingImage = null;
  attachmentList.value = attachmentList.value.filter((a) => a.id !== item.id);
}

function getCompletedAttachments(): AttachmentItem[] {
  return attachmentList.value.filter((a) => a.progress >= 100 && !a.error);
}

async function runNonProjectDrawingReviewFromChat(
  text: string,
  messageAttachments: ChatMessageAttachment[],
  sourceAttachments?: AttachmentItem[],
) {
  const drawingAtt = findDrawingAttachment(messageAttachments);
  if (!drawingAtt) return;
  const uploaded = sourceAttachments?.find((a) => {
    if (drawingAtt.url && a.accessUrl === drawingAtt.url) return true;
    return a.name === drawingAtt.name;
  });
  const file = uploaded?.file ?? null;
  if (!file) {
    ElMessage.warning('无法获取图纸文件，请重新拖入后发送');
    return;
  }

  const drawingLabel = drawingAtt.name || file.name || '图纸';
  const displayText = text.trim() || '请分析该图纸的问题';
  const optionsJson = JSON.stringify({ strictness: 'normal', source: 'chat' });

  if (currentRouteName.value !== 'AiSmartReview') {
    await router.push({ name: 'AiSmartReview' }).catch((err: any) => { if (err.name !== 'NavigationDuplicated') console.error(err); });
    await nextTick();
  }

  let reviewObjectKey = '';
  await runDrawingReview({
    drawingLabel,
    userMessage: displayText,
    userAttachments: messageAttachments,
    options: optionsJson,
    submitAndPoll: async (onProgress) => {
      const uploadResult = await uploadDrawing(file);
      reviewObjectKey = uploadResult.storedPath;
      window.dispatchEvent(new CustomEvent('aipd-ai-review-drawings-changed'));
      const { taskId } = await submitDesignReviewTask(reviewObjectKey, undefined, optionsJson);
      setAiReviewPolling(reviewObjectKey, taskId);
      const result = await pollDesignReviewResult(taskId, 1500, onProgress);
      setAiReviewDone(reviewObjectKey, taskId, result);
      return result;
    },
    onOpenReport: async () => {
      if (currentRouteName.value !== 'AiSmartReview') {
        await router.push({ name: 'AiSmartReview' }).catch((err: any) => { if (err.name !== 'NavigationDuplicated') console.error(err); });
        await nextTick();
      }
      window.dispatchEvent(new CustomEvent('aipd-ai-review-open-report', {
        detail: { objectKey: reviewObjectKey, fileName: drawingLabel },
      }));
    },
  });
}

async function runChatAttachmentDrawingReview(
  text: string,
  messageAttachments: ChatMessageAttachment[],
  sourceAttachments?: AttachmentItem[],
) {
  if (!hasProjectContext.value || projectId.value <= 0) {
    await runNonProjectDrawingReviewFromChat(text, messageAttachments, sourceAttachments);
    return;
  }
  const drawingAtt = findDrawingAttachment(messageAttachments);
  if (!drawingAtt) return;
  const accessUrl = String(drawingAtt.url ?? '').trim();
  const uploaded = sourceAttachments?.find((a) => {
    if (a.accessUrl && a.accessUrl === accessUrl) return true;
    return a.name === drawingAtt.name && Boolean(a.objectKey || a.accessUrl);
  });
  const objectKey =
    uploaded?.objectKey
    || drawingAtt.objectKey
    || extractObjectKeyFromFileAccessUrl(accessUrl);
  if (!accessUrl && !objectKey) {
    ElMessage.warning('无法识别图纸附件，请重新上传后重试');
    return;
  }
  const drawingLabel = drawingAtt.name || issueAttachmentFileName(accessUrl) || '图纸';
  const displayText = text.trim() || '请分析该图纸的问题';
  await runDrawingReview({
    drawingLabel,
    userMessage: displayText,
    userAttachments: messageAttachments,
    options: JSON.stringify({ strictness: 'normal', source: 'chat' }),
    submitAndPoll: async (onProgress) => {
      const { taskId } = await submitChatDrawingReviewTask(
        { accessUrl: accessUrl || undefined, objectKey: objectKey || undefined },
        undefined,
        JSON.stringify({ strictness: 'normal', source: 'chat' }),
      );
      return pollChatDrawingReviewResult(taskId, 1500, onProgress);
    },
  });
}

function getLastCompletedSorAnalyzeMessage(): ChatMessage | null {
  const list = getSessionMessages();
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const m = list[i];
    if (m.role !== 'assistant') continue;
    const sr = m.structuredResult as Record<string, unknown> | null | undefined;
    if (sr?.kind === 'sor_requirement_analyze' && sr?.status === 'completed') {
      return m;
    }
  }
  return null;
}

function isShortAffirmativeReply(text: string): boolean {
  const t = text.trim();
  return /^(需要|好的|可以|行|嗯|要|继续|是的|是的[,，]?需要|要的)[。.!！]?$/u.test(t);
}

async function tryReplySorAnalyzeAffirmative(userText: string): Promise<boolean> {
  const lastSor = getLastCompletedSorAnalyzeMessage();
  if (!lastSor || !isShortAffirmativeReply(userText)) return false;
  const sr = (lastSor.structuredResult || {}) as Record<string, unknown>;
  const docName = String(sr.docName || 'SOR 文档');
  const count = Number(sr.requirementCount || 0);
  const reply = [
    `已确认。SOR 需求解析结果已写入左侧需求清单（《${docName}》，共 ${count} 条）。`,
    '您可以直接在清单中查看、编辑各条目的包需求、设计需求、设计规格与测试规格。',
    '若需讨论某几条具体需求，请在清单中引用对应行后向我提问，我会结合该条目内容作答。',
  ].join('\n');
  addSessionMessage('user', userText);
  const assistantMsg = addSessionMessage(
    'assistant',
    reply,
    undefined,
    undefined,
    { kind: 'sor_requirement_analyze', status: 'followup', docName, requirementCount: count },
  );
  await nextTick();
  await revealAssistantMessage(assistantMsg.id, effectiveSessionId(), conversationScenario.value);
  await persistConversation();
  return true;
}

// 待发送的截图 base64（由 ExpandedAiComposer 粘贴事件设置）
let pendingImageBase64: string | null = null;
const pendingImagePreview = ref('');

function onAiInputPaste(e: ClipboardEvent) {
  const items = e.clipboardData?.items;
  if (!items) return;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type.startsWith('image/')) {
      e.preventDefault();
      const file = item.getAsFile();
      if (!file) continue;
      const msg = validateChatAttachmentFiles([file], {
        total: attachmentList.value.length,
        images: attachmentList.value.filter((attachment) => attachment.type === 'image').length,
      });
      if (msg) {
        ElMessage.warning(msg);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = (reader.result as string).split(',')[1] || (reader.result as string);
        pendingImageBase64 = b64;
        const url = reader.result as string;
        attachmentList.value.push({ id: 'paste_' + Date.now(), name: 'Screenshot', type: 'image' as const, objectUrl: url, progress: 100, file });
      };
      reader.readAsDataURL(file);
      return;
    }
  }
}

async function sendAiMessageDirect(
  text: string,
  messageAttachments: ChatMessageAttachment[],
  sourceAttachments?: AttachmentItem[],
) {
  const normalized = text.trim();
  const allowEmptyForPcFieldEdit =
    projectCreateFieldEditPending.value &&
    currentRouteName.value === 'ProjectCreate';
  const allowEmptyForPageFieldEdit = !!pageContentEditPending.value;
  const allowEmptyForSmartDocSkill = smartDocAi.active && !!smartDocAi.selectedSkillId;
  const allowEmptyForSmartDocTemplate =
    smartDocAi.hasTemplateAtToken || smartDocTemplateGenerate.canContinue;
  if (
    !normalized
    && !messageAttachments.length
    && !allowEmptyForPcFieldEdit
    && !allowEmptyForPageFieldEdit
    && !allowEmptyForSmartDocSkill
    && !allowEmptyForSmartDocTemplate
  ) return;
  if (!(await resolvePendingRevertBeforeSend())) return;
  snapshotComposerDraft(normalized);
  if (await tryReplySorAnalyzeAffirmative(normalized)) return;
  if (await tryRenameExistingProjectFromChat(normalized)) return;
  if (await tryHandleWbsHoursDialogue(normalized)) return;
  // 创建页：一诺多候选 WBS 模板确认回复
  try {
    const applyTpl = (window as unknown as { __aipdTryApplyYinuoWbsTemplate?: (t: string) => boolean })
      .__aipdTryApplyYinuoWbsTemplate;
    if (typeof applyTpl === 'function' && applyTpl(normalized)) {
      addSessionMessage('user', normalized);
      await persistConversation();
      return;
    }
  } catch {
    /* ignore */
  }
  if (await tryCreateWbsTaskFromChat(normalized)) return;
  if (shouldRouteAttachmentToDrawingReview(normalized, messageAttachments)) {
    await runChatAttachmentDrawingReview(normalized, messageAttachments, sourceAttachments);
    return;
  }
  // 智能文档：附件导入 → 对话内另存为模版进度（无弹窗）
  {
    const importFile = shouldRouteSmartDocFileToSaveAsTemplate(normalized, sourceAttachments);
    if (importFile) {
      if (smartDocTemplateAuthor.active) {
        smartDocTemplateAuthor.endTemplateAuthoring();
      }
      bindSmartDocSaveAsFromFileContext();
      aiLoading.value = true;
      isThinking.value = true;
      try {
        await smartDocSaveAsFromFile.startFromFile(importFile, normalized);
        // 确保进入智能文档工作台以展示提炼向导
        const name = String(route.name || '');
        const wid = smartDocAi.workspaceId || '0';
        if (name !== 'SmartDocumentWorkbench' && name !== 'SmartDocumentWorkbenchHome') {
          await router.push(
            wid === '0'
              ? { name: 'SmartDocumentWorkbenchHome' }
              : { name: 'SmartDocumentWorkbench', params: { id: wid } },
          );
        }
        await persistConversation();
      } finally {
        isThinking.value = false;
        aiLoading.value = false;
      }
      await nextTick();
      scrollAiToBottom();
      return;
    }
  }
  {
    const { useCalculatorFormulaImportStore } = await import('@/stores/useCalculatorFormulaImportStore');
    const importStore = useCalculatorFormulaImportStore();
    // 公式导入会话：以 importStore.active 为准；场景键应对齐 calculator_formula_import
    if (
      importStore.active
      && (
        conversationScenario.value === CALCULATOR_FORMULA_IMPORT_SCENARIO
        || currentRouteName.value === 'AiCalculator'
      )
    ) {
      if (store.currentScenario !== CALCULATOR_FORMULA_IMPORT_SCENARIO) {
        store.currentScenario = CALCULATOR_FORMULA_IMPORT_SCENARIO;
      }
      expandRightPanel(true);
      mainTab.value = 'new';
      try {
        await importStore.sendUserMessage(normalized);
      } finally {
        await nextTick();
        scrollAiToBottom();
      }
      return;
    }
  }
  // 智能文档：仅继续已开启的模版创作会话（不再通过意图新进入对话创建）
  if (smartDocAi.active && smartDocTemplateAuthor.active) {
    const entering = false;
    smartDocTemplateAuthor.bindPanelContext({
      scenario: conversationScenario.value,
      sessionId: currentSessionId.value,
    });
    if (entering) {
      if (smartDocSkillAuthor.active) {
        smartDocSkillAuthor.endSkillAuthoring();
      }
      await beginSmartDocTemplateAuthoring();
    }
    const pureIntent = false;
    if (entering && pureIntent) {
      return;
    }
    isThinking.value = true;
    isStreaming.value = true;
    streamStopped.value = false;
    currentThoughtDraft.value = [];
    const abort = new AbortController();
    streamAbortController.value = abort;
    aiLoading.value = true;
    try {
      smartDocTemplateAuthor.bindPanelContext({
        scenario: conversationScenario.value,
        sessionId: currentSessionId.value,
      });
      await smartDocTemplateAuthor.sendUserMessage(normalized, {
        signal: abort.signal,
        onThought: (steps) => {
          currentThoughtDraft.value = steps;
          void nextTick().then(() => scrollAiToBottom());
        },
      });
      await persistConversation();
    } finally {
      isThinking.value = false;
      isStreaming.value = false;
      if (streamAbortController.value === abort) {
        streamAbortController.value = null;
      }
      currentThoughtDraft.value = [];
      aiLoading.value = false;
    }
    await nextTick();
    scrollAiToBottom();
    return;
  }
  // 智能文档：技能创作 / 优化多轮对话
  if (smartDocAi.active && (smartDocSkillAuthor.active || isCreateSkillIntent(normalized))) {
    const entering = !smartDocSkillAuthor.active && isCreateSkillIntent(normalized);
    smartDocSkillAuthor.bindPanelContext({
      scenario: conversationScenario.value,
      sessionId: currentSessionId.value,
    });
    if (entering) {
      await beginSmartDocSkillAuthoring('create');
    }
    const pureIntent = /^(?:请|帮我)?(?:创建|新建|做一个)(?:一个|一项|一份)?(?:Skill|技能|方法)(?:吧|呀)?[。.!！]?$/i
      .test(normalized.trim());
    if (entering && pureIntent) {
      return;
    }
    isThinking.value = true;
    isStreaming.value = true;
    streamStopped.value = false;
    currentThoughtDraft.value = [];
    const abort = new AbortController();
    streamAbortController.value = abort;
    aiLoading.value = true;
    try {
      smartDocSkillAuthor.bindPanelContext({
        scenario: conversationScenario.value,
        sessionId: currentSessionId.value,
      });
      await smartDocSkillAuthor.sendUserMessage(normalized, {
        signal: abort.signal,
        onThought: (steps) => {
          currentThoughtDraft.value = steps;
          void nextTick().then(() => scrollAiToBottom());
        },
      });
      await persistConversation();
    } finally {
      isThinking.value = false;
      isStreaming.value = false;
      if (streamAbortController.value === abort) {
        streamAbortController.value = null;
      }
      currentThoughtDraft.value = [];
      aiLoading.value = false;
    }
    await nextTick();
    scrollAiToBottom();
    return;
  }
  // 智能文档：模版引用多轮生成（优先于纯技能执行；可附带多个技能）
  // 以输入文案中的 @模板 为兜底：发送清空输入区时 token 可能已被剪枝
  if (shouldRouteToSmartDocTemplateGenerate(normalized)) {
    await ensureSmartDocProjectTokenFromInput(normalized);
    const ok = await ensureSmartDocTemplateTokenFromInput(normalized);
    if (ok && (smartDocAi.hasTemplateAtToken || smartDocTemplateGenerate.canContinue)) {
      await sendSmartDocumentTemplateGenerateMessage(normalized, messageAttachments);
      return;
    }
    if (!ok) return;
  }
  // 智能文档：Skill 落库目标澄清（新建 vs 改当前），优先于重新执行 Skill
  if (
    smartDocAi.active
    && smartDocAi.pendingApplyTargetClarification
    && smartDocAi.execution?.executionId
    && !textHasSmartDocTemplateMention(normalized)
  ) {
    await sendSmartDocumentSkillApplyTargetClarification(normalized);
    return;
  }
  // 智能文档：技能多轮补充（上一轮生成留下的待确认问题）
  if (
    smartDocAi.active
    && smartDocAi.selectedSkillId
    && Array.isArray(smartDocAi.pendingFollowUpQuestions)
    && smartDocAi.pendingFollowUpQuestions.length > 0
    && !textHasSmartDocTemplateMention(normalized)
  ) {
    if (
      smartDocAi.lastSkillProjectRefId
      && !smartDocAi.atTokens.some((t) => t.kind === 'project')
    ) {
      smartDocAi.addAtRef({
        kind: 'project',
        name: smartDocAi.lastSkillProjectName || '项目',
        refId: smartDocAi.lastSkillProjectRefId,
        label: buildSmartDocProjectOnlyLabel(smartDocAi.lastSkillProjectName || '项目'),
      });
    }
    const followText =
      `【用户补充信息】\n${normalized}\n\n`
      + '请根据以上补充信息，修订产品总体方案设计书中对应的【待确认】/【建议值】项，输出完整 HTML 文档正文。';
    smartDocAi.clearPendingFollowUp();
    await sendSmartDocumentSkillMessage(followText);
    return;
  }
  // 智能文档：已选 Skill / 输入含 @技能 时走 Skill 解析生成，禁止落入客服手册问答
  if (shouldRouteToSmartDocSkillExecution(normalized)) {
    const skillOk = await ensureSmartDocSkillTokenFromInput(normalized);
    if (skillOk && smartDocAi.selectedSkillId) {
      // @项目 缺失时尽量回填；失败不阻断技能执行（后端也会从文案解析）
      await ensureSmartDocProjectTokenFromInput(normalized);
      await sendSmartDocumentSkillMessage(normalized);
      return;
    }
    if (!skillOk) return;
    ElMessage.warning('请先选择 Skill 后再生成');
    return;
  }
  // 已 @技能 但未选 Skill：引导打开工作室（兜底，正常已被上方拦截）
  if (smartDocAi.active && smartDocAi.hasSkillAtToken && !smartDocAi.selectedSkillId) {
    openSmartDocSkillStudio('list');
    ElMessage.warning('请先选择 Skill 后再生成');
    return;
  }
  // 智能文档：输出意图澄清（新建/合入/修正/问答）多轮补充
  if (
    isOnSmartDocumentWorkbenchRoute()
    && smartDocAi.pendingOutputIntentClarification
    && !textHasSmartDocTemplateMention(normalized)
    && !shouldRouteToSmartDocSkillExecution(normalized)
  ) {
    await sendSmartDocumentIntelligentMessage(normalized, messageAttachments);
    return;
  }
  if (isExplicitNewProjectIntent(normalized)) {
    pageContentEditPending.value = null;
    projectCreateFieldEditPending.value = null;
    mainTab.value = 'new';
    projectSelectionVisible.value = false;
    pendingProjectMessage.value = null;
    if (!canAccessProjectCreateRoute(permissionStore)) {
      await replyProjectCreateNoAccess(normalized, messageAttachments);
      return;
    }
    projectPromptMessage.value = '已识别为新建项目需求，正在创建独立项目草稿。';
    await startProjectCreateNewProject(normalized, messageAttachments);
    return;
  }
  // 智能文档页：由 LLM 判定创建文档 / 合入 / 修正 / 仅问答（不确定则澄清）
  // 必须以当前路由为准，禁止用 sticky smartDocAi.active（keep-alive 切到项目页后会把普通提问全标成「智能文档对话」）
  if (
    isOnSmartDocumentWorkbenchRoute()
    && !textHasSmartDocTemplateMention(normalized)
    && !smartDocAi.hasSkillAtToken
    && !smartDocAi.selectedSkillId
  ) {
    ensureSmartDocWorkspaceBound({ force: true });
    await sendSmartDocumentIntelligentMessage(normalized, messageAttachments);
    return;
  }
  if (
    pageContentEditPending.value &&
    currentRouteName.value !== 'ProjectCreate'
  ) {
    const pending = pageContentEditPending.value;
    pageContentEditPending.value = null;
    const discussIntent = pending.mode === 'discuss' || isPageContentDiscussIntent(normalized);
    if (discussIntent) {
      await sendPageContentDiscussMessage(pending, normalized || '(附带附件)', messageAttachments);
    } else {
      await sendPageContentFieldEditMessage(pending, normalized || '(附带附件)', messageAttachments);
    }
    return;
  }
  if (
    projectCreateFieldEditPending.value &&
    currentRouteName.value === 'ProjectCreate'
  ) {
    const pending = projectCreateFieldEditPending.value;
    projectCreateFieldEditPending.value = null;
    await sendProjectCreateFieldEditMessage(pending, normalized || '(附带附件)', messageAttachments);
    return;
  }
  const continueCreateProject =
    !isProjectStatusOrReportIntent(normalized) &&
    !isRenameExistingProjectIntent(normalized) &&
    shouldContinueProjectCreateConversation({
      routeName: currentRouteName.value,
      hasProjectContext: hasProjectContext.value,
      hasPendingPageEdit: Boolean(pageContentEditPending.value),
      createIntent: isCreateProjectIntent(normalized),
      lastAssistantSuggestedCreateProject: lastAssistantSuggestedCreateProject(),
      likelyCreateSupplement: isLikelyCreateProjectSupplementText(normalized),
    });
  if (continueCreateProject) {
    mainTab.value = 'new';
    projectSelectionVisible.value = false;
    pendingProjectMessage.value = null;
    if (!canAccessProjectCreateRoute(permissionStore)) {
      await replyProjectCreateNoAccess(normalized, messageAttachments);
      return;
    }
    if (currentRouteName.value === 'ProjectCreate') {
      projectPromptMessage.value = '正在更新当前项目草稿。';
      await sendAiMessageWithProject('', normalized, messageAttachments, {
        projectCreateMode: 'update_current_draft',
        draftKey: currentProjectCreateDraftKey(),
      });
    } else {
      const messages = getSessionMessages(currentSessionId.value, conversationScenario.value);
      const hasUserMessages = messages.some((m) => m.role === 'user');
      const lastCreate = lastAssistantSuggestedCreateProject();
      if (shouldForkProjectCreateConversation({
        explicitNew: false,
        hasUserMessages,
        lastAssistantSuggestedCreateProject: lastCreate,
      })) {
        projectPromptMessage.value = '正在创建独立项目草稿。';
        await startProjectCreateNewProject(normalized, messageAttachments);
      } else {
        projectPromptMessage.value = '已识别为创建项目需求，正在进入创建项目对话流程。';
        // 已在创建流程中的后续轮次保留旧会话的可信字段；不传显式模式，走兼容的 Dashboard 新建逻辑。
        await sendCreateProjectIntentResponse(normalized, messageAttachments);
      }
    }
    return;
  }
  // 「需要项目上下文」的选项目弹窗不再由前端关键词在发送前拦截：
  // 消息一律先发，由 sendAiMessageWithProject 依据后端意图（PROJECT_STATUS）当轮触发，
  // 避免长名称创建指令等被关键词误判后连发送机会都没有
  const requestSnapshot = capturePageContextSnapshot();
  if (hasProjectContext.value && requestSnapshot.projectId <= 0) {
    ElMessage.info('当前项目上下文正在加载，请稍后重试');
    return;
  }
  const chatProjectId = hasProjectContext.value
    ? String(requestSnapshot.projectId)
    : await resolveChatProjectId(normalized);
  await sendAiMessageWithProject(
    chatProjectId,
    normalized,
    messageAttachments,
    currentRouteName.value === 'ProjectCreate'
      ? { projectCreateMode: 'update_current_draft', draftKey: currentProjectCreateDraftKey() }
      : {},
    requestSnapshot,
  );
}

async function sendAiMessage() {
  if (!canSend.value) return;
  const text = aiInput.value.trim();
  const completed = getCompletedAttachments();
  const hasPcFieldRef =
    projectCreateFieldEditPending.value &&
    currentRouteName.value === 'ProjectCreate';
  const hasPageFieldRef = !!pageContentEditPending.value;
  const hasSmartDocSkill = smartDocAi.active && !!smartDocAi.selectedSkillId;
  const hasSmartDocTemplate = smartDocAi.hasTemplateAtToken
    || smartDocTemplateGenerate.canContinue
    || textHasSmartDocTemplateMention(text);
  const hasContent = text || completed.length > 0 || !!hasPcFieldRef || !!hasPageFieldRef || hasSmartDocSkill || hasSmartDocTemplate;
  if (!hasContent) return;

  const messageAttachments: ChatMessageAttachment[] = completed
    .filter((a) =>
      (a.accessUrl != null && String(a.accessUrl).trim() !== '')
      || (a.objectKey != null && String(a.objectKey).trim() !== ''))
    .map((a) => ({
      name: a.name,
      processingName: a.processingName,
      processingObjectKey: a.processingObjectKey,
      processingUrl: a.processingUrl,
      url: a.accessUrl != null ? String(a.accessUrl) : '',
      type: a.type === 'image' ? 'image' : 'document',
      objectKey: a.objectKey,
    }));

  const completedSnap = [...completed];
  snapshotComposerDraft(text);
  // 先清空输入再发消息：空文本不会 prune @token；文本已快照到 text 参数
  if (!pendingImageBase64) {
    for (const att of messageAttachments) {
      if (att.type === 'image' && att.url) {
        try {
          const imgResp = await fetch(att.url);
          const blob = await imgResp.blob();
          pendingImageBase64 = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => { const s = r.result as string; resolve(s.split(',')[1] || s); };
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
          pendingImagePreview.value = URL.createObjectURL(blob);
        } catch (e) { /* fallback */ }
        break;
      }
    }
  }

  try {
    // 立即清空输入区（空文本不会 prune @token），避免等 AI 回复完才清空
    clearComposerDraftForCurrentScenario();
    attachmentList.value.forEach((a) => {
      if (a.objectUrl) URL.revokeObjectURL(a.objectUrl);
    });
    attachmentList.value = [];
    await sendAiMessageDirect(text, messageAttachments, completedSnap);
  } catch (e) {
    // 发送失败时恢复草稿，避免用户内容丢失
    if (text) {
      aiInput.value = text;
      snapshotComposerDraft(text);
    }
    throw e;
  }
}

/** 项目管理页「展开式」输入区：外部文本走与侧栏相同的发送逻辑；发送后清空侧栏草稿，避免残留 */
async function submitExpandedInput(external?: string, externalAttachments: ChatMessageAttachment[] = []) {
  const t = typeof external === 'string' ? external.trim() : '';
  const text = t || aiInput.value.trim();
  snapshotComposerDraft(text);
  await nextTick();
  try {
    clearComposerDraftForCurrentScenario();
    await sendAiMessageDirect(text, externalAttachments);
  } catch (e) {
    if (text) {
      aiInput.value = text;
      snapshotComposerDraft(text);
    }
    throw e;
  }
  await nextTick();
  focusComposer();
}

function setDraftMessage(text: string) {
  aiInput.value = text;
  void nextTick(() => focusComposer());
}

function focusComposer() {
  const el = document.querySelector('.ai-textarea') as HTMLTextAreaElement | null;
  el?.focus();
  const len = el?.value?.length ?? 0;
  el?.setSelectionRange(len, len);
}

function expandRightPanel(force = false) {
  if (isCollapsed.value && suppressAutoExpand.value && !force) return;
  if (isCollapsed.value) {
    isCollapsed.value = false;
    emit('collapse-change', false);
    schedulePersistPanelSettings();
  }
  if (force) {
    suppressAutoExpand.value = false;
  }
}

function scrollAiToBottom() {
  const el = aiChatDisplayRef.value ?? (document.querySelector('.ai-chat-display') as HTMLElement | null);
  if (el) {
    el.scrollTop = el.scrollHeight;
  }
  // 思考步骤预览有独立 max-height 滚动区，同步滚到底避免最新步骤被裁切
  const root = el ?? document;
  const clips = (root instanceof HTMLElement ? root : document).querySelectorAll(
    '.ai-message-thought--live .ai-thought-preview-clip',
  );
  clips.forEach((clip) => {
    const node = clip as HTMLElement;
    node.scrollTop = node.scrollHeight;
  });
}

function setCalculatorFormulaImportBusy(busy: boolean) {
  if (busy) {
    formulaImportBusyOwned.value = true;
    expandRightPanel(true);
    mainTab.value = 'new';
    isThinking.value = true;
    aiLoading.value = true;
    void nextTick().then(() => scrollAiToBottom());
    return;
  }
  if (!formulaImportBusyOwned.value) return;
  formulaImportBusyOwned.value = false;
  if (!taskBoundScenario.value) {
    isThinking.value = false;
    aiLoading.value = false;
  }
}

function bindCalculatorFormulaImportPanelHost() {
  calculatorFormulaImportStore.bindPanelHost({
    getCurrentSessionId: () => currentSessionId.value,
    setImportSessionTitle,
    archiveAndStartNewSession,
    persistConversation: async (overrideTitle?: string) => {
      await persistConversation(overrideTitle);
    },
    expandRightPanel,
    setImportBusy: setCalculatorFormulaImportBusy,
    scrollToBottom: scrollAiToBottom,
  });
}

function onChatScroll(e: Event) {
  const el = e.target as HTMLElement;
  if (!el) return;
  // 向上滚动接近顶部时，按步长增加可见消息数量，实现隐式加载历史消息
  if (el.scrollTop <= 20) {
    const fullList = getSessionMessages();
    if (visibleMessageCount.value < fullList.length) {
      const prevHeight = el.scrollHeight;
      visibleMessageCount.value = Math.min(
        visibleMessageCount.value + VISIBLE_MESSAGE_STEP,
        fullList.length,
      );
      void nextTick().then(() => {
        const newHeight = el.scrollHeight;
        el.scrollTop = newHeight - prevHeight + el.scrollTop;
      });
    }
  }
}

function toggleStreamPause() {
  if (!isStreaming.value) return;
  isStreamPaused.value = !isStreamPaused.value;
}

function stopCurrentStream() {
  if (!isStreaming.value && !streamAbortController.value && !smartDocSkillAuthor.sending && !smartDocTemplateAuthor.sending && !smartDocTemplateGenerate.sending && !smartDocAi.sending) return;
  streamStopped.value = true;
  streamAbortController.value?.abort();
  streamAbortController.value = null;
  if (smartDocAi.active) {
    smartDocSkillAuthor.cancelSending();
    smartDocTemplateAuthor.cancelSending();
    smartDocTemplateGenerate.cancelSending();
  }
  isStreaming.value = false;
  isStreamPaused.value = false;
  isThinking.value = false;
  currentThoughtDraft.value = [];
  if (currentAssistantId.value) {
    const list = getSessionMessages();
    const lastAssistant = [...list].reverse().find(
      (m) => m.role === 'assistant' && m.id === currentAssistantId.value,
    );
    if (lastAssistant && !lastAssistant.content) {
      lastAssistant.content = '已终止本次任务。';
    }
  }
}

onMounted(() => {
  bindCalculatorFormulaImportPanelHost();
  emit('collapse-change', isCollapsed.value);
  emit('width-change', panelWidth.value);
  syncFloatBtnPositionOnResize();
  window.addEventListener('resize', syncFloatBtnPositionOnResize);
  window.addEventListener('pointerdown', onDocPointerDownForCompactHistory, true);
  window.addEventListener('resize', updateCompactHistoryPopoverPosition);
  window.addEventListener('scroll', updateCompactHistoryPopoverPosition, true);
  if (typeof ResizeObserver !== 'undefined') {
    headerActionsResizeObserver = new ResizeObserver(() => updateCompactHeaderActions());
    if (aiHeaderRef.value) headerActionsResizeObserver.observe(aiHeaderRef.value);
  }
  void nextTick(() => updateCompactHeaderActions());
  void ensureUploadLimits();
  void loadAiChatOptions();
  loadDashboardForPanel();
  // 加载历史对话后，确保滚动到底部
  loadConversationHistory().then(() => {
    // 等待 DOM 渲染完成后滚动到底部
    void nextTick().then(() => {
      requestAnimationFrame(() => {
        scrollAiToBottom();
      });
    });
  });
});

const unsubWbsManualEdit = subscribeProjectWbsTasksManuallyEdited((projectId) => {
  markProjectWbsManuallyEdited(projectId);
});

onBeforeUnmount(() => {
  calculatorFormulaImportStore.bindPanelHost(null);
  conversationHistoryLoadGen += 1;
  window.removeEventListener('resize', syncFloatBtnPositionOnResize);
  window.removeEventListener('pointerdown', onDocPointerDownForCompactHistory, true);
  window.removeEventListener('resize', updateCompactHistoryPopoverPosition);
  window.removeEventListener('scroll', updateCompactHistoryPopoverPosition, true);
  headerActionsResizeObserver?.disconnect();
  headerActionsResizeObserver = null;
  cleanupFloatBtnDragListeners();
  unsubWbsManualEdit();
});

watch(compactHeaderActions, (compact) => {
  if (!compact) closeCompactHistoryPopover();
});

watch(isCollapsed, (collapsed) => {
  if (collapsed) closeCompactHistoryPopover();
});

// 用户切换/重新登录时（userId/tenant 变化）加载该用户的面板设置，并同步布局
watch(
  () => shellStore.sidebarCollapsed,
  () => {
    syncFloatBtnPositionOnResize();
  },
);

watch(
  () => [isCollapsed.value, panelWidth.value, props.embeddedLayout] as const,
  () => {
    if (isCollapsed.value) return;
    void nextTick(() => {
      if (aiHeaderRef.value && headerActionsResizeObserver) {
        headerActionsResizeObserver.observe(aiHeaderRef.value);
      }
      updateCompactHeaderActions();
    });
  },
);

watch(
  () => settingsStorageKey.value,
  () => {
    applyPanelSettings(readPanelSettings());
    emit('collapse-change', isCollapsed.value);
    emit('width-change', panelWidth.value);
    currentSessionId.value = generateSessionId();
    activeHistoryId.value = null;
    selectedSessionId.value = currentSessionId.value;
    historyList.value = [];
    visibleMessageCount.value = VISIBLE_MESSAGE_INITIAL;
    collapsedThoughtIds.value = new Set();
    aiInput.value = '';
    attachmentList.value = [];
    projectCreateFieldEditPending.value = null;
    void loadConversationHistory({ applyLastSession: true });
  },
);

// 设置变化自动持久化
watch(
  () => [isCollapsed.value, inputInnerHeight.value, panelWidth.value, mainTab.value, floatBtnPosition.value] as const,
  () => {
    schedulePersistPanelSettings();
  }
);

// 项目 ID 变化时刷新指挥舱数据
watch(
  () => projectId.value,
  () => {
    loadDashboardForPanel();
  },
  { immediate: false }
);

// 消息列表变化时（加载历史后），滚动到底部
watch(
  () => aiMessages.value.length,
  (newLen, oldLen) => {
    if (newLen > 0 && newLen !== oldLen) {
      void nextTick().then(() => {
        requestAnimationFrame(() => {
          scrollAiToBottom();
        });
      });
    }
  }
);

// 思考步骤 / 打字机预览增长时跟随滚到底（用户已上滑阅读历史时不打断）
watch(
  [liveThoughtSteps, isTaskThinkingVisible, typingContentMap],
  () => {
    scheduleFollowChatToBottomIfPinned();
  },
  { deep: true },
);

// 页面上下文切换：归档离开页会话，进入新页时恢复该页上次会话或新建
const trackedPageContextKey = ref<string | null>(null);
const trackedPersistenceScenario = ref<string | null>(null);
const _wasOnCalcPage = ref(false);
let pageContextSwitchGen = 0;

watch(
  () => aiPageContextKey.value,
  async (nextKey, prevKey) => {
    const gen = ++pageContextSwitchGen;
    const prevSid = currentSessionId.value;
    const prevScenario = trackedPersistenceScenario.value;
    const contextChanged = prevKey != null && prevKey !== nextKey;
    if (contextChanged) {
      // 归档旧会话可能需要等待网络；先切到空的新 session，避免等待期间把旧项目消息显示在当前页。
      const freshSessionId = generateSessionId();
      currentSessionId.value = freshSessionId;
      selectedSessionId.value = freshSessionId;
      activeHistoryId.value = null;
      visibleMessageCount.value = VISIBLE_MESSAGE_INITIAL;
      collapsedThoughtIds.value = new Set();
      mainTab.value = 'new';
    }
    if (trackedPersistenceScenario.value == null) {
      trackedPersistenceScenario.value = persistenceScenario.value;
    }
    const routeName = currentRouteName.value;
    // 进入计算器公式页：生成独立 session，与其他页面隔离
    const isCalcDetailRoute =
      routeName === 'AiCalculatorDetail' || routeName === 'AiCalculatorFormulaShare';

    if (isCalcDetailRoute) {
      if (prevKey != null && prevKey !== nextKey) {
        await archiveCurrentSessionIfNeeded(prevSid, prevScenario || undefined);
        if (gen !== pageContextSwitchGen) return;
      }
      trackedPageContextKey.value = nextKey;
      currentSessionId.value = generateSessionId();
      selectedSessionId.value = currentSessionId.value;
      activeHistoryId.value = null;
      _wasOnCalcPage.value = true;
      trackedPersistenceScenario.value = persistenceScenario.value;
      return;
    }
    // AI 创建项目跳转：会话已在 project_assistance 下，按 hydration 会话 ID 对齐即可，避免重建空会话
    const pendingHydration = shellStore.pendingAiProjectCreateHydration;
    if (
      pendingHydration?.conversationId?.trim()
      && String(nextKey || '').startsWith('ProjectCreate:')
    ) {
      const sid = pendingHydration.conversationId.trim();
      if (prevKey != null && prevKey !== nextKey) {
        await archiveCurrentSessionIfNeeded(prevSid, prevScenario || undefined);
        if (gen !== pageContextSwitchGen) return;
      }
      trackedPageContextKey.value = nextKey;
      currentSessionId.value = sid;
      selectedSessionId.value = sid;
      writeLastSessionId(persistenceScenario.value, sid);
      mainTab.value = 'new';
      sessionNotify.markRead(persistenceScenario.value, sid);
      clearCompletedTypingPreviews(sid, conversationScenario.value);
      trackedPersistenceScenario.value = persistenceScenario.value;
      return;
    }

    // 离开计算器页：强制清掉任务绑定，防止 taskBoundSessionId/Scenario 污染新页
    if (_wasOnCalcPage.value && prevKey != null && prevKey !== nextKey) {
      taskBoundSessionId.value = null;
      taskBoundScenario.value = null;
      currentThoughtDraft.value = [];
      _wasOnCalcPage.value = false;
    }
    // conversationScenario 在路由变化时已经切到新项目，必须用切换前记下的 scenario 归档。
    if (prevKey != null && prevKey !== nextKey) {
      await archiveCurrentSessionIfNeeded(prevSid, prevScenario || undefined);
      if (gen !== pageContextSwitchGen) return;
    }
    trackedPageContextKey.value = nextKey;
    await activateSessionForCurrentPageContext(gen);
    if (gen !== pageContextSwitchGen) return;
    trackedPersistenceScenario.value = persistenceScenario.value;
  },
  { immediate: true },
);

watch(
  () => shellStore.newConversationTick,
  () => {
    if (!shellStore.newConversationTick) return;
    void handleNewConversation();
  },
);

// 场景切换：隔离输入草稿 + 重新加载会话历史
watch(
  () => persistenceScenario.value,
  async (next, prev) => {
    if (!next || next === prev) return;
    if (prev) {
      saveComposerDraftForScenario(prev);
    }
    restoreComposerDraftForScenario(next);
    await loadConversationHistory({ applyLastSession: false });
  },
);

// 路由驱动的场景隔离：
// - 通用部件库相关页面：统一进入 component_library 场景（与其他场景隔离）
// - 离开该场景时：恢复用户上一次的“非通用部件”场景
watch(
  () => currentRouteName.value,
  (name) => {
    const isComponentRoute = name === 'Resources' || name === 'ResourceCreate' || name === 'ResourceDetail';
    if (isComponentRoute) {
      if (conversationScenario.value !== COMPONENT_SCENARIO) {
        // 记录离开前场景，便于返回其它页面恢复
        writeLastNonComponentScenario(conversationScenario.value || DEFAULT_CONVERSATION_SCENARIO);
      }
      conversationScenario.value = COMPONENT_SCENARIO;
      return;
    }

    // 非通用部件页面：如果当前仍停留在 component_library，则恢复之前的场景
    if (conversationScenario.value === COMPONENT_SCENARIO) {
      const last = readLastNonComponentScenario();
      conversationScenario.value = last && last.trim() ? last.trim() : DEFAULT_CONVERSATION_SCENARIO;
    }
  },
  { immediate: true }
);

/** 创建项目分屏页与 Dashboard 侧栏会话对齐 */
function setExternalSessionId(id: string) {
  const t = id?.trim();
  if (!t) return;
  currentSessionId.value = t;
  selectedSessionId.value = t;
  writeLastSessionId(persistenceScenario.value, t);
}

/** 将创建项目会话迁入目标项目场景，并从通用列表会话中移除 */
function transferSessionToProject(projectId: number, sessionId?: string) {
  const sid = String(sessionId || currentSessionId.value || '').trim();
  if (!sid || !projectId || projectId <= 0) return;
  const sourceScenario = 'project_assistance';
  const targetScenario = resolveProjectSpaceScenario(projectId);
  const msgs = store.getMessages(sourceScenario, sid);
  if (msgs.length) {
    store.setMessages(targetScenario, msgs, sid);
    store.clearConversation(sourceScenario, sid);
  }
  writeLastSessionId(targetScenario, sid);
  try {
    localStorage.removeItem(lastSessionStorageKey(sourceScenario));
  } catch {
    // ignore
  }
}

type RunSorRequirementAnalyzeArgs = {
  projectId: number;
  projectName?: string;
  docName: string;
  /** 用户选定的参考知识库文档标题，展示在对话中 */
  referenceKnowledgeTitles?: string[];
  execute: (hooks: {
    reportThoughtSteps: (steps: SorAnalyzeStepStatus[]) => void;
    signal: AbortSignal;
  }) => Promise<{
    requirementCount: number;
    docName: string;
    taskId?: string;
    steps: SorAnalyzeStepStatus[];
    categoryStats?: Record<string, number>;
    unusedSpecFiles?: string[];
  }>;
};

function restoreSorAnalyzeInProgress(opts: {
  steps: SorAnalyzeStepStatus[];
  sessionId?: string;
  docName?: string;
  done?: boolean;
}) {
  const scenario = conversationScenario.value;
  if (opts.sessionId) {
    const sid = opts.sessionId.trim();
    if (sid) {
      currentSessionId.value = sid;
      selectedSessionId.value = sid;
      writeLastSessionId(scenario, sid);
    }
  }
  mainTab.value = 'new';
  expandRightPanel();
  const steps = opts.done
    ? opts.steps.map((s) => ({ ...s, status: 'done' as const }))
    : opts.steps;
  currentThoughtDraft.value = mapSorAnalyzeStepsToThought(steps);
  if (opts.done) {
    isThinking.value = false;
    aiLoading.value = false;
  } else {
    isThinking.value = true;
    aiLoading.value = true;
  }
  void nextTick().then(() => scrollAiToBottom());
}

async function runSorRequirementAnalyze(args: RunSorRequirementAnalyzeArgs) {
  sorRequirementContext.value = null;
  const shortName =
    args.docName.length > 18 ? `${args.docName.slice(0, 18)}…` : args.docName;
  const { generation, action } = beginAiSessionAction({
    kind: 'sor_requirement_analyze',
    label: `SOR 解析：${shortName}`,
    discard: async () => {
      await sorAnalyzeControlStore.handlers?.rollback?.();
    },
    retry: () => runSorRequirementAnalyze(args),
  });
  return runInAiTaskQueue({
    kind: 'sor_requirement_analyze',
    label: `SOR 解析：${shortName}`,
    sessionAction: action,
    lifecycle: {
      onPause: () => {
        sorAnalyzeControlStore.handlers?.pause();
      },
      onResume: () => {
        sorAnalyzeControlStore.handlers?.resume();
      },
      onRollback: () => action.discard(),
      onStop: () => {
        sorAnalyzeControlStore.handlers?.stop();
      },
    },
    run: async (boundSessionId, ctx) => {
      mainTab.value = 'new';
      expandRightPanel();
      const scenario = conversationScenario.value;
      const kbTitles = (args.referenceKnowledgeTitles || []).filter(Boolean);
      const kbLine = kbTitles.length
        ? `\n参考知识库：${kbTitles.slice(0, 5).join('、')}${kbTitles.length > 5 ? '…' : ''}`
        : '';
      const userLine = `【SOR 需求解析】${args.docName}${kbLine}`;
      addSessionMessage('user', userLine, undefined, undefined, undefined, boundSessionId, scenario);
      await nextTick();
      scrollAiToBottom();
      isThinking.value = true;
      aiLoading.value = true;
      currentThoughtDraft.value = mapSorAnalyzeStepsToThought([]);
      try {
        const result = await args.execute({
          reportThoughtSteps: (steps) => {
            if (!isAiSessionActionActive(action, generation)) return;
            currentThoughtDraft.value = mapSorAnalyzeStepsToThought(steps);
          },
          signal: ctx?.signal ?? new AbortController().signal,
        });
        if (!isAiSessionActionActive(action, generation) || ctx?.signal?.aborted) {
          throw new DOMException('已取消', 'AbortError');
        }
        currentThoughtDraft.value = mapSorAnalyzeStepsToThought(
          result.steps.map((s) => ({ ...s, status: 'done' as const })),
        );
        const statsText = result.categoryStats
          ? Object.entries(result.categoryStats)
              .map(([k, v]) => `${k} ${v} 条`)
              .join('、')
          : '';
        const unused = (result.unusedSpecFiles || []).filter(Boolean);
        const unusedHint = unused.length
          ? `以下规范文件${unused.map((n) => `【${n}】`).join('')}未被台账引用`
          : '';
        const summary = [
          `SOR 需求解析已完成：对《${result.docName}》共提取 ${result.requirementCount} 条结构化需求条目。`,
          statsText ? `需求大类分布：${statsText}。` : '',
          '所有条目已整理至需求清单表格，您可在表格中直接查看、编辑包需求、设计需求、设计规格与测试规格，无需重复提取。',
          unusedHint,
        ]
          .filter(Boolean)
          .join('\n');
        const structured: Record<string, unknown> = {
          kind: 'sor_requirement_analyze',
          status: 'completed',
          docName: result.docName,
          requirementCount: result.requirementCount,
          taskId: result.taskId,
        };
        const assistantMsg = addSessionMessage(
          'assistant',
          summary,
          undefined,
          thoughtStepsToJson(currentThoughtDraft.value),
          structured,
          boundSessionId,
          scenario,
        );
        sorRequirementContext.value = { messageId: assistantMsg.id };
        await nextTick();
        expandedThoughtIds.value.add(assistantMsg.id);
        currentThoughtDraft.value = [];
        isThinking.value = false;
        await revealAssistantMessage(assistantMsg.id, boundSessionId, scenario);
        await persistConversation(undefined, boundSessionId);
        ElMessage.success('SOR 需求解析完成');
        return result;
      } catch (e: unknown) {
        currentThoughtDraft.value = [];
        if (isTaskCancelledError(e)) {
          await persistConversation(undefined, boundSessionId);
          throw e;
        }
        const err = e as { message?: string };
        const msg = err?.message || 'SOR 需求解析失败';
        ElMessage.error(msg);
        const assistantMsg = addSessionMessage(
          'assistant',
          `SOR 需求解析失败：${msg}`,
          undefined,
          undefined,
          { kind: 'sor_requirement_analyze', status: 'failed', docName: args.docName },
          boundSessionId,
          scenario,
        );
        sorRequirementContext.value = { messageId: assistantMsg.id };
        await persistConversation(undefined, boundSessionId);
        throw e;
      } finally {
        isThinking.value = false;
        aiLoading.value = false;
        currentThoughtDraft.value = [];
      }
    },
  });
}

function getBomLlmRequestParams(): { llmProvider: string; llmRunMode: string } {
  const providerForRequest =
    !allowUserModelSelection.value || llmProvider.value === LLM_PROVIDER_AUTO
      ? LLM_PROVIDER_AUTO
      : llmProvider.value;
  return {
    llmProvider: providerForRequest,
    llmRunMode: resolvedBomRunMode.value,
  };
}

/** 冒烟测试：展开面板并写入当前 AI 会话的进度日志 */
async function beginSmokeTestSession() {
  expandRightPanel();
  mainTab.value = 'new';
  await handleNewConversation();
  try {
    await applySessionTitle(currentSessionId.value, '冒烟测试', persistenceScenario.value);
  } catch {
    /* 持久化标题失败不阻塞冒烟流程 */
  }
  addSessionMessage(
    'assistant',
    '🧪 **冒烟测试会话已开始**\n\n进度将实时输出在此。测试过程中请勿用鼠标点击页面（点击会自动暂停；暂停后可自行操作，再次点击「冒烟测试」按钮可继续）。',
  );
}

function appendSmokeTestProgress(content: string) {
  expandRightPanel();
  mainTab.value = 'new';
  const text = (content || '').trim();
  if (!text) return;
  addSessionMessage('assistant', text);
}

type AppendAssistantNoticeOptions = {
  /** true：仅当前路由+会话内存展示，不写共享 store / 不 persist */
  ephemeral?: boolean;
  sessionId?: string;
};

/** 业务页注入一诺提示（如 WBS 模板候选确认） */
function appendAssistantNotice(content: string, opts?: AppendAssistantNoticeOptions) {
  const text = (content || '').trim();
  if (!text) return;
  const sid = String(opts?.sessionId || currentSessionId.value || '').trim();
  if (opts?.ephemeral) {
    if (currentRouteName.value === 'ProjectCreate') {
      expandRightPanel();
    }
    ephemeralAssistantNotices.value = [
      ...ephemeralAssistantNotices.value.filter(
        (n) => !(n.sessionId === sid && n.content === text),
      ),
      {
        id: `eph_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        content: text,
        sessionId: sid,
        routeName: currentRouteName.value || 'ProjectCreate',
        createdAt: Date.now(),
      },
    ];
    return;
  }
  expandRightPanel();
  mainTab.value = 'new';
  addSessionMessage('assistant', text);
  void persistConversation();
}

defineExpose({
  submitExpandedInput,
  pendingImagePreview,
  setPendingImageBase64: (b64: string | null) => { pendingImageBase64 = b64; },
  setDraftMessage: setComposerDraftMessage,
  focusComposer,
  expandRightPanel,
  beginSmokeTestSession,
  appendSmokeTestProgress,
  appendAssistantNotice,
  appendQuoteToComposer,
  setConversationTab,
  mainTab,
  isThinking,
  aiLoading,
  liveThoughtSteps,
  currentThoughtDraft,
  getCurrentSessionId: () => currentSessionId.value,
  getCurrentSessionTitle: () => currentSessionTitle.value,
  startHeaderTitleEdit,
  renameCurrentSessionTitle,
  getScopeQueueHint: () => scopeQueueHint.value,
  getScopeActiveQueueTasks: () => scopeActiveQueueTasks.value,
  onCancelQueueTask,
  onPauseScopeTask,
  onStopScopeTask,
  onResumeScopeTask,
  onRollbackScopeTask,
  onRollbackSessionAction,
  onRetrySessionAction,
  onContinueScopeQueue,
  consumeRestoredComposerDraft,
  showComposerTaskStop,
  showComposerRetryRollback,
  sessionActionMessageActionsVisible,
  rollbackSessionActionFromMessage,
  retrySessionActionFromMessage,
  isHistorySessionRunning,
  isHistorySessionUnread,
  historyHasUnread: () => historyHasUnread.value,
  setExternalSessionId,
  transferSessionToProject,
  loadHistoryItem,
  loadConversationHistory,
  removeUserMessage,
  confirmDeleteHistoryItem: confirmDelete,
  persistConversation,
  handleNewConversation,
  archiveAndStartNewSession,
  initCalculatorFormulaSession,
  setImportSessionTitle,
  collapseToFloatingButton,
  pinFloatingAiPanel,
  getIsPanelCollapsed: () => isCollapsed.value,
  getPanelWidth: () => Number(panelWidth.value) || 320,
  isStreaming,
  stopCurrentStream,
  beginProjectCreateFieldEdit,
  beginPageContentEdit,
  getPageContentEditPending: () => pageContentEditPending.value,
  clearPageContentFieldRef: () => { clearPageContentFieldRef(); },
  locatePageContentQuote: (quoted: string, fieldPath: string, options?: { paragraphIndex?: number; paragraphLabel?: string; cellKey?: string }) => {
    return highlightPageContentQuote(quoted, fieldPath, options);
  },
  acceptPageContentFieldEdit,
  rejectPageContentFieldEdit,
  acceptProjectCreateFieldEdit,
  rejectProjectCreateFieldEdit,
  runDocumentDeepReview,
  runComponentRecommend,
  runComponentBomImport,
  runComponentAutoFill,
  runBomGenerate,
  runBomCostEstimate,
  getBomLlmRequestParams,
  runDrawingReview,
  promptBomDrawingReviewExisting,
  runTrReview,
  runProjectIntroRewrite,
  runWbsGenerate,
  promptOrRunWbsTaskReassign,
  isWbsTaskReassignConfirm,
  acceptWbsTaskReassign,
  declineWbsTaskReassign,
  runWbsTaskReassign,
  regenerateWbsFromMessage,
  rollbackWbsFromMessage,
  wbsGenerateActionsVisible,
  isWbsGenerateBusyForMessage,
  canRollbackWbsFromMessage,
  wbsRollbackDisabledTitle,
  wbsGenerateStatus,
  runSorRequirementAnalyze,
  isSorRequirementMessage,
  sorRequirementActionsVisible,
  rollbackSorRequirementFromMessage,
  retrySorRequirementFromMessage,
  restoreSorAnalyzeInProgress,
});
</script>

<style scoped>
/* Dashboard 全屏一诺：子节点为 position:fixed，父级 overflow 无法裁剪，必须在此压制侧栏 UI */
.ai-assistant-root--embedded {
  height: 100%;
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-assistant-root--embedded .ai-content-side {
  position: relative;
  right: auto;
  top: auto;
  left: auto;
  width: 100%;
  flex: 1;
  min-height: 0;
  z-index: 1;
}

.ai-assistant-root--embedded .ai-panel {
  height: 100%;
  max-height: none;
  min-height: 0;
  border-radius: 8px;
  box-shadow:
    0 2px 12px rgba(15, 35, 52, 0.08),
    0 1px 3px rgba(15, 35, 52, 0.06);
  border: 1px solid #e8ecf2;
}

.ai-assistant-root--embedded .ai-panel-body {
  flex: 1 1 0;
  min-height: 0;
}

.component-recommend-block {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e5e6eb;
}
.component-recommend-title {
  font-size: 12px;
  font-weight: 600;
  color: #4e5969;
  margin-bottom: 8px;
}
.component-recommend-stats {
  font-size: 12px;
  line-height: 1.6;
  color: #4e5969;
  margin: 0;
}
.bom-generate-block {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e5e6eb;
}
.calc-formula-gen-reply {
  font-size: 13px;
  line-height: 1.6;
  color: #1f2329;
  margin-bottom: 8px;
}
.calc-formula-name-list {
  margin: 0 0 10px 18px;
  padding: 0;
  font-size: 12px;
  color: #4e5969;
  line-height: 1.5;
}
.bom-generate-stats {
  font-size: 13px;
  line-height: 1.6;
  color: #1f2329;
  margin: 0 0 10px;
}
.bom-generate-stats--warn {
  color: #d48806;
}
.bom-generate-progress {
  font-size: 12px;
  line-height: 1.5;
  color: #0052d9;
  margin: 0 0 8px;
}
.bom-generate-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.bom-generate-btn {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #1f2329;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
}
.bom-generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.bom-generate-btn--primary {
  border-color: #0052d9;
  background: #0052d9;
  color: #fff;
}
.bom-generate-btn--danger {
  border-color: #f56c6c;
  background: #fff;
  color: #f56c6c;
}
.bom-generate-btn--danger:hover:not(:disabled) {
  border-color: #f56c6c;
  background: #fef0f0;
  color: #f56c6c;
}
.bom-generate-done {
  font-size: 12px;
  color: #86909c;
}

.tpl-gen-review-list {
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tpl-gen-review-item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  text-align: left;
  padding: 6px 8px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  background: #fafbfc;
  color: #1d2129;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
}
.tpl-gen-review-item:hover {
  border-color: #94bfff;
  background: #f2f7ff;
}
.tpl-gen-review-item__idx {
  flex: 0 0 auto;
  color: #86909c;
}
.tpl-gen-review-item__label {
  flex: 1 1 auto;
  min-width: 0;
}
.tpl-gen-review-item__action {
  flex: 0 0 auto;
  color: #165dff;
  font-size: 12px;
}

.tr-review-report-card {
  margin-top: 10px;
  padding: 12px;
  border: 1px solid #b8d7ff;
  border-radius: 10px;
  background: #f8fbff;
}
.tr-review-report-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.tr-review-report-head h3 {
  margin: 0;
  font-size: 15px;
}
.tr-review-report-head span {
  color: #d97706;
  background: #fff7e6;
  border: 1px solid #ffd99a;
  border-radius: 14px;
  padding: 2px 10px;
  font-size: 12px;
}
.tr-review-report-desc {
  margin: 0 0 10px;
  color: #52647b;
  line-height: 1.55;
  font-size: 13px;
}
.tr-review-report-summary {
  display: grid;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 13px;
}
.tr-review-report-summary b {
  margin-right: 8px;
}
.tr-review-report-summary span {
  color: #52647b;
}
.tr-review-report-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
}

.tr-review-block {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e5e6eb;
}
.tr-review-verdict,
.tr-review-reason,
.tr-review-impact,
.tr-review-suggestions,
.tr-review-hint {
  font-size: 13px;
  line-height: 1.6;
  color: #1f2329;
  margin: 0 0 8px;
}
.tr-review-suggestions-list {
  margin: 6px 0 0;
  padding-left: 20px;
}
.tr-review-suggestions-list li {
  margin-bottom: 4px;
}
.tr-review-hint {
  font-size: 12px;
  color: #4e5969;
}
.tr-verdict-pass {
  color: #00b42a;
  font-weight: 600;
}
.tr-verdict-fail {
  color: #f53f3f;
  font-weight: 600;
}

.doc-deep-review-block {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e8ecf2;
}
.doc-deep-review-findings-title {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 8px;
  color: #1f2329;
}
.doc-deep-review-findings {
  max-height: 280px;
  overflow: auto;
  margin-bottom: 10px;
}
.doc-deep-review-item {
  padding: 8px 10px;
  border-radius: 8px;
  background: #f7f8fa;
  margin-bottom: 8px;
  font-size: 12px;
}
.doc-deep-review-item-head {
  margin-bottom: 6px;
}
.doc-deep-review-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e8f3ff;
  color: #0052d9;
  font-size: 11px;
}
.doc-deep-review-row {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  align-items: flex-start;
}
.doc-deep-review-k {
  flex: 0 0 2.5em;
  color: #8f959e;
}
.doc-deep-review-v {
  flex: 1;
  color: #2b2f36;
  word-break: break-word;
}
.doc-deep-review-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.doc-deep-review-btn {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #1f2329;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
}
.doc-deep-review-btn--primary {
  background: #0052d9;
  border-color: #0052d9;
  color: #fff;
}
.doc-deep-review-btn:hover {
  filter: brightness(0.97);
}
.doc-deep-review-done {
  font-size: 12px;
  color: #8f959e;
}

.pc-field-edit-footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e8ecf2;
}

.pc-field-edit-footer-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pc-field-edit-done-row {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}

.pc-field-edit-btn {
  padding: 4px 14px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #dcdfe6;
  background: #fff;
  cursor: pointer;
  color: #606266;
}

.pc-field-edit-btn--primary {
  border-color: #0052d9;
  color: #0052d9;
}

.pc-field-edit-btn--primary:hover {
  background: #e8f0ff;
}

.ai-pc-ref-composer-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 0 4px 8px;
  flex-shrink: 0;
}

.ai-pc-ref-remove {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #8a8fa6;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
}

.ai-pc-ref-remove:hover {
  background: #f0f1f5;
  color: #1f2329;
}

.ai-smart-doc-at-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 4px 8px;
  flex-shrink: 0;
}

.ai-smart-doc-at-btn {
  border: 1px solid #d0d3d9;
  background: #fff;
  color: #3370ff;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  cursor: pointer;
  line-height: 1.5;
}

.ai-smart-doc-at-btn:hover {
  background: #f0f4ff;
  border-color: #adc6ff;
}

.ai-smart-doc-quick-prompts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 4px 8px;
  flex-shrink: 0;
}

.ai-smart-doc-quick-prompt {
  border: 1px solid #d0d3d9;
  background: #fff;
  color: #3370ff;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  line-height: 1.4;
}

.ai-smart-doc-quick-prompt:hover {
  background: #f0f4ff;
  border-color: #adc6ff;
}

.ai-textarea-stack {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}

/* 与 textarea 共用同一套字体/换行度量，避免高亮层与光标错位 */
.ai-textarea-stack.has-smart-doc-mention .ai-textarea-highlight,
.ai-textarea-stack.has-smart-doc-mention .ai-textarea {
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  font-style: normal;
  font-variant: normal;
  line-height: 1.5;
  letter-spacing: normal;
  word-spacing: normal;
  text-transform: none;
  text-indent: 0;
  text-align: left;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  word-break: break-word;
  tab-size: 4;
  -moz-tab-size: 4;
}

.ai-textarea-highlight {
  position: absolute;
  inset: 0;
  /* 置于 textarea 下方：光标/选区/输入法以 textarea 为准，颜色由高亮层提供 */
  z-index: 0;
  overflow: auto;
  pointer-events: none;
  padding: 10px 44px 34px 8px;
  color: #1f2329;
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ai-textarea-highlight::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

/* 输入法 composition：保持蓝色引用高亮；拼音主要看系统候选框，避免整段变黑 */
.ai-textarea-stack.is-composing .ai-textarea-highlight {
  visibility: visible;
}

.ai-textarea-highlight :deep(.ai-smart-doc-mention) {
  /* 仅改颜色，禁止 margin/padding/字重，否则与透明 textarea 宽度不一致导致光标错位 */
  color: #69b1ff;
  font: inherit;
  letter-spacing: inherit;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  display: inline;
  white-space: inherit;
  word-break: inherit;
  overflow-wrap: inherit;
  pointer-events: none;
  cursor: text;
}

.ai-smart-doc-mention-hover {
  position: absolute;
  z-index: 6;
  max-width: min(280px, calc(100% - 16px));
  padding: 8px 10px;
  border: 1px solid #d9ecff;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(15, 35, 80, 0.12);
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.ai-smart-doc-mention-hover-detail {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.45;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-smart-doc-mention-hover-remove {
  flex-shrink: 0;
  margin: 0;
  padding: 2px 8px;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  background: #fff2f0;
  color: #cf1322;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
}

.ai-smart-doc-mention-hover-remove:hover {
  background: #ffccc7;
}

.chat-message-user-plain {
  line-height: 1.55;
  word-break: break-word;
  overflow-wrap: anywhere;
  /* normal：折叠引用两侧多余换行，与正文连续行内混排 */
  white-space: normal;
}

.chat-message-user-plain :deep(.ai-smart-doc-mention--chat) {
  display: inline !important;
  float: none !important;
  clear: none !important;
  width: auto !important;
  max-width: none !important;
  margin: 0 0.15em;
  padding: 0;
  border: none;
  background: none;
  color: #69b1ff;
  font: inherit;
  font-weight: 400;
  line-height: inherit;
  vertical-align: baseline;
  text-align: left;
  cursor: pointer;
  text-decoration: none;
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: anywhere;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

.chat-message-user-plain :deep(.ai-smart-doc-mention--chat:hover) {
  color: #409eff;
  text-decoration: underline;
  text-decoration-color: rgba(64, 158, 255, 0.55);
}

.ai-smart-doc-mention-detail {
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid #d9ecff;
  border-radius: 6px;
  background: #f5faff;
  font-size: 12px;
  line-height: 1.5;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-smart-doc-mention-detail-label {
  margin-bottom: 4px;
  color: #409eff;
  font-weight: 500;
}

.ai-smart-doc-mention-detail-body {
  color: #606266;
}

.ai-smart-doc-execution {
  margin: 0 4px 8px;
  padding: 10px;
  border: 1px solid #e5e6eb;
  border-radius: 10px;
  background: #fff;
  flex-shrink: 0;
  max-height: 220px;
  overflow: auto;
  font-size: 12px;
}

.ai-smart-doc-execution-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.ai-smart-doc-preview {
  border-top: 1px dashed #eee;
  padding-top: 8px;
  line-height: 1.55;
  color: #1f2329;
}

.ai-smart-doc-citations {
  margin-top: 8px;
}

.ai-smart-doc-citations-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.ai-smart-doc-citation {
  display: block;
  width: 100%;
  text-align: left;
  margin-bottom: 4px;
  border: 1px solid #e5e6eb;
  background: #f7f8fa;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  cursor: pointer;
  color: #1f2329;
}

.ai-smart-doc-error {
  color: #c62828;
  margin: 6px 0 0;
}

.ai-smart-doc-apply {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.ai-smart-doc-exec-btn {
  border: 1px solid #d0d3d9;
  background: #fff;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
}

.ai-smart-doc-exec-btn.primary {
  background: #3370ff;
  border-color: #3370ff;
  color: #fff;
}

.ai-pc-ref-chip {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: nowrap;
  gap: 4px 6px;
  max-width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: default;
  color: #2563eb;
  text-decoration: underline;
  text-decoration-color: rgba(37, 99, 235, 0.35);
  text-underline-offset: 3px;
}

.ai-pc-ref-chip:hover {
  color: #1d4ed8;
  text-decoration-color: rgba(29, 78, 216, 0.55);
}

.ai-pc-ref-chip--composer {
  flex: 1;
  min-width: 0;
  cursor: text;
}

.ai-pc-ref-chip--clickable,
.ai-pc-ref-chip--composer.ai-pc-ref-chip--clickable {
  cursor: pointer;
}

.chat-message-user-plain :deep(.quote-pill),
.chat-message-selectable :deep(.quote-pill) {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 100%;
  margin: 0 0 6px;
  padding: 6px 10px;
  border: 1px solid #d0e3ff;
  border-radius: 8px;
  background: #f0f6ff;
  color: #0052d9;
  font-size: 12px;
  line-height: 1.45;
  text-align: left;
  cursor: pointer;
}

.chat-message-user-plain :deep(.quote-pill--link:hover),
.chat-message-selectable :deep(.quote-pill--link:hover) {
  background: #e8f1ff;
  border-color: #0052d9;
}

.chat-message-user-plain :deep(.quote-pill__icon),
.chat-message-selectable :deep(.quote-pill__icon) {
  flex-shrink: 0;
  font-weight: 600;
}

.chat-message-user-plain :deep(.quote-pill__meta),
.chat-message-selectable :deep(.quote-pill__meta) {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.chat-message-user-plain :deep(.quote-pill__file),
.chat-message-selectable :deep(.quote-pill__file) {
  font-weight: 600;
}

.chat-message-user-plain :deep(.quote-pill__summary),
.chat-message-selectable :deep(.quote-pill__summary) {
  color: #4e5969;
  font-weight: 400;
  word-break: break-word;
}

.ai-pc-ref-chip-prefix {
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
}

.ai-pc-ref-chip-text {
  font-weight: 500;
  font-size: 12px;
  flex-shrink: 0;
  white-space: nowrap;
}

.ai-pc-ref-chip-snippet {
  font-size: 12px;
  font-weight: 400;
  color: #4e5969;
  text-decoration: none;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-pc-ref-chip--in-chat .ai-pc-ref-chip-snippet {
  color: #646a73;
}

.chat-message-user-pc-ref {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.chat-message-user-pc-ref .ai-pc-ref-chip--in-chat {
  cursor: pointer;
}

.ai-pc-ref-detail {
  width: 100%;
  margin-top: 2px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fafbfc;
}

.ai-pc-ref-detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #646a73;
  margin-bottom: 6px;
}

.ai-pc-ref-detail-body {
  font-size: 12px;
  line-height: 1.55;
  color: #1f2329;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-pc-user-ref-requirement {
  font-size: 12px;
  line-height: 1.55;
  color: #1f2329;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-pc-user-ref-requirement.is-placeholder {
  color: #8a8fa6;
  font-size: 12px;
}

.chat-message-selectable {
  user-select: text;
}

.ai-assistant-root--chrome-hidden .ai-float-btn,
.ai-assistant-root--chrome-hidden .ai-content-side {
  position: fixed !important;
  left: -10000px !important;
  top: 0 !important;
  right: auto !important;
  width: 1px !important;
  min-width: 0 !important;
  height: 1px !important;
  max-height: 1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  clip-path: inset(50%) !important;
  opacity: 0 !important;
  pointer-events: none !important;
  visibility: hidden !important;
  box-shadow: none !important;
  z-index: -1 !important;
}

.ai-expand-toggle-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #4e5969;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ai-expand-toggle-icon {
  display: block;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background-color: currentColor;
  -webkit-mask-image: url('../assets/codex-sidebar-expanded.svg');
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-image: url('../assets/codex-sidebar-expanded.svg');
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
}

.ai-expand-toggle-btn:hover {
  background: #f3f4fb;
  color: #0052d9;
}

.ai-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e6eb;
  margin-bottom: 8px;
}

.ai-header-tabs-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  left: 0;
  top: 0;
  white-space: nowrap;
}

.ai-content-side {
  position: fixed;
  right: 10px;
  top: 66px;
  z-index: 100;
}

.ai-content-side.is-resize-left-edge {
  cursor: ew-resize;
}

.ai-panel {
  background: #ffffff;
  border-radius: 6px;
  /* 更明显的 3D 阴影效果 */
  box-shadow:
    0 14px 28px rgba(15, 35, 52, 0.25),
    0 10px 10px rgba(15, 35, 52, 0.22);
  padding: 16px;
  height: calc(100vh - 76px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-panel-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.ai-panel-body.is-file-dragging {
  outline: 2px dashed #0052d9;
  outline-offset: -2px;
}

.ai-panel-drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 82, 217, 0.08);
  border-radius: 8px;
  font-size: 14px;
  color: #0052d9;
  font-weight: 500;
  pointer-events: none;
}

.ai-chat-display {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0;
  padding: 8px;
  padding-bottom: 12px;
  background: #f7f8fc;
  border-radius: 4px;
  scrollbar-width: none;
}

.ai-chat-display::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.ai-header-title-wrap {
  flex: 1;
  min-width: 0;
}

.ai-title {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #1f2329;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-title--editable {
  cursor: text;
}

.ai-title-input {
  width: 100%;
  margin: 0;
  padding: 2px 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1f2329;
  line-height: 1.3;
  border: 1px solid #3370ff;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
}

.ai-queue-hint {
  margin: 2px 0 0;
  font-size: 11px;
  line-height: 1.3;
  color: #86909c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-task-queue-strip {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 8px 10px;
  border-radius: 0;
  border: none;
  border-bottom: 1px solid #e5e6eb;
  background: #f7f8fc;
  max-height: 120px;
  overflow-y: auto;
}

.ai-task-queue-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 4px 6px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  color: #4e5969;
}

.ai-task-queue-item.is-running {
  background: #eef3ff;
  color: #1d4ed8;
}

.ai-task-queue-item.is-queued {
  opacity: 0.88;
}

.ai-task-queue-item.is-paused {
  background: #fff7e6;
  color: #d48806;
}

.ai-task-queue-indicator {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ai-task-queue-spinner {
  width: 10px;
  height: 10px;
  border: 1.5px solid #c9d7ff;
  border-top-color: #0052d9;
  border-radius: 50%;
  animation: ai-task-queue-spin 0.75s linear infinite;
}

.ai-task-queue-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #c9cdd4;
}

.ai-task-queue-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-task-queue-status {
  flex-shrink: 0;
  font-size: 11px;
  color: #86909c;
}

.ai-task-queue-item.is-running .ai-task-queue-status {
  color: #4e7cff;
}

.ai-task-queue-item.is-paused .ai-task-queue-status {
  color: #d48806;
}

.ai-task-queue-continue {
  flex-shrink: 0;
  margin-left: 4px;
  padding: 0 8px;
  height: 22px;
  border: 1px solid #b7d1ff;
  border-radius: 4px;
  background: #fff;
  color: #0052d9;
  font-size: 11px;
  cursor: pointer;
}

.ai-task-queue-continue:hover {
  background: #eef3ff;
}

.ai-task-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #e5e6eb;
  border-radius: 6px;
  background: #fff;
  color: #4e5969;
  font-size: 12px;
  cursor: pointer;
}

.ai-task-action-btn:hover {
  background: #f7f8fa;
}

.ai-task-action-btn--primary {
  border-color: #b7d1ff;
  color: #0052d9;
  background: #eef3ff;
}

.ai-send-btn--pause {
  font-size: 12px;
  letter-spacing: -1px;
}

.ai-send-btn--stop {
  background: #cf1322;
}

.ai-send-btn--stop:hover {
  background: #a8071a;
}

.ai-send-stop-icon {
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 1px;
  background: #ffffff;
}

.ai-task-queue-stop {
  flex-shrink: 0;
  margin-left: 4px;
  padding: 0 8px;
  height: 22px;
  border: 1px solid #f5c2c7;
  border-radius: 4px;
  background: #fff;
  color: #cf1322;
  font-size: 11px;
  line-height: 20px;
  cursor: pointer;
}

.ai-task-queue-stop:hover {
  background: #fff1f0;
  border-color: #ffa39e;
}

@keyframes ai-task-queue-spin {
  to {
    transform: rotate(360deg);
  }
}

.ai-header-right-cluster {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.ai-main-tabs {
  display: inline-flex;
  flex-shrink: 0;
  border-radius: 999px;
  background: #f3f4fb;
  padding: 2px;
}

.ai-tab-btn {
  position: relative;
  border: none;
  background: transparent;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #4e5969;
  cursor: pointer;
}

.ai-tab-unread-dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #f53f3f;
}

.ai-tab-btn.active {
  background: #ffffff;
  color: #0052d9;
  box-shadow: 0 1px 3px rgba(15, 35, 52, 0.12);
}

.ai-session-round-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.ai-compact-history-wrap {
  position: relative;
  flex-shrink: 0;
}

.ai-compact-history-btn {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: #f3f4fb;
  color: #4e5969;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
}

.ai-compact-history-btn:hover,
.ai-compact-history-btn.active {
  background: #e1e6ff;
  color: #0052d9;
}

.ai-history-clock-icon {
  width: 14px;
  height: 14px;
  display: block;
}

.ai-compact-history-btn .ai-tab-unread-dot {
  top: 1px;
  right: 1px;
}

.ai-compact-history-popover {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  max-height: min(360px, calc(100vh - 96px));
  padding: 8px;
  border: 1px solid #e5e6eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow:
    0 12px 28px rgba(15, 35, 52, 0.16),
    0 4px 10px rgba(15, 35, 52, 0.08);
}

.ai-compact-history-popover-title {
  flex-shrink: 0;
  padding: 4px 8px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #1f2329;
}

.ai-compact-history-popover-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-right: 2px;
}

.ai-compact-history-item {
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
}

.ai-compact-history-item:hover {
  background: #f3f4fb;
}

.ai-compact-history-item.active {
  background: #e8efff;
}

.ai-compact-history-item-main {
  min-width: 0;
}

.ai-compact-history-item-title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 13px;
  color: #1f2329;
  line-height: 1.35;
}

.ai-compact-history-item-title-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-compact-history-item.is-running .ai-compact-history-item-title-text {
  color: #1d4ed8;
}

.ai-compact-history-item-meta {
  margin-top: 2px;
  font-size: 11px;
  color: #8a8fa6;
  line-height: 1.3;
}

.ai-compact-history-empty {
  padding: 16px 10px;
  font-size: 12px;
  color: #8a8fa6;
  text-align: center;
}

.ai-new-session-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: #e1e6ff;
  color: #0052d9;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  font-weight: 500;
}

.ai-new-session-btn:hover {
  background: #0052d9;
  color: #fff;
}

.ai-collapse-to-float-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: #f3f4fb;
  color: #4e5969;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  font-weight: 500;
}

.ai-collapse-to-float-btn:hover {
  background: #e1e6ff;
  color: #0052d9;
}

.ai-collapse-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: #f3f4fb;
  color: #4e5969;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.ai-collapse-btn:hover {
  background: #e1e6ff;
}

/* 展开态主体：占满剩余高度，内部可滚动（position 见上方 .ai-panel-body） */

/* 收起后悬浮 AI 按钮（位置由 inline style + 用户偏好持久化） */
.ai-float-btn {
  position: fixed;
  z-index: 1003;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #0052d9 0%, #2684ff 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: grab;
  user-select: none;
  touch-action: none;
  box-shadow: 0 4px 12px rgba(0, 82, 217, 0.4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.ai-float-btn--dragging {
  cursor: grabbing;
  transition: none;
  box-shadow: 0 8px 20px rgba(0, 82, 217, 0.55);
}

.ai-float-btn:hover {
  background: linear-gradient(135deg, #0047c5 0%, #1a73e8 100%);
  box-shadow: 0 6px 16px rgba(0, 82, 217, 0.5);
}

.ai-float-btn--working {
  animation: ai-float-btn-pulse 2s ease-in-out infinite;
}

.ai-float-btn--working::before {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid rgba(0, 82, 217, 0.45);
  animation: ai-float-btn-ring 2s ease-out infinite;
  pointer-events: none;
}

@keyframes ai-float-btn-pulse {
  0%,
  100% {
    box-shadow: 0 4px 12px rgba(0, 82, 217, 0.4);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 4px 20px rgba(0, 82, 217, 0.65);
    transform: scale(1.04);
  }
}

@keyframes ai-float-btn-ring {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.45);
    opacity: 0;
  }
}

.ai-stats {
  display: none;
}

.ai-message-thought {
  margin: 6px 0 8px;
  border: 1px solid #ededed;
  background: #fafafa;
  border-radius: 10px;
  overflow: hidden;
}

.ai-message-thought--inline {
  margin-bottom: 8px;
}

.ai-message-thought--live {
  margin: 8px 0 12px;
  border: 1px dashed var(--el-border-color-lighter, #dcdfe6);
  background: var(--el-fill-color-lighter, #f5f7fa);
}

.ai-message-thought--live .ai-thought-toggle--static {
  cursor: default;
}

.ai-thought-step.is-running .ai-thought-step-head strong {
  color: var(--el-color-primary, #409eff);
}

.ai-message-thought .ai-thought-toggle {
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #969696;
  font-size: 12px;
  cursor: pointer;
}

.ai-message-thought .ai-thought-preview {
  padding: 0 10px 10px;
}

.ai-message-thought .ai-thought-preview-clip {
  max-height: 144px;
  overflow-y: auto;
  overflow-x: hidden;
  display: grid;
  gap: 6px;
}

.ai-message-thought .ai-thought-step {
  border-left: 2px solid #e7e7e7;
  padding: 4px 0 4px 8px;
}

.ai-message-thought .ai-thought-step-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
  color: #969696;
}

.ai-message-thought .ai-thought-step-body {
  color: #969696;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.stat-item {
  padding: 8px;
  border-radius: 4px;
  text-align: center;
}

.stat-item.completion {
  background: #ddeaff;
}

.stat-item.risk {
  background: #ffe2e2;
}

.stat-item.delay {
  background: #fff3c9;
}

.stat-label {
  font-size: 11px;
  color: #4e5969;
  display: block;
}

.stat-value {
  font-size: 14px;
  font-weight: 500;
  display: block;
  margin-top: 4px;
}

.ai-chat-display {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 12px;
  padding: 8px;
  background: #f7f8fc;
  border-radius: 4px;
  min-height: 100px;
}

.ai-history-panel {
  margin-bottom: 8px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ai-history-hint {
  margin-bottom: 8px;
  padding: 8px 10px;
  border-radius: 4px;
  background: #f5f7ff;
  font-size: 11px;
  color: #4e5969;
}

.ai-history-list {
  flex: 1;
  min-height: 120px;
  max-height: 280px;
  overflow-y: auto;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ai-history-item {
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ai-history-item:hover {
  background: #f3f4fb;
}

.ai-history-item.active {
  background: #e1e6ff;
}

.ai-history-title {
  font-size: 12px;
  color: #1f2329;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.ai-history-title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
}

.ai-session-running-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #0052d9;
  box-shadow: 0 0 0 0 rgba(0, 82, 217, 0.45);
  animation: ai-session-running-pulse 1.25s ease-in-out infinite;
}

.ai-session-unread-dot {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f53f3f;
}

.ai-history-item.has-unread .ai-history-title-text {
  padding-right: 2px;
}

.ai-history-item.is-running .ai-history-title-text {
  color: #1d4ed8;
}

@keyframes ai-session-running-pulse {
  0% {
    transform: scale(0.92);
    box-shadow: 0 0 0 0 rgba(0, 82, 217, 0.45);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(0, 82, 217, 0);
  }
  100% {
    transform: scale(0.92);
    box-shadow: 0 0 0 0 rgba(0, 82, 217, 0);
  }
}

.ai-history-meta {
  font-size: 11px;
  color: #8a8fa6;
}

.ai-history-empty {
  font-size: 12px;
  color: #8a8fa6;
  padding: 4px 2px;
}

.ai-history-actions {
  display: inline-flex;
  gap: 4px;
}

.ai-history-title-input {
  width: 100%;
  border: 1px solid #d0d2da;
  border-radius: 3px;
  padding: 2px 4px;
  font-size: 12px;
  font-family: inherit;
  color: #1f2329;
  box-sizing: border-box;
}

.ai-history-title-input:focus {
  outline: none;
  border-color: #0052d9;
  box-shadow: 0 0 0 1px rgba(0, 82, 217, 0.15);
}

.ai-history-icon-btn {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: none;
  background: transparent;
  color: #8a8fa6;
  cursor: pointer;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ai-history-icon-btn:hover {
  background: #e5e7ff;
  color: #0052d9;
}

.chat-message {
  position: relative;
  margin-bottom: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.6;
  max-width: 100%;
}
.chat-message.chat-message-with-copy {
  padding-top: 10px;
}

.chat-message.system {
  background: #e3edff;
  color: #1f2329;
  align-self: flex-start;
}

.chat-message.user {
  background: #f4f4f4;
  color: #1f2329;
  align-self: flex-end;
  margin-left: auto;
  max-width: 254px;
}
.chat-message.user:hover {
  background: #f2f6fd;
}

.chat-message-actions {
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.chat-message.user:hover .chat-message-actions {
  opacity: 1;
}
.chat-message-action-btn {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  color: #4e5969;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.chat-message-action-btn svg {
  width: 13px;
  height: 13px;
  fill: currentColor;
}
.chat-message-action-btn:hover:not(:disabled) {
  color: #0052d9;
}
.chat-message-action-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.chat-message-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.smart-doc-output-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.smart-doc-move-project-btn {
  appearance: none;
  border: 1px solid #2f6fed;
  background: #2f6fed;
  color: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
}

.smart-doc-move-project-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.smart-doc-move-hint {
  font-size: 12px;
  color: #667085;
}

.chat-attachment-thumb {
  border-radius: 6px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.06);
}

.chat-attachment-image {
  width: 56px;
  height: 56px;
  cursor: pointer;
  flex-shrink: 0;
}

.chat-attachment-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.chat-attachment-image:hover {
  opacity: 0.9;
}

.chat-attachment-doc {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  max-width: 120px;
  font-size: 11px;
  color: inherit;
  cursor: pointer;
}

.chat-attachment-doc:hover {
  opacity: 0.85;
}

.chat-attachment-doc-icon {
  flex-shrink: 0;
}

.chat-attachment-doc-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


/* 单次消息下的思考过程样式已移除，避免占位；具体展示改由消息内容块控制 */

.tag {
  padding: 6px 10px;
  border-radius: 999px;
  background: #e3edff;
  color: #0052d9;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid transparent;
}

.tag:hover {
  background: #d0e3ff;
  border-color: #c2d7ff;
}

.ai-input-area {
  border-top: 1px solid #f0f0f5;
  padding-top: 12px;
  flex-shrink: 0;
  position: relative;
  width: 100%;
  max-width: 100%;
  border-radius: 8px;
  border: 1px solid #e5e6eb;
  background: #fafbfc;
  min-height: 100px;
  box-sizing: border-box;
}

.ai-input-area.has-queue-strip {
  padding-top: 0;
  overflow: hidden;
}

.ai-input-area.is-dragging {
  border-color: #0052d9;
  background: #e8f0fe;
}

/* 拖拽提示已移至 ai-panel-body 全区域 overlay */
.ai-input-area.is-dragging-legacy {
  border-color: #0052d9;
  background: #e8f0fe;
}

/* 鼠标在上框线附近时显示拖拽调整高度光标 */
.ai-input-area.is-resize-edge {
  cursor: ns-resize;
}

.ai-input-drop-hint {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 82, 217, 0.08);
  border-radius: 8px;
  font-size: 14px;
  color: #0052d9;
  font-weight: 500;
}

.ai-attachment-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 8px 0;
  margin-bottom: 4px;
}

.ai-attachment-item {
  position: relative;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  overflow: hidden;
  background: #e5e6eb;
  flex-shrink: 0;
}

.ai-attachment-item.is-uploading {
  background: #e8f0fe;
}

.ai-attachment-item.is-error {
  background: #ffe2e2;
}

.ai-attachment-item.is-doc {
  width: auto;
  min-width: 26px;
  max-width: 56px;
  height: 26px;
  padding: 2px 4px 0;
}

.ai-attachment-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ai-attachment-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: #8a8fa6;
}

.ai-attachment-doc-icon {
  position: absolute;
  left: 50%;
  top: 2px;
  transform: translateX(-50%);
  font-size: 12px;
}

.ai-attachment-name {
  position: absolute;
  left: 2px;
  right: 2px;
  bottom: 2px;
  font-size: 9px;
  color: #1f2329;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

.ai-attachment-progress-wrap {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 0 0 4px 4px;
}

.ai-attachment-progress {
  height: 100%;
  background: #0052d9;
  border-radius: 0 0 0 4px;
  transition: width 0.15s ease;
}

.ai-attachment-cancel,
.ai-attachment-remove {
  position: absolute;
  top: 1px;
  right: 1px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.ai-attachment-cancel:hover,
.ai-attachment-remove:hover {
  background: rgba(0, 0, 0, 0.7);
}

.ai-input-inner {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-height: 72px;
  max-height: 220px;
  box-sizing: border-box;
  flex-shrink: 0;
  overflow: visible;
}

.ai-input-options {
  position: absolute;
  left: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 4;
  max-width: calc(100% - 88px);
  overflow: visible;
}

.ai-textarea {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  resize: none;
  border: none;
  border-radius: 0 0 8px 8px;
  padding: 10px 44px 34px 8px;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  background: transparent;
  display: block;
  box-sizing: border-box;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ai-textarea::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.ai-textarea--mention-overlay {
  z-index: 1;
  color: transparent;
  -webkit-text-fill-color: transparent;
  caret-color: #1f2329;
  background: transparent;
}

/*
 * 输入法拼写阶段保持 transparent，引用继续由高亮层显示蓝色。
 * 候选字看系统 IME 窗口；光标对齐后候选框位置也会正确。
 */
.ai-textarea--mention-overlay.is-composing {
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.ai-textarea--mention-overlay::placeholder {
  color: #c0c4cc;
  -webkit-text-fill-color: #c0c4cc;
  opacity: 1;
}

.ai-textarea:focus {
  outline: none;
}

.chat-message.system .dot {
  animation: dotBlink 1s infinite alternate;
  opacity: 0.3;
}

.chat-message.system .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.chat-message.system .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotBlink {
  from { opacity: 0.2; }
  to { opacity: 1; }
}

.ai-chat-display h1,
.ai-chat-display h2,
.ai-chat-display h3 {
  margin: 8px 0 4px;
  font-weight: 600;
}

.ai-chat-display h1 {
  font-size: 16px;
}

.ai-chat-display h2 {
  font-size: 14px;
}

.ai-chat-display h3 {
  font-size: 13px;
}

.ai-chat-display table {
  border-collapse: collapse;
  margin: 6px 0;
  max-width: 100%;
  font-size: 12px;
}

.ai-chat-display th,
.ai-chat-display td {
  border: 1px solid #e5e6eb;
  padding: 4px 8px;
}

.ai-chat-display th {
  background: #f5f6fa;
  font-weight: 600;
}

.project-selection-overlay {
  position: fixed;
  inset: 0;
  z-index: 5500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
  box-sizing: border-box;
}

.project-selection-panel {
  border-top: 1px solid #e5e6eb;
  padding: 10px 0 0;
  margin-bottom: 10px;
}

.project-selection-panel--modal {
  width: min(420px, 100%);
  max-height: min(520px, 90vh);
  margin: 0;
  padding: 16px;
  border: 1px solid #e5e6eb;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.project-selection-panel--modal .project-selection-list {
  max-height: min(280px, 45vh);
}

.project-selection-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.project-selection-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2329;
}

.project-selection-tip {
  margin-top: 2px;
  font-size: 12px;
  color: #8a8fa6;
}

.project-selection-close {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: #f3f4fb;
  color: #4e5969;
  cursor: pointer;
}

.project-selection-list {
  max-height: 220px;
  overflow-y: auto;
  display: grid;
  gap: 6px;
}

.project-selection-item {
  border: 1px solid #e5e6eb;
  background: #fff;
  border-radius: 8px;
  padding: 10px;
  text-align: left;
  cursor: pointer;
}

.project-selection-item.active {
  border-color: #0052d9;
  background: #f4f8ff;
}

.project-selection-item-name {
  font-size: 13px;
  color: #1f2329;
  font-weight: 600;
}

.project-selection-item-meta {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 4px;
  font-size: 12px;
  color: #8a8fa6;
}

.project-selection-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.project-selection-confirm {
  border: none;
  background: #0052d9;
  color: #fff;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
}

.project-selection-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ai-input-actions {
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 1;
}

.ai-file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.ai-attach-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #e5e6eb;
  color: #4e5969;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ai-attach-btn:hover {
  background: #d0d2da;
  color: #1f2329;
}

.ai-attach-btn svg {
  width: 14px;
  height: 14px;
  display: block;
  flex-shrink: 0;
}

.ai-send-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #0052d9;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ai-loading-wave {
  display: inline-flex;
  gap: 2px;
  align-items: center;
}

.ai-loading-wave i {
  width: 3px;
  height: 10px;
  border-radius: 999px;
  background: #fff;
  opacity: 0.7;
  animation: wave 0.9s infinite ease-in-out;
}

.ai-loading-wave i:nth-child(2) { animation-delay: 0.12s; }
.ai-loading-wave i:nth-child(3) { animation-delay: 0.24s; }

@keyframes wave {
  0%, 80%, 100% { transform: scaleY(0.45); opacity: 0.5; }
  40% { transform: scaleY(1); opacity: 1; }
}

.ai-send-btn:hover:not(:disabled) {
  background: #0047c5;
}

.ai-send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 992px) {
  .ai-content-side {
    position: static;
    width: 100%;
    margin-top: 12px;
  }

  .ai-panel {
    height: auto;
    max-height: 420px;
  }

  .ai-assistant-root--embedded .ai-panel {
    height: 100%;
    max-height: none;
    min-height: 0;
  }
}

/* 创建项目分屏内嵌：对话区可滚动但不显示滚动条（页面级滚动仅左侧表单） */
.ai-assistant-root--embedded .ai-chat-display {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.ai-assistant-root--embedded .ai-chat-display::-webkit-scrollbar {
  width: 0;
  height: 0;
}
<style scoped>
.ai-preview-wrap { position:relative;display:inline-block;margin:4px 0;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden; }
.ai-preview-img { max-height:100px;max-width:100%;display:block; }
.ai-preview-close { position:absolute;top:2px;right:2px;width:18px;height:18px;border:none;border-radius:50%;background:rgba(0,0,0,0.5);color:#fff;font-size:11px;cursor:pointer;display:flex;align-items:center;justify-content:center; }

:global(.ai-session-control-confirm-overlay.el-overlay) {
  z-index: 5001 !important;
}
</style>
